import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Center,
  Text,
  Flex,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  useToast,
  Checkbox,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Divider,
  Box,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  Spinner,
  Textarea,
  Tooltip,
  Grid,
  Collapse,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Portal,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BellIcon,
} from "@chakra-ui/icons";
import ReactModal from "react-modal";
import DraggableResizableModal from "../components/DraggableResizableModal";
import { useNavigate, useLocation } from "react-router-dom";
import { normalizeRole } from "../config/roles";
import {
  getSupabase,
  supabasePublic,
  supabaseSchema,
} from "../components/supabaseClient";
import SchedulesTable, { mergeSchemaResults } from "../components/SchedulesTable";
import {
  clientForSchema,
  readCacheRow,
  schemaFromReportsPath,
} from "../utils/reportsPathSchema";
import {
  byCreatedDesc,
  groupBySchema,
  isFailedSms,
  providerSchemas,
  querySmsAcrossSchemas,
  smsDbId,
  smsSchemaOf,
  tagSmsRows,
} from "../utils/smsProviders";
import { fetchUnreadMessages } from "../utils/unreadSms";
import ProvidersDropdown from "../components/ProvidersDropdown";
import { ActionMenu, ActionPanel, ToggleRow } from "../components/admin/ActionMenu";
import PatientTypeBreakdownDrawer from "../components/PatientTypeBreakdownDrawer";
import { useSelectedProviders } from "../hooks/useSelectedProviders";
import AddPatientModal from "../components/AddPatientModal";
import AppointmentInbox from "../components/appointments/AppointmentInbox";
import PatientImportModal from "../components/PatientImportModal";
import EditPatientModal from "../components/EditPatientModal";
import BulkFormGenerationModal from "../components/BulkFormGenerationModal";
import AppointmentConfirmationModal from "../components/AppointmentConfirmationModal";
import ConfirmationRemindersPanel from "../components/ConfirmationRemindersPanel";
import TriggerPreviewDialog, {
  buildReminderPreview,
  buildConfirmationPreview,
} from "../components/reminders/TriggerPreviewDialog";
import {
  getSendFormsUrl,
  colid2,
  tableName,
  prompts,
  FORM_CONFIG,
  bucketName_files,
  supabaseUrl,
  by_appointment_date_method,
  getWTFFormsConfig,
  BASE_URLS,
  getBackendURL,
  SMS_STATUS_BEFORE_GRACE_PERIOD_DAYS,
  UPGRADE_DEFAULTS,
} from "../components/env";
import { fillContactEmail } from "../utils/clinicContactEmail";
import { ADMIN_PASSWORD } from "../components/env";
import { handleFormSending, handleWelcomeSending } from "../utils/formUtils";
import { searchUsers as searchStaffUsers } from "../services/clinicComplianceService";
import { confirmBadNumbers, fetchBadNumbers } from "../utils/badNumbers";
import { summarizeConfirmationChannels, planConfirmationSend } from "../utils/confirmationChannels";
import { altPhoneFor, rowPhones, rowPhonesNormalized } from "../utils/smsPhone";
import PatientPhones from "../components/sms/PatientPhones";
import ConfirmationChannelDialog from "../components/reminders/ConfirmationChannelDialog";
import { rebuildNumberHealth } from "../utils/numberStatus";
import PhoneStatusBadge from "../components/sms/PhoneStatusBadge";
import DuplicatePatientModal from "../components/DuplicatePatientModal";
import DebouncedInput from "../components/DebouncedInput";
import FollowupMessageModal from "../components/FollowupMessageModal";
// SendFaxModal removed - now using Send tab directly instead of modal
import SendFaxTabForm from "../components/SendFaxTabForm";
import FaxFormFields from "../components/FaxFormFields";
import AddContactModal from "../components/AddContactModal";
import EditContactModal from "../components/EditContactModal";
import PatientMessagingModal from "../components/PatientMessagingModal";
import LLMModelSelectionModal from "../components/LLMModelSelectionModal";
import SearchComponent from "../components/SearchComponent";
import AutoReminderSettingsModal from "../components/AutoReminderSettingsModal";
import ComplianceAdminSettings from "../components/ComplianceAdminSettings";
import ComplianceQualityDashboard from "../components/ComplianceQualityDashboard";
import { parseComplianceSettings } from "../utils/complianceSettings";
import { isConfirmationScheduledRow } from "../utils/scheduledMessageTypes";
import { storageSchemaOf, dbForRow } from "../utils/rowSchema";
import ScheduledSMSModal from "../components/ScheduledSMSModal";
import {
  fuzzyMatchFormType,
  getFormConfig,
  getFormUrl,
  getFormMessageTemplate,
  normalizeFormTypes,
  createFormUrls,
  debugFormMapping,
  findPatientField,
  extractPatientInfo,
  debugFieldMapping,
  FORM_TYPE_MAPPINGS,
} from "../utils/fuzzyFormMapping";
import { extractAllSubmissionLinks } from "../utils/submissionUtils";
import { processGeneratedFormResponse } from "../utils/formSubmissionUtils";
import { getSignedFilesUrl } from "../utils/signedUrls";
import {
  faxDocumentKey,
  faxMatchedVisitDate,
  formatVisitDate,
  parseFaxReportPath,
  faxPatientDisplayName,
} from "../utils/faxReportPath";
import { faxSchemaTooltip, matchedTablesLabel } from "../utils/faxMatchedTables";
import FaxDateHeader from "../components/fax/FaxDateHeader";
import { useCollapsedDates } from "../hooks/useCollapsedDates";
import FaxMatchedDate from "../components/fax/FaxMatchedDate";
import FaxPendingIdentity from "../components/fax/FaxPendingIdentity";
import {
  faxFiltersHiding,
  partitionForBulkMatch,
  scrollFaxIntoView,
} from "../utils/scrollFaxIntoView";
import FaxSchemaBadges from "../components/fax/FaxSchemaBadges";
import FaxSchemaFilter from "../components/fax/FaxSchemaFilter";
import { useFaxInboxSchemas } from "../hooks/useFaxInboxSchemas";
import { useHideFloatingBubbles } from "../utils/floatingBubbles";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import { fetchViewerIanaTimezone } from "../utils/scheduleRowTimezone.js";
import {
  getMetadataColor,
  getMetadataBgColor,
  isHighlightedCategory,
  METADATA_COLORS,
} from "../utils/metadataColors.js";
import {
  isFaxFailureClassification,
  _computeExtractionStatus,
} from "../utils/faxExtractionStatus.js";
import {
  RepeatIcon,
  DeleteIcon,
  CheckCircleIcon,
  WarningIcon,
  CloseIcon,
  AddIcon,
  InfoOutlineIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  getTwilioHealthWithMessages,
  resendTwilioMessage,
  getTwilioConfiguration,
  syncTwilioMessages,
  getSyncProgress,
} from "../components/settings/twilioConfig";
import { TwilioHealthStatus } from "../components/settings";
import FailedNumbersPanel from "../components/sms/FailedNumbersPanel";
import { GoodNumbersPanel, NotTextedPanel } from "../components/sms/NumberListPanels";
import NumberHealthChanges from "../components/sms/NumberHealthChanges";
import {
  PATIENT_TABLE_CONFIRMATION,
  PATIENT_TABLE_PROGRESS_MESSAGES,
  runPatientTableJob,
} from "../utils/patientTableJob";
import { beginActivity, endActivity } from "../utils/activityTracker";
import {
  continueOcrExtractionInBatches,
  continueOcrIncompleteToast,
  continueOcrProgressLabel,
  continueOcrUpdatedTruncation,
} from "../utils/continueOcrExtraction";

// The fax view-URL endpoint now requires a verified bearer token
// (ai_assistant.get_fax_view_url).
async function authHeader() {
  try {
    const supabase = await getSupabase();
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (e) {
    console.warn("Could not read session for signed URL request:", e);
    return {};
  }
}

const getFormUrls = (userSettings = {}) => {
  return {
    NP_FORM_URL: userSettings.NP_FORM_URL || "",
    NS_FORM_URL: userSettings.NS_FORM_URL || "",
    CL1_FORM_URL: userSettings.CL1_FORM_URL || "",
    CL2_FORM_URL: userSettings.CL2_FORM_URL || "",
  };
};

// Check if a specific form URL is configured - returns the URL or null if not configured
// Falls back to default form URLs if not configured in schema_settings
const getFormUrlForType = (userSettings = {}, formType) => {
  const urls = getFormUrls(userSettings);
  let formUrl = null;

  if (formType.includes("NP")) formUrl = urls.NP_FORM_URL;
  else if (formType.includes("NS")) formUrl = urls.NS_FORM_URL;
  else if (formType.includes("CL1")) formUrl = urls.CL1_FORM_URL;
  else if (formType.includes("CL2")) formUrl = urls.CL2_FORM_URL;

  // If URL is configured in userSettings (schema_settings), use it
  if (formUrl) {
    return formUrl;
  }

  // Fall back to default form URLs from FORM_TYPE_MAPPINGS
  const config = getWTFFormsConfig();
  const normalizedType = formType.toUpperCase().trim();

  // Map form type to FORM_TYPE_MAPPINGS key
  let mappingKey = null;
  if (normalizedType.includes("NP")) mappingKey = "NP";
  else if (normalizedType.includes("NS")) mappingKey = "NS";
  else if (normalizedType.includes("CL1")) mappingKey = "CL1";
  else if (normalizedType.includes("CL2")) mappingKey = "CL2";

  if (mappingKey && FORM_TYPE_MAPPINGS[mappingKey]) {
    const defaultFormId = FORM_TYPE_MAPPINGS[mappingKey].formId;
    if (defaultFormId) {
      return `${config.baseURL}${config.formsPath}/${defaultFormId}`;
    }
  }

  return null;
};

const _STATUS_STYLES = {
  failed: { color: "red.700", bg: "red.100" },
  warning: { color: "orange.700", bg: "orange.100" },
  processing: { color: "blue.700", bg: "blue.50" },
};

// Renders one of three things, picked in order:
//   - red failure badge for retired/inferred failures
//   - blue/orange "extraction in progress / non-fatal error" badge for
//     unmatched faxes that haven't extracted text yet
//   - teal "Category: <classification>" pill (the original normal case)
const FaxClassificationBadge = ({ fax }) => {
  if (!fax) return null;

  const isUnmatchedWithoutText =
    fax.is_faxed_report &&
    fax.is_unmatched &&
    !(fax.has_full_text || fax.full_text);

  if (isFaxFailureClassification(fax.classification) || isUnmatchedWithoutText) {
    const status = _computeExtractionStatus(fax);
    if (status) {
      const style = _STATUS_STYLES[status.kind] || _STATUS_STYLES.failed;
      const badge = (
        <Text
          fontSize="xs"
          color={style.color}
          fontWeight="bold"
          bg={style.bg}
          px={2}
          py={1}
          borderRadius="md"
        >
          {status.label}
        </Text>
      );
      return status.tooltip ? (
        <Tooltip label={status.tooltip} hasArrow placement="top">
          {badge}
        </Tooltip>
      ) : (
        badge
      );
    }
  }

  if (!fax.classification) return null;
  return (
    <HStack spacing={2}>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        Category:
      </Text>
      <Text
        fontSize="xs"
        color="teal.600"
        fontWeight="bold"
        bg="teal.50"
        px={2}
        py={1}
        borderRadius="md"
      >
        {fax.classification}
      </Text>
    </HStack>
  );
};

const DISABLE_DUPLICATE = true; // Toggle this to enable/disable duplicate functionality

// Debug flag - set to true to enable verbose logging (set false for production/performance)
const DEBUG_LOGGING = false;
const debugLog = (...args) => {
  if (DEBUG_LOGGING) {
    console.log(...args);
  }
};

// Function to get default date string (today)
const getDefaultDateString = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [reminderViewerTimezone, setReminderViewerTimezone] =
    useState("America/New_York");
  useEffect(() => {
    (async () => {
      const supabase = await getSupabase();
      setReminderViewerTimezone(await fetchViewerIanaTimezone(supabase));
    })();
  }, []);

  const [patients, setPatients] = useState([]);
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isBulkFormModalOpen, setIsBulkFormModalOpen] = useState(false);
  const [isLLMModelModalOpen, setIsLLMModelModalOpen] = useState(false);
  const [isSendSMSModalOpen, setIsSendSMSModalOpen] = useState(false);
  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false);
  const [followupPatientData, setFollowupPatientData] = useState(null);
  const [isAddDNTModalOpen, setIsAddDNTModalOpen] = useState(false);
  const [dntPhoneInput, setDntPhoneInput] = useState("");
  const [dntSubmitting, setDntSubmitting] = useState(false);
  const [dntEntries, setDntEntries] = useState([]);
  const [dntLoading, setDntLoading] = useState(false);
  const [dntRemovingPhone, setDntRemovingPhone] = useState(null);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [isSendingWelcome, setIsSendingWelcome] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [isSMSStatusModalOpen, setIsSMSStatusModalOpen] = useState(false);
  const [isTwilioHealthModalOpen, setIsTwilioHealthModalOpen] = useState(false);
  const [smsHealthData, setSmsHealthData] = useState(null);
  const [failedSMS, setFailedSMS] = useState([]);
  const [allSMSForDate, setAllSMSForDate] = useState([]);
  const [loadingSMSStatus, setLoadingSMSStatus] = useState(false);
  const [loadingSMSByAppointmentDate, setLoadingSMSByAppointmentDate] =
    useState(false);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [allSMSForAppointmentDate, setAllSMSForAppointmentDate] = useState([]);
  const [failedSMSForAppointmentDate, setFailedSMSForAppointmentDate] =
    useState([]);
  const [selectedAppointmentDateSMS, setSelectedAppointmentDateSMS] = useState(
    {},
  );
  const [
    selectedAppointmentDateErrorCodes,
    setSelectedAppointmentDateErrorCodes,
  ] = useState([]); // Error codes filter for appointment date tab
  const [
    selectedAppointmentDateSMSForResend,
    setSelectedAppointmentDateSMSForResend,
  ] = useState({}); // Selected SMS for resend in appointment date tab
  const [activeSMSTab, setActiveSMSTab] = useState(0); // 0 = Created At tab, 1 = Appointment Date tab, 2 = Failed numbers, 3 = Good numbers, 4 = Not texted
  const [resendingFailedSMS, setResendingFailedSMS] = useState(false);
  const [selectedSMSDate, setSelectedSMSDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [selectedError, setSelectedError] = useState(null);
  const [twilioErrorDetails, setTwilioErrorDetails] = useState(null);
  const [loadingTwilioDetails, setLoadingTwilioDetails] = useState(false);
  const [syncingSMSMessages, setSyncingSMSMessages] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const {
    isOpen: isErrorDialogOpen,
    onOpen: onErrorDialogOpen,
    onClose: onErrorDialogClose,
  } = useDisclosure();
  const [selectedErrorCodes, setSelectedErrorCodes] = useState([]);
  const [editedSMS, setEditedSMS] = useState({}); // Store edited phone numbers and messages: { messageId: { to: "...", message: "..." } }
  const [editingSMSId, setEditingSMSId] = useState(null); // Track which SMS is being edited
  const [splitSMS, setSplitSMS] = useState({}); // Store split preference for each SMS: { messageId: true/false }
  const [splitAllMessages, setSplitAllMessages] = useState(false); // Global option to split all messages
  const [selectedSMSForResend, setSelectedSMSForResend] = useState({}); // Store selected SMS for resend: { messageId: true/false }
  const [failedSMSCountsByDate, setFailedSMSCountsByDate] = useState({}); // Store failed SMS counts by date: { "YYYY-MM-DD": count }
  const [totalSMSCountsByDate, setTotalSMSCountsByDate] = useState({}); // Store total SMS counts by date: { "YYYY-MM-DD": count }
  const [selectedSMSPatient, setSelectedSMSPatient] = useState(null); // Store selected patient for SMS operations
  const [categorizedSMSMessages, setCategorizedSMSMessages] = useState({
    categorized: {},
    uncategorized: [],
  }); // Categorized SMS messages for selected patient
  const [expandedSMSAppointments, setExpandedSMSAppointments] = useState({}); // Expanded state for SMS appointment sections
  const [loadingSMSCategorization, setLoadingSMSCategorization] =
    useState(false); // Loading state for SMS categorization
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // Store current calendar month for navigation
  const [loadingCalendarData, setLoadingCalendarData] = useState(false); // Loading state for calendar data
  const [appointmentCalendarMonth, setAppointmentCalendarMonth] = useState(
    new Date(),
  ); // Store current calendar month for appointment date navigation
  const [
    failedSMSCountsByAppointmentDate,
    setFailedSMSCountsByAppointmentDate,
  ] = useState({}); // Store failed SMS counts by appointment date: { "YYYY-MM-DD": count }
  const [totalSMSCountsByAppointmentDate, setTotalSMSCountsByAppointmentDate] =
    useState({}); // Store total SMS counts by appointment date: { "YYYY-MM-DD": count }
  const [loadingAppointmentCalendarData, setLoadingAppointmentCalendarData] =
    useState(false); // Loading state for appointment calendar data
  // Cache for preloaded SMS messages grouped by appointment date
  const [appointmentDateSmsCache, setAppointmentDateSmsCache] = useState({});
  // Track which month has been preloaded to avoid re-preloading
  const [preloadedAppointmentMonth, setPreloadedAppointmentMonth] =
    useState(null);
  // Current appointment month for tracking preloading
  const [currentAppointmentMonth, setCurrentAppointmentMonth] = useState(
    new Date(),
  );
  const {
    isOpen: isEditSMSModalOpen,
    onOpen: onEditSMSModalOpen,
    onClose: onEditSMSModalClose,
  } = useDisclosure();
  const [selectedLLMModel, setSelectedLLMModel] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sendFormsButtonText, setSendFormsButtonText] = useState("Send Forms");
  const [isSendingForms, setIsSendingForms] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const [generatedForm, setGeneratedForm] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new URLSearchParams(location.search).get("date")
      ? new URLSearchParams(location.search).get("date")
      : getDefaultDateString(),
  );
  const {
    isOpen: isSendFormOpen,
    onOpen: onSendFormOpen,
    onClose: onSendFormClose,
  } = useDisclosure();
  const {
    isOpen: isReportModalOpen,
    onOpen: onReportModalOpen,
    onClose: onReportModalClose,
  } = useDisclosure();
  const [sendFormsReport, setSendFormsReport] = useState({
    successful: [],
    skipped: [],
    failed: [],
  });
  const [reminderReport, setReminderReport] = useState({
    successful: [],
    skipped: [],
    failed: [],
  });
  // Auto-reminder report state (for poll-and-schedule endpoint)
  const [autoReminderReport, setAutoReminderReport] = useState({
    scheduled: [],
    skipped: [],
  });
  const {
    isOpen: isReminderReportModalOpen,
    onOpen: onReminderReportModalOpen,
    onClose: onReminderReportModalClose,
  } = useDisclosure();
  // Auto-reminder report modal disclosure
  const {
    isOpen: isAutoReminderReportModalOpen,
    onOpen: onAutoReminderReportModalOpen,
    onClose: onAutoReminderReportModalClose,
  } = useDisclosure();
  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onClose: onPasswordClose,
  } = useDisclosure();
  const cancelRef = useRef();
  const schedulesTableActionsRef = useRef(null); // SchedulesTable's imperative actions (openReports)
  const faxFormFieldsRef = useRef(); // Ref for FaxFormFields component in Send tab
  const searchQueryRef = useRef(); // Ref for search input to avoid re-renders during typing
  const searchPatientNameRef = useRef(); // Ref for patient name filter
  const [formType, setFormType] = useState([]);
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Role string (not just the isAdmin flag) — ProvidersDropdown hides itself
  // for anyone without clinical-admin permissions.
  const [userRole, setUserRole] = useState(null);
  // Providers the user has picked in the dropdown (shared with Schedules /
  // Patients via localStorage). `providersKey` is the stable primitive the
  // fetch effect depends on, so a new array identity per render can't cause a
  // refetch loop — same pattern as SchedulesTable.
  const { selected: selectedProviders, homeSchema } = useSelectedProviders();
  const providersKey = (selectedProviders || []).join(",");
  // SMS Status (every tab) covers every selected provider, not just the
  // viewer's own schema — see utils/smsProviders for how rows are merged.
  const smsStatusSchemas = providerSchemas(selectedProviders, homeSchema, supabaseSchema);
const smsPreloadGen = useRef(0);
  // "Refresh DB" in SMS Status: rebuilds sms_number_health, shows which numbers
  // changed (NumberHealthChanges), then remounts the Failed / Good / Not texted
  // tabs (via their key) so each one reloads.
  const [rebuildingNumberHealth, setRebuildingNumberHealth] = useState(false);
  const [numberHealthGen, setNumberHealthGen] = useState(0);
  const [numberHealthResult, setNumberHealthResult] = useState(null);
  const handleRebuildNumberHealth = async () => {
    setRebuildingNumberHealth(true);
    try {
      const data = await rebuildNumberHealth(smsStatusSchemas);
      const { results = [], numbers = 0, total_changes: changed = 0 } = data;
      setNumberHealthResult(data);
      const failed = results.filter((r) => r.error);
      toast({
        title: failed.length ? "Number status partly refreshed" : "Number status refreshed",
        description:
          `${numbers} number${numbers === 1 ? "" : "s"} checked, ${changed} changed` +
          (failed.length ? `. Failed: ${failed.map((r) => `${r.schema} (${r.error})`).join(", ")}` : ""),
        status: failed.length ? "warning" : "success",
        duration: failed.length ? 8000 : 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not refresh number status",
        description: error?.message || "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setNumberHealthGen((g) => g + 1);
      setRebuildingNumberHealth(false);
    }
  };
  // Breakout panel: the selected day's type mix.
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [contactMethods, setContactMethods] = useState({
    email: false,
    phone: false,
  });
  const [splitMessage, setSplitMessage] = useState(false);
  // Store contact info for each selected patient
  const [selectedPatientsContact, setSelectedPatientsContact] = useState({});
  const {
    isOpen: isSettingsOpen,
    onOpen: onSettingsOpen,
    onClose: onSettingsClose,
  } = useDisclosure();
  const [userSettings, setUserSettings] = useState({});
  const [aiChatEnabled, setAiChatEnabled] = useState(true);
  const [loadingAiChatSetting, setLoadingAiChatSetting] = useState(false);
  // Auto-reminders state for 1-day before appointment reminders
  const [autoRemindersEnabled, setAutoRemindersEnabled] = useState(false);
  const [loadingAutoRemindersSetting, setLoadingAutoRemindersSetting] =
    useState(false);
  const [complianceEnabled, setComplianceEnabled] = useState(true);
  const [loadingComplianceSetting, setLoadingComplianceSetting] =
    useState(false);
  // Auto SMS resend state for failed messages with exceeded length error
  const [autoSmsEnabled, setAutoSmsEnabled] = useState(false);
  const [loadingAutoSmsSetting, setLoadingAutoSmsSetting] = useState(false);
  // Per-schema SMS master switch: when off, ALL outbound SMS is blocked for this schema
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [loadingSmsEnabledSetting, setLoadingSmsEnabledSetting] = useState(false);
  // "Upgrade to Pro" button on the Schedules page — per-schema on/off + link,
  // falling back to UPGRADE_DEFAULTS (global env) when unset for this schema.
  const [upgradeEnabled, setUpgradeEnabled] = useState(UPGRADE_DEFAULTS.enabled);
  const [loadingUpgradeSetting, setLoadingUpgradeSetting] = useState(false);
  const [upgradeLink, setUpgradeLink] = useState(UPGRADE_DEFAULTS.link);
  const [upgradeLinkDraft, setUpgradeLinkDraft] = useState(UPGRADE_DEFAULTS.link);
  const {
    isOpen: isUpgradeLinkOpen,
    onOpen: onUpgradeLinkOpen,
    onClose: onUpgradeLinkClose,
  } = useDisclosure();
  // Custom SMS character limit setting (default 1000) - stored in localStorage
  const [customSmsCharacterLimit, setCustomSmsCharacterLimit] = useState(() => {
    const stored = localStorage.getItem("custom_sms_character_limit");
    return stored ? parseInt(stored, 10) : 1000;
  });
  const [loadingCustomSmsLimit, setLoadingCustomSmsLimit] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [isConfirmationHubOpen, setIsConfirmationHubOpen] = useState(false);
  // Send Reminders modal: 0 = form reminders (the original modal), 1 = the
  // confirmation reminders copy of it.
  const [reminderModalTab, setReminderModalTab] = useState(0);
  const [isTriggeringAutoConfirmations, setIsTriggeringAutoConfirmations] = useState(false);
  // Bumped to make the confirmations tab reload its settings and queue.
  const [confirmationPanelRefresh, setConfirmationPanelRefresh] = useState(0);
  // The Trigger buttons preview before they run: which trigger is being
  // previewed ("reminders" | "confirmations" | null), the dry-run result
  // shaped for the dialog, and its loading/error state.
  const [triggerPreview, setTriggerPreview] = useState({ kind: null, loading: false, error: null, preview: null });
  const [isSendingConfirmations, setIsSendingConfirmations] = useState(false);
  // Set while the "text, email, or both?" dialog is open (summarizeConfirmationChannels).
  const [confirmationChannelSummary, setConfirmationChannelSummary] = useState(null);
  // Store schedule data from SchedulesTable to fix synchronization issues
  const [currentScheduleData, setCurrentScheduleData] = useState({});
  // Fax inbox state
  const {
    isOpen: isFaxInboxOpen,
    onOpen: onFaxInboxOpen,
    onClose: onFaxInboxClose,
  } = useDisclosure();
  const [allFaxes, setAllFaxes] = useState([]);
  const [orphanedFilePaths, setOrphanedFilePaths] = useState(new Set());
  const [loadingFaxes, setLoadingFaxes] = useState(false);
  const [faxFilter, setFaxFilter] = useState("all"); // 'all', 'unmatched', 'matched'
  const [reportTypeFilter, setReportTypeFilter] = useState("all"); // 'all', 'fax', 'reports' (non-fax)
  const [taskFaxes, setTaskFaxes] = useState([]); // Faxes moved to Tasks tab
  const [spamFaxes, setSpamFaxes] = useState([]); // Spam faxes
  const [loadingSpamFaxes, setLoadingSpamFaxes] = useState(false);
  const [faxNumber, setFaxNumber] = useState(""); // Fax number from backend
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0); // Total unread messages count for notification icon
  const [loadingUnreadMessages, setLoadingUnreadMessages] = useState(false);
  const [unreadMessagesList, setUnreadMessagesList] = useState([]); // List of unread messages for popover
  // State for messaging modal when clicking on unread notification
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const [selectedPatientForMessaging, setSelectedPatientForMessaging] = useState(null);
  const [selectedSpamFaxes, setSelectedSpamFaxes] = useState(new Set()); // Selected spam faxes for mass operations
  const [markingAsSpam, setMarkingAsSpam] = useState(false);
  const [deletingSpamFaxes, setDeletingSpamFaxes] = useState(false);
  const [checkingForSpam, setCheckingForSpam] = useState(false); // Checking selected faxes for spam
  const [movingSpamToInbox, setMovingSpamToInbox] = useState(false); // Moving selected spam faxes to inbox
  const [selectedFaxForTask, setSelectedFaxForTask] = useState(null); // Fax selected for task assignment
  const [myAssignedTasks, setMyAssignedTasks] = useState([]); // Tasks assigned to current user
  const [currentUserId, setCurrentUserId] = useState(null); // Current user ID
  const [currentUserName, setCurrentUserName] = useState(null); // Current user name
  const [currentUserEmail, setCurrentUserEmail] = useState(null); // Current user email
  const [selectedFaxForAssignment, setSelectedFaxForAssignment] =
    useState(null);
  const [assigningFax, setAssigningFax] = useState(false);
  const [selectedPatientForAssignment, setSelectedPatientForAssignment] =
    useState(null);
  const [selectedFaxForRename, setSelectedFaxForRename] = useState(null);
  const [newFaxName, setNewFaxName] = useState("");
  const [renamingFax, setRenamingFax] = useState(false);
  const [selectedFaxForClassification, setSelectedFaxForClassification] =
    useState(null);
  const [newFaxClassification, setNewFaxClassification] = useState("");
  const [changingFaxClassification, setChangingFaxClassification] =
    useState(false);
  const {
    isOpen: isRenameFaxOpen,
    onOpen: onRenameFaxOpen,
    onClose: onRenameFaxClose,
  } = useDisclosure();
  const {
    isOpen: isChangeFaxClassificationOpen,
    onOpen: onChangeFaxClassificationOpen,
    onClose: onChangeFaxClassificationClose,
  } = useDisclosure();
  const [selectedFaxes, setSelectedFaxes] = useState(new Set()); // For checkboxes
  const [matchingFaxes, setMatchingFaxes] = useState(null); // For matching operation - stores file_path of fax being matched
  const [rematchingFax, setRematchingFax] = useState(null); // Rematch (unmatch+match) of a single fax — stores the original file_path in progress
  const [bulkRematching, setBulkRematching] = useState(false); // Bulk Rematch of the checked faxes in progress
  const [confirmationModal, setConfirmationModal] = useState(null); // { file_path, extracted_name, matched_patient }
  const {
    isOpen: isConfirmMatchOpen,
    onOpen: onConfirmMatchOpen,
    onClose: onConfirmMatchClose,
  } = useDisclosure();
  const [deletingFax, setDeletingFax] = useState(false);
  const [faxToDelete, setFaxToDelete] = useState(null);
  const [massAssigning, setMassAssigning] = useState(false);
  const [massDeleting, setMassDeleting] = useState(false);
  const {
    isOpen: isDeleteFaxOpen,
    onOpen: onDeleteFaxOpen,
    onClose: onDeleteFaxClose,
  } = useDisclosure();
  const {
    isOpen: isMassAssignOpen,
    onOpen: onMassAssignOpen,
    onClose: onMassAssignClose,
  } = useDisclosure();
  const {
    isOpen: isMassDeleteOpen,
    onOpen: onMassDeleteOpen,
    onClose: onMassDeleteClose,
  } = useDisclosure();
  // Reminder functionality
  const {
    isOpen: isReminderModalOpen,
    onOpen: onReminderModalOpen,
    onClose: onReminderModalClose,
  } = useDisclosure();
  const [patientsWithoutForms, setPatientsWithoutForms] = useState([]);
  const [selectedReminderPatients, setSelectedReminderPatients] = useState({});
  const [reminderContactMethods, setReminderContactMethods] = useState({
    email: false,
    phone: false,
  });
  /** When true, SMS reminders use split path (same as Send Forms). Ignored for email-only. */
  const [reminderSplitSms, setReminderSplitSms] = useState(false);
  const [scheduleReminderOneDayBefore, setScheduleReminderOneDayBefore] =
    useState(false);
  const [reminderScheduledDate, setReminderScheduledDate] = useState("");
  const [reminderScheduledTime, setReminderScheduledTime] = useState("09:00");

  // Independent manual reminder states
  const [manualReminderContactMethods, setManualReminderContactMethods] = useState({
    email: false,
    phone: false,
  });
  const [manualScheduleReminder, setManualScheduleReminder] = useState(false);
  // List of manually-added scheduled reminders. Each entry: { id, date: 'YYYY-MM-DD', time: 'HH:MM' }.
  // Replaces the single-date `manualReminderScheduledDate`/`manualReminderScheduledTime` so admins
  // can queue multiple reminders per patient batch (e.g. 1 week before + 1 day before). All entries
  // must have different dates (validated client-side AND server-side).
  const [manualScheduledReminders, setManualScheduledReminders] = useState([]);
  const [manualReminderPatientTypeFilter, setManualReminderPatientTypeFilter] = useState([]);
  const [pendingScheduledReminders, setPendingScheduledReminders] = useState(
    [],
  );
  const [
    loadingPendingScheduledReminders,
    setLoadingPendingScheduledReminders,
  ] = useState(false);
  const [cancellingScheduledReminderId, setCancellingScheduledReminderId] =
    useState(null);
  const [cancellingAllScheduledReminders, setCancellingAllScheduledReminders] =
    useState(false);
  const [reminderMessageTemplate, setReminderMessageTemplate] =
    useState("default");
  // Preview state for welcome message confirmation
  const [welcomePreviewMessage, setWelcomePreviewMessage] = useState(null);
  // Preview state for send forms
  const [showFormsPreview, setShowFormsPreview] = useState(false);
  const [formsPreviewData, setFormsPreviewData] = useState(null);
  const {
    isOpen: isWelcomePreviewOpen,
    onOpen: onWelcomePreviewOpen,
    onClose: onWelcomePreviewClose,
  } = useDisclosure();
  // Preview state for scheduled reminder messages
  const [previewReminderMessage, setPreviewReminderMessage] = useState(null);
  const {
    isOpen: isPreviewReminderModalOpen,
    onOpen: onPreviewReminderModalOpen,
    onClose: onPreviewReminderModalClose,
  } = useDisclosure();
  // Auto Reminder Settings Modal disclosure
  const {
    isOpen: isAutoReminderSettingsOpen,
    onOpen: onAutoReminderSettingsOpen,
    onClose: onAutoReminderSettingsClose,
  } = useDisclosure();
  const {
    isOpen: isComplianceSettingsOpen,
    onOpen: onComplianceSettingsOpen,
    onClose: onComplianceSettingsClose,
  } = useDisclosure();
  const {
    isOpen: isComplianceDashboardOpen,
    onOpen: onComplianceDashboardOpen,
    onClose: onComplianceDashboardClose,
  } = useDisclosure();
  // Scheduled SMS Modal disclosure
  const {
    isOpen: isScheduledSMSModalOpen,
    onOpen: onScheduledSMSModalOpen,
    onClose: onScheduledSMSModalClose,
  } = useDisclosure();
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [isTriggeringAutoReminders, setIsTriggeringAutoReminders] =
    useState(false);
  const [reminderStatus, setReminderStatus] = useState({
    total_patients: 0,
    patients_with_forms: 0,
    patients_without_forms: 0,
    completion_rate: 0,
  });
  const [reminderPatientTypeFilter, setReminderPatientTypeFilter] = useState(
    [],
  ); // Array of selected patient types to filter by
  const [reminderDaysBeforeAppointment, setReminderDaysBeforeAppointment] =
    useState(1); // Days before appointment to send reminders (default: 1 day)
  const [userSchema, setUserSchema] = useState("public"); // Default to 'public' schema
  // Appointment requests + availability. Everything lives in
  // components/appointments/*; this page only owns the open/closed flag.
  const [isAppointmentInboxOpen, setIsAppointmentInboxOpen] = useState(false);
  // Agent permissions (components/agents/*); this page only owns the open/closed flag.
  const [userSchemaLoaded, setUserSchemaLoaded] = useState(false); // Track when schema is loaded from DB
  // Tasks tab state
  const [activeFaxTab, setActiveFaxTab] = useState(0); // 0 = Inbox, 1 = Tasks
  const [processingTasks, setProcessingTasks] = useState({}); // Track which tasks are processing
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPatientName, setSearchPatientName] = useState("");
  const [searchReportType, setSearchReportType] = useState("");
  const [searchReportTypeFilter, setSearchReportTypeFilter] = useState("all"); // 'all', 'fax', 'reports'
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFaxes, setSearchingFaxes] = useState(false);
  const [assignTaskUserId, setAssignTaskUserId] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [selectedUserForTask, setSelectedUserForTask] = useState(null);
  const {
    isOpen: isAssignTaskModalOpen,
    onOpen: onAssignTaskModalOpen,
    onClose: onAssignTaskModalClose,
  } = useDisclosure();
  const {
    isOpen: isViewTextModalOpen,
    onOpen: onViewTextModalOpen,
    onClose: onViewTextModalClose,
  } = useDisclosure();
  const [viewingTextData, setViewingTextData] = useState(null); // { file_path, full_text, filename }
  const [isReExtractingText, setIsReExtractingText] = useState(false); // Track re-extraction in modal
  // Truncation confirmation state
  const [truncationInfo, setTruncationInfo] = useState(null); // { file_path, filename, total_pages, pages_processed, remaining_pages }
  const [isContinuingExtraction, setIsContinuingExtraction] = useState(false);
  const [continueProgress, setContinueProgress] = useState(null);
  const {
    isOpen: isTruncationModalOpen,
    onOpen: onTruncationModalOpen,
    onClose: onTruncationModalClose,
  } = useDisclosure();

  // Helper functions for localStorage truncation tracking
  const TRUNCATION_STORAGE_KEY = "pending_truncations";
  const DECLINED_TRUNCATION_KEY = "declined_truncations";

  const getPendingTruncations = () => {
    try {
      const stored = localStorage.getItem(TRUNCATION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error reading truncation storage:", e);
      return {};
    }
  };

  const getDeclinedTruncations = () => {
    try {
      const stored = localStorage.getItem(DECLINED_TRUNCATION_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error reading declined truncation storage:", e);
      return {};
    }
  };

  const savePendingTruncation = (filePath, truncationData) => {
    try {
      const pending = getPendingTruncations();
      pending[filePath] = truncationData;
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      console.log(
        "Saved pending truncation to localStorage:",
        filePath,
        truncationData,
      );
    } catch (e) {
      console.error("Error saving truncation storage:", e);
    }
  };

  const saveDeclinedTruncation = (filePath) => {
    try {
      const declined = getDeclinedTruncations();
      declined[filePath] = true;
      localStorage.setItem(DECLINED_TRUNCATION_KEY, JSON.stringify(declined));
      console.log("Saved declined truncation to localStorage:", filePath);
    } catch (e) {
      console.error("Error saving declined truncation storage:", e);
    }
  };

  const hasDeclinedTruncation = (filePath) => {
    const declined = getDeclinedTruncations();
    return !!declined[filePath];
  };

  const clearPendingTruncation = (filePath) => {
    try {
      const pending = getPendingTruncations();
      delete pending[filePath];
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      console.log("Cleared pending truncation from localStorage:", filePath);
    } catch (e) {
      console.error("Error clearing truncation storage:", e);
    }
  };

  // Send fax state (modal removed - now using Send tab directly)
  const [selectedFaxForSending, setSelectedFaxForSending] = useState(null);
  const [sendingFax, setSendingFax] = useState(false);
  const [sentFaxes, setSentFaxes] = useState([]);
  const [loadingSentFaxes, setLoadingSentFaxes] = useState(false);
  const [sendFaxForm, setSendFaxForm] = useState({
    fax_number: "1", // Default to "1" for US country code (users can edit for international)
    recipient_name: "",
    sender_name: "",
    sender_fax: "",
    sender_email: "",
    subject: "",
    cover_page: false,
    cover_page_text: "",
  });
  // SRFax authorized emails from backend
  const [srfaxAuthEmails, setSrfaxAuthEmails] = useState([]);

  // Fax contacts for quick selection
  const [faxContacts, setFaxContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [updatingContact, setUpdatingContact] = useState(false);

  /** Send Fax tab: expand/collapse attachment (upload + inbox) section */
  const [sendFaxAttachmentsExpanded, setSendFaxAttachmentsExpanded] =
    useState(false);
  // Collapsed date groups, one set per list so the inbox and Spam don't share
  // state. Persisted — the inbox refetches on a timer and after every
  // match/assign, and an unpersisted collapse would spring open under the user.
  // The picker owns the selection; the fetch reads it through a ref so
  // fetchAllReports keeps a stable identity for the many effects that call it.
  const { selected: faxInboxSchemas } = useFaxInboxSchemas();
  const faxInboxSchemasRef = useRef(faxInboxSchemas);
  useEffect(() => {
    faxInboxSchemasRef.current = faxInboxSchemas;
  }, [faxInboxSchemas]);
  // The floating Reports/Record/Chat bubbles overlap this modal's footer,
  // so they step aside while it is open.
  useHideFloatingBubbles(!!selectedFaxForAssignment);
  const inboxDates = useCollapsedDates("inbox");
  // The fax to return to once the inbox has RELOADED.
  //
  // Held as a DOCUMENT key (the file name), because matching renames the file
  // and the path that identified the row before the action is gone after it.
  // `since` is the fax list as it was when the action started: until that array
  // is replaced the action has not landed, and moving the page before then
  // would yank it out from under the click.
  const [pendingFocus, setPendingFocus] = useState(null);
  const spamDates = useCollapsedDates("spam");
  const [isBuildingPatientTable, setIsBuildingPatientTable] = useState(false);
  const [isPatientTableConfirmOpen, setIsPatientTableConfirmOpen] =
    useState(false);
  const [isPatientImportOpen, setIsPatientImportOpen] = useState(false);
  const [patientTableProgressIndex, setPatientTableProgressIndex] = useState(0);

  const closePatientTableConfirm = () => {
    if (!isBuildingPatientTable) {
      setIsPatientTableConfirmOpen(false);
    }
  };

  useEffect(() => {
    if (!isBuildingPatientTable) {
      setPatientTableProgressIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setPatientTableProgressIndex((current) =>
        Math.min(current + 1, PATIENT_TABLE_PROGRESS_MESSAGES.length - 1),
      );
    }, 2500);

    return () => window.clearInterval(intervalId);
  }, [isBuildingPatientTable]);

  const handleBuildPatientTable = async () => {
    setPatientTableProgressIndex(0);
    setIsBuildingPatientTable(true);
    try {
      const result = await runPatientTableJob({
        backendUrl: getBackendURL(),
        schema: supabaseSchema || "public",
      });
      const processed = result?.summary?.total_visit_rows_processed ?? 0;
      const withEhrId = result?.inserted?.patients_with_ehrid ?? 0;
      const withoutEhrId = result?.inserted?.patients_no_ehrid ?? 0;

      toast({
        title: "Patient table built",
        description: `Processed ${processed} visits. Inserted ${withEhrId} EHR patients and ${withoutEhrId} non-EHR patients.`,
        status: "success",
        duration: 7000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error building patient table:", error);
      toast({
        title: "Patient table failed",
        description: error.message || "Failed to build patient table",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsBuildingPatientTable(false);
      setIsPatientTableConfirmOpen(false);
    }
  };

  // Fetch SRFax authorized emails from backend config
  useEffect(() => {
    const fetchSrfaxAuthEmails = async () => {
      try {
        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;

        const response = await fetch(`${baseUrl}/api/config/`);
        if (response.ok) {
          const data = await response.json();
          if (
            data.srfaxAuthEmails &&
            Array.isArray(data.srfaxAuthEmails) &&
            data.srfaxAuthEmails.length > 0
          ) {
            setSrfaxAuthEmails(data.srfaxAuthEmails);
            // Set default email to first in list
            setSendFaxForm((prev) => ({
              ...prev,
              sender_email: data.srfaxAuthEmails[0],
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching SRFax authorized emails:", error);
      }
    };

    fetchSrfaxAuthEmails();
  }, []);

  // Fetch AI Chat settings on mount
  useEffect(() => {
    fetchAiChatSetting();
    fetchComplianceSetting();
    fetchSmsEnabledSetting();
    fetchUpgradeSetting();
  }, []);

  useEffect(() => {
    if (!isComplianceSettingsOpen) {
      fetchComplianceSetting();
    }
  }, [isComplianceSettingsOpen]);

  // Fetch fax contacts for the current user
  const fetchFaxContacts = async () => {
    if (!currentUserId) return;

    setLoadingContacts(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Include schema parameter to filter contacts by clinic
      const response = await fetch(
        `${baseUrl}/api/fax/contacts/?user_id=${currentUserId}&schema=${encodeURIComponent(userSchema)}`,
      );
      if (response.ok) {
        const data = await response.json();
        if (data.contacts) {
          setFaxContacts(data.contacts);
        }
      }
    } catch (error) {
      console.error("Error fetching fax contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Fetch contacts when fax inbox opens and user is authenticated
  useEffect(() => {
    if (isFaxInboxOpen && currentUserId) {
      fetchFaxContacts();
    }
  }, [isFaxInboxOpen, currentUserId]);

  // Add a new fax contact
  const handleAddContact = async (contactData) => {
    if (!currentUserId) {
      toast({
        title: "Error",
        description: "You must be logged in to add contacts",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!contactData.name.trim() || !contactData.fax_number.trim()) {
      toast({
        title: "Error",
        description: "Contact name and fax number are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSavingContact(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/contacts/add/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: currentUserId,
          name: contactData.name.trim(),
          fax_number: contactData.fax_number.trim(),
          business_type: contactData.business_type,
          notes: contactData.notes.trim(),
          address: contactData.address.trim(),
          phone_number: contactData.phone_number.trim(),
          schema: userSchema, // Include schema to isolate contacts by clinic
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast({
            title: "Contact Added",
            description: `${contactData.name} has been added to your contacts`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          // Refresh contacts list
          fetchFaxContacts();
          // Close modal (form reset is handled by the modal component)
          setIsAddContactOpen(false);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add contact");
      }
    } catch (error) {
      console.error("Error adding contact:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add contact",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingContact(false);
    }
  };

  // Update an existing fax contact
  const handleUpdateContact = async (contactId, contactData) => {
    if (!currentUserId) {
      toast({
        title: "Error",
        description: "You must be logged in to update contacts",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUpdatingContact(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/fax/contacts/${contactId}/update/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: currentUserId,
            name: contactData.name.trim(),
            fax_number: contactData.fax_number.trim(),
            business_type: contactData.business_type,
            notes: contactData.notes.trim(),
            address: contactData.address.trim(),
            phone_number: contactData.phone_number.trim(),
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast({
            title: "Contact Updated",
            description: `${contactData.name} has been updated`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          fetchFaxContacts();
          setIsEditContactOpen(false);
          setEditingContact(null);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update contact");
      }
    } catch (error) {
      console.error("Error updating contact:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update contact",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingContact(false);
    }
  };

  // Handle delete from edit modal
  const handleDeleteFromEditModal = async (contactId, contactName) => {
    await handleDeleteContact(contactId, contactName);
    setIsEditContactOpen(false);
    setEditingContact(null);
  };

  // Delete a fax contact
  const handleDeleteContact = async (contactId, contactName) => {
    if (!currentUserId) return;

    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/fax/contacts/${contactId}/delete/?user_id=${currentUserId}`,
        { method: "DELETE" },
      );

      if (response.ok) {
        toast({
          title: "Contact Deleted",
          description: `${contactName} has been removed`,
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        fetchFaxContacts();
      } else {
        throw new Error("Failed to delete contact");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast({
        title: "Error",
        description: "Failed to delete contact",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Select a contact and fill in the fax form
  const handleSelectContact = (contact) => {
    // Update the isolated FaxFormFields component if available
    if (faxFormFieldsRef.current) {
      faxFormFieldsRef.current.setFaxNumber(contact.fax_number);
      faxFormFieldsRef.current.setRecipientName(contact.name);
    }
    // Also update legacy state for backwards compatibility
    setSendFaxForm((prev) => ({
      ...prev,
      fax_number: contact.fax_number,
      recipient_name: contact.name,
    }));
    toast({
      title: "Contact Selected",
      description: `${contact.name} - ${contact.fax_number}`,
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const isFFPatient = useMemo(() => {
    if (!selectedPatient) return false;
    const patientType =
      findPatientField(selectedPatient, "type")?.toUpperCase() || "";
    return patientType === "FFP" || patientType === "FFS";
  }, [selectedPatient]);

  const shouldShowFFPOptions = useMemo(() => {
    const selectedIds = Object.keys(selectedRows).filter(
      (id) => selectedRows[id],
    );

    // If no multi-selection, fall back to single selectedPatient check
    if (selectedIds.length === 0) {
      if (!selectedPatient) return false;
      const type =
        findPatientField(selectedPatient, "type")?.toUpperCase() || "";
      return type.includes("FF");
    }

    // Check if ALL selected patients are FF
    for (const id of selectedIds) {
      const patient =
        currentScheduleData[id] || patients.find((p) => p.id === id);
      if (!patient) continue;

      const type = findPatientField(patient, "type")?.toUpperCase() || "";
      // If we find any patient that is NOT an FF type, we should hide FF options
      if (!type.includes("FF")) {
        return false;
      }
    }

    return true;
  }, [selectedRows, selectedPatient, currentScheduleData, patients]);

  // Memoized fax counts to avoid expensive filtering on every render
  const faxCounts = useMemo(() => {
    const reports = allFaxes.filter((f) => !f.is_faxed_report).length;
    const fax = allFaxes.filter((f) => f.is_faxed_report).length;
    const unmatched = allFaxes.filter(
      (f) => f.is_faxed_report && f.is_unmatched,
    ).length;
    const matched = allFaxes.filter(
      (f) => f.is_faxed_report && !f.is_unmatched,
    ).length;
    return { reports, fax, unmatched, matched };
  }, [allFaxes]);

  // Memoized filtered faxes list based on current filters
  const filteredFaxesList = useMemo(() => {
    return allFaxes.filter((fax) => {
      // Filter by matched/unmatched status
      if (faxFilter === "unmatched" && !fax.is_unmatched) return false;
      if (faxFilter === "matched" && fax.is_unmatched) return false;

      // Filter by report type (fax vs non-fax)
      if (reportTypeFilter === "fax" && !fax.is_faxed_report) return false;
      if (reportTypeFilter === "reports" && fax.is_faxed_report) return false;

      return true;
    });
  }, [allFaxes, faxFilter, reportTypeFilter]);

  // Memoized grouped faxes by date for rendering
  const groupedFaxesByDate = useMemo(() => {
    const groupedByDate = {};
    const dateToKeyMap = {};

    filteredFaxesList.forEach((fax) => {
      let dateKey, originalDate;
      if (fax.created_at) {
        originalDate = new Date(fax.created_at);
        dateKey = originalDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        dateToKeyMap[dateKey] = originalDate;
      } else {
        dateKey = "Unknown Date";
        dateToKeyMap[dateKey] = new Date(0);
      }

      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(fax);
    });

    // Sort dates (most recent first)
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
      if (a === "Unknown Date") return 1;
      if (b === "Unknown Date") return -1;
      return dateToKeyMap[b] - dateToKeyMap[a];
    });

    return { groupedByDate, sortedDates };
  }, [filteredFaxesList]);

  // What the bulk button will actually do, given what is ticked. An unmatched
  // fax needs matching; a matched one needs unmatching first. Labelling it
  // "Rematch" regardless offered an action that refused every unmatched fax.
  const bulkMatchAction = useMemo(
    () => partitionForBulkMatch(
      filteredFaxesList.filter((f) => selectedFaxes.has(f.file_path)),
      faxDocumentKey,
    ),
    [filteredFaxesList, selectedFaxes],
  );

  // Read inside the focus effect below, but deliberately NOT in its dependency
  // list. useCollapsedDates returns a fresh object every render, so depending
  // on it re-ran the effect on every render and its cleanup cancelled the
  // pending scroll before it could fire — the filter would change and then
  // nothing happened. Refs give the effect the current values without making
  // it churn.
  const groupedFaxesRef = useRef(groupedFaxesByDate);
  const inboxDatesRef = useRef(inboxDates);
  groupedFaxesRef.current = groupedFaxesByDate;
  inboxDatesRef.current = inboxDates;

  // Acting on a fax reloads the whole inbox, which drops you back at the top —
  // so the entry you just matched is gone from view and you have to find it
  // again to see what happened to it. Return to it instead.
  //
  // Does nothing until the list has actually been replaced: firing on the click
  // would scroll the page while the request was still in flight.
  useEffect(() => {
    if (!pendingFocus) return undefined;
    if (allFaxes === pendingFocus.since) return undefined; // not reloaded yet

    const { doc } = pendingFocus;
    const row = allFaxes.find((f) => faxDocumentKey(f) === doc);
    if (!row) {
      setPendingFocus(null); // no longer in this inbox at all
      return undefined;
    }

    // A filter can hide the very entry you acted on — matching moves it out of
    // "Unmatched", unmatching moves it out of "Matched". Relax only the filters
    // actually in the way.
    const hiding = faxFiltersHiding(row, { faxFilter, reportTypeFilter });
    if (hiding.status || hiding.type) {
      if (hiding.status) setFaxFilter("all");
      if (hiding.type) setReportTypeFilter("all");
      return undefined; // re-renders; this effect runs again
    }

    // Retry rather than fire once: the row may need a render to appear after a
    // filter change, and another after its date group is expanded.
    let timer = null;
    let attempts = 0;
    const tick = () => {
      if (scrollFaxIntoView(doc)) {
        setPendingFocus(null);
        return;
      }
      const { groupedByDate, sortedDates } = groupedFaxesRef.current;
      const dateKey = sortedDates.find((d) =>
        (groupedByDate[d] || []).some((f) => faxDocumentKey(f) === doc)
      );
      if (dateKey && inboxDatesRef.current.isCollapsed(dateKey)) {
        inboxDatesRef.current.toggle(dateKey);
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(tick, 100);
      } else {
        setPendingFocus(null); // give up rather than retry forever
      }
    };
    timer = setTimeout(tick, 80);

    return () => clearTimeout(timer);
  }, [pendingFocus, allFaxes, faxFilter, reportTypeFilter]);

  // Function to fetch patients - moved outside useEffect to be accessible to the component
  const fetchPatients = async () => {
    const supabase = await getSupabase();
    try {
      // Keep the patient list in sync with the same date that reminders use
      const urlDate = new URLSearchParams(location.search).get("date");
      const dateToUse = urlDate || selectedDate || getDefaultDateString();

      // Match the table below: pull each selected provider's schema and merge,
      // tagging every row with `_schema` so bulk actions (SMS, reminders,
      // confirmations) write back to the schema the row actually lives in.
      const schemas =
        selectedProviders && selectedProviders.length
          ? selectedProviders
          : [homeSchema || supabaseSchema];

      const results = await Promise.all(
        schemas.map(async (schema) => {
          try {
            let query = supabase.schema(schema).from(tableName).select("*");

            // If we have a date, scope patients to that day's schedule
            if (dateToUse) {
              query = query.eq("date_", dateToUse);
            }

            const { data, error } = await query;
            if (error) throw error;
            return { schema, rows: data || [] };
          } catch (err) {
            console.warn(
              `[AdminPage] fetchPatients failed for schema "${schema}":`,
              err?.message || err,
            );
            return { schema, rows: null };
          }
        }),
      );

      const failed = results.filter((r) => r.rows === null);
      if (failed.length === results.length) {
        throw new Error(
          `Could not load patients for: ${failed.map((r) => r.schema).join(", ")}`,
        );
      }

      const data = mergeSchemaResults(results);
      debugLog("Fetched patients:", {
        dateToUse,
        schemas,
        count: data?.length || 0,
        data,
      }); // Debug: Verify patient data
      setPatients(data || []);
    } catch (error) {
      console.error("Error fetching patients:", error.message);
    }
  };

  // Function to fetch total unread messages count for notification icon
  const fetchTotalUnreadMessages = async () => {
    if (!patients || patients.length === 0) {
      setTotalUnreadMessages(0);
      setUnreadMessagesList([]);
      return;
    }

    setLoadingUnreadMessages(true);
    try {
      const supabase = await getSupabase();

      // Collect every normalized number (cell, home — utils/smsPhone) per patient
      const patientPhones = patients
        .map((p) => ({
          phone: [...rowPhonesNormalized(p)][0] || null,
          phones: [...rowPhonesNormalized(p)],
          patientName: p[prompts[0][0]] || p.name || p[colid2] || "Unknown",
          patientId: p.id,
          patientData: p,
        }))
        .filter((p) => p.phone);

      if (patientPhones.length === 0) {
        setTotalUnreadMessages(0);
        setUnreadMessagesList([]);
        return;
      }

      // Count messages from patients (incoming messages) that are not reviewed
      // Filter by direction = 'inbound' AND reviewed = false. One query per
      // schema (per chunk of phones) instead of one awaited query per patient.
      // Cross-schema fan-outs tag every row with `_schema`, and SMS lives in a
      // per-schema table, so each phone is looked up in its row's schema.
      const { total: totalUnread, entries: allUnreadMessages } =
        await fetchUnreadMessages(supabase, patientPhones, supabaseSchema);

      setTotalUnreadMessages(totalUnread);
      setUnreadMessagesList(allUnreadMessages);
    } catch (error) {
      console.error("Error fetching total unread messages:", error);
    } finally {
      setLoadingUnreadMessages(false);
    }
  };

  useEffect(() => {
    const checkUserRole = async () => {
      console.log(
        "[AdminPage] checkUserRole: starting — waiting for Supabase client...",
      );
      const supabase = await getSupabase();
      console.log(
        "[AdminPage] checkUserRole: Supabase client ready, fetching session...",
      );
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session) {
          console.warn(
            "[AdminPage] checkUserRole: no session →",
            sessionError?.message || "no session",
          );
          toast({
            title: "Authentication Error",
            description: "Please log in to access this page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate("/auth");
          return;
        }

        const userEmail = session.user.email;
        const userId = session.user.id;
        setCurrentUserId(userId); // Store current user ID
        setCurrentUserEmail(userEmail);

        // Use the already-parsed active schema from supabaseClient (single source of truth)
        setUserSchema(supabaseSchema);
        setUserSchemaLoaded(true);
        debugLog("User schema set to:", supabaseSchema);

        console.log(
          "[AdminPage] checkUserRole: session found for",
          userEmail,
          "— querying users.role...",
        );
        const rolePromise = supabase
          .from("users")
          .select("role, name")
          .eq("email", userEmail)
          .single();
        const roleTimeout = new Promise((_, reject) =>
          setTimeout(
            () =>
              reject(
                new Error(
                  "SELECT role FROM users timed out after 12s.\n" +
                    "This is a direct Supabase PostgREST query. It's slow because the Supabase DB is under high load.\n" +
                    "Likely contributors: activity_history JSONB being read/written on every page load by multiple users, " +
                    "versioning SELECT * on document_backups, and/or admin page notes-c table scans.\n" +
                    "Check the Monitor page CPU and Disk I/O graphs.",
                ),
              ),
            12000,
          ),
        );
        const { data, error } = await Promise.race([rolePromise, roleTimeout]);

        debugLog("data of user", data);

        if (error) {
          console.error(
            "[AdminPage] checkUserRole: users.role query failed →",
            error.message,
          );
          throw error;
        }
        console.log("[AdminPage] checkUserRole: role =", data?.role);

        if (data.name) {
          setCurrentUserName(data.name);
        }

        setUserRole(normalizeRole(data.role));

        if (normalizeRole(data.role) === "admin") {
          setIsAdmin(true);
          setIsAuthenticated(true);
          localStorage.setItem("isAdminAuthenticated", "true");
        } else if (normalizeRole(data.role) === "doctor") {
          setIsAdmin(false);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error checking user role:", error.message);
        toast({
          title: "Error",
          description: "Failed to verify user role.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/auth");
      }
    };

    checkUserRole();
    // No fetchPatients() here or on provider changes: the embedded
    // SchedulesTable loads the day (on mount and on date/provider changes)
    // and writes its rows into `patients` through setSchedules, which
    // overwrote a fetch from here within a second anyway. fetchPatients
    // stays for the refreshes after bulk actions.
  }, [navigate, toast]);

  // Fetch total unread messages count when the listed patients change. Keyed
  // on what the badge reads (schema, id, phone, name), not on the array:
  // SchedulesTable writes `patients` again with the same rows (live
  // transcription flags), and each write used to re-run the whole badge.
  const unreadKey = useMemo(
    () =>
      (patients || [])
        .filter(Boolean)
        .map((p) =>
          [
            p._schema || "",
            p.id,
            p.tel_num || p.phone || p.phone_number || p["Phone Number"] || "",
            p[prompts[0][0]] || p.name || p[colid2] || "",
          ].join("|"),
        )
        .sort()
        .join("\n"),
    [patients],
  );
  useEffect(() => {
    // unreadKey is "" only when there are no patients to look up.
    if (isAuthenticated && unreadKey) {
      fetchTotalUnreadMessages();
    }
  }, [unreadKey, isAuthenticated]);

  // --- SMS Pre-fetch Cache ---
  const [smsCache, setSmsCache] = useState(null);

  // Pre-fetch all SMS data after patients load
  useEffect(() => {
    if (!patients || patients.length === 0) return;

    const prefetchSMS = async () => {
      try {
        const supabase = await getSupabase();

        // Group phones by the schema their OWNING patient actually lives in.
        // The Admin patient list can be fanned in from every selected
        // provider schema (fetchPatients tags each row with `_schema`), and
        // SMS is a per-schema table — a single query against the viewer's
        // default schema silently missed every message belonging to a
        // patient pulled in from another provider.
        const phonesBySchema = new Map();
        patients.forEach((p) => {
          // Both of the patient's numbers (cell, home): texts can go to either.
          const phones = rowPhonesNormalized(p);
          if (!phones.size) return;
          const schema = p._schema || supabaseSchema || "public";
          if (!phonesBySchema.has(schema)) phonesBySchema.set(schema, new Set());
          phones.forEach((phone) => phonesBySchema.get(schema).add(phone));
        });

        if (phonesBySchema.size === 0) return;

        // Compute 1-month window around the schedule date
        const urlDate = new URLSearchParams(location.search).get("date");
        const scheduleDate = urlDate || selectedDate || getDefaultDateString();
        const baseDate = new Date(scheduleDate + "T12:00:00");
        const dateFrom = new Date(baseDate);
        dateFrom.setMonth(dateFrom.getMonth() - 1);
        const dateTo = new Date(baseDate);
        dateTo.setMonth(dateTo.getMonth() + 1);
        const dateFromStr = dateFrom.toISOString();
        const dateToStr = dateTo.toISOString();

        // Group by phone into cache map, querying each schema's SMS table
        // with only that schema's own phones (build OR filter per schema).
        const cache = {};
        let totalFetched = 0;
        await Promise.all(
          [...phonesBySchema.entries()].map(async ([schema, phoneSet]) => {
            const phones = [...phoneSet];
            phones.forEach((phone) => {
              if (!cache[phone]) cache[phone] = [];
            });
            const orFilter = phones
              .map((ph) => `from.ilike.%${ph},to.ilike.%${ph}`)
              .join(",");

            const { data, error } = await supabase
              .schema(schema)
              .from("SMS")
              .select("*")
              .or(orFilter)
              .gte("created_at", dateFromStr)
              .lte("created_at", dateToStr)
              .order("created_at", { ascending: true });

            if (error) {
              console.error(`[SMS Prefetch] Error for schema "${schema}":`, error);
              return;
            }

            totalFetched += (data || []).length;
            for (const sms of data || []) {
              const smsFrom = sms.from || "";
              const smsTo = sms.to || "";
              for (const phone of phones) {
                if (smsFrom.includes(phone) || smsTo.includes(phone)) {
                  cache[phone].push(sms);
                }
              }
            }
          }),
        );

        // A phone can (rarely) belong to patients in two different schemas;
        // re-sort after merging so its thread still reads chronologically.
        Object.keys(cache).forEach((phone) => {
          cache[phone].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at),
          );
        });

        setSmsCache(cache);
        console.log(
          "[SMS Prefetch] Cached SMS for",
          Object.keys(cache).length,
          "phones across",
          phonesBySchema.size,
          "schema(s),",
          totalFetched,
          "total messages",
        );
      } catch (err) {
        console.error("[SMS Prefetch] Error:", err);
      }
    };

    prefetchSMS();
  }, [patients]);

  // The cached appointment-date buckets belong to the provider set they were
  // built from, so drop them when the selection changes. They are not
  // preloaded on page load (that sent one categorize request per phone):
  // opening SMS Status or its Appointment Date tab rebuilds them through
  // fetchSMSCountsForAppointmentMonth.
  useEffect(() => {
    setAppointmentDateSmsCache({});
  }, [providersKey]);

  // Fetch faxes on page load to show correct count - wait for userSchema to be loaded
  useEffect(() => {
    if (userSchemaLoaded) {
      fetchAllFaxes({ light: true });
    }
  }, [userSchemaLoaded, userSchema]);

  // Update selectedDate when URL changes
  useEffect(() => {
    const urlDate = new URLSearchParams(location.search).get("date");
    if (urlDate && urlDate !== selectedDate) {
      setSelectedDate(urlDate);
      // Clear all patient selection state when date changes
      setSelectedRows({});
      setSelectedPatientsContact({});
      setCurrentScheduleData({});
      setSelectedPatient(null);
      setSendFormsButtonText("Send Forms");
      // Also clear reminder-specific selections
      setPatientsWithoutForms([]);
      setSelectedReminderPatients({});
    }
    if (!urlDate && selectedDate !== getDefaultDateString()) {
      setSelectedDate(getDefaultDateString());
      // Clear all patient selection state when date changes
      setSelectedRows({});
      setSelectedPatientsContact({});
      setCurrentScheduleData({});
      setSelectedPatient(null);
      setSendFormsButtonText("Send Forms");
      // Also clear reminder-specific selections
      setPatientsWithoutForms([]);
      setSelectedReminderPatients({});
    }
  }, [location.search, selectedDate]);

  // Update Send Reminders button pending count when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchReminderStatus();
    } else {
      setReminderStatus({
        total_patients: 0,
        patients_with_forms: 0,
        patients_without_forms: 0,
        completion_rate: 0,
      });
    }
  }, [selectedDate]);

  // Categorize SMS messages by appointment using the backend API (same logic as PatientMessagingModal)
  const categorizeSMSMessagesByAppointment = useCallback(
    async (messagesToCategorize, patientPhone, patientRow = null) => {
      console.log(
        "[SMS Categorize] Starting categorization for",
        messagesToCategorize?.length,
        "messages for phone:",
        patientPhone,
      );

      if (
        !patientPhone ||
        !messagesToCategorize ||
        messagesToCategorize.length === 0
      ) {
        console.log("[SMS Categorize] Skipping - no phone or no messages");
        return { categorized: {}, uncategorized: messagesToCategorize || [] };
      }

      try {
        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;

        console.log(
          "[SMS Categorize] Calling backend:",
          `${baseUrl}/twilio/categorize-messages/`,
        );

        const response = await fetch(`${baseUrl}/twilio/categorize-messages/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: messagesToCategorize,
            patient_phone: patientPhone,
            // A patient fanned in from another selected provider lives in
            // ITS schema — the backend looks up appointment history by
            // schema to categorize by appointment date.
            schema: patientRow?._schema || supabaseSchema,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "[SMS Categorize] Failed - status:",
            response.status,
            "error:",
            errorText,
          );
          return { categorized: {}, uncategorized: messagesToCategorize };
        }

        const result = await response.json();
        console.log("[SMS Categorize] Response received:", result.status);

        return {
          categorized: result.categorized || {},
          uncategorized: result.uncategorized || [],
        };
      } catch (error) {
        console.error("[SMS Categorize] Error:", error);
        return { categorized: {}, uncategorized: messagesToCategorize };
      }
    },
    [supabaseSchema],
  );

  // Categorize SMS messages when a patient is selected in SMS Status modal
  // Find the appointment date closest to today for a given patient
  const findClosestAppointmentDate = useCallback(
    (categorizedMessages, patientData) => {
      if (!categorizedMessages?.categorized || !patientData) return null;

      const appointmentDates = Object.keys(categorizedMessages.categorized);
      if (appointmentDates.length === 0) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get patient's appointment date from their data
      const patientAppointmentDate = patientData.date_;

      if (patientAppointmentDate) {
        // If patient has a specific appointment date, find the categorized section closest to that
        const patientApptDateObj = new Date(
          patientAppointmentDate + "T12:00:00",
        );

        // Find the section closest to patient's appointment date
        let closestDate = null;
        let closestDiff = Infinity;

        for (const apptDate of appointmentDates) {
          const sectionDate = new Date(apptDate + "T12:00:00");
          const diff = Math.abs(
            sectionDate.getTime() - patientApptDateObj.getTime(),
          );
          if (diff < closestDiff) {
            closestDiff = diff;
            closestDate = apptDate;
          }
        }

        return closestDate;
      }

      // Fallback: find section closest to today's date
      let closestDate = null;
      let closestDiff = Infinity;

      for (const apptDate of appointmentDates) {
        const sectionDate = new Date(apptDate + "T12:00:00");
        const diff = Math.abs(sectionDate.getTime() - today.getTime());
        if (diff < closestDiff) {
          closestDiff = diff;
          closestDate = apptDate;
        }
      }

      return closestDate;
    },
    [],
  );

  // Fetch all SMS messages for a specific patient (not limited by date)
  const fetchAllSMSForPatient = useCallback(async (patientPhone, patientRow = null) => {
    try {
      const supabase = await getSupabase();

      // Normalize the phone number
      const normalizePhone = (phone) => {
        if (!phone) return null;
        const digits = String(phone).replace(/\D/g, "");
        if (digits.length === 11 && digits.startsWith("1")) {
          return digits.slice(1);
        }
        return digits.slice(-10);
      };

      // Every number of the patient (cell, home) when the row is known —
      // texts can have gone to either.
      const phones = new Set(patientRow ? rowPhonesNormalized(patientRow) : []);
      const primary = normalizePhone(patientPhone);
      if (primary) phones.add(primary);
      if (!phones.size) {
        console.log("[fetchAllSMSForPatient] Invalid phone number");
        return [];
      }

      const toFilter = [...phones].map((ph) => `to.ilike.%${ph}%`).join(",");

      // Only fetch messages sent TO the patient (not from the patient)
      // This shows what the clinic sent to the patient. `patientRow` can be
      // fanned in from another selected provider schema — SMS is a
      // per-schema table, so an unqualified query here silently found
      // nothing for those patients.
      const { data, error } = await dbForRow(supabase, patientRow, supabaseSchema)
        .from("SMS")
        .select("*")
        .or(toFilter)
        .order("created_at", { ascending: false })
        .limit(500);

      if (error) {
        console.error("[fetchAllSMSForPatient] Error fetching SMS:", error);
        return [];
      }

      console.log(
        "[fetchAllSMSForPatient] Fetched",
        data?.length || 0,
        "messages sent TO patient",
      );
      return tagSmsRows(smsSchemaOf(patientRow, supabaseSchema), data);
    } catch (error) {
      console.error("[fetchAllSMSForPatient] Error:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (selectedSMSPatient && patients.length > 0) {
      // Get selected patient data
      const selectedPatientData = patients.find(
        (p) => p.id === selectedSMSPatient,
      );

      // Get patient phone
      // Primary number (cell, else home); the fetch itself covers both.
      const patientPhone = rowPhones(selectedPatientData)[0] || null;

      if (patientPhone) {
        // Fetch ALL messages for the patient (not just from selected date)
        setLoadingSMSCategorization(true);
        fetchAllSMSForPatient(patientPhone, selectedPatientData)
          .then((allPatientMessages) => {
            if (allPatientMessages.length > 0) {
              // Trigger categorization with ALL messages
              categorizeSMSMessagesByAppointment(
                allPatientMessages,
                patientPhone,
                selectedPatientData,
              )
                .then((result) => {
                  setCategorizedSMSMessages(result);

                  // Auto-expand the appointment section closest to the patient's appointment
                  const closestDate = findClosestAppointmentDate(
                    result,
                    selectedPatientData,
                  );
                  if (closestDate) {
                    setExpandedSMSAppointments((prev) => ({
                      ...prev,
                      [closestDate]: true,
                    }));
                  }

                  setLoadingSMSCategorization(false);
                })
                .catch((error) => {
                  console.error("Error categorizing SMS messages:", error);
                  setLoadingSMSCategorization(false);
                });
            } else {
              setCategorizedSMSMessages({ categorized: {}, uncategorized: [] });
              setLoadingSMSCategorization(false);
            }
          })
          .catch((error) => {
            console.error("Error fetching patient SMS:", error);
            setLoadingSMSCategorization(false);
          });
      } else {
        setLoadingSMSCategorization(false);
      }
    }
  }, [
    selectedSMSPatient,
    patients,
    categorizeSMSMessagesByAppointment,
    findClosestAppointmentDate,
    fetchAllSMSForPatient,
  ]);

  const handlePasswordSubmit = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem("isAdminAuthenticated", "true");
      onPasswordClose();
    } else {
      toast({
        title: "Error",
        description: "Incorrect password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSelectedRowChange = async (data) => {
    if (!data) {
      setSelectedRows({});
      setSelectedPatient(null);
      setSendFormsButtonText("Send Forms");
      setSelectedPatientsContact({});
      setCurrentScheduleData({});
      return;
    }

    // Store the current schedule data for use in sendForms
    if (data.scheduleData) {
      setCurrentScheduleData((prev) => ({
        ...prev,
        [data.scheduleData.id]: data.scheduleData,
      }));
    }

    // Handle the new format where data contains both selection and scheduleData
    if (data.selection) {
      setSelectedRows(data.selection);

      // Process multiple selected patients
      const newSelectedPatientsContact = {};
      const selectedCount = Object.keys(data.selection).filter(
        (key) => data.selection[key],
      ).length;

      // If there are selected patients, initialize contact info for each
      if (selectedCount > 0) {
        Object.keys(data.selection).forEach((patientId) => {
          if (data.selection[patientId]) {
            // Prioritize scheduleData if available, otherwise search in patients array
            let patientData = null;

            // First, check if we have scheduleData for this patient ID
            if (currentScheduleData[patientId]) {
              patientData = currentScheduleData[patientId];
              debugLog(
                `[SYNC] Using stored scheduleData for patient ${patientId}`,
              );
            } else if (
              data.scheduleData &&
              data.scheduleData.id === patientId
            ) {
              patientData = data.scheduleData;
              debugLog(
                `[SYNC] Using new scheduleData for patient ${patientId}`,
              );
            } else {
              // Fallback: search in the patients array
              patientData = patients.find((p) => p.id === patientId);
              if (patientData) {
                debugLog(`[SYNC] Found patient ${patientId} in patients array`);
              } else {
                debugLog(`[SYNC] WARNING: Patient ${patientId} not found`);
              }
            }

            if (patientData) {
              const extractedInfo = extractPatientInfo(patientData);
              newSelectedPatientsContact[patientId] = {
                email: extractedInfo.email || "",
                phone: extractedInfo.phone || "",
                name:
                  patientData[prompts[0][0]] ||
                  patientData.name ||
                  patientData[colid2] ||
                  "Unknown Patient",
              };
            } else {
              // Create a placeholder entry to prevent undefined errors
              newSelectedPatientsContact[patientId] = {
                email: "",
                phone: "",
                name: "Unknown Patient",
              };
              debugLog(
                `[SYNC] Created placeholder contact info for missing patient ${patientId}`,
              );
            }
          }
        });
      }

      setSelectedPatientsContact(newSelectedPatientsContact);

      // Set the first selected patient as the primary selected patient for backward compatibility
      if (selectedCount > 0) {
        const firstSelectedId = Object.keys(data.selection).find(
          (key) => data.selection[key],
        );
        let firstSelectedPatient = null;

        // Prioritize scheduleData if available
        if (currentScheduleData[firstSelectedId]) {
          firstSelectedPatient = currentScheduleData[firstSelectedId];
          debugLog(
            `[SYNC] Using stored scheduleData as selectedPatient for ${firstSelectedId}`,
          );
        } else if (
          data.scheduleData &&
          data.scheduleData.id === firstSelectedId
        ) {
          firstSelectedPatient = data.scheduleData;
          debugLog(
            `[SYNC] Using new scheduleData as selectedPatient for ${firstSelectedId}`,
          );
        } else {
          // Fallback: search in the patients array
          firstSelectedPatient = patients.find((p) => p.id === firstSelectedId);
          if (firstSelectedPatient) {
            debugLog(
              `[SYNC] Found selectedPatient ${firstSelectedId} in patients array`,
            );
          } else {
            debugLog(
              `[SYNC] WARNING: Selected patient ${firstSelectedId} not found`,
            );
          }
        }

        if (firstSelectedPatient) {
          setSelectedPatient(firstSelectedPatient);
        } else {
          setSelectedPatient(null);
        }
      } else {
        setSelectedPatient(null);
      }
    }

    if (data.scheduleData) {
      // Ensure we have all the necessary data from the patient record
      const patientData = {
        ...data.scheduleData,
        // Ensure these fields are present
        id: data.scheduleData.id,
        date_: data.scheduleData.date_,
        time_: data.scheduleData.time_,
        // Add any other required fields from prompts
        ...prompts.reduce((acc, [key]) => {
          acc[key] = data.scheduleData[key] || "";
          return acc;
        }, {}),
      };

      // Update the send forms button text
      const buttonText = await getSendFormsButtonText();
      setSendFormsButtonText(buttonText);

      debugLog("[FUZZY FIELD MAPPING] Patient data extracted:", {
        originalData: patientData,
        extractedInfo: extractPatientInfo(patientData),
      });
    }
  };

  const handleDuplicatePatient = () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to duplicate.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setIsDuplicateModalOpen(true);
  };

  const handleLLMModelSave = async (model) => {
    setSelectedLLMModel(model);
    setUserSettings((prev) => ({
      ...prev,
      SELECTED_LLM_MODEL: model,
    }));

    // Save to database
    const supabase = await getSupabase();
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Not authenticated", status: "error", duration: 3000 });
        return;
      }

      const userId = session.user.id;

      const { error } = await supabasePublic()
        .from("users")
        .update({
          settings: {
            ...userSettings,
            SELECTED_LLM_MODEL: model,
          },
        })
        .eq("id", userId);

      if (error) {
        toast({
          title: "Failed to update LLM model",
          status: "error",
          duration: 3000,
        });
        console.error("Save LLM model error:", error);
      }
    } catch (error) {
      console.error("Unexpected save LLM model error:", error);
      toast({
        title: "Error saving LLM model",
        status: "error",
        duration: 3000,
      });
    }
  };

  const fetchUserSettings = async () => {
    const supabase = await getSupabase();
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        toast({ title: "Not authenticated", status: "error", duration: 3000 });
        return;
      }

      debugLog("Fetching schema settings for:", supabaseSchema);

      // Query schema_settings table instead of users table
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("settings")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        // If no record exists, create one with default settings for this schema
        // NOTE: Form URLs (NP_FORM_URL, NS_FORM_URL, CL1_FORM_URL, CL2_FORM_URL)
        // are REQUIRED and must be configured in schema_settings table
        if (error.code === "PGRST116") {
          const defaultSettings = {
            // NP_FORM_URL, NS_FORM_URL, CL1_FORM_URL, CL2_FORM_URL must be configured
            // No defaults - these MUST come from schema_settings table
            // No ZOHO_EMAIL / ZOHO_PASSWORD either: the backend falls back to
            // its .env sender, and a mailbox password never belongs in browser code.
            DOCUMENT_ROOT: "MedDocs/CustomSchema",
            TWILIO_PHONE_NUMBER: "+13236132761",
          };
          const { error: insertError } = await supabasePublic()
            .from("schema_settings")
            .insert({ schema_name: supabaseSchema, settings: defaultSettings });

          if (!insertError) {
            setUserSettings(defaultSettings);
            return defaultSettings;
          }
        }
        toast({
          title: "Failed to fetch settings",
          status: "error",
          duration: 3000,
        });
        console.error("Fetch settings error:", error);
        return;
      }

      debugLog("Schema settings loaded:", data?.settings);
      const settings = data?.settings || {};
      setUserSettings(settings);
      // Set the LLM model from settings if available
      if (settings.SELECTED_LLM_MODEL !== undefined) {
        setSelectedLLMModel(settings.SELECTED_LLM_MODEL);
      }
      return settings; // Return the settings for immediate use
    } catch (error) {
      console.error("Unexpected fetch error:", error);
      toast({
        title: "Error fetching settings",
        status: "error",
        duration: 3000,
      });
      return {};
    }
  };

  const saveUserSettings = async () => {
    const supabase = await getSupabase();
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Not authenticated", status: "error", duration: 3000 });
        return;
      }

      // Save to schema_settings table instead of users table
      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          settings: userSettings,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        toast({
          title: "Failed to update settings",
          status: "error",
          duration: 3000,
        });
        console.error("Save settings error:", error);
        return;
      }

      toast({ title: "Settings updated", status: "success", duration: 3000 });
      onSettingsClose();
    } catch (error) {
      console.error("Unexpected save error:", error);
      toast({
        title: "Error saving settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Fetch AI Chat enabled setting for current schema
  const fetchAiChatSetting = async () => {
    try {
      const supabase = await supabasePublic();

      // Query schema_settings table for current schema
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("ai_chat_enabled")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        // If no record exists, create one with default value
        if (error.code === "PGRST116") {
          // No rows returned
          const { error: insertError } = await supabasePublic()
            .from("schema_settings")
            .insert({ schema_name: supabaseSchema, ai_chat_enabled: true });

          if (!insertError) {
            setAiChatEnabled(true);
          }
          return;
        }
        console.error("Error fetching AI chat setting:", error);
        return;
      }

      setAiChatEnabled(data?.ai_chat_enabled ?? true);
    } catch (error) {
      console.error("Error fetching AI chat setting:", error);
    }
  };

  // Toggle AI Chat enabled setting for current schema
  const toggleAiChatSetting = async () => {
    setLoadingAiChatSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !aiChatEnabled;

      // Try to update existing record
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .upsert(
          {
            schema_name: supabaseSchema,
            ai_chat_enabled: newValue,
          },
          { onConflict: "schema_name" },
        )
        .select();

      if (error) {
        console.error("Error updating AI chat setting:", error);
        toast({
          title: "Error",
          description: "Failed to update AI chat setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setAiChatEnabled(newValue);
      toast({
        title: newValue ? "AI Chat Enabled" : "AI Chat Disabled",
        description: newValue
          ? "Patients can now use AI chat via SMS"
          : "AI chat has been disabled for patients",
        status: newValue ? "success" : "info",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error toggling AI chat setting:", error);
      toast({
        title: "Error",
        description: "Failed to update AI chat setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingAiChatSetting(false);
    }
  };

  // Fetch Auto-Reminders setting on mount
  useEffect(() => {
    fetchAutoRemindersSetting();
  }, []);

  const fetchComplianceSetting = async () => {
    try {
      const supabase = await supabasePublic();
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("compliance_monitoring")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setComplianceEnabled(true);
          return;
        }
        console.error("Error fetching compliance setting:", error);
        return;
      }
      setComplianceEnabled(
        parseComplianceSettings(data?.compliance_monitoring).enabled,
      );
    } catch (error) {
      console.error("Error fetching compliance setting:", error);
    }
  };

  const toggleComplianceSetting = async () => {
    setLoadingComplianceSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !complianceEnabled;

      const { data: existing, error: fetchErr } = await supabasePublic()
        .from("schema_settings")
        .select("compliance_monitoring")
        .eq("schema_name", supabaseSchema)
        .single();

      if (fetchErr && fetchErr.code !== "PGRST116") {
        throw fetchErr;
      }

      const merged = {
        ...parseComplianceSettings(existing?.compliance_monitoring),
        enabled: newValue,
      };

      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          compliance_monitoring: merged,
        },
        { onConflict: "schema_name" },
      );

      if (error) throw error;

      setComplianceEnabled(newValue);
      toast({
        title: newValue ? "Compliance monitoring enabled" : "Compliance monitoring disabled",
        description: newValue
          ? "Medication and preventive gap alerts are active for this clinic."
          : "Gap scans and alerts are paused until re-enabled.",
        status: newValue ? "success" : "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error toggling compliance setting:", error);
      toast({
        title: "Error",
        description: "Failed to update compliance setting",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingComplianceSetting(false);
    }
  };

  // Fetch Auto-Reminders enabled setting for current schema
  const fetchAutoRemindersSetting = async () => {
    try {
      const supabase = await supabasePublic();

      // Query schema_settings table for current schema
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("auto_reminders_enabled, auto_reminder_settings")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        // If no record exists, create one with default value
        if (error.code === "PGRST116") {
          // No rows returned
          const defaultSettings = {
            days_before: 1,
            time: "09:00:00",
            include_email: true,
            include_phone: true,
            patient_types: ["NP", "NS"],
            schedule_enabled: true,
          };
          const { error: insertError } = await supabasePublic()
            .from("schema_settings")
            .insert({
              schema_name: supabaseSchema,
              auto_reminders_enabled: false,
              auto_reminder_settings: defaultSettings,
            });

          if (!insertError) {
            setAutoRemindersEnabled(false);
            setReminderDaysBeforeAppointment(1);
            setReminderScheduledTime("09:00");
            setReminderContactMethods({ email: true, phone: true });
            setReminderPatientTypeFilter(["NP", "NS"]);
            setScheduleReminderOneDayBefore(false);
          }
          return;
        }
        console.error("Error fetching auto reminders setting:", error);
        return;
      }

      setAutoRemindersEnabled(data?.auto_reminders_enabled ?? false);

      // Parse the JSON settings
      const reminderSettings = data?.auto_reminder_settings || {};
      setReminderDaysBeforeAppointment(reminderSettings.days_before ?? null);

      // Convert time from "HH:MM:SS" to "HH:MM" format
      const timeStr = reminderSettings.time || "";
      const timeFormatted = timeStr ? timeStr.substring(0, 5) : "";
      setReminderScheduledTime(timeFormatted);

      setReminderContactMethods({
        email: reminderSettings.include_email ?? false,
        phone: reminderSettings.include_phone ?? false,
      });
      // Sanitize: only NP/NS are allowed for auto reminders. Strip any
      // stale values (e.g. "FFP"/"FFS") saved in older sessions so the
      // Send Form Reminders list never shows non-NP/NS patients.
      const ALLOWED_REMINDER_TYPES = ["NP", "NS"];
      const sanitizeTypes = (arr) =>
        (Array.isArray(arr) ? arr : []).filter((t) =>
          ALLOWED_REMINDER_TYPES.includes(String(t).toUpperCase().trim()),
        );
      setReminderPatientTypeFilter(
        sanitizeTypes(reminderSettings.patient_types ?? []),
      );
      setScheduleReminderOneDayBefore(
        reminderSettings.schedule_enabled ?? false,
      );
    } catch (error) {
      console.error("Error fetching auto reminders setting:", error);
    }
  };

  // Toggle Auto-Reminders enabled setting for current schema
  const toggleAutoRemindersSetting = async () => {
    setLoadingAutoRemindersSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !autoRemindersEnabled;

      // Try to update existing record
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .upsert(
          {
            schema_name: supabaseSchema,
            auto_reminders_enabled: newValue,
          },
          { onConflict: "schema_name" },
        )
        .select();

      if (error) {
        console.error("Error updating auto reminders setting:", error);
        toast({
          title: "Error",
          description: "Failed to update auto reminders setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setAutoRemindersEnabled(newValue);

      // If enabling auto-reminders, also trigger poll and schedule for all existing future patients
      if (newValue) {
        try {
          const hostname = window.location.hostname;
          const baseUrl =
            hostname === "localhost"
              ? BASE_URLS.local.backend
              : BASE_URLS.render.backend;
          const pollScheduleUrl = `${baseUrl}/api/reminders/poll-and-schedule/`;

          console.log(
            "🏥 AdminPage: Triggering poll and schedule auto-reminders for all existing future patients",
          );

          const pollResponse = await fetch(pollScheduleUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              schema: supabaseSchema,
            }),
          });

          if (pollResponse.ok) {
            const pollData = await pollResponse.json();
            console.log("🏥 AdminPage: Poll and schedule result:", pollData);
            toast({
              title: "Auto-Reminders Enabled",
              description: `Automatic reminders enabled. Scheduled ${pollData.reminders_scheduled || 0} reminder(s) for future appointments. Skipped ${pollData.skipped || 0} existing reminders.`,
              status: "success",
              duration: 5000,
            });
          } else {
            console.warn(
              "🏥 AdminPage: Failed to trigger poll and schedule:",
              pollResponse.status,
            );
            // Don't show error toast - the setting was still enabled
            toast({
              title: "Auto-Reminders Enabled",
              description:
                "Automatic appointment reminders will be sent 1 day before appointments. (Background job will process existing patients)",
              status: "success",
              duration: 5000,
            });
          }
        } catch (pollError) {
          console.warn(
            "🏥 AdminPage: Error triggering poll and schedule:",
            pollError.message,
          );
          // Don't fail the whole operation if poll fails
          toast({
            title: "Auto-Reminders Enabled",
            description:
              "Automatic appointment reminders will be sent 1 day before appointments. (Background job will process existing patients)",
            status: "success",
            duration: 5000,
          });
        }
      } else {
        toast({
          title: "Auto-Reminders Disabled",
          description: "Automatic reminders have been disabled",
          status: "info",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error toggling auto reminders setting:", error);
      toast({
        title: "Error",
        description: "Failed to update auto reminders setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingAutoRemindersSetting(false);
    }
  };

  // Fetch Auto-SMS enabled setting on mount
  useEffect(() => {
    fetchAutoSmsSetting();
  }, []);

  // Fetch Auto-SMS enabled setting for current schema
  const fetchAutoSmsSetting = async () => {
    try {
      const supabase = await supabasePublic();

      // Query schema_settings table for current schema
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("auto_sms_enabled")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        // If no record exists, create one with default value
        if (error.code === "PGRST116") {
          const { error: insertError } = await supabasePublic()
            .from("schema_settings")
            .insert({
              schema_name: supabaseSchema,
              auto_sms_enabled: false,
            });

          if (!insertError) {
            setAutoSmsEnabled(false);
          }
          return;
        }
        console.error("Error fetching auto SMS setting:", error);
        return;
      }

      setAutoSmsEnabled(data?.auto_sms_enabled ?? false);
    } catch (error) {
      console.error("Error fetching auto SMS setting:", error);
    }
  };

  // Toggle Auto-SMS enabled setting for current schema
  const toggleAutoSmsSetting = async () => {
    setLoadingAutoSmsSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !autoSmsEnabled;

      // Try to update existing record
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .upsert(
          {
            schema_name: supabaseSchema,
            auto_sms_enabled: newValue,
          },
          { onConflict: "schema_name" },
        )
        .select();

      if (error) {
        console.error("Error updating auto SMS setting:", error);
        toast({
          title: "Error",
          description: "Failed to update auto SMS setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setAutoSmsEnabled(newValue);

      if (newValue) {
        toast({
          title: "Auto SMS Resend Enabled",
          description:
            "Failed SMS messages with 'exceeded message length' error will be automatically retried after 5 minutes with split messaging enabled.",
          status: "success",
          duration: 5000,
        });
      } else {
        toast({
          title: "Auto SMS Resend Disabled",
          description: "Automatic SMS retry has been disabled",
          status: "info",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error toggling auto SMS setting:", error);
      toast({
        title: "Error",
        description: "Failed to update auto SMS setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingAutoSmsSetting(false);
    }
  };

  // Fetch the per-schema upgrade_enabled/upgrade_link settings on mount.
  // NULL columns fall back to the global UPGRADE_DEFAULTS from env.js.
  const fetchUpgradeSetting = async () => {
    try {
      const supabase = await supabasePublic();

      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("upgrade_enabled, upgrade_link")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setUpgradeEnabled(UPGRADE_DEFAULTS.enabled);
          setUpgradeLink(UPGRADE_DEFAULTS.link);
          setUpgradeLinkDraft(UPGRADE_DEFAULTS.link);
          return;
        }
        console.error("Error fetching upgrade setting:", error);
        return;
      }

      const resolvedEnabled =
        data?.upgrade_enabled ?? UPGRADE_DEFAULTS.enabled;
      const resolvedLink = data?.upgrade_link || UPGRADE_DEFAULTS.link;
      setUpgradeEnabled(resolvedEnabled);
      setUpgradeLink(resolvedLink);
      setUpgradeLinkDraft(resolvedLink);
    } catch (error) {
      console.error("Error fetching upgrade setting:", error);
    }
  };

  // Toggle the per-schema upgrade_enabled flag
  const toggleUpgradeSetting = async () => {
    setLoadingUpgradeSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !upgradeEnabled;

      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          upgrade_enabled: newValue,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        console.error("Error updating upgrade_enabled setting:", error);
        toast({
          title: "Error",
          description: "Failed to update Upgrade button setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setUpgradeEnabled(newValue);
      toast({
        title: newValue ? "Upgrade Button Enabled" : "Upgrade Button Disabled",
        description: newValue
          ? `The Upgrade to Pro button will now show on the Schedules page for ${supabaseSchema || "public"}.`
          : `The Upgrade to Pro button is now hidden on the Schedules page for ${supabaseSchema || "public"}.`,
        status: newValue ? "success" : "warning",
        duration: 5000,
      });
    } catch (error) {
      console.error("Error toggling upgrade_enabled setting:", error);
      toast({
        title: "Error",
        description: "Failed to update Upgrade button setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingUpgradeSetting(false);
    }
  };

  // Save a per-schema override for the Stripe checkout link.
  // Passing an empty string clears the override (falls back to the global default).
  const saveUpgradeLink = async (nextLink) => {
    setLoadingUpgradeSetting(true);
    try {
      const supabase = await supabasePublic();
      const trimmed = (nextLink || "").trim();

      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          upgrade_link: trimmed || null,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        console.error("Error updating upgrade_link setting:", error);
        toast({
          title: "Error",
          description: "Failed to update Upgrade link",
          status: "error",
          duration: 3000,
        });
        return;
      }

      const resolved = trimmed || UPGRADE_DEFAULTS.link;
      setUpgradeLink(resolved);
      setUpgradeLinkDraft(resolved);
      toast({
        title: "Upgrade Link Saved",
        description: trimmed
          ? `${supabaseSchema || "public"} now uses its own checkout link.`
          : `${supabaseSchema || "public"} reverted to the global default link.`,
        status: "success",
        duration: 4000,
      });
    } catch (error) {
      console.error("Error saving upgrade_link setting:", error);
      toast({
        title: "Error",
        description: "Failed to update Upgrade link",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingUpgradeSetting(false);
    }
  };

  // Fetch the per-schema sms_enabled master switch on mount
  const fetchSmsEnabledSetting = async () => {
    try {
      const supabase = await supabasePublic();

      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("sms_enabled")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setSmsEnabled(true);
          return;
        }
        console.error("Error fetching sms_enabled setting:", error);
        return;
      }

      setSmsEnabled(data?.sms_enabled ?? true);
    } catch (error) {
      console.error("Error fetching sms_enabled setting:", error);
    }
  };

  // Toggle the per-schema sms_enabled master switch
  const toggleSmsEnabledSetting = async () => {
    setLoadingSmsEnabledSetting(true);
    try {
      const supabase = await supabasePublic();
      const newValue = !smsEnabled;

      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          sms_enabled: newValue,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        console.error("Error updating sms_enabled setting:", error);
        toast({
          title: "Error",
          description: "Failed to update SMS setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setSmsEnabled(newValue);
      toast({
        title: newValue ? "SMS Enabled" : "SMS Disabled",
        description: newValue
          ? `All outbound SMS for ${supabaseSchema || "public"} is now allowed.`
          : `All outbound SMS for ${supabaseSchema || "public"} is now blocked. Patient replies will still be recorded but no auto-reply will be sent.`,
        status: newValue ? "success" : "warning",
        duration: 5000,
      });
    } catch (error) {
      console.error("Error toggling sms_enabled setting:", error);
      toast({
        title: "Error",
        description: "Failed to update SMS setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingSmsEnabledSetting(false);
    }
  };

  // Save Custom SMS character limit to localStorage
  const saveCustomSmsCharacterLimit = (newLimit) => {
    try {
      // Validate the limit
      const limitValue = parseInt(newLimit, 10);
      if (isNaN(limitValue) || limitValue < 1 || limitValue > 10000) {
        toast({
          title: "Invalid Character Limit",
          description: "Please enter a value between 1 and 10000",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Save to localStorage
      localStorage.setItem("custom_sms_character_limit", limitValue.toString());
      setCustomSmsCharacterLimit(limitValue);

      toast({
        title: "Setting Saved",
        description: `Custom SMS character limit set to ${limitValue} characters`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving custom SMS character limit:", error);
      toast({
        title: "Error",
        description: "Failed to save custom SMS character limit",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Trigger manual auto SMS reschedule for testing
  const [triggeringAutoReschedule, setTriggeringAutoReschedule] =
    useState(false);

  const triggerAutoReschedule = async () => {
    setTriggeringAutoReschedule(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Use the new endpoint that schedules SMS for patients in the selected date
      const response = await fetch(`${baseUrl}/twilio/schedule-sms-for-date/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          schema: supabaseSchema,
          date: selectedDate, // Use the selected date from the admin page
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "SMS Scheduled Successfully",
          description:
            data.message ||
            `Scheduled ${data.scheduled_count || 0} SMS messages for ${selectedDate}`,
          status: "success",
          duration: 5000,
        });
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to schedule SMS messages",
          status: "error",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error scheduling SMS:", error);
      toast({
        title: "Error",
        description: "Failed to schedule SMS messages",
        status: "error",
        duration: 5000,
      });
    } finally {
      setTriggeringAutoReschedule(false);
    }
  };

  const handleApplyAutoReminderSettings = async (settings) => {
    // Save settings to database
    try {
      const supabase = await supabasePublic();

      // Convert time from "HH:MM" to "HH:MM:SS" format for database
      const timeStr = settings.scheduledTime
        ? settings.scheduledTime + ":00"
        : "";

      // Create the JSON settings object
      const reminderSettings = {
        days_before: settings.daysBeforeAppointment ?? null,
        time: timeStr,
        include_email: settings.includeEmail ?? false,
        include_phone: settings.includePhone ?? false,
        patient_types: settings.patientTypeFilter ?? [],
        schedule_enabled: settings.scheduleReminders ?? false,
      };

      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          auto_reminder_settings: reminderSettings,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        console.error("Error saving auto reminder settings:", error);
        toast({
          title: "Error",
          description: "Failed to save auto reminder settings to database",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving auto reminder settings:", error);
    }

    // Also update local state
    if (settings.daysBeforeAppointment !== undefined) {
      setReminderDaysBeforeAppointment(settings.daysBeforeAppointment);
      // Update reminderScheduledDate based on new daysBeforeAppointment value
      const newScheduledDate = getDayBeforeDateString(
        selectedDate,
        settings.daysBeforeAppointment,
      );
      setReminderScheduledDate(newScheduledDate || "");
    }
    if (settings.scheduledTime) {
      setReminderScheduledTime(settings.scheduledTime);
    }
    if (settings.patientTypeFilter !== undefined) {
      setReminderPatientTypeFilter(settings.patientTypeFilter);
    }
    if (
      settings.includeEmail !== undefined ||
      settings.includePhone !== undefined
    ) {
      setReminderContactMethods({
        email: settings.includeEmail,
        phone: settings.includePhone,
      });
    }
    if (settings.scheduleReminders !== undefined) {
      setScheduleReminderOneDayBefore(settings.scheduleReminders);
    }
  };

  const handleGenerateForm = async () => {
    debugLog("[FORM DEBUG] Starting form generation process");
    debugLog("[FORM DEBUG] Patient data:", selectedPatient);
    debugLog("[FORM DEBUG] Patient ID:", selectedPatient?.id);

    if (!selectedPatient) {
      toast({
        title: "Error",
        description: "Patient data is required to generate forms",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setGenerating(true);
    setProgress(20);

    try {
      // Get current date for form generation
      const today = new Date();
      const year = today.getFullYear().toString();
      const date = today.toISOString().split("T")[0]; // YYYY-MM-DD format

      // Handle patient name extraction with better fallbacks
      let patientName = "";
      if (selectedPatient.name) {
        patientName = selectedPatient.name;
      } else if (selectedPatient.LastName && selectedPatient.FirstName) {
        patientName = `${selectedPatient.LastName}, ${selectedPatient.FirstName}`;
      } else if (selectedPatient.LastName) {
        patientName = selectedPatient.LastName;
      } else if (selectedPatient.FirstName) {
        patientName = selectedPatient.FirstName;
      } else {
        // Use fuzzy field mapping to extract name
        const extractedInfo = extractPatientInfo(selectedPatient);
        patientName =
          extractedInfo.name ||
          `Patient ${selectedPatient?.id?.substring(0, 8) || "Unknown"}`;
      }

      debugLog("[FORM DEBUG] Form generation parameters:", {
        uuid: selectedPatient?.id,
        patient_name: patientName,
        year: year,
        date: date,
        model: selectedLLMModel,
      });

      setProgress(40);

      // Call the backend API to generate form
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      debugLog(
        "[FORM DEBUG] Making API call to:",
        `${baseUrl}/api/generate-form/`,
      );

      const response = await fetch(`${baseUrl}/api/generate-form/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: selectedPatient?.id,
          patient_name: patientName,
          year: year,
          date: date,
          model: selectedLLMModel, // Use selected LLM model
        }),
      });

      debugLog("[FORM DEBUG] API response status:", response.status);
      setProgress(60);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate form");
      }

      const result = await response.json();
      debugLog("[FORM DEBUG] API response data:", result);
      setProgress(80);

      if (result.success) {
        debugLog("[FORM DEBUG] Form generation successful");
        debugLog(
          "[FORM DEBUG] Generated form data keys:",
          Object.keys(result.form_data || {}),
        );
        debugLog(
          "[FORM DEBUG] Number of form items:",
          result.form_data?.items?.length || 0,
        );

        // Store the complete form data including form_id and form_link for later use
        const completeFormData = {
          ...result.form_data,
          form_id:
            result.form_data?.form_id || result.form_data?.metadata?.form_id,
          form_link:
            result.form_data?.form_link ||
            result.form_data?.metadata?.form_link,
          local_path:
            result.form_data?.local_path ||
            result.form_data?.metadata?.local_path,
        };

        debugLog("[FORM DEBUG] Complete form data:", completeFormData);
        setGeneratedForm(completeFormData);
        setProgress(100);

        toast({
          title: "Form Generated",
          description: "Patient form has been generated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        debugLog("[FORM DEBUG] Form generation failed:", result.error);
        throw new Error(result.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Error generating form:", error);
      toast({
        title: "Generation Failed",
        description:
          error.message || "An error occurred while generating the form",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  const handleSaveGeneratedForm = async () => {
    if (!generatedForm) return;

    try {
      // Form is already generated and saved during handleGenerateForm
      // We just need to add it to the submissions_array column
      debugLog(
        "[FORM SUBMISSION] Adding already generated form to submissions:",
      );

      // Create a result object that matches what processGeneratedFormResponse expects
      // The function looks for form_id at the top level or in form_data.metadata
      const resultData = {
        form_id: generatedForm.form_id,
        form_link: generatedForm.form_link,
        local_path: generatedForm.local_path,
        success: true,
        // Also include form_data structure for backward compatibility
        form_data: {
          metadata: {
            form_id: generatedForm.form_id,
            form_link: generatedForm.form_link,
            local_path: generatedForm.local_path,
            storage_path: generatedForm.local_path,
          },
        },
      };

      const addedToSubmissions = await processGeneratedFormResponse(
        selectedPatient?.id,
        resultData,
      );

      if (addedToSubmissions) {
        toast({
          title: "Form Saved and Added to Submissions",
          description:
            "The generated form has been saved and added to the patient's submission column",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Form Saved",
          description:
            "The generated form has been saved to the patient record",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      onSendFormClose();
    } catch (error) {
      console.error("Error saving form:", error);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save the generated form",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleOpenSettings = async () => {
    await fetchUserSettings();
    onSettingsOpen();
  };

  const handleSendFormsClick = async () => {
    const selectedPatientIds = Object.keys(selectedRows).filter(
      (key) => selectedRows[key],
    );

    const selectedCount = selectedPatientIds.length;
    if (selectedCount === 0) {
      // Auto select all FFP and FFS patients
      const ffpFfsPatients = patients.filter((p) => {
        const type = findPatientField(p, "type")?.toUpperCase();
        return type === "FFP" || type === "FFS";
      });

      if (ffpFfsPatients.length === 0) {
        toast({
          title: "No FFP/FFS Patients",
          description: "No FFP or FFS patients found in the current list.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const newSelectedRows = { ...selectedRows };
      ffpFfsPatients.forEach((p) => {
        if (p.id) {
          newSelectedRows[p.id] = true;
          // Also update schedule data if available
          const scheduleData =
            currentScheduleData[p.id] ||
            patients.find((pat) => pat.id === p.id);
          if (scheduleData) {
            setCurrentScheduleData((prev) => ({
              ...prev,
              [p.id]: scheduleData,
            }));
          }
        }
      });
      setSelectedRows(newSelectedRows);

      // Trigger the callback to sync with SchedulesTable
      if (handleSelectedRowChange) {
        handleSelectedRowChange({ selection: newSelectedRows });
      }

      toast({
        title: "Selected FFP and FFS Patients",
        description: `Auto-selected ${ffpFfsPatients.length} FFP and FFS patients.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Auto-select form type if FFP/FFS patients are selected
    // Find the selected patient objects
    const selectedPatientsList = patients.filter((p) =>
      selectedPatientIds.includes(p.id),
    );

    // Check if we have FFP/FFS patients
    let hasFFP = false;
    let formTypeToSelect = null;

    // Check types of selected patients
    for (const patient of selectedPatientsList) {
      const type = findPatientField(patient, "type")?.toUpperCase();
      if (
        type &&
        (type.includes("FFP") || type.includes("FFS") || type.includes("FF"))
      ) {
        hasFFP = true;
        // If it's specifically FFP or FFS, use that. Default to "FFS" if just "FF" (based on modal logic)
        formTypeToSelect = type;
        break; // Found an FFP/FFS patient, so we'll default to that form
      }
    }

    if (hasFFP) {
      debugLog(
        `[FORM SELECTION] Auto-selecting ${formTypeToSelect} form for FFP/FFS patient(s)`,
      );
      // Use the exact type if it's FFP/FFS, otherwise default to "FFS" as fallback
      // The modal logic handles just the type string for FFP/FFS
      const typeToSet = formTypeToSelect || "FFS";
      setFormType([typeToSet]);
    } else {
      setFormType([]);
    }

    onSendFormOpen();
  };

  // Two-state button, same shape as Send Forms: nothing selected opens the
  // hub; a selection first asks how to send — text, email, or both
  // (ConfirmationChannelDialog greys out what nobody selected can receive).
  // Rows already at C or R are filtered here and server-side too, so a stale
  // table can't re-ask someone who already answered.
  const handleConfirmationsClick = async () => {
    const selectedIds = Object.keys(selectedRows).filter((id) => selectedRows[id]);
    if (selectedIds.length === 0) {
      setIsConfirmationHubOpen(true);
      return;
    }
    const rows = selectedIds.map((pid) => {
      const row = currentScheduleData[pid] || patients.find((p) => p.id === pid);
      // Every number the patient can be texted on (cell, then home — see
      // utils/smsPhone); the backend texts the first that is not bad.
      const phones = row ? rowPhones(row) : [];
      if (row && !phones.length && extractPatientInfo(row).phone) phones.push(extractPatientInfo(row).phone);
      return {
        id: pid,
        phone: phones[0] || "",
        phones,
        email: row?.email || "",
        status: row?.confirmation_status,
      };
    });
    const badNumbers = await fetchBadNumbers(rows.flatMap((r) => r.phones));
    setConfirmationChannelSummary(summarizeConfirmationChannels(rows, badNumbers));
  };

  const sendConfirmations = async (choice, { includeBad = false } = {}) => {
    const summary = confirmationChannelSummary;
    setConfirmationChannelSummary(null);
    if (!summary) return;
    const plan = planConfirmationSend(summary, choice, { includeBad });
    const rowOf = (pid) => currentScheduleData[pid] || patients.find((p) => p.id === pid);
    const unreached = summary.total - summary.answered
      - new Set([...plan.sms, ...plan.email].map(String)).size;

    setIsSendingConfirmations(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" ? BASE_URLS.local.backend : BASE_URLS.render.backend;

      // A clinical admin's table can merge in rows from a linked provider's
      // schema (tagged `_schema` — see utils/rowSchema.js), so the viewer's
      // own `supabaseSchema` is not necessarily where a given selected row
      // actually lives. Sending them all under one blanket schema makes the
      // backend look up a foreign row in the wrong schema's notes-c: the
      // query succeeds but matches nothing, and the send is silently
      // "skipped: row not found" for every id that wasn't actually a home
      // row. Group by each row's real schema instead.
      const bySchema = (ids) => {
        const out = new Map();
        ids.forEach((pid) => {
          const schema = storageSchemaOf(rowOf(pid), supabaseSchema || "public");
          if (!out.has(schema)) out.set(schema, []);
          out.get(schema).push(pid);
        });
        return out;
      };
      const post = async (path, body) => {
        const response = await fetch(`${baseUrl}/twilio/${path}/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const payload = await response.json();
        if (!response.ok || payload.status !== "success") {
          throw new Error(payload.error || `HTTP ${response.status}`);
        }
        return payload;
      };

      const results = [];
      const confirmBad = new Set(plan.confirmBad.map(String));
      for (const [schema, ids] of bySchema(plan.sms)) {
        const bad = ids.filter((pid) => confirmBad.has(String(pid)));
        // Texts only: staff chose the channel, so no automatic email here.
        const payload = await post("send-confirmations", {
          schema, patient_ids: ids, email_fallback: false,
          ...(bad.length ? { confirm_bad_number_ids: bad } : {}),
        });
        results.push(...(payload.results || []).map((r) => ({ ...r, channel: "sms" })));
      }
      for (const [schema, ids] of bySchema(plan.email)) {
        const payload = await post("send-email-confirmations", { schema, patient_ids: ids });
        results.push(...(payload.results || []).map((r) => ({ ...r, channel: "email" })));
      }

      const count = (pred) => results.filter(pred).length;
      const texted = count((r) => r.channel === "sms" && (r.result === "sent" || r.result === "queued"));
      const emailed = count((r) => r.result === "emailed");
      const skipped = count((r) => r.result === "skipped") + unreached;
      const failed = count((r) => r.result === "failed");
      const first = (kind) => (results.find((r) => r.result === kind) || {}).reason;
      const parts = [];
      if (plan.sms.length) parts.push(`${texted} text${texted === 1 ? "" : "s"}`);
      if (plan.email.length) parts.push(`${emailed} email${emailed === 1 ? "" : "s"}`);
      toast({
        title: `Sent ${parts.join(" and ") || "nothing"}`,
        description:
          `${skipped} skipped, ${failed} failed.` +
          (failed ? ` First failure: ${first("failed")}.` : "") +
          // When nothing went out, say why — otherwise "0 sent, 1 skipped"
          // for a past-dated appointment reads like a silent failure.
          (texted + emailed === 0 && count((r) => r.result === "skipped")
            ? ` Reason: ${first("skipped")}.`
            : ""),
        status: failed ? "warning" : texted + emailed === 0 ? "info" : "success",
        duration: 7000,
        isClosable: true,
      });
      await fetchPatients();
      // SchedulesTable owns the Status badge's "no auto" grading; make it reload.
      window.dispatchEvent(new CustomEvent("patientDataRefresh"));
    } catch (error) {
      toast({
        title: "Failed to send confirmations",
        description: error.message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsSendingConfirmations(false);
    }
  };

  // Runs the confirmation scheduler pass for the whole schema now — the
  // confirmations tab's counterpart of Trigger Auto-Reminders.
  const handleTriggerAutoConfirmations = async () => {
    setIsTriggeringAutoConfirmations(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" ? BASE_URLS.local.backend : BASE_URLS.render.backend;
      // revive_cancelled: the Trigger also puts cancelled texts whose send
      // time is still ahead back on the queue — the background pass never does.
      const response = await fetch(`${baseUrl}/twilio/schedule-confirmations/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schema: supabaseSchema || "public", revive_cancelled: true }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      toast({
        title: "Auto-confirmations scheduled",
        description:
          `Queued ${payload.scheduled}` +
          (payload.revived ? ` (${payload.revived} restored from cancelled)` : "") +
          `, cancelled ${payload.cancelled}` +
          (payload.errors ? `, ${payload.errors} error(s)` : "") +
          ".",
        status: payload.errors ? "warning" : "success",
        duration: 5000,
        isClosable: true,
      });
      setConfirmationPanelRefresh((n) => n + 1);
      // SchedulesTable owns the Status badge's "no auto" grading; make it reload.
      window.dispatchEvent(new CustomEvent("patientDataRefresh"));
    } catch (error) {
      toast({
        title: "Could not schedule auto-confirmations",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsTriggeringAutoConfirmations(false);
    }
  };

  const handleSelectAllUnconfirmed = (ids) => {
    if (ids.length === 0) {
      toast({
        title: "Nothing to confirm",
        description:
          "Every patient on this date has already replied, or has no phone number.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const next = { ...selectedRows };
    ids.forEach((id) => {
      next[id] = true;
    });
    setSelectedRows(next);
    // Keeps SchedulesTable's checkboxes in sync — the same call Send Forms makes.
    if (handleSelectedRowChange) {
      handleSelectedRowChange({ selection: next });
    }
    toast({
      title: `Selected ${ids.length} unconfirmed patient${ids.length === 1 ? "" : "s"}`,
      description: "Click Send Confirmation to text them.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });
  };

  // Multi-patient SMS selector by patient type
  const [smsPatientTypeFilter, setSmsPatientTypeFilter] = useState([]); // Array of selected patient types for SMS
  const [smsSelectedPatients, setSmsSelectedPatients] = useState({}); // Selected patients for SMS by type

  // Get unique patient types from all patients
  const patientTypes = useMemo(() => {
    const types = new Set();
    patients.forEach((patient) => {
      const type = findPatientField(patient, "type")?.toUpperCase();
      if (type) {
        types.add(type);
      }
    });
    return Array.from(types).sort();
  }, [patients]);

  // Get patients by selected types
  const patientsByType = useMemo(() => {
    if (smsPatientTypeFilter.length === 0) {
      return {};
    }
    const grouped = {};
    patients.forEach((patient) => {
      const type = findPatientField(patient, "type")?.toUpperCase();
      if (smsPatientTypeFilter.includes(type)) {
        if (!grouped[type]) {
          grouped[type] = [];
        }
        grouped[type].push(patient);
      }
    });
    return grouped;
  }, [patients, smsPatientTypeFilter]);

  // Get total patient count per type (regardless of filter selection)
  const totalPatientsByType = useMemo(() => {
    const counts = {};
    patients.forEach((patient) => {
      const type = findPatientField(patient, "type")?.toUpperCase();
      if (type) {
        counts[type] = (counts[type] || 0) + 1;
      }
    });
    return counts;
  }, [patients]);

  // Handle patient type selection for SMS - auto-selects patients when type is checked
  const handleSmsPatientTypeChange = (type, isChecked) => {
    setSmsPatientTypeFilter((prev) => {
      if (isChecked) {
        // When type is checked, auto-select all patients of that type
        setSmsSelectedPatients((prevSel) => {
          const updated = { ...prevSel };
          patients.forEach((patient) => {
            const patientType = findPatientField(
              patient,
              "type",
            )?.toUpperCase();
            if (patientType === type) {
              updated[patient.id] = true;
            }
          });
          return updated;
        });
        return [...prev, type];
      } else {
        // When type is unchecked, remove all patients of that type from selection
        setSmsSelectedPatients((prevSel) => {
          const updated = { ...prevSel };
          patients.forEach((patient) => {
            const patientType = findPatientField(
              patient,
              "type",
            )?.toUpperCase();
            if (patientType === type) {
              delete updated[patient.id];
            }
          });
          return updated;
        });
        return prev.filter((t) => t !== type);
      }
    });
  };

  // Select/deselect all patients of a type
  const handleSelectAllByType = (type, select) => {
    setSmsSelectedPatients((prev) => {
      const updated = { ...prev };
      patients.forEach((patient) => {
        const patientType = findPatientField(patient, "type")?.toUpperCase();
        if (patientType === type) {
          updated[patient.id] = select;
        }
      });
      return updated;
    });
  };

  // Toggle individual patient selection in SMS modal
  const handleSmsPatientToggle = (patientId) => {
    // Get the patient data from the patients array
    const patient = patients.find((p) => p.id === patientId);

    setSmsSelectedPatients((prev) => {
      const isSelecting = !prev[patientId];

      if (isSelecting && patient) {
        // When selecting, also populate selectedPatientsContact with phone info
        const extractedInfo = extractPatientInfo(patient);
        setSelectedPatientsContact((prevContact) => ({
          ...prevContact,
          [patientId]: {
            email: extractedInfo.email || "",
            phone: extractedInfo.phone || "",
            name:
              patient[prompts[0][0]] ||
              patient.name ||
              patient[colid2] ||
              "Unknown Patient",
          },
        }));
      } else if (!isSelecting) {
        // When deselecting, remove from selectedPatientsContact
        setSelectedPatientsContact((prevContact) => {
          const newContact = { ...prevContact };
          delete newContact[patientId];
          return newContact;
        });
      }

      return {
        ...prev,
        [patientId]: !prev[patientId],
      };
    });
  };

  // Handle Send Followup Click - open modal with patient data (supports multiple patients)
  const handleSendFollowupClick = async () => {
    // Get selected patient data from selectedRows or selectedPatient
    const selectedIds = Object.keys(selectedRows).filter(
      (id) => selectedRows[id],
    );

    let patientsList = [];

    // First check if there are selected patients in selectedRows
    if (selectedIds.length > 0) {
      // Collect all selected patients
      selectedIds.forEach((patientId) => {
        const patient =
          currentScheduleData[patientId] ||
          patients.find((p) => p.id === patientId);

        if (patient) {
          // Extract patient info
          const extractedInfo = extractPatientInfo(patient);
          const patientNameField = prompts[0][0];
          const patientInfo = {
            patientId: patient.id,
            patientName:
              patient[patientNameField] ||
              patient.name ||
              patient[colid2] ||
              "Unknown Patient",
            patientPhone: extractedInfo.phone || rowPhones(patient)[0] || "",
            appointmentDate: patient.date_ || null,
            appointmentType: patient.type || null,
            // Pass full patient object for document fetching
            patientRecord: patient,
            patientNameField: patientNameField,
          };
          patientsList.push(patientInfo);
        }
      });
    } else if (selectedPatient) {
      // Fallback to selectedPatient (single patient)
      const extractedInfo = extractPatientInfo(selectedPatient);
      const patientNameField = prompts[0][0];
      patientsList.push({
        patientId: selectedPatient.id,
        patientName:
          selectedPatient[patientNameField] ||
          selectedPatient.name ||
          selectedPatient[colid2] ||
          "Unknown Patient",
        patientPhone: extractedInfo.phone || rowPhones(selectedPatient)[0] || "",
        appointmentDate: selectedPatient.date_ || null,
        appointmentType: selectedPatient.type || null,
        // Pass full patient object for document fetching
        patientRecord: selectedPatient,
        patientNameField: patientNameField,
      });
    }

    if (patientsList.length === 0) {
      toast({
        title: "No Patient Selected",
        description:
          "Please select at least one patient to send a follow-up message",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if any patient has a phone number
    const patientsWithPhone = patientsList.filter((p) => p.patientPhone);
    if (patientsWithPhone.length === 0) {
      toast({
        title: "No Phone Numbers",
        description: "None of the selected patients have a phone number",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Open the follow-up modal with patients list (could be single or multiple)
    // If only one patient, pass as single object for backward compatibility
    // Otherwise pass as array
    const patientData =
      patientsList.length === 1 ? patientsList[0] : { patients: patientsList };

    setFollowupPatientData(patientData);
    setIsFollowupModalOpen(true);
  };

  // Handle Send SMS Click - open modal to select patients; carry over schedule selection if any
  const handleSendSMSClick = async () => {
    const selectedIds = Object.keys(selectedRows).filter(
      (id) => selectedRows[id],
    );
    if (selectedIds.length > 0) {
      const nextSmsSelection = {};
      selectedIds.forEach((id) => {
        nextSmsSelection[id] = true;
      });
      setSmsSelectedPatients(nextSmsSelection);
      const nextContact = { ...selectedPatientsContact };
      selectedIds.forEach((patientId) => {
        const patient = patients.find((p) => p.id === patientId);
        if (patient) {
          const extractedInfo = extractPatientInfo(patient);
          nextContact[patientId] = {
            email: extractedInfo.email || "",
            phone: extractedInfo.phone || "",
            name:
              patient[prompts[0][0]] ||
              patient.name ||
              patient[colid2] ||
              "Unknown Patient",
          };
        }
      });
      setSelectedPatientsContact(nextContact);
    }
    setIsSendSMSModalOpen(true);
  };

  // Normalize phone to 10 digits (matches backend utils.normalize_phone)
  const normalizePhoneForDNT = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
    if (digits.length >= 10) return digits.slice(-10);
    return null;
  };

  // PostgREST caps a response at 1000 rows, so page through the whole list.
  const DNT_PAGE_SIZE = 1000;
  const dntFetchSeq = useRef(0);

  // silent: background refresh (page load / tab focus) — no spinner, no error toast.
  const fetchDoNotTextEntries = async ({ silent = false } = {}) => {
    const seq = ++dntFetchSeq.current;
    if (!silent) setDntLoading(true);
    try {
      const supabase = await getSupabase();
      const all = [];
      for (let from = 0; ; from += DNT_PAGE_SIZE) {
        const { data, error } = await supabase
          .from("Do_Not_Text")
          .select("id, phone_number, reason, created_at")
          .order("created_at", { ascending: false })
          .order("id", { ascending: false })
          .range(from, from + DNT_PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < DNT_PAGE_SIZE) break;
      }
      // Drop responses superseded by a newer fetch (e.g. focus refresh racing an add).
      if (seq === dntFetchSeq.current) setDntEntries(all);
    } catch (err) {
      if (!silent) {
        toast({
          title: "Failed to load Do Not Text list",
          description: err?.message || "Could not load the list.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      if (!silent) setDntLoading(false);
    }
  };

  // The modal's phone input doubles as a search box: the list is filtered to entries
  // whose digits contain what's typed ("+1 626…" also matches, leading 1 dropped).
  const dntSearchDigits = String(dntPhoneInput || "").replace(/\D/g, "");
  const dntFilteredEntries = dntSearchDigits
    ? dntEntries.filter((e) => {
        const phone = String(e.phone_number).replace(/\D/g, "");
        return (
          phone.includes(dntSearchDigits) ||
          (dntSearchDigits.startsWith("1") &&
            phone.startsWith(dntSearchDigits.slice(1)))
        );
      })
    : dntEntries;
  const dntInputNormalized = normalizePhoneForDNT(dntPhoneInput);
  const dntInputOnList =
    !!dntInputNormalized &&
    dntEntries.some((e) => String(e.phone_number) === dntInputNormalized);

  const openDoNotTextModal = () => {
    setIsAddDNTModalOpen(true);
    fetchDoNotTextEntries();
  };

  const handleRemoveDoNotText = async (phoneNumber) => {
    setDntRemovingPhone(phoneNumber);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from("Do_Not_Text")
        .delete()
        .eq("phone_number", phoneNumber);
      if (error) throw error;
      setDntEntries((prev) =>
        prev.filter((e) => e.phone_number !== phoneNumber),
      );
      toast({
        title: "Removed from Do Not Text",
        description: `Phone ...${String(phoneNumber).slice(-4)} can receive texts again.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: err?.message || "Could not remove from Do Not Text list.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDntRemovingPhone(null);
    }
  };

  const handleAddDoNotText = async () => {
    const normalized = normalizePhoneForDNT(dntPhoneInput);
    if (!normalized || normalized.length < 10) {
      toast({
        title: "Invalid phone number",
        description:
          "Enter at least 10 digits (e.g. 6265551234 or +1 626-555-1234).",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setDntSubmitting(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from("Do_Not_Text")
        .upsert(
          { phone_number: normalized, reason: "Added from Admin" },
          { onConflict: "phone_number" },
        );
      if (error) throw error;
      toast({
        title: "Added to Do Not Text",
        description: `Phone ...${normalized.slice(-4)} has been added.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setDntPhoneInput("");
      fetchDoNotTextEntries();
    } catch (err) {
      toast({
        title: "Failed to add",
        description: err?.message || "Could not add to Do Not Text list.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDntSubmitting(false);
    }
  };

  // With patients checked, the "Do Not Text List" button becomes "Add to Do Not Text List",
  // or "Remove from Do Not Text List" when every checked patient's phone is already listed.
  const hasSelectedPatients = Object.values(selectedRows).some(Boolean);

  const selectedDntInfo = useMemo(() => {
    const selectedIds = Object.keys(selectedRows).filter(
      (id) => selectedRows[id],
    );
    const phones = new Set();
    let missingPhone = 0;
    selectedIds.forEach((id) => {
      const patient =
        patients.find((p) => p.id === id) || currentScheduleData[id];
      const normalized = patient
        ? normalizePhoneForDNT(extractPatientInfo(patient).phone)
        : null;
      if (normalized) phones.add(normalized);
      else missingPhone += 1;
    });
    return { selectedIds, phones: [...phones].sort(), missingPhone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, patients, currentScheduleData]);

  // The Do Not Text list is loaded on page load and refreshed whenever the tab regains
  // focus, so the Add/Remove button label flips instantly on checkbox clicks instead of
  // waiting on a per-click query. Every add/remove also updates dntEntries directly.
  useEffect(() => {
    const refresh = () => fetchDoNotTextEntries({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dntPhoneSet = useMemo(
    () => new Set(dntEntries.map((e) => String(e.phone_number))),
    [dntEntries],
  );

  const allSelectedOnDnt =
    selectedDntInfo.phones.length > 0 &&
    selectedDntInfo.phones.every((p) => dntPhoneSet.has(p));

  const describeSelectedPhones = (verb) => {
    const { phones, missingPhone } = selectedDntInfo;
    return (
      (phones.length === 1
        ? `Phone ...${phones[0].slice(-4)} has been ${verb}.`
        : `${phones.length} phone numbers have been ${verb}.`) +
      (missingPhone
        ? ` ${missingPhone} patient(s) skipped (no valid phone).`
        : "")
    );
  };

  const toastNoSelectedPhone = (title) => {
    toast({
      title,
      description:
        selectedDntInfo.selectedIds.length === 1
          ? "The selected patient has no valid phone number."
          : "None of the selected patients have a valid phone number.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  // Add the phone numbers of the patients checked in the patient list to Do Not Text.
  const handleAddSelectedToDoNotText = async () => {
    const { phones, missingPhone } = selectedDntInfo;
    if (phones.length === 0) {
      toastNoSelectedPhone("Failed to add");
      return;
    }

    setDntSubmitting(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from("Do_Not_Text").upsert(
        phones.map((phone_number) => ({
          phone_number,
          reason: "Added from Admin",
        })),
        // Leave already-listed rows alone so a patient's own QUIT/EXIT reason survives.
        { onConflict: "phone_number", ignoreDuplicates: true },
      );
      if (error) throw error;
      setDntEntries((prev) => [
        ...phones
          .filter((p) => !prev.some((e) => String(e.phone_number) === p))
          .map((phone_number) => ({
            phone_number,
            reason: "Added from Admin",
            created_at: new Date().toISOString(),
          })),
        ...prev,
      ]);
      // Pick up the real ids/timestamps for the modal.
      fetchDoNotTextEntries({ silent: true });
      toast({
        title: "Added to Do Not Text",
        description: describeSelectedPhones("added"),
        status: missingPhone ? "warning" : "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Failed to add",
        description: err?.message || "Could not add to Do Not Text list.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDntSubmitting(false);
    }
  };

  // Remove the phone numbers of the checked patients from Do Not Text.
  const handleRemoveSelectedFromDoNotText = async () => {
    const { phones, missingPhone } = selectedDntInfo;
    if (phones.length === 0) {
      toastNoSelectedPhone("Failed to remove");
      return;
    }

    setDntSubmitting(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from("Do_Not_Text")
        .delete()
        .in("phone_number", phones);
      if (error) throw error;
      setDntEntries((prev) =>
        prev.filter((e) => !phones.includes(String(e.phone_number))),
      );
      toast({
        title: "Removed from Do Not Text",
        description: describeSelectedPhones("removed"),
        status: missingPhone ? "warning" : "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: err?.message || "Could not remove from Do Not Text list.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDntSubmitting(false);
    }
  };

  const handleSendSMS = async () => {
    // Get selected patient IDs from the SMS modal
    const selectedPatientIds = Object.keys(smsSelectedPatients).filter(
      (id) => smsSelectedPatients[id],
    );

    if (selectedPatientIds.length === 0) {
      toast({
        title: "No Patients Selected",
        description: "Please select at least one patient to send SMS to.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!smsMessage.trim()) {
      toast({
        title: "No Message",
        description: "Please enter a message to send.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check for missing phone numbers before sending (fallback to patient row when using Select All)
    const patientsWithoutPhone = [];
    const smsRecipients = [];
    for (const patientId of selectedPatientIds) {
      let contactInfo = selectedPatientsContact[patientId];
      let patient = null;
      if (currentScheduleData[patientId]) {
        patient = currentScheduleData[patientId];
      } else {
        patient = patients.find((p) => p.id === patientId);
      }
      if (patient && (!contactInfo || !contactInfo.phone?.trim())) {
        const extracted = extractPatientInfo(patient);
        contactInfo = {
          phone: extracted.phone || rowPhones(patient)[0] || "",
          email: contactInfo?.email || extracted.email || "",
        };
      }
      const patientName =
        patient?.[prompts[0][0]] ||
        patient?.name ||
        patient?.[colid2] ||
        "Unknown Patient";
      if (!contactInfo || !contactInfo.phone?.trim()) {
        patientsWithoutPhone.push(patientName);
      } else {
        smsRecipients.push({
          phones: [contactInfo.phone, altPhoneFor(patient, contactInfo.phone)].filter(Boolean),
          name: patientName,
        });
      }
    }

    // If any patients are missing phone numbers, show alert and stop
    if (patientsWithoutPhone.length > 0) {
      toast({
        title: "Missing Phone Numbers",
        description: `The following patients are missing phone numbers and need to be edited: ${patientsWithoutPhone.join(
          ", ",
        )}. Please edit these patients to add their phone numbers before sending SMS.`,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
      return;
    }

    const badNumberGuard = await confirmBadNumbers(smsRecipients);
    if (!badNumberGuard) return;

    setIsSendingSMS(true);

    try {
      const settings = await fetchUserSettings();
      debugLog("Using settings:", settings);

      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      // Check if we have the necessary Twilio credentials
      if (!settings.TWILIO_PHONE_NUMBER) {
        toast({
          title: "Missing Twilio Configuration",
          description: "Please configure your Twilio phone number in settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Send SMS to all selected patients
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const patientId of selectedPatientIds) {
        let contactInfo = selectedPatientsContact[patientId];

        // Prioritize scheduleData if available, otherwise search in patients array
        let patient = null;
        if (currentScheduleData[patientId]) {
          patient = currentScheduleData[patientId];
          debugLog(`[SYNC] Using scheduleData for SMS: ${patientId}`);
        } else {
          patient = patients.find((p) => p.id === patientId);
          if (patient) {
            debugLog(`[SYNC] Using patients array for SMS: ${patientId}`);
          } else {
            debugLog(
              `[SYNC] WARNING: No patient data found for SMS: ${patientId}`,
            );
          }
        }

        // When using Select All, selectedPatientsContact is empty – fallback to patient row
        if (patient && (!contactInfo || !contactInfo.phone?.trim())) {
          const extracted = extractPatientInfo(patient);
          contactInfo = {
            phone: extracted.phone || rowPhones(patient)[0] || "",
            email: contactInfo?.email || extracted.email || "",
          };
        }

        const patientName =
          patient?.[prompts[0][0]] ||
          patient?.name ||
          patient?.[colid2] ||
          "Patient";

        if (!contactInfo || !contactInfo.phone?.trim()) {
          errorCount++;
          errors.push(`Missing phone number for ${patientName}`);
          continue;
        }

        if (badNumberGuard.skipped(contactInfo.phone)) {
          errorCount++;
          errors.push(`${patientName}: skipped (known bad number)`);
          continue;
        }

        // Schedule date: use patient's appointment date or Admin page selected date (required for SMS Status to show correct date)
        const scheduleDate =
          patient?.date_ ||
          patient?.["Scheduled Date"] ||
          patient?.appointment_date ||
          selectedDate ||
          "";
        const requestData = {
          df1: {
            name: patientName,
            phone: contactInfo.phone.replace(/\D/g, ""),
            alt_phone: altPhoneFor(patient, contactInfo.phone, { digitsOnly: true }),
            message: smsMessage,
            twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
            split: splitMessage,
            user_id: userId,
            date_: scheduleDate,
            appointment_date: scheduleDate,
            character_limit: customSmsCharacterLimit, // Custom SMS character limit setting
            contact_methods: { phone: true, email: false }, // SMS-only
            confirm_bad_number: badNumberGuard.confirmed(contactInfo.phone),
          },
          fpath: "manual_sms",
          schedule_date: scheduleDate || undefined, // Top-level so backend always gets Admin page date
          schema: supabaseSchema, // For backend to store in correct schema
        };

        debugLog(`[SMS] Sending SMS to patient ${patientId}:`, requestData);

        try {
          const sendFormsUrl = getSendFormsUrl();
          const response = await fetch(sendFormsUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            credentials: "include",
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            // Check if blocked by Do Not Text list
            if (
              errorData.error_code === "DNT_BLOCKED" ||
              errorData.error?.includes("Do Not Text")
            ) {
              errorCount++;
              errors.push(`${patientName}: On do not text list`);
              toast({
                title: "Message Blocked",
                description: `Message to ${patientName} cannot be sent! They are on do not text list.`,
                status: "warning",
                duration: 8000,
                isClosable: true,
              });
              continue;
            }
            throw new Error(
              errorData.error || errorData.message || "Failed to send SMS",
            );
          }

          const responseData = await response.json();
          debugLog(`[SMS] Response for patient ${patientId}:`, responseData);
          successCount++;
        } catch (error) {
          console.error(`Error sending SMS to patient ${patientId}:`, error);
          errorCount++;
          errors.push(`${patientName}: ${error.message}`);
        }
      }

      // Show summary results
      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "SMS Sent Successfully",
          description: `SMS sent to all ${successCount} selected patients.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial Success",
          description: `SMS sent to ${successCount} patients. ${errorCount} failed: ${errors.join(
            ", ",
          )}`,
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to Send SMS",
          description: `All ${errorCount} patients failed: ${errors.join(
            ", ",
          )}`,
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      }

      setIsSendSMSModalOpen(false);
      setSmsMessage("");
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast({
        title: "Send Failed",
        description:
          error.message || "An error occurred while sending the SMS.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingSMS(false);
    }
  };

  // Send Welcome Message to selected patients
  const handleSendWelcome = async () => {
    // Get selected patient IDs from the SMS modal
    const selectedPatientIds = Object.keys(smsSelectedPatients).filter(
      (id) => smsSelectedPatients[id],
    );

    if (selectedPatientIds.length === 0) {
      toast({
        title: "No Patients Selected",
        description:
          "Please select at least one patient to send welcome message to.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Preview the welcome message before sending
    try {
      // Get first selected patient's name for preview
      const firstPatientId = selectedPatientIds[0];
      let patient = null;
      if (currentScheduleData[firstPatientId]) {
        patient = currentScheduleData[firstPatientId];
      } else {
        patient = patients.find((p) => p.id === firstPatientId);
      }
      const patientName =
        patient?.[prompts[0][0]] ||
        patient?.name ||
        patient?.[colid2] ||
        "Patient";

      const previewUrl = getSendFormsUrl().replace(
        "/send-forms",
        "/preview-welcome",
      );

      const response = await fetch(previewUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          patient_name: patientName,
          schema: supabaseSchema,
          appointment_date: patient?.date_ || selectedDate || "",
          appointment_time: patient?.time_ || "",
          patient_id: patient?.id || firstPatientId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to preview welcome message");
      }

      const responseData = await response.json();
      setWelcomePreviewMessage(responseData.message);
      onWelcomePreviewOpen();
    } catch (error) {
      console.error("Error previewing welcome message:", error);
      toast({
        title: "Preview Failed",
        description:
          error.message ||
          "An error occurred while previewing welcome message.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const confirmSendWelcome = async () => {
    onWelcomePreviewClose();

    const selectedPatientIds = Object.keys(smsSelectedPatients).filter(
      (id) => smsSelectedPatients[id],
    );

    const targets = selectedPatientIds.map((patientId) => {
      let contactInfo = selectedPatientsContact[patientId];

      // Get patient data
      let patient = null;
      if (currentScheduleData[patientId]) {
        patient = currentScheduleData[patientId];
      } else {
        patient = patients.find((p) => p.id === patientId);
      }

      const patientName =
        patient?.[prompts[0][0]] ||
        patient?.name ||
        patient?.[colid2] ||
        "Patient";

      // When using "Select All", selectedPatientsContact is never filled – fallback to patient row
      if (patient && (!contactInfo || !contactInfo.phone?.trim())) {
        const extracted = extractPatientInfo(patient);
        contactInfo = {
          phone: extracted.phone || rowPhones(patient)[0] || "",
          email: contactInfo?.email || extracted.email || "",
          name: patientName,
        };
      }
      return { patientId, patient, patientName, contactInfo };
    });

    const badNumberGuard = await confirmBadNumbers(
      targets.map((t) => ({
        phones: [t.contactInfo?.phone, altPhoneFor(t.patient, t.contactInfo?.phone)].filter(Boolean),
        name: t.patientName,
      })),
    );
    if (!badNumberGuard) return;

    setIsSendingWelcome(true);

    try {
      const settings = await fetchUserSettings();
      const backendUrl = getSendFormsUrl().replace(
        "/send-forms",
        "/send-welcome",
      );

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const { patientId, patient, patientName, contactInfo } of targets) {
        if (!contactInfo || !contactInfo.phone?.trim()) {
          errorCount++;
          errors.push(`Missing phone number for ${patientName}`);
          continue;
        }

        if (badNumberGuard.skipped(contactInfo.phone)) {
          errorCount++;
          errors.push(`${patientName}: skipped (known bad number)`);
          continue;
        }

        try {
          const response = await fetch(backendUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              patient_name: patientName,
              phone: contactInfo.phone.replace(/\D/g, ""),
              home_phone: altPhoneFor(patient, contactInfo.phone, { digitsOnly: true }),
              email: contactInfo.email || "",
              appointment_date: patient?.date_ || selectedDate || "",
              appointment_time: patient?.time_ || "",
              patient_id: patient?.id || patientId,
              schema: supabaseSchema,
              twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
              zoho_email: settings.ZOHO_EMAIL || "",
              zoho_password: settings.ZOHO_PASSWORD || "",
              confirm_bad_number: badNumberGuard.confirmed(contactInfo.phone),
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.error || "Failed to send welcome message",
            );
          }

          const responseData = await response.json();
          debugLog(
            `[WELCOME] Response for patient ${patientId}:`,
            responseData,
          );
          successCount++;
        } catch (error) {
          console.error(
            `Error sending welcome to patient ${patientId}:`,
            error,
          );
          errorCount++;
          errors.push(`${patientName}: ${error.message}`);
        }
      }

      // Show summary results
      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "Welcome Messages Sent",
          description: `Welcome message sent to all ${successCount} selected patients.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial Success",
          description: `Welcome message sent to ${successCount} patients. ${errorCount} failed: ${errors.join(", ")}`,
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to Send",
          description: `All ${errorCount} patients failed: ${errors.join(", ")}`,
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      }

      setIsSendSMSModalOpen(false);
    } catch (error) {
      console.error("Error sending welcome messages:", error);
      toast({
        title: "Send Failed",
        description:
          error.message || "An error occurred while sending welcome messages.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingWelcome(false);
    }
  };

  // SMS Status functionality
  const handleSMSStatusClick = async () => {
    setIsSMSStatusModalOpen(true);
    // Use the selected date from URL query parameter (same as SchedulesTable uses)
    const urlDate = new URLSearchParams(location.search).get("date");
    const dateToUse = urlDate || selectedDate || getDefaultDateString();
    setSelectedSMSDate(dateToUse);
    setSelectedAppointmentDate(dateToUse); // Update appointment date to match selected date
    setSelectedErrorCodes([]); // Reset error code filters when opening modal
    setSelectedAppointmentDateErrorCodes([]); // Reset error code filters for appointment date tab
    setEditedSMS({}); // Reset edited SMS when opening modal
    setSplitSMS({}); // Reset split preferences when opening modal
    setSplitAllMessages(false); // Reset global split option
    setSelectedSMSForResend({}); // Reset selected SMS for resend (Created Date tab)
    setSelectedAppointmentDateSMSForResend({}); // Reset selected SMS for resend (Appointment Date tab)
    setSelectedSMSPatient(""); // Reset patient selection

    // Set calendar month to the selected date's month
    const selectedDateObj = new Date(dateToUse);
    setCalendarMonth(selectedDateObj);

    // Set appointment calendar month to the selected date's month
    setAppointmentCalendarMonth(new Date(dateToUse));

    // Fetch calendar data for the month (created date)
    await fetchFailedSMSCountsForMonth(
      selectedDateObj.getFullYear(),
      selectedDateObj.getMonth(),
    );

    // Fetch appointment calendar data for the month
    await fetchSMSCountsForAppointmentMonth(
      new Date(dateToUse).getFullYear(),
      new Date(dateToUse).getMonth(),
    );

    // Fetch patients for the SMS date (from schedule table)
    await fetchPatientsForSMSDate(dateToUse);

    await fetchSMSHealthStatus(dateToUse);
  };

  // Fetch patients for a specific date (used by SMS Status modal)
  const fetchPatientsForSMSDate = async (date) => {
    const supabase = await getSupabase();
    try {
      // Same fan-out as fetchPatients: this replaces the page's patient list,
      // so it must keep every selected provider's rows (tagged `_schema`).
      const results = await Promise.all(
        smsStatusSchemas.map(async (schema) => {
          try {
            let query = supabase.schema(schema).from(tableName).select("*");
            // If we have a date, scope patients to that day's schedule
            if (date) {
              query = query.eq("date_", date);
            }
            const { data: rows, error } = await query;
            if (error) throw error;
            return { schema, rows: rows || [] };
          } catch (err) {
            console.warn(
              `[SMS Status] patients for "${schema}" failed:`,
              err?.message || err,
            );
            return { schema, rows: null };
          }
        }),
      );
      if (results.every((r) => r.rows === null)) {
        throw new Error(
          `Could not load patients for: ${smsStatusSchemas.join(", ")}`,
        );
      }
      const data = mergeSchemaResults(results);

      debugLog("Fetched patients for SMS date:", {
        date,
        count: data?.length || 0,
        data,
      });

      setPatients(data || []);
    } catch (error) {
      console.error("Error fetching patients for SMS date:", error.message);
    }
  };

  // Helper function to format date string (YYYY-MM-DD) to locale date string without timezone issues
  const formatDateString = (dateString) => {
    if (!dateString) return "";
    // Parse date string (YYYY-MM-DD) to avoid timezone issues
    const [year, month, day] = dateString.split("-");
    const dateObj = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
    );
    return dateObj.toLocaleDateString();
  };

  // Helper function to format ISO datetime string to local datetime string
  // This correctly shows when the reminder will be sent in PST
  const formatLocalDateTimeString = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  };

  const handleSMSDateChange = async (date) => {
    setSelectedSMSDate(date);
    setSelectedErrorCodes([]); // Reset error code filters when date changes
    setEditedSMS({}); // Reset edited SMS when date changes
    setSplitSMS({}); // Reset split preferences when date changes
    setSplitAllMessages(false); // Reset global split option
    setSelectedSMSForResend({}); // Reset selected SMS when date changes
    setSelectedSMSPatient(""); // Reset patient selection when date changes

    // Update calendar month if date changes to a different month
    const dateObj = new Date(date);
    const currentMonth = calendarMonth.getMonth();
    const currentYear = calendarMonth.getFullYear();
    if (
      dateObj.getMonth() !== currentMonth ||
      dateObj.getFullYear() !== currentYear
    ) {
      setCalendarMonth(dateObj);
      await fetchFailedSMSCountsForMonth(
        dateObj.getFullYear(),
        dateObj.getMonth(),
      );
    }

    // Fetch patients for the new date
    await fetchPatientsForSMSDate(date);

    await fetchSMSHealthStatus(date);
  };

  // Get Twilio error code description
  const getTwilioErrorDescription = (errorCode) => {
    const errorCodeMap = {
      30001:
        "Queue overflow - The queue limit has been reached. Wait and try again.",
      30002:
        "Account suspended - Your account has been suspended. Contact Twilio support.",
      30003:
        "Unreachable destination handset - The destination phone number is unreachable. The phone may be turned off or out of coverage.",
      30004:
        "Message blocked - The message was blocked. Check if the destination number is blocked or if there are carrier restrictions.",
      30005:
        "Unknown destination handset - The destination phone number is unknown or invalid.",
      30006:
        "Landline or unreachable carrier - The destination number is a landline or the carrier is unreachable.",
      30007:
        "Carrier violation - The message violates carrier policies. Review message content.",
      30008: "Unknown error - An unknown error occurred. Try again later.",
      30009:
        "Missing segment - A message segment is missing. This may occur with multipart messages.",
      30010:
        "Message price exceeds max price - The message cost exceeds your configured maximum price.",
      30011:
        "Application error - An application error occurred. Check your application configuration.",
      30012:
        "Invalid source address - The source phone number is invalid or not authorized.",
      30013:
        "Invalid destination address - The destination phone number is invalid.",
      30014:
        "Unroutable message - The message cannot be routed to the destination.",
      30015:
        "Gateway unavailable - The SMS gateway is temporarily unavailable. Try again later.",
      30016:
        "No route to destination - No route found to the destination number.",
      30017:
        "No route to destination - mobile - No mobile route found to the destination number.",
      30018:
        "Unroutable - The message is unroutable. Check the destination number format.",
      30019:
        "Content size exceeds carrier limit - The message failed because the size of the content exceeded what the carrier allows. Shorten the message or break it into multiple messages.",
      30020:
        "Content not allowed - The message content is not allowed by carrier policies.",
      30021:
        "Service unavailable - The SMS service is temporarily unavailable. Try again later.",
      30022:
        "Destination unreachable - The destination is unreachable. The phone may be off or out of coverage.",
      30023:
        "Message expired - The message expired before delivery. Check message validity period.",
      30024:
        "Invalid message body - The message body is invalid or contains unsupported characters.",
      30025:
        "Destination number not reachable - The destination number is not reachable.",
      30026:
        "Message rejected - The message was rejected by the carrier or destination.",
      30027: "Unsupported message type - The message type is not supported.",
      30028:
        "Message delivery failed - The message delivery failed. Check destination number and carrier status.",
      30029:
        "Invalid source number - The source phone number is invalid or not configured.",
      30030: "Destination number blocked - The destination number is blocked.",
      21211:
        "Invalid 'To' Phone Number - The 'To' phone number is invalid. Check the phone number format.",
      21212:
        "Invalid 'From' Phone Number - The 'From' phone number is invalid. Check the phone number format.",
      21408:
        "Permission to send an SMS has not been enabled for the region indicated by the 'To' number - SMS sending is not enabled for this region.",
      21610:
        "Unsubscribed recipient - The recipient has unsubscribed from receiving messages.",
      21614:
        "'To' number is not a valid mobile number - The destination number is not a valid mobile number.",
      21617:
        "The message body exceeds the configured SMS length limit. Split the message or raise the limit in Change Limit.",
      21618:
        "The 'From' number provided is not a valid Twilio number - The source number is not a valid Twilio number.",
    };

    // Convert errorCode to string for lookup
    const codeStr = String(errorCode);

    // Return mapped description if available
    if (errorCodeMap[codeStr]) {
      return errorCodeMap[codeStr];
    }

    return null;
  };

  // Get unique error codes from allSMSForDate
  const getUniqueErrorCodes = () => {
    const errorCodes = new Set();
    allSMSForDate.forEach((msg) => {
      if (
        msg.error_code &&
        msg.error_code !== "None" &&
        msg.error_code !== null &&
        msg.error_code !== ""
      ) {
        errorCodes.add(msg.error_code);
      }
    });
    return Array.from(errorCodes).sort();
  };

  // Filter messages based on selected error codes
  const getFilteredMessages = () => {
    if (selectedErrorCodes.length === 0) {
      return allSMSForDate;
    }
    return allSMSForDate.filter((msg) => {
      if (
        !msg.error_code ||
        msg.error_code === "None" ||
        msg.error_code === null ||
        msg.error_code === ""
      ) {
        return false; // Exclude messages without error codes when filters are active
      }
      return selectedErrorCodes.includes(msg.error_code);
    });
  };

  const handleErrorCodeToggle = (errorCode) => {
    setSelectedErrorCodes((prev) => {
      if (prev.includes(errorCode)) {
        return prev.filter((code) => code !== errorCode);
      } else {
        return [...prev, errorCode];
      }
    });
    // Reset SMS selection when error codes change
    setSelectedSMSForResend({});
  };

  // Get filtered failed SMS based on selected error codes
  const getFilteredFailedSMS = () => {
    if (selectedErrorCodes.length === 0) {
      return failedSMS;
    }
    return failedSMS.filter((msg) => {
      return msg.error_code && selectedErrorCodes.includes(msg.error_code);
    });
  };

  // Get unique error codes from allSMSForAppointmentDate
  const getAppointmentDateUniqueErrorCodes = () => {
    const errorCodes = new Set();
    allSMSForAppointmentDate.forEach((msg) => {
      if (
        msg.error_code &&
        msg.error_code !== "None" &&
        msg.error_code !== null &&
        msg.error_code !== ""
      ) {
        errorCodes.add(msg.error_code);
      }
    });
    return Array.from(errorCodes).sort();
  };

  // Filter messages based on selected error codes for appointment date tab
  const getFilteredAppointmentDateMessages = () => {
    if (selectedAppointmentDateErrorCodes.length === 0) {
      return allSMSForAppointmentDate;
    }
    return allSMSForAppointmentDate.filter((msg) => {
      if (
        !msg.error_code ||
        msg.error_code === "None" ||
        msg.error_code === null ||
        msg.error_code === ""
      ) {
        return false; // Exclude messages without error codes when filters are active
      }
      return selectedAppointmentDateErrorCodes.includes(msg.error_code);
    });
  };

  // Get filtered failed SMS for appointment date tab based on selected error codes
  const getFilteredFailedSMSForAppointmentDate = () => {
    if (selectedAppointmentDateErrorCodes.length === 0) {
      return failedSMSForAppointmentDate;
    }
    return failedSMSForAppointmentDate.filter((msg) => {
      return (
        msg.error_code &&
        selectedAppointmentDateErrorCodes.includes(msg.error_code)
      );
    });
  };

  // Handle error code toggle for appointment date tab
  const handleAppointmentDateErrorCodeToggle = (errorCode) => {
    setSelectedAppointmentDateErrorCodes((prev) => {
      if (prev.includes(errorCode)) {
        return prev.filter((code) => code !== errorCode);
      } else {
        return [...prev, errorCode];
      }
    });
    // Reset SMS selection when error codes change
    setSelectedAppointmentDateSMSForResend({});
  };

  // Handle resend all failed SMS for appointment date
  const handleResendAllFailedSMSForAppointmentDate = async () => {
    // Get filtered failed SMS based on error code selection
    const filteredFailedSMS = getFilteredFailedSMSForAppointmentDate();

    // Filter to only include selected SMS messages
    const smsToResend = filteredFailedSMS.filter(
      (sms) => sms.id && selectedAppointmentDateSMSForResend[sms.id],
    );

    if (smsToResend.length === 0) {
      toast({
        title: "No SMS Selected",
        description: "Please select at least one SMS message to resend.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to resend ${smsToResend.length} selected SMS message(s)?${
        selectedAppointmentDateErrorCodes.length > 0
          ? `\n(Filtered by error codes: ${selectedAppointmentDateErrorCodes.join(", ")})`
          : ""
      }`,
    );

    if (!confirmed) {
      return;
    }

    const badNumberGuard = await confirmBadNumbers(
      smsToResend.map((sms) => ({ phone: sms.to })),
    );
    if (!badNumberGuard) return;

    setResendingFailedSMS(true);
    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    const errors = [];

    try {
      for (const sms of smsToResend) {
        try {
          // Use edited values if they exist, otherwise use original values
          const phoneNumber = sms.to;
          const message = sms.message;
          const shouldSplit = splitAllMessages || splitSMS[sms.id] || false;

          if (badNumberGuard.skipped(phoneNumber)) {
            skippedCount++;
            continue;
          }

          const response = await resendTwilioMessage(
            phoneNumber,
            message,
            shouldSplit,
            selectedAppointmentDate,
            smsDbId(sms), // Pass the old SMS ID to delete the failed record
            sms.appointment_date, // Pass the appointment date from the original SMS
            smsSchemaOf(sms, supabaseSchema), // Send from / store in / delete in the row's own provider
            badNumberGuard.confirmed(phoneNumber),
          );

          if (response.status === "success") {
            successCount++;
          } else {
            errorCount++;
            errors.push(`${phoneNumber}: ${response.error || "Unknown error"}`);
          }
        } catch (error) {
          errorCount++;
          const phoneNumber = sms.to;
          errors.push(`${phoneNumber}: ${error.message || "Failed to resend"}`);
        }
      }

      if (successCount > 0) {
        toast({
          title: "Resend Complete",
          description: `Successfully resent ${successCount} SMS message(s). ${errorCount > 0 ? `${errorCount} failed.` : ""}${skippedCount > 0 ? ` ${skippedCount} skipped (known bad number).` : ""}`,
          status:
            successCount === failedSMSForAppointmentDate.length
              ? "success"
              : "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      if (successCount === 0 && skippedCount > 0) {
        toast({
          title: "Nothing Resent",
          description: `${skippedCount} SMS message(s) skipped (known bad number).${errorCount > 0 ? ` ${errorCount} failed.` : ""}`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }

      if (errorCount > 0 && errors.length > 0) {
        console.error("Resend errors:", errors);
      }

      // Clear selected SMS for resend checkboxes
      setSelectedAppointmentDateSMSForResend({});

      // Refresh the data
      await fetchSMSByAppointmentDate(selectedAppointmentDate);
    } catch (error) {
      console.error("Error resending failed SMS:", error);
      toast({
        title: "Error",
        description:
          "An error occurred while resending SMS: " +
          (error.message || "Unknown error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResendingFailedSMS(false);
    }
  };

  // Handle opening edit modal for a specific SMS
  const handleEditSMS = (msg) => {
    setEditingSMSId(msg.id);
    // Initialize with existing values or edited values if they exist
    setEditedSMS((prev) => ({
      ...prev,
      [msg.id]: {
        to: editedSMS[msg.id]?.to || msg.to || "",
        message: editedSMS[msg.id]?.message || msg.message || "",
      },
    }));
    // Initialize split preference if not set — only for exceeded-content-length errors
    if (splitSMS[msg.id] === undefined) {
      const isLengthError =
        msg.error_code === "30019" || // Content size exceeds carrier limit
        msg.error_code === "21617"; // Message body is too long (Twilio)
      setSplitSMS((prev) => ({
        ...prev,
        [msg.id]: isLengthError,
      }));
    }
    onEditSMSModalOpen();
  };

  // Handle saving edited SMS
  const handleSaveEditedSMS = () => {
    if (!editingSMSId) return;
    onEditSMSModalClose();
    setEditingSMSId(null);
  };

  // Handle canceling edit
  const handleCancelEditSMS = () => {
    setEditingSMSId(null);
    onEditSMSModalClose();
  };

  const fetch24hSMSStatsAcrossProviders = async () => {
    try {
      const supabase = await getSupabase();
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { rows } = await querySmsAcrossSchemas(supabase, smsStatusSchemas, (q) =>
        q.select("status, error_code").gte("created_at", since),
      );
      const errorCount = rows.filter(isFailedSms).length;
      return {
        messageCount: rows.length,
        errorCount,
        successRate: rows.length
          ? Math.round(((rows.length - errorCount) / rows.length) * 100)
          : 100,
      };
    } catch (error) {
      console.warn("[SMS Status] 24h stats across providers failed:", error);
      return {};
    }
  };

  const fetchSMSHealthStatus = async (date = null) => {
    setLoadingSMSStatus(true);
    try {
      // Use provided date or selected date, default to today
      const targetDate = date || selectedSMSDate;

      // Fetch SMS health data with recent messages (for overall health status).
      // Its 24h counts cover only the viewer's schema; replace them with the
      // counts across every selected provider.
      const [healthData, providerStats] = await Promise.all([
        getTwilioHealthWithMessages(0, ""),
        fetch24hSMSStatsAcrossProviders(),
      ]);
      setSmsHealthData({ ...healthData, ...providerStats });

      // Fetch all SMS and failed SMS for the selected date
      await fetchAllSMSForDate(targetDate);
    } catch (error) {
      console.error("Error fetching SMS health status:", error);
      toast({
        title: "Error",
        description:
          "Failed to fetch SMS health status: " +
          (error.message || "Unknown error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSMSStatus(false);
    }
  };

  const fetchAllSMSForDate = async (date) => {
    try {
      const supabase = await getSupabase();

      // Parse date string (YYYY-MM-DD) to avoid timezone issues
      // Create date range for the selected day in local timezone
      const [year, month, day] = date.split("-");

      // Create start of day in local timezone (midnight local time)
      const startOfDayLocal = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        0,
        0,
        0,
        0,
      );
      // Create end of day in local timezone (11:59:59.999 PM local time)
      const endOfDayLocal = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        23,
        59,
        59,
        999,
      );

      // Convert to UTC ISO strings for database query
      // The database stores timestamps in UTC
      // We need to get the UTC timestamps that correspond to the local day boundaries
      // Use toISOString() which automatically converts to UTC
      const startISO_utc = startOfDayLocal.toISOString();
      const endISO_utc = endOfDayLocal.toISOString();

      console.log("SMS Date Query Debug:", {
        selectedDate: date,
        startOfDayLocal: startOfDayLocal.toLocaleString(),
        endOfDayLocal: endOfDayLocal.toLocaleString(),
        startISO_utc,
        endISO_utc,
      });

      // Get Twilio phone number to filter outbound messages
      let twilioPhoneNumber = null;
      try {
        const config = await getTwilioConfiguration();
        twilioPhoneNumber = config.phoneNumber;
      } catch (e) {
        console.warn("Could not fetch Twilio config:", e);
      }

      // Query for all SMS from the selected date (created_at stores Admin schedule date when sent from Admin page),
      // in every selected provider. All senders are shown (no twilioPhoneNumber filter).
      let allMessages = null;
      let queryError = null;
      try {
        ({ rows: allMessages } = await querySmsAcrossSchemas(
          supabase,
          smsStatusSchemas,
          (q) =>
            q
              .select("*")
              .gte("created_at", startISO_utc)
              .lte("created_at", endISO_utc)
              .order("created_at", { ascending: false }),
        ));
        allMessages.sort(byCreatedDesc);
      } catch (e) {
        queryError = e;
      }

      if (queryError) {
        console.error("Error fetching SMS:", queryError);
        setAllSMSForDate([]);
        setFailedSMS([]);
        return;
      }

      // Set all messages for the date
      setAllSMSForDate(allMessages || []);

      // Filter messages that actually have error_code or failed status
      const failedMessages = (allMessages || []).filter(
        (msg) =>
          msg.status === "failed" ||
          msg.status === "undelivered" ||
          (msg.error_code &&
            msg.error_code !== "None" &&
            msg.error_code !== null &&
            msg.error_code !== ""),
      );

      setFailedSMS(failedMessages);

      // Select all failed SMS by default
      const defaultSelection = {};
      failedMessages.forEach((msg) => {
        if (msg.id) {
          defaultSelection[msg.id] = true;
        }
      });
      setSelectedSMSForResend(defaultSelection);
    } catch (error) {
      console.error("Error fetching SMS for date:", error);
      setAllSMSForDate([]);
      setFailedSMS([]);
    }
  };

  // Normalize phone helper (same logic as PatientMessagingModal)
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const digits = String(phone).replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      return digits.slice(1);
    }
    return digits.slice(-10);
  };

  // Complex "By Appointment Date" method: categorize each phone's messages via
  // the backend. Done per provider schema, because the backend matches them
  // against THAT schema's appointments.
  const categorizeSMSByAppointmentAcrossProviders = async (allMessages, baseUrl, logTag) => {
    const cache = {};
    const totals = {};
    const failed = {};
    for (const [schema, schemaMessages] of groupBySchema(allMessages || [])) {
      const uniquePhones = [
        ...new Set(
          schemaMessages
            .map((msg) => normalizePhone(msg.from) || normalizePhone(msg.to))
            .filter(Boolean),
        ),
      ];
      console.log(logTag, "Processing", uniquePhones.length, "unique phone numbers in", schema);

      for (const patientPhone of uniquePhones) {
        const patientMessages = schemaMessages.filter(
          (msg) =>
            normalizePhone(msg.from) === patientPhone ||
            normalizePhone(msg.to) === patientPhone,
        );
        if (patientMessages.length === 0) continue;

        try {
          const response = await fetch(`${baseUrl}/twilio/categorize-messages/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: patientMessages,
              patient_phone: patientPhone,
              schema,
              before_grace_period_days: SMS_STATUS_BEFORE_GRACE_PERIOD_DAYS,
            }),
          });
          if (!response.ok) continue;

          const { categorized } = await response.json();
          for (const [apptDate, data] of Object.entries(categorized || {})) {
            const messagesWithDate = data.messages || [];
            cache[apptDate] = [...(cache[apptDate] || []), ...messagesWithDate];
            totals[apptDate] = (totals[apptDate] || 0) + messagesWithDate.length;
            failed[apptDate] =
              (failed[apptDate] || 0) + messagesWithDate.filter(isFailedSms).length;
          }
        } catch (catError) {
          console.error(logTag, "Error categorizing for phone:", patientPhone, catError);
        }
      }
    }
    return { cache, totals, failed };
  };

  // Fetch SMS by appointment_date. When by_appointment_date_method: one DB query (tmp). Otherwise: cache + backend API.
  const fetchSMSByAppointmentDate = async (date) => {
    setSelectedAppointmentDateErrorCodes([]);
    setSelectedAppointmentDateSMSForResend({});
    setLoadingSMSByAppointmentDate(true);
    try {
      const dateStr = date.length >= 10 ? date.slice(0, 10) : date;

      if (by_appointment_date_method) {
        // By-appointment-date method (from tmp): one DB query, no backend
        const supabase = await getSupabase();
        let messages = null;
        let error = null;
        try {
          ({ rows: messages } = await querySmsAcrossSchemas(
            supabase,
            smsStatusSchemas,
            (q) =>
              q
                .select("*")
                .eq("appointment_date", dateStr)
                .order("created_at", { ascending: false }),
          ));
          messages.sort(byCreatedDesc);
        } catch (e) {
          error = e;
        }

        if (error) {
          console.error("Error fetching SMS by appointment_date:", error);
          setAllSMSForAppointmentDate([]);
          setFailedSMSForAppointmentDate([]);
          setLoadingSMSByAppointmentDate(false);
          return;
        }

        const allMessages = messages || [];
        const failedMessages = allMessages.filter(
          (msg) =>
            msg.status === "failed" ||
            msg.status === "undelivered" ||
            (msg.error_code &&
              msg.error_code !== "None" &&
              msg.error_code !== null &&
              msg.error_code !== ""),
        );

        setAllSMSForAppointmentDate(allMessages);
        setFailedSMSForAppointmentDate(failedMessages);
        setTotalSMSCountsByAppointmentDate((prev) => ({
          ...prev,
          [dateStr]: allMessages.length,
        }));
        setFailedSMSCountsByAppointmentDate((prev) => ({
          ...prev,
          [dateStr]: failedMessages.length,
        }));

        const defaultSelection = {};
        failedMessages.forEach((msg) => {
          if (msg.id) defaultSelection[msg.id] = true;
        });
        setSelectedAppointmentDateSMSForResend(defaultSelection);
        setLoadingSMSByAppointmentDate(false);
        return;
      }

      // Non-by-appointment-date: use cache first, then fallback to API
      const cachedData = appointmentDateSmsCache[dateStr];
      if (cachedData && cachedData.length > 0) {
        console.log(
          "[SMS By Appointment] Using cached data for",
          dateStr,
          "(",
          cachedData.length,
          "messages)",
        );

        setAllSMSForAppointmentDate(cachedData);
        setFailedSMSForAppointmentDate(
          cachedData.filter(
            (msg) =>
              msg.status === "failed" ||
              msg.status === "undelivered" ||
              (msg.error_code &&
                msg.error_code !== "None" &&
                msg.error_code !== null &&
                msg.error_code !== ""),
          ),
        );
        setLoadingSMSByAppointmentDate(false);
        return;
      }

      // No cache: call backend sms-by-appointment-date API
      console.log(
        "[SMS By Appointment] No cache for",
        dateStr,
        ", fetching from API (complex method)...",
      );

      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // One backend call per provider; its rows come back untagged.
      const perSchema = await Promise.all(
        smsStatusSchemas.map(async (schema) => {
          try {
            const response = await fetch(
              `${baseUrl}/twilio/sms-by-appointment-date/`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  appointment_date: dateStr,
                  schema,
                  before_grace_period_days: SMS_STATUS_BEFORE_GRACE_PERIOD_DAYS,
                }),
              },
            );
            if (!response.ok) throw new Error(response.statusText);
            const result = await response.json();
            if (result.status !== "success") throw new Error(result.error);
            return {
              messages: tagSmsRows(schema, result.messages),
              failed: tagSmsRows(schema, result.failed_messages),
            };
          } catch (e) {
            console.error(`Error fetching SMS by appointment date for "${schema}":`, e);
            return null;
          }
        }),
      );

      const loaded = perSchema.filter(Boolean);
      if (loaded.length === 0) {
        setAllSMSForAppointmentDate([]);
        setFailedSMSForAppointmentDate([]);
        setLoadingSMSByAppointmentDate(false);
        return;
      }

      const messages = loaded.flatMap((r) => r.messages).sort(byCreatedDesc);
      const failed_messages = loaded.flatMap((r) => r.failed).sort(byCreatedDesc);

      if (messages && messages.length > 0) {
        setAppointmentDateSmsCache((prev) => ({
          ...prev,
          [dateStr]: messages,
        }));
      }

      setAllSMSForAppointmentDate(messages || []);
      setFailedSMSForAppointmentDate(failed_messages || []);
      setLoadingSMSByAppointmentDate(false);
    } catch (error) {
      console.error("Error fetching SMS by appointment date:", error);
      setAllSMSForAppointmentDate([]);
      setFailedSMSForAppointmentDate([]);
      setLoadingSMSByAppointmentDate(false);
    }
  };

  // Fetch failed SMS counts for a date range (for calendar display)
  const fetchFailedSMSCountsForMonth = async (year, month) => {
    setLoadingCalendarData(true);
    try {
      const supabase = await getSupabase();

      // Create date range for the entire month
      const startOfMonth = new Date(year, month, 1, 0, 0, 0, 0);
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const startISO_utc = startOfMonth.toISOString();
      const endISO_utc = endOfMonth.toISOString();

      // Get Twilio phone number to filter outbound messages
      let twilioPhoneNumber = null;
      try {
        const config = await getTwilioConfiguration();
        twilioPhoneNumber = config.phoneNumber;
      } catch (e) {
        console.warn("Could not fetch Twilio config:", e);
      }

      // Query for SMS from the selected month, in every selected provider
      // (all senders; no twilioPhoneNumber filter)
      let messages = null;
      let queryError = null;
      try {
        ({ rows: messages } = await querySmsAcrossSchemas(
          supabase,
          smsStatusSchemas,
          (q) =>
            q
              .select("created_at, status, error_code")
              .gte("created_at", startISO_utc)
              .lte("created_at", endISO_utc),
        ));
      } catch (e) {
        queryError = e;
      }

      if (queryError) {
        console.error("Error fetching failed SMS counts:", queryError);
        setFailedSMSCountsByDate({});
        setTotalSMSCountsByDate({});
        return;
      }

      // Aggregate failed and total SMS counts by date
      const failedCountsByDate = {};
      const totalCountsByDate = {};
      (messages || []).forEach((msg) => {
        const isFailed =
          msg.status === "failed" ||
          msg.status === "undelivered" ||
          (msg.error_code &&
            msg.error_code !== "None" &&
            msg.error_code !== null &&
            msg.error_code !== "");

        if (msg.created_at) {
          const dateStr = new Date(msg.created_at).toISOString().split("T")[0];
          // Count all messages
          totalCountsByDate[dateStr] = (totalCountsByDate[dateStr] || 0) + 1;
          // Count failed messages
          if (isFailed) {
            failedCountsByDate[dateStr] =
              (failedCountsByDate[dateStr] || 0) + 1;
          }
        }
      });

      setFailedSMSCountsByDate(failedCountsByDate);
      setTotalSMSCountsByDate(totalCountsByDate);
    } catch (error) {
      console.error("Error fetching failed SMS counts for month:", error);
      setFailedSMSCountsByDate({});
      setTotalSMSCountsByDate({});
    } finally {
      setLoadingCalendarData(false);
    }
  };

  // Fetch SMS counts for a date range (for appointment calendar display)
  const fetchSMSCountsForAppointmentMonth = async (year, month) => {
    setLoadingAppointmentCalendarData(true);
    try {
      const supabase = await getSupabase();
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;

      console.log(
        `[SMS Counts] Fetching SMS counts for ${monthStr} using ` +
          (by_appointment_date_method
            ? "by-appointment-date method (one query, tmp)"
            : "complex method (backend API)"),
      );

      if (by_appointment_date_method) {
        // Local calendar month strings (avoid UTC shift from Date#toISOString).
        const pad = (n) => String(n).padStart(2, "0");
        const m1 = month + 1;
        const startRange = `${year}-${pad(m1)}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const endRange = `${year}-${pad(m1)}-${pad(lastDay)}`;

        let messages = null;
        let queryError = null;
        try {
          ({ rows: messages } = await querySmsAcrossSchemas(
            supabase,
            smsStatusSchemas,
            (q) =>
              q
                .select("appointment_date, status, error_code")
                .gte("appointment_date", startRange)
                .lte("appointment_date", endRange),
          ));
        } catch (e) {
          queryError = e;
        }

        if (queryError) {
          console.error(
            "Error fetching SMS counts by appointment_date:",
            queryError,
          );
          setFailedSMSCountsByAppointmentDate({});
          setTotalSMSCountsByAppointmentDate({});
          setLoadingAppointmentCalendarData(false);
          return;
        }

        const failedCountsByDate = {};
        const totalCountsByDate = {};
        (messages || []).forEach((msg) => {
          const isFailed =
            msg.status === "failed" ||
            msg.status === "undelivered" ||
            (msg.error_code &&
              msg.error_code !== "None" &&
              msg.error_code !== null &&
              msg.error_code !== "");

          if (msg.appointment_date) {
            const dateStr =
              typeof msg.appointment_date === "string"
                ? msg.appointment_date.slice(0, 10)
                : String(msg.appointment_date).slice(0, 10);
            totalCountsByDate[dateStr] = (totalCountsByDate[dateStr] || 0) + 1;
            if (isFailed) {
              failedCountsByDate[dateStr] =
                (failedCountsByDate[dateStr] || 0) + 1;
            }
          }
        });

        setFailedSMSCountsByAppointmentDate(failedCountsByDate);
        setTotalSMSCountsByAppointmentDate(totalCountsByDate);

        console.log(
          "[SMS Counts] By-appointment-date method:",
          Object.keys(totalCountsByDate).length,
          "appointment dates",
        );
      } else {
        // Complex method - fetch all SMS (every selected provider), then
        // categorize per phone via backend API
        let allMessages;
        try {
          ({ rows: allMessages } = await querySmsAcrossSchemas(
            supabase,
            smsStatusSchemas,
            (q) => q.select("*").order("created_at", { ascending: false }).limit(10000),
          ));
          allMessages.sort(byCreatedDesc);
        } catch (fetchError) {
          console.error("Error fetching SMS messages:", fetchError);
          setFailedSMSCountsByAppointmentDate({});
          setTotalSMSCountsByAppointmentDate({});
          setLoadingAppointmentCalendarData(false);
          return;
        }

        const {
          cache,
          totals: newTotalCounts,
          failed: newFailedCounts,
        } = await categorizeSMSByAppointmentAcrossProviders(
          allMessages,
          baseUrl,
          "[SMS Counts]",
        );

        setAppointmentDateSmsCache((prev) => ({ ...prev, ...cache }));
        setTotalSMSCountsByAppointmentDate((prev) => ({
          ...prev,
          ...newTotalCounts,
        }));
        setFailedSMSCountsByAppointmentDate((prev) => ({
          ...prev,
          ...newFailedCounts,
        }));

        console.log(
          "[SMS Counts] Complex method:",
          Object.keys(newTotalCounts).length,
          "appointment dates",
        );
      }
    } catch (error) {
      console.error("Error fetching SMS counts for appointment month:", error);
      setFailedSMSCountsByAppointmentDate({});
      setTotalSMSCountsByAppointmentDate({});
    } finally {
      setLoadingAppointmentCalendarData(false);
    }
  };

  const fetchTwilioMessageDetails = async (messageSid) => {
    if (!messageSid) return null;

    setLoadingTwilioDetails(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/twilio/message-details/?message_sid=${encodeURIComponent(messageSid)}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch Twilio details: ${response.status}`);
      }

      const data = await response.json();
      return data.message_details || null;
    } catch (error) {
      console.error("Error fetching Twilio message details:", error);
      toast({
        title: "Warning",
        description: `Could not fetch full error details from Twilio: ${error.message}`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return null;
    } finally {
      setLoadingTwilioDetails(false);
    }
  };

  const handleResendAllFailedSMS = async () => {
    // Get filtered failed SMS based on error code selection
    const filteredFailedSMS = getFilteredFailedSMS();

    // Filter to only include selected SMS messages
    const smsToResend = filteredFailedSMS.filter(
      (sms) => sms.id && selectedSMSForResend[sms.id],
    );

    if (smsToResend.length === 0) {
      toast({
        title: "No SMS Selected",
        description: "Please select at least one SMS message to resend.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to resend ${smsToResend.length} selected SMS message(s)?${
        selectedErrorCodes.length > 0
          ? `\n(Filtered by error codes: ${selectedErrorCodes.join(", ")})`
          : ""
      }`,
    );

    if (!confirmed) {
      return;
    }

    const badNumberGuard = await confirmBadNumbers(
      smsToResend.map((sms) => ({ phone: editedSMS[sms.id]?.to || sms.to })),
    );
    if (!badNumberGuard) return;

    setResendingFailedSMS(true);
    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    const errors = [];

    try {
      for (const sms of smsToResend) {
        try {
          // Use edited values if they exist, otherwise use original values
          const phoneNumber = editedSMS[sms.id]?.to || sms.to;
          const message = editedSMS[sms.id]?.message || sms.message;
          // Determine if message should be split:
          // - Global option (split all)
          // - Per-message setting (only auto-enabled for exceeded-content-length errors 21617/30019)
          const shouldSplit = splitAllMessages || splitSMS[sms.id] || false;

          if (badNumberGuard.skipped(phoneNumber)) {
            skippedCount++;
            continue;
          }

          const response = await resendTwilioMessage(
            phoneNumber,
            message,
            shouldSplit,
            selectedSMSDate,
            smsDbId(sms), // Pass the old SMS ID to delete the failed record
            sms.appointment_date, // Pass the appointment date from the original SMS
            smsSchemaOf(sms, supabaseSchema), // Send from / store in / delete in the row's own provider
            badNumberGuard.confirmed(phoneNumber),
          );

          if (response.status === "success") {
            successCount++;
          } else {
            errorCount++;
            errors.push(`${phoneNumber}: ${response.error || "Unknown error"}`);
          }
        } catch (error) {
          errorCount++;
          const phoneNumber = editedSMS[sms.id]?.to || sms.to;
          errors.push(`${phoneNumber}: ${error.message || "Failed to resend"}`);
        }
      }

      if (successCount > 0) {
        toast({
          title: "Resend Complete",
          description: `Successfully resent ${successCount} SMS message(s). ${errorCount > 0 ? `${errorCount} failed.` : ""}${skippedCount > 0 ? ` ${skippedCount} skipped (known bad number).` : ""}`,
          status: successCount === failedSMS.length ? "success" : "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      if (successCount === 0 && skippedCount > 0) {
        toast({
          title: "Nothing Resent",
          description: `${skippedCount} SMS message(s) skipped (known bad number).${errorCount > 0 ? ` ${errorCount} failed.` : ""}`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }

      if (errorCount > 0 && errors.length > 0) {
        console.error("Resend errors:", errors);
      }

      // Clear selected SMS for resend checkboxes
      setSelectedSMSForResend({});

      // Refresh the data
      await fetchSMSHealthStatus(selectedSMSDate);
    } catch (error) {
      console.error("Error resending failed SMS:", error);
      toast({
        title: "Error",
        description:
          "An error occurred while resending SMS: " +
          (error.message || "Unknown error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResendingFailedSMS(false);
    }
  };

  // Reminder functionality
  const handleSendRemindersClick = async () => {
    // Ensure we have a valid date before fetching
    if (!selectedDate) {
      toast({
        title: "No Date Selected",
        description: "Please select a date before sending reminders.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    console.log("[REMINDER] Opening reminder modal for date:", selectedDate);
    // Initialize default scheduled reminder date/time when opening modal (use current settings).
    // Seed manualScheduledReminders with ONE default row only when "Schedule reminders" is on
    // (the modal opens with it off by default; flipping the checkbox initializes the row).
    const defaultScheduledDate =
      getDayBeforeDateString(selectedDate, reminderDaysBeforeAppointment) ||
      selectedDate ||
      "";
    const defaultScheduledTime = reminderScheduledTime || "09:00";
    if (scheduleReminderOneDayBefore) {
      setManualScheduledReminders([
        {
          id: `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          date: defaultScheduledDate,
          time: defaultScheduledTime,
        },
      ]);
    } else {
      setManualScheduledReminders([]);
    }
    setManualReminderContactMethods({
      email: reminderContactMethods.email,
      phone: reminderContactMethods.phone,
    });
    setManualScheduleReminder(scheduleReminderOneDayBefore);
    // Reset filter to empty (show all) when opening modal
    setManualReminderPatientTypeFilter([]);
    // Clear previous selection before fetching new data to prevent stale patient selections
    setSelectedReminderPatients({});
    setReminderModalTab(0);
    onReminderModalOpen();
    await fetchPendingScheduledReminders();
    await fetchPatientsWithoutForms();
  };

  const getLocalDayRangeISO = (dateStr) => {
    // dateStr: YYYY-MM-DD (local calendar day)
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr
      .trim()
      .split("-")
      .map((x) => parseInt(x, 10));
    if (parts.length < 3) return null;
    const [y, m, d] = parts;
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    const startLocal = new Date(y, m - 1, d, 0, 0, 0, 0);
    const endLocal = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
    if (
      Number.isNaN(startLocal.getTime()) ||
      Number.isNaN(endLocal.getTime())
    ) {
      return null;
    }
    return {
      startISO: startLocal.toISOString(),
      endISO: endLocal.toISOString(),
    };
  };

  const getDayBeforeDateString = (dateStr, daysBefore = 1) => {
    const [y, m, d] = (dateStr || "").split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
    dt.setDate(dt.getDate() - daysBefore);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatScheduledDateDisplay = (isoDateStr) => {
    if (!isoDateStr) return "";
    try {
      const dt = new Date(isoDateStr);
      if (Number.isNaN(dt.getTime())) return "";
      // Convert to PST timezone for display
      const pstDateStr = dt.toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      // Parse the formatted string back to show in our desired format
      // Format: "MM/DD/YYYY at HH:MM AM/PM"
      const match = pstDateStr.match(
        /(\d{1,2})\/(\d{1,2})\/(\d{4,}),\s+(\d{1,2}):(\d{2})\s+(AM|PM)/,
      );
      if (match) {
        const mm = String(match[1]).padStart(2, "0");
        const dd = String(match[2]).padStart(2, "0");
        const yyyy = match[3];
        let hours = parseInt(match[4], 10);
        const minutes = match[5];
        const ampm = match[6];
        return `${yyyy}-${mm}-${dd} at ${hours}:${minutes} ${ampm}`;
      }
      // Fallback to simple format if parsing fails
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
      return "";
    }
  };

  const fetchPendingScheduledReminders = async () => {
    if (!selectedDate) return;

    setLoadingPendingScheduledReminders(true);
    try {
      const supabase = await getSupabase();
      // Show all scheduled reminders for patients whose *appointment* is on the selected date
      let data = null;
      const result = await supabase
        .from("scheduled_messages")
        .select("id, created_at, scheduled_for, status, attempts, payload")
        .in("status", ["scheduled", "processing", "sent", "failed"])
        .eq("payload->df1->>date_", selectedDate)
        .order("scheduled_for", { ascending: true });
      if (result.error) {
        // Fallback if JSONB path filter not supported: fetch recent and filter in memory
        const fallback = await supabase
          .from("scheduled_messages")
          .select("id, created_at, scheduled_for, status, attempts, payload")
          .in("status", ["scheduled", "processing", "sent", "failed"])
          .order("scheduled_for", { ascending: false })
          .limit(500);
        if (fallback.error) throw fallback.error;
        data = (fallback.data || []).filter(
          (row) =>
            row?.payload?.df1?.date_ === selectedDate ||
            row?.payload?.df1?.appointment_date === selectedDate,
        );
      } else {
        data = result.data;
      }

      // Appointment confirmations share this table; they have their own list
      // in the confirmation hub and must never show up (or be cancelled) here.
      data = (data || []).filter((row) => !isConfirmationScheduledRow(row));

      // Extract unique patient names to look up their types from the patients table
      const patientNames = [
        ...new Set(
          (data || []).map((row) => row?.payload?.df1?.name).filter(Boolean),
        ),
      ];
      let patientTypesMap = {};

      if (patientNames.length > 0) {
        // Try to get patient types from the patients table by matching names
        try {
          // Query patients table for these names (case-insensitive)
          const patientsResult = await supabase
            .from(tableName)
            .select("name, Type")
            .or(patientNames.map((name) => `name.ilike.${name}`).join(","));

          if (patientsResult.data) {
            // Build a map of name -> Type (case-insensitive)
            patientsResult.data.forEach((patient) => {
              if (patient.name) {
                patientTypesMap[patient.name.toLowerCase()] =
                  patient.Type || "";
              }
            });
          }
        } catch (lookupError) {
          console.log(
            "[REMINDER] Could not lookup patient types:",
            lookupError,
          );
        }
      }

      const normalized = (data || []).map((row) => {
        const df1 = row?.payload?.df1 || {};
        // Extract appointment date and time from the payload for display
        // This ensures we show the appointment date, not the UTC scheduled_for time
        const appointmentDate = df1.date_ || df1.appointment_date || "";
        const appointmentTime = df1.time_ || df1.appointment_time || "";

        // Get type from payload first (check multiple possible field names), fallback to patients table lookup
        const payloadType = df1.type || df1.patient_type || df1.form_type || "";
        const patientName = df1.name || "";
        const lookupType = patientName.toLowerCase()
          ? patientTypesMap[patientName.toLowerCase()] || ""
          : "";
        const patientType = payloadType || lookupType;

        return {
          id: row.id,
          created_at: row.created_at,
          scheduled_for: row.scheduled_for,
          status: row.status,
          attempts: row.attempts ?? 0,
          name: df1.name || "Unknown",
          phone: df1.phone || "",
          email: df1.email || "",
          message: df1.message || "",
          // Include appointment date/time for display purposes
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          // Include patient type for display purposes (from payload or lookup)
          type: patientType,
        };
      });

      setPendingScheduledReminders(normalized);
    } catch (e) {
      console.error(
        "[REMINDER] Error fetching pending scheduled reminders:",
        e,
      );
      setPendingScheduledReminders([]);
    } finally {
      setLoadingPendingScheduledReminders(false);
    }
  };

  const cancelScheduledReminder = async (scheduledId) => {
    if (!scheduledId) return;
    setCancellingScheduledReminderId(scheduledId);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from("scheduled_messages")
        .update({
          status: "cancelled",
          processed_at: new Date().toISOString(),
          error_message: "Cancelled by user",
        })
        .eq("id", scheduledId)
        .in("status", ["scheduled", "processing"]);

      if (error) throw error;

      toast({
        title: "Scheduling Cancelled",
        description: `Cancelled scheduled reminder #${scheduledId}.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      await fetchPendingScheduledReminders();
    } catch (e) {
      console.error("[REMINDER] Error cancelling scheduled reminder:", e);
      toast({
        title: "Cancel Failed",
        description:
          e?.message ||
          "Failed to cancel scheduled reminder. Please try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCancellingScheduledReminderId(null);
    }
  };

  const cancelAllScheduledRemindersForDay = async () => {
    const cancellable = (pendingScheduledReminders || []).filter((r) =>
      ["scheduled", "processing"].includes(
        String(r.status || "").toLowerCase(),
      ),
    );
    if (cancellable.length === 0) {
      toast({
        title: "Nothing to cancel",
        description: "No scheduled or processing reminders for this day.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to cancel all ${cancellable.length} scheduled reminder(s) for this day?`,
    );
    if (!confirmed) return;
    setCancellingAllScheduledReminders(true);
    try {
      const supabase = await getSupabase();
      const ids = cancellable.map((r) => r.id);
      const { error } = await supabase
        .from("scheduled_messages")
        .update({
          status: "cancelled",
          processed_at: new Date().toISOString(),
          error_message: "Cancelled by user (all for day)",
        })
        .in("id", ids)
        .in("status", ["scheduled", "processing"]);

      if (error) throw error;

      toast({
        title: "Scheduling Cancelled",
        description: `Cancelled ${ids.length} scheduled reminder(s) for this day.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      await fetchPendingScheduledReminders();
    } catch (e) {
      console.error("[REMINDER] Error cancelling all scheduled reminders:", e);
      toast({
        title: "Cancel Failed",
        description:
          e?.message ||
          "Failed to cancel scheduled reminders. Please try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCancellingAllScheduledReminders(false);
    }
  };

  // Fetch reminder counts for the Send Reminders button (pending count)
  const fetchReminderStatus = async () => {
    if (!selectedDate) return;
    try {
      // The date effect fires before the tenant schema has loaded, and
      // `supabaseSchema` still reads its "public" default until
      // loadSupabaseSchema() resolves; getSupabase() waits for that.
      await getSupabase();
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;
      // The schema must travel with the request: without it the backend
      // counts the public schema and every tenant's button reads "(0 pending)".
      const apiUrl = `${baseUrl}/api/reminders/status/?date=${encodeURIComponent(selectedDate)}&schema=${encodeURIComponent(supabaseSchema || "public")}`;
      const response = await fetch(apiUrl, { credentials: "include" });
      const data = await response.json();
      if (response.ok && data?.success) {
        setReminderStatus({
          total_patients: data.total_patients ?? 0,
          patients_with_forms: data.patients_with_forms ?? 0,
          patients_without_forms: data.patients_without_forms ?? 0,
          completion_rate: data.completion_rate ?? 0,
        });
      }
    } catch (e) {
      console.error("[REMINDER] Error fetching reminder status for button:", e);
    }
  };

  // Types the Send Form Reminders modal may ever target: NP/NS, narrowed by
  // the settings filter when one is set. Everything else is never selected.
  const getSettingsAllowedReminderTypes = () => {
    const ALLOWED_REMINDER_TYPES = ["NP", "NS"];
    const fromSettings = (reminderPatientTypeFilter || [])
      .map((t) => String(t).toUpperCase().trim())
      .filter((t) => ALLOWED_REMINDER_TYPES.includes(t));
    return fromSettings.length > 0 ? fromSettings : ALLOWED_REMINDER_TYPES;
  };

  const buildInitialReminderSelection = (list) => {
    const allowedTypes = getSettingsAllowedReminderTypes();
    const selection = {};
    (list || []).forEach((patient) => {
      if (!patient || !patient.id) return;
      const patientType = (patient.type || patient.Type || "")
        .toUpperCase()
        .trim();
      if (allowedTypes.includes(patientType)) {
        selection[patient.id] = true;
      }
    });
    return selection;
  };

  const fetchPatientsWithoutForms = async () => {
    setLoadingReminders(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const apiUrl = `${baseUrl}/api/reminders/patients-without-forms/`;
      const requestBody = {
        date: selectedDate,
        schema: supabaseSchema, // Include current schema from supabase client
      };

      debugLog("[REMINDER] Fetching patients without forms:", {
        apiUrl,
        requestBody,
        hostname,
        baseUrl,
        selectedDate,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include", // Include cookies for authentication
      });

      debugLog(
        "[REMINDER] Response status:",
        response.status,
        response.statusText,
      );
      debugLog(
        "[REMINDER] Response headers:",
        Object.fromEntries(response.headers.entries()),
      );

      // Try to parse response as JSON, but handle non-JSON responses
      let responseText = "";
      let data = null;

      try {
        responseText = await response.text();
        debugLog("[REMINDER] Response text:", responseText);
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error(
          "[REMINDER] Failed to parse response as JSON:",
          parseError,
        );
        console.error("[REMINDER] Response text was:", responseText);
        throw new Error(
          `Invalid response from server. Status: ${response.status}. Response: ${responseText.substring(0, 200)}`,
        );
      }

      if (!response.ok) {
        const errorMessage =
          data?.error ||
          data?.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        console.error("[REMINDER] API error:", errorMessage, data);
        throw new Error(errorMessage);
      }

      // Validate response structure
      if (!data || typeof data !== "object") {
        console.error("[REMINDER] Invalid response data structure:", data);
        throw new Error("Invalid response data structure from server");
      }

      debugLog("[REMINDER] Successfully loaded data:", {
        total_patients: data.total_patients,
        patients_with_forms: data.patients_with_forms,
        patients_without_forms: data.patients_without_forms,
        patients_list_length: data.patients_without_forms_list?.length || 0,
      });

      const patientsList = data.patients_without_forms_list || [];

      // Fetch schedule data from the schedule table to get patient types
      // The type field name from prompts configuration
      const typeFieldName = prompts.find((p) => p[0] === "type")?.[0] || "type";

      try {
        const supabase = await getSupabase();
        // Select all fields to ensure we get the type field regardless of its exact name
        const { data: scheduleData, error: scheduleError } = await supabase
          .from(tableName)
          .select("*")
          .eq("date_", selectedDate);

        debugLog(
          "[REMINDER] Fetched schedule data:",
          scheduleData?.length || 0,
          "records",
        );
        debugLog("[REMINDER] Schedule error:", scheduleError);
        if (scheduleData && scheduleData.length > 0) {
          debugLog(
            "[REMINDER] First schedule record keys:",
            Object.keys(scheduleData[0]),
          );
          debugLog("[REMINDER] First schedule record type fields:", {
            [typeFieldName]: scheduleData[0][typeFieldName],
            Type: scheduleData[0].Type,
            type: scheduleData[0].type,
            TYPE: scheduleData[0].TYPE,
          });
        }

        if (!scheduleError && scheduleData) {
          // Create a map of patient ID to type for quick lookup
          const typeMap = {};
          scheduleData.forEach((schedule) => {
            if (schedule.id) {
              // Try multiple field name variations - check all possible cases
              const type =
                schedule[typeFieldName] ||
                schedule.Type ||
                schedule.type ||
                schedule.TYPE ||
                schedule.PatientType ||
                schedule.patient_type ||
                "";
              if (type && type.toString().trim()) {
                typeMap[schedule.id] = type.toString().toUpperCase().trim();
                debugLog(
                  `[REMINDER] Mapped patient ${schedule.id} to type: "${typeMap[schedule.id]}"`,
                );
              } else {
                debugLog(
                  `[REMINDER] No type found for patient ${schedule.id}. Available keys:`,
                  Object.keys(schedule),
                );
              }
            }
          });

          debugLog("[REMINDER] Type map created:", typeMap);
          debugLog(
            "[REMINDER] Schedule data sample:",
            scheduleData.slice(0, 3),
          );

          // Enhance patient data with type from schedule table
          const enhancedPatientsList = patientsList.map((patient) => {
            // First try to get type from the schedule table lookup
            let patientType = typeMap[patient.id] || "";

            debugLog(
              `[REMINDER] Patient ${patient.id} (${patient.name}): typeMap lookup = "${patientType}"`,
            );

            // Fallback to API response
            if (!patientType) {
              patientType = (patient.type || patient.Type || "")
                .toUpperCase()
                .trim();
              debugLog(
                `[REMINDER] Patient ${patient.id}: API response type = "${patientType}"`,
              );
            }

            // Also check currentScheduleData as final fallback
            if (!patientType && currentScheduleData[patient.id]) {
              const schedulePatient = currentScheduleData[patient.id];
              patientType = (
                schedulePatient[typeFieldName] ||
                schedulePatient.Type ||
                schedulePatient.type ||
                ""
              )
                .toUpperCase()
                .trim();
              debugLog(
                `[REMINDER] Patient ${patient.id}: currentScheduleData type = "${patientType}"`,
              );
            }

            const finalType = patientType || "";
            debugLog(
              `[REMINDER] Patient ${patient.id} final type: "${finalType}"`,
            );

            // Return enhanced patient with type
            return {
              ...patient,
              type: finalType, // Don't set to "N/A", leave empty if not found
            };
          });

          debugLog(
            "[REMINDER] Enhanced patients list:",
            enhancedPatientsList.slice(0, 3),
          );
          setPatientsWithoutForms(enhancedPatientsList);

          // Pre-select only patients whose type is allowed for reminders
          setSelectedReminderPatients(
            buildInitialReminderSelection(enhancedPatientsList),
          );
        } else {
          // If schedule fetch fails, use API response data as-is
          console.warn(
            "[REMINDER] Could not fetch schedule data for types:",
            scheduleError,
          );
          setPatientsWithoutForms(patientsList);

          // Pre-select only patients whose type is allowed for reminders
          setSelectedReminderPatients(
            buildInitialReminderSelection(patientsList),
          );
        }
      } catch (error) {
        console.error(
          "[REMINDER] Error fetching schedule data for types:",
          error,
        );
        // Fallback to API response data
        setPatientsWithoutForms(patientsList);

        // Pre-select only patients whose type is allowed for reminders
        setSelectedReminderPatients(
          buildInitialReminderSelection(patientsList),
        );
      }
      setReminderStatus({
        total_patients: data.total_patients || 0,
        patients_with_forms: data.patients_with_forms || 0,
        patients_without_forms: data.patients_without_forms || 0,
        completion_rate:
          data.total_patients > 0
            ? Math.round((data.patients_with_forms / data.total_patients) * 100)
            : 0,
      });

      debugLog("[REMINDER] Loaded patients without forms:", data);

      // Show success message if no patients found (informational)
      if (patientsList.length === 0 && data.total_patients > 0) {
        toast({
          title: "No Patients Without Forms",
          description: `All ${data.total_patients} patients have completed their forms.`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("[REMINDER] Error fetching patients without forms:", error);
      console.error("[REMINDER] Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });

      // Reset state on error
      setPatientsWithoutForms([]);
      setReminderStatus({
        total_patients: 0,
        patients_with_forms: 0,
        patients_without_forms: 0,
        completion_rate: 0,
      });
      setSelectedReminderPatients({});

      toast({
        title: "Error Loading Patients",
        description:
          error.message ||
          "Failed to load patients without forms. Please check the console for details.",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setLoadingReminders(false);
    }
  };

  // Filter patients by type for reminders.
  // Only patients whose type is allowed by the settings filter (NP/NS by
  // default) are ever shown. The manual type filter narrows that further
  // when one is selected; otherwise all settings-allowed types show.
  const filteredPatientsWithoutForms = useMemo(() => {
    const ALLOWED_REMINDER_TYPES = ["NP", "NS"];
    const settingsAllowedTypes = (reminderPatientTypeFilter || [])
      .map((t) => String(t).toUpperCase().trim())
      .filter((t) => ALLOWED_REMINDER_TYPES.includes(t));
    const baseAllowed =
      settingsAllowedTypes.length > 0 ? settingsAllowedTypes : ALLOWED_REMINDER_TYPES;

    const allowed =
      manualReminderPatientTypeFilter.length > 0
        ? manualReminderPatientTypeFilter
            .map((t) => String(t).toUpperCase().trim())
            .filter((t) => baseAllowed.includes(t))
        : baseAllowed;

    if (allowed.length === 0) return [];
    return patientsWithoutForms.filter((patient) => {
      const patientType = (patient.type || "").toUpperCase().trim();
      return allowed.includes(patientType);
    });
  }, [patientsWithoutForms, manualReminderPatientTypeFilter, reminderPatientTypeFilter]);

  // Number of selected patients that are actually shown in the list - this is
  // exactly the set handleSendReminders will target.
  const selectedVisibleReminderCount = useMemo(
    () =>
      filteredPatientsWithoutForms.filter(
        (p) => p && p.id && selectedReminderPatients[p.id],
      ).length,
    [filteredPatientsWithoutForms, selectedReminderPatients],
  );

  // Patients whose type is allowed by the settings filter, regardless of
  // any narrower manual filter selection. Used for counts and the "All
  // Types" checkbox label in the Send Form Reminders modal.
  // Only NP and NS are ever allowed — strip anything else defensively so
  // stale DB values cannot leak non-NP/NS patients into the list.
  const ALLOWED_REMINDER_TYPES = ["NP", "NS"];
  const _settingsAllowedTypes = (reminderPatientTypeFilter || [])
    .map((t) => String(t).toUpperCase().trim())
    .filter((t) => ALLOWED_REMINDER_TYPES.includes(t));
  const _baseAllowedTypes =
    _settingsAllowedTypes.length > 0 ? _settingsAllowedTypes : ALLOWED_REMINDER_TYPES;
  const allowedPatientsWithoutForms = patientsWithoutForms.filter((patient) => {
    const _pt = (patient.type || "").toUpperCase().trim();
    return _baseAllowedTypes.includes(_pt);
  });

  // Auto-select all filtered patients when filter changes
  useEffect(() => {
    if (filteredPatientsWithoutForms.length > 0) {
      const autoSelection = {};
      filteredPatientsWithoutForms.forEach((patient) => {
        if (patient && patient.id) {
          autoSelection[patient.id] = true;
        }
      });
      setSelectedReminderPatients(autoSelection);
    } else {
      // Nothing is shown (no allowed-type patients, or the manual filter
      // matches none) - clear so hidden patients can never stay selected.
      setSelectedReminderPatients({});
    }
  }, [manualReminderPatientTypeFilter, filteredPatientsWithoutForms]); // Auto-select when filter or filtered list changes

  // Get unique patient types from the list
  const availablePatientTypes = useMemo(() => {
    const types = new Set();
    debugLog(
      "[REMINDER] Extracting types from patientsWithoutForms:",
      patientsWithoutForms.length,
      "patients",
    );
    patientsWithoutForms.forEach((patient, index) => {
      // Try multiple field name variations
      const type = (patient.type || patient.Type || patient.TYPE || "")
        .toUpperCase()
        .trim();

      if (type && type !== "N/A" && type !== "") {
        types.add(type);
        debugLog(
          `[REMINDER] Patient ${index} (${patient.id || patient.name}): type = "${type}"`,
        );
      } else {
        debugLog(
          `[REMINDER] Patient ${index} (${patient.id || patient.name}): No type found. Patient data:`,
          patient,
        );
      }
    });
    // Only allow NP and NS for reminders; ignore any other patient types.
    const REMINDER_ALLOWED_TYPES = ["NP", "NS"];
    const uniqueTypes = Array.from(types)
      .filter((t) => REMINDER_ALLOWED_TYPES.includes(t))
      .sort();
    debugLog("[REMINDER] Available patient types found:", uniqueTypes);
    return uniqueTypes;
  }, [patientsWithoutForms]);

  // Display data for each row of the Send Form Reminders patient list.
  // Memoized: matching a reminder patient to its schedule row by name used to
  // run extractPatientInfo over every loaded patient, for every row, on every
  // AdminPage render - enough work (and console output) to freeze the page.
  const reminderPatientRows = useMemo(() => {
    let byName = null; // lowercased name -> [{ p, schedDate }], built on first need
    const findByNameAndDate = (name, date) => {
      if (!byName) {
        byName = new Map();
        patients.forEach((p) => {
          const schedName = (extractPatientInfo(p)?.name || "")
            .toLowerCase()
            .trim();
          if (!schedName) return;
          if (!byName.has(schedName)) byName.set(schedName, []);
          byName
            .get(schedName)
            .push({ p, schedDate: (p.date || p.date_ || p.time_ || "") + "" });
        });
      }
      const candidates = byName.get(name) || [];
      const hit = candidates.find(
        ({ schedDate }) => !!schedDate && schedDate.startsWith(date),
      );
      return hit ? hit.p : null;
    };

    return filteredPatientsWithoutForms
      .filter((patient) => patient && patient.id)
      .map((patient) => {
        const patientName = patient.name || "Unknown Patient";

        // Prefer contact info from the schedules table when available
        let patientPhone = patient.phone || "";
        let patientEmail = patient.email || "";

        // Try to match this reminder patient to a schedule record: by ID
        // first, then by an exact name + date match.
        let schedulePatient =
          currentScheduleData[patient.id] ||
          patients.find((p) => p.id === patient.id) ||
          null;
        if (!schedulePatient) {
          const reminderInfo = extractPatientInfo(patient);
          const reminderName = (reminderInfo?.name || patientName || "")
            .toLowerCase()
            .trim();
          const reminderDate =
            (patient.date ||
              patient.date_ ||
              patient?.scheduled_time ||
              selectedDate ||
              "") + "";
          if (reminderName && reminderDate) {
            schedulePatient = findByNameAndDate(reminderName, reminderDate);
          }
        }

        if (schedulePatient) {
          const extractedInfo = extractPatientInfo(schedulePatient);
          if (extractedInfo?.phone) patientPhone = extractedInfo.phone;
          if (extractedInfo?.email) patientEmail = extractedInfo.email;
        }

        let reminderDisplayTime = patient.scheduled_time || "N/A";
        if (schedulePatient?.time_ && schedulePatient?.date_) {
          const c = convertScheduledTime(
            schedulePatient.date_,
            schedulePatient.time_,
            schedulePatient.timezone_source ?? null,
            reminderViewerTimezone,
          );
          if (c !== "N/A") reminderDisplayTime = c;
        }

        return {
          patient,
          patientName,
          // The API returns 'type' (lowercase) - see reminders.py
          patientType: patient.type || "N/A",
          submissionCount: patient.submission_count || 0,
          patientPhone,
          patientEmail,
          reminderDisplayTime,
        };
      });
  }, [
    filteredPatientsWithoutForms,
    currentScheduleData,
    patients,
    selectedDate,
    reminderViewerTimezone,
  ]);

  // Exactly what Trigger Auto-Reminders posts, so the preview (dry_run) and
  // the real run are computed from the same date, schema and settings.
  const buildAutoReminderRequestBody = () => ({
    date: selectedDate,
    schema: supabaseSchema,
    auto_reminder_settings: {
      days_before: reminderDaysBeforeAppointment,
      time: reminderScheduledTime ? (reminderScheduledTime.includes(':') && reminderScheduledTime.split(':').length === 2 ? reminderScheduledTime + ':00' : reminderScheduledTime) : "",
      include_email: reminderContactMethods.email,
      include_phone: reminderContactMethods.phone,
      patient_types: reminderPatientTypeFilter,
    },
  });

  // Clicking a Trigger button opens the preview: a dry run of the same
  // request, shown as "when / for whom / under which filters", with Proceed
  // doing the real thing. Nothing is written until Proceed.
  const openTriggerPreview = async (kind) => {
    setTriggerPreview({ kind, loading: true, error: null, preview: null });
    try {
      const hostname = window.location.hostname;
      const baseUrl = hostname === "localhost" ? BASE_URLS.local.backend : BASE_URLS.render.backend;
      let response;
      if (kind === "reminders") {
        response = await fetch(`${baseUrl}/api/reminders/poll-and-schedule/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ...buildAutoReminderRequestBody(), dry_run: true }),
        });
      } else {
        response = await fetch(`${baseUrl}/twilio/schedule-confirmations/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ schema: supabaseSchema || "public", dry_run: true, revive_cancelled: true }),
        });
      }
      const data = await response.json();
      if (!response.ok || data?.error || (kind === "confirmations" && data?.status !== "success")) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }
      const preview = kind === "reminders"
        ? buildReminderPreview(data, { schema: supabaseSchema || "public", date: selectedDate })
        : buildConfirmationPreview(data, { schema: supabaseSchema || "public" });
      setTriggerPreview({ kind, loading: false, error: null, preview });
    } catch (error) {
      setTriggerPreview({ kind, loading: false, error: error.message || String(error), preview: null });
    }
  };

  const closeTriggerPreview = () => setTriggerPreview({ kind: null, loading: false, error: null, preview: null });

  const proceedWithTrigger = async () => {
    const kind = triggerPreview.kind;
    closeTriggerPreview();
    if (kind === "reminders") await handleTriggerAutoReminders();
    else if (kind === "confirmations") await handleTriggerAutoConfirmations();
  };

  const handleTriggerAutoReminders = async () => {
    setIsTriggeringAutoReminders(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const apiUrl = `${baseUrl}/api/reminders/poll-and-schedule/`;

      const requestBody = buildAutoReminderRequestBody();

      console.log("[AUTO-REMINDER] Triggering auto-reminders job:", {
        apiUrl,
        requestBody,
        hostname,
        baseUrl,
        selectedDate,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      let responseText = "";
      let data = null;

      try {
        responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error(
          "[AUTO-REMINDER] Failed to parse response as JSON:",
          parseError,
        );
        throw new Error(
          `Invalid response from server. Status: ${response.status}. Response: ${responseText.substring(0, 200)}`,
        );
      }

      if (!response.ok) {
        const errorMessage =
          data?.error ||
          data?.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        console.error("[AUTO-REMINDER] API error:", errorMessage, data);
        throw new Error(errorMessage);
      }

      console.log("[AUTO-REMINDER] Success:", data);

      // Build the report data for the popup
      const scheduledPatients = data?.scheduled_patients || [];
      const skippedReasons = data?.skipped_reasons || {};

      // Convert skipped_reasons object to array with details
      const skippedPatients = Object.entries(skippedReasons).map(
        ([key, value]) => {
          // Handle both old format (string) and new format (object)
          if (typeof value === "string") {
            return {
              name: key,
              reason: value,
              patient_id: "",
              appointment_date: "",
            };
          }
          return {
            name: key,
            reason: value.reason || "",
            patient_id: value.patient_id || "",
            appointment_date: value.appointment_date || "",
            type: value.type || "",
            phone: value.phone || "",
            email: value.email || "",
          };
        },
      );

      // Set the auto-reminder report data
      setAutoReminderReport({
        scheduled: scheduledPatients,
        skipped: skippedPatients,
        totalChecked: data?.appointments_checked || 0,
        totalScheduled: data?.reminders_scheduled || 0,
        totalSkipped: data?.skipped || 0,
      });

      // Show the popup report
      onAutoReminderReportModalOpen();

      toast({
        title: "Auto-Reminders Scheduled",
        description:
          data?.message ||
          `Scheduled ${data?.reminders_scheduled || data?.appointments_checked || 0} automatic reminders for patients.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Refresh the pending scheduled reminders list
      await fetchPendingScheduledReminders();
    } catch (error) {
      console.error("[AUTO-REMINDER] Error:", error);
      toast({
        title: "Error Triggering Auto-Reminders",
        description:
          error.message ||
          "Failed to trigger auto-reminders. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTriggeringAutoReminders(false);
    }
  };

  // Shared builder so the footer "Preview Message" button and the per-row
  // Preview button in the scheduled-reminders list render the EXACT same
  // form-completion reminder message.
  const buildFormReminderMessage = (
    patientName,
    appointmentStr,
    patientType,
    settings,
  ) => {
    const formUrl = getFormUrlForType(settings, patientType);
    const formLabel =
      getFormMessageTemplate(patientType || "NP") || "patient intake forms";

    const templateBody = `COMPLETE YOUR FORMS BEFORE YOUR VISIT - FOOTHILL PULMONARY
----------------------------------------

Hello {name},

To save time at your appointment, please complete your intake forms online at least 24 hours before your visit on:
{appointment}

Click here to fill out your {form_label}: {form_url}

It's important to be as accurate as possible, especially with your medication list and medical history. This helps improve efficiency and accuracy in your care.

Please complete these forms at least 24 hours before your appointment to allow our team to review your information.

For questions, email {contact_email}
To opt out of text communications, text QUIT or email {contact_email}.

Thank you, and we look forward to seeing you soon!

• Foothill Pulmonary
www.vitamedai.com
{contact_email}
914-844-1859`;

    const greetingName = patientName.includes(",")
      ? patientName.split(",")[0]
      : patientName;
    return fillContactEmail(templateBody, settings)
      .replace("{name}", greetingName)
      .replace("{appointment}", appointmentStr)
      .replace("{form_label}", formLabel)
      .replace(
        "{form_url}",
        formUrl || "(form URL not configured for this patient type)",
      );
  };

  // Per-row Preview in the scheduled-reminders list. Uses the same builder as
  // the footer "Preview Message" button so both previews always match
  // (previously this showed the stored payload message, which was empty or an
  // appointment-style reminder instead of the form reminder).
  const handlePreviewScheduledReminder = async (r) => {
    const settings = await fetchUserSettings();
    setUserSettings(settings);

    const appointmentStr = r?.appointment_date
      ? `${r.appointment_date}${r.appointment_time ? ` ${r.appointment_time} PST` : ""}`
      : "your upcoming appointment";

    setPreviewReminderMessage(
      buildFormReminderMessage(
        r?.name || "Patient",
        appointmentStr,
        (r?.type || "").toUpperCase().trim(),
        settings,
      ),
    );
    onPreviewReminderModalOpen();
  };

  const handlePreviewReminderMessage = async () => {
    // Pick the first selected patient, or fall back to the first patient
    // shown in the filtered list, so the preview always has a sample to render.
    const firstSelectedId = Object.keys(selectedReminderPatients).find(
      (id) => selectedReminderPatients[id],
    );
    const samplePatient =
      (firstSelectedId &&
        filteredPatientsWithoutForms.find((p) => p.id === firstSelectedId)) ||
      filteredPatientsWithoutForms[0] ||
      null;

    if (!samplePatient) {
      toast({
        title: "No Patients to Preview",
        description:
          "Select at least one patient (or change the filter) to preview the reminder message.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Resolve contact info + schedule row for the sample patient so the
    // preview matches what the send path will actually deliver.
    let schedulePatient = currentScheduleData[samplePatient.id] ||
      patients.find((p) => p.id === samplePatient.id) ||
      null;
    if (!schedulePatient) {
      const reminderInfo = extractPatientInfo(samplePatient);
      const reminderName = (reminderInfo?.name || samplePatient.name || "")
        .toLowerCase()
        .trim();
      const reminderDate =
        (samplePatient.date ||
          samplePatient.date_ ||
          samplePatient?.scheduled_time ||
          selectedDate ||
          "") + "";
      schedulePatient =
        patients.find((p) => {
          const schedInfo = extractPatientInfo(p);
          const schedName = (schedInfo?.name || "").toLowerCase().trim();
          const schedDate = (p.date || p.date_ || p.time_ || "") + "";
          return (
            schedName &&
            reminderName &&
            schedName === reminderName &&
            !!reminderDate &&
            !!schedDate &&
            schedDate.startsWith(reminderDate)
          );
        }) || null;
    }

    const extractedInfo = schedulePatient
      ? extractPatientInfo(schedulePatient)
      : extractPatientInfo(samplePatient);

    const patientName =
      extractedInfo?.name || samplePatient.name || "Patient";
    const patientType = (samplePatient.type || schedulePatient?.type || "")
      .toUpperCase()
      .trim();

    // Fetch fresh settings so the preview uses the same URLs that the send
    // path will use (settings are persisted in schema_settings).
    const settings = await fetchUserSettings();
    setUserSettings(settings);

    const appointmentStr =
      (schedulePatient?.date_ && schedulePatient?.time_
        ? `${schedulePatient.date_} ${schedulePatient.time_} PST`
        : samplePatient.date_ && samplePatient.time_
          ? `${samplePatient.date_} ${samplePatient.time_} PST`
          : samplePatient.date || samplePatient.scheduled_time || selectedDate ||
            "your upcoming appointment");

    setPreviewReminderMessage(
      buildFormReminderMessage(
        patientName,
        appointmentStr,
        patientType,
        settings,
      ),
    );
    onPreviewReminderModalOpen();
  };

  const handleSendReminders = async () => {
    // Only send to selected patients that are actually in the filtered list
    // (NP/NS + settings filter + manual filter). A selection entry for a
    // patient that is not shown must never be sent.
    const visibleIds = new Set(
      filteredPatientsWithoutForms.map((p) => String(p.id)),
    );
    const patientIds = Object.keys(selectedReminderPatients).filter(
      (id) => selectedReminderPatients[id] && visibleIds.has(String(id)),
    );

    if (patientIds.length === 0) {
      toast({
        title: "No Patients Selected",
        description: "Please select at least one patient to send reminders to.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!manualReminderContactMethods.phone && !manualReminderContactMethods.email) {
      toast({
        title: "No Contact Method Selected",
        description:
          "Please select at least one contact method (phone or email).",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate the manual scheduled-reminders list (when scheduling is enabled).
    // Rules: at least one entry, every entry has a date, and no two entries share a date.
    if (manualScheduleReminder) {
      if (manualScheduledReminders.length === 0) {
        toast({
          title: "No Scheduled Dates",
          description:
            'Add at least one send date using "+ Add another reminder date".',
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      const missingDate = manualScheduledReminders.find((r) => !r.date);
      if (missingDate) {
        toast({
          title: "Missing Date",
          description:
            "Each scheduled reminder needs a date. Fill in the empty date or remove the row.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      const seen = new Set();
      const dup = manualScheduledReminders.find((r) => {
        if (seen.has(r.date)) return true;
        seen.add(r.date);
        return false;
      });
      if (dup) {
        toast({
          title: "Duplicate Reminder Dates",
          description: `Two reminders share the date ${dup.date}. Each scheduled reminder must be on a different date.`,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
        return;
      }
    }

    const reminderPatientOf = (patientId) =>
      currentScheduleData[patientId] || patients.find((p) => p.id === patientId);
    const badNumberGuard = await confirmBadNumbers(
      manualReminderContactMethods.phone
        ? patientIds.map((patientId) => {
            const patient = reminderPatientOf(patientId);
            const phone =
              selectedPatientsContact[patientId]?.phone ||
              (patient ? extractPatientInfo(patient).phone || rowPhones(patient)[0] : "");
            return {
              phones: [phone, patient ? altPhoneFor(patient, phone) : ""].filter(Boolean),
              name: patient?.[prompts[0][0]] || patient?.name || patient?.[colid2] || "",
            };
          })
        : [],
    );
    if (!badNumberGuard) return;

    setIsSendingReminders(true);

    try {
      const settings = await fetchUserSettings();
      debugLog("Using settings:", settings);

      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      // Check if we have the necessary Twilio credentials
      if (!settings.TWILIO_PHONE_NUMBER) {
        setIsSendingReminders(false);
        toast({
          title: "Missing Twilio Configuration",
          description: "Please configure your Twilio phone number in settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Send reminders to all selected patients
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const errors = [];
      const successfulPatients = [];
      const skippedPatients = [];
      const failedPatients = [];

      // Get the reminder message template (use same format as Send Forms)
      const reminderMessages = {
        default: `COMPLETE YOUR FORMS BEFORE YOUR VISIT - FOOTHILL PULMONARY
----------------------------------------

Hello {name},

To save time at your appointment, please complete your intake forms online at least 24 hours before your visit on:
{appointment}

Click here to fill out your {form_label}: {form_url}

It's important to be as accurate as possible, especially with your medication list and medical history. This helps improve efficiency and accuracy in your care.

Please complete these forms at least 24 hours before your appointment to allow our team to review your information.

For questions, email {contact_email}
To opt out of text communications, text QUIT or email {contact_email}.

Thank you, and we look forward to seeing you soon!

• Foothill Pulmonary
www.vitamedai.com
{contact_email}
914-844-1859`,
      };

      const reminderMessage =
        reminderMessages[reminderMessageTemplate] ||
        reminderMessages["default"];

      // Form URLs are now looked up per-patient-type using getFormUrlForType

const computeScheduledForISO = (patientRecord, dateOverride, timeOverride) => {
        // Each scheduled-reminder row passes its own date/time. When called
        // without overrides we fall back to the appointment date / patient time
        // and (legacy behaviour) shift to 1 day before the appointment.
        // Pacific time automatically switches between PST (UTC-8) and PDT (UTC-7).
        const hasExplicitDate =
          typeof dateOverride === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(dateOverride);
        const baseDateStr =
          dateOverride || patientRecord?.date_ || selectedDate || "";
        const timeStr =
          (timeOverride && String(timeOverride).includes(":")
            ? String(timeOverride).trim().slice(0, 5)
            : null) ||
          (patientRecord?.time_ && String(patientRecord.time_).includes(":")
            ? String(patientRecord.time_).trim().slice(0, 5)
            : null) ||
          "09:00";

        if (!baseDateStr || !/^\d{4}-\d{2}-\d{2}$/.test(baseDateStr)) {
          // Fallback: day before selectedDate at 9 AM Pacific time
          const fallbackDate =
            getDayBeforeDateString(
              selectedDate,
              reminderDaysBeforeAppointment,
            ) || selectedDate;
          return convertPacificToISO(fallbackDate, "09:00");
        }

        let dt = parsePacificTime(baseDateStr, timeStr);

        if (!dt || Number.isNaN(dt.getTime())) {
          const fallbackDate =
            getDayBeforeDateString(
              selectedDate,
              reminderDaysBeforeAppointment,
            ) || selectedDate;
          dt = convertPacificToISO(fallbackDate, "09:00");
        }

        // Legacy behavior: when the user hasn't set a scheduled date AND we're
        // not in the multi-reminder flow, send 1 day before appointment.
        if (!hasExplicitDate && !manualScheduledReminders.length && dt) {
          dt.setDate(dt.getDate() - 1);
        }

        return dt ? dt.toISOString() : new Date().toISOString();
      };

      // Get the correct Pacific timezone offset (handles PST/PDT automatically)
      const getPacificOffsetMinutes = (year, month, day) => {
        // Create a date at noon in Pacific timezone to check DST
        const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

        // Get offset by comparing UTC to Pacific
        const getOffset = (d) => {
          const utcStr = d.toLocaleString("en-US", {
            timeZone: "UTC",
            hour12: false,
          });
          const pstStr = d.toLocaleString("en-US", {
            timeZone: "America/Los_Angeles",
            hour12: false,
          });
          const utcDate = new Date(utcStr);
          const pstDate = new Date(pstStr);
          return (pstDate - utcDate) / 60000;
        };

        return getOffset(date);
      };

      const convertPacificToISO = (dateStr, timeStr) => {
        // Parse the date and time components
        const [year, month, day] = dateStr.split("-").map(Number);
        const [hours, minutes] = timeStr.split(":").map(Number);

        // Get the Pacific timezone offset for this specific date
        const pacificOffsetMinutes = getPacificOffsetMinutes(year, month, day);

        // Calculate UTC time: Pacific time minus offset
        // Example: 9 AM PST (-480 min) = 9*60 - (-480) = 540 + 480 = 1020 min UTC = 17:00 UTC
        // Example: 9 AM PDT (-420 min) = 9*60 - (-420) = 540 + 420 = 960 min UTC = 16:00 UTC
        const utcMinutes = hours * 60 + minutes - pacificOffsetMinutes;

        // Create UTC date from minutes
        const utcDate = new Date(
          Date.UTC(year, month - 1, day, 0, 0, 0) + utcMinutes * 60000,
        );

        return Number.isNaN(utcDate.getTime()) ? null : utcDate;
      };

      // Parse Pacific time and return UTC Date object
      const parsePacificTime = (dateStr, timeStr) => {
        return convertPacificToISO(dateStr, timeStr);
      };

      for (const patientId of patientIds) {
        let contactInfo = selectedPatientsContact[patientId];

        // Prioritize scheduleData if available, otherwise search in patients array
        let patient = null;
        if (currentScheduleData[patientId]) {
          patient = currentScheduleData[patientId];
          debugLog(`[REMINDER] Using scheduleData for reminder: ${patientId}`);
        } else {
          patient = patients.find((p) => p.id === patientId);
          if (patient) {
            debugLog(
              `[REMINDER] Using patients array for reminder: ${patientId}`,
            );
          } else {
            debugLog(
              `[REMINDER] WARNING: No patient data found for reminder: ${patientId}`,
            );
          }
        }

        // If contactInfo is missing or incomplete, try to extract from patient data
        if (patient) {
          const extractedInfo = extractPatientInfo(patient);
          const fallbackPhone = extractedInfo.phone || rowPhones(patient)[0] || "";
          if (!contactInfo) {
            contactInfo = {
              email: extractedInfo.email || "",
              phone: fallbackPhone,
              name: extractedInfo.name || "Unknown Patient",
            };
          } else {
            // Fill in missing fields
            if (!contactInfo.email && extractedInfo.email)
              contactInfo.email = extractedInfo.email;
            if (!contactInfo.phone && fallbackPhone)
              contactInfo.phone = fallbackPhone;
          }
        }

        const patientName =
          patient?.[prompts[0][0]] ||
          patient?.name ||
          patient?.[colid2] ||
          "Unknown Patient";

        // Check for contact info based on selected methods
        const hasPhone = contactInfo?.phone?.trim() || false;
        const hasEmail = contactInfo?.email?.trim() || false;
        const phoneRequired = manualReminderContactMethods.phone;
        const emailRequired = manualReminderContactMethods.email;

        // Determine if patient should be skipped
        // Skip only if patient has NONE of the selected contact methods
        // If both methods are selected, patient needs at least one (either is fine)
        const missingPhone = phoneRequired && !hasPhone;
        const missingEmail = emailRequired && !hasEmail;

        // Skip only if ALL required contact methods are missing
        if (missingPhone && missingEmail) {
          skippedCount++;
          let reason = "";
          if (phoneRequired && emailRequired) {
            reason = "Missing phone and email";
          } else if (phoneRequired) {
            reason = "Missing phone number";
          } else if (emailRequired) {
            reason = "Missing email address";
          }
          skippedPatients.push({
            name: patientName,
            reason: reason,
          });
          debugLog(`[REMINDER] Skipping patient ${patientId} - ${reason}`);
          continue;
        }

        // "Skip" in the bad-number pop-up drops the text; email still goes.
        let contactMethods = manualReminderContactMethods;
        if (phoneRequired && hasPhone && badNumberGuard.skipped(contactInfo.phone)) {
          if (!(emailRequired && hasEmail)) {
            skippedCount++;
            skippedPatients.push({ name: patientName, reason: "Known bad number" });
            continue;
          }
          contactMethods = { ...manualReminderContactMethods, phone: false };
        }

        // Determine the appropriate form URL based on patient type
        const patientType = (patient?.type || patient?.Type || "")
          .toUpperCase()
          .trim();

        // Check if this is an FFP/FFS patient type that needs dynamic form generation
        const isFFPType =
          patientType.includes("FFP") || patientType.includes("FFS");

        // Use getFormUrlForType to get the URL for this specific patient type.
        // Pull from the freshly-fetched `settings` (not the stale React-state
        // `userSettings`) so the URL matches what is currently saved in
        // schema_settings.
        let formUrl = getFormUrlForType(settings, patientType);

        // For FFP/FFS patients, generate form dynamically if no static URL exists
        if (isFFPType && !formUrl) {
          debugLog(
            `[REMINDER] FFP/FFS patient detected - generating form dynamically for patient ${patientId}`,
          );
          try {
            const hostname = window.location.hostname;
            const baseUrl =
              hostname === "localhost"
                ? BASE_URLS.local.backend
                : BASE_URLS.render.backend;

            // Call the form generation API
            const response = await fetch(`${baseUrl}/api/generate-form/`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                uuid: patientId,
                patient_name: patientName,
                year: new Date().getFullYear().toString(),
                date: new Date().toISOString().split("T")[0],
                model: selectedLLMModel,
              }),
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.form_data) {
                formUrl =
                  result.form_data.form_link ||
                  result.form_data.metadata?.form_link;
                debugLog(
                  `[REMINDER] Generated form URL for patient ${patientId}:`,
                  formUrl,
                );
              }
            } else {
              debugLog(
                `[REMINDER] Form generation failed for patient ${patientId}:`,
                response.status,
              );
            }
          } catch (error) {
            debugLog(
              `[REMINDER] Error generating form for patient ${patientId}:`,
              error,
            );
          }
        }

        // Show warning only if the URL for this specific patient type is missing
        if (!formUrl) {
          debugLog(
            `[REMINDER] No form URL configured for patient type: ${patientType} - skipping patient ${patientId}`,
          );
          skippedCount++;
          skippedPatients.push({
            name: patientName,
            reason: `Form URL not configured for ${patientType} patients`,
          });
          continue;
        }

        // Get a human-friendly label for the form type (e.g. "New Pulmonary Patient forms")
        const formLabel =
          getFormMessageTemplate(patientType || "NP") || "patient intake forms";

        // Build an appointment string similar to the send forms template
        const appointmentStr =
          (patient?.date_ && patient?.time_
            ? `${patient.date_} ${patient.time_} PST`
            : selectedDate) || "your upcoming appointment";

        const formattedMessage = fillContactEmail(reminderMessage, settings)
          .replace(
            "{name}",
            patientName.includes(",") ? patientName.split(",")[0] : patientName,
          )
          .replace("{appointment}", appointmentStr)
          .replace("{form_label}", formLabel)
          .replace("{form_url}", formUrl);

        // When scheduling is on, queue one POST per (patient × scheduled date).
        // When off, send a single immediate POST per patient (legacy behavior).
        const scheduleEntries = manualScheduleReminder
          ? manualScheduledReminders
          : [{ id: "immediate", date: "", time: "" }];

        for (const schedEntry of scheduleEntries) {
          const scheduledForISO = manualScheduleReminder
            ? computeScheduledForISO(patient, schedEntry.date, schedEntry.time)
            : null;

          const requestData = {
            df1: {
              name: patientName,
              patient_id: patientId,
              patient_type: patientType || "",
              form_type: patientType || "",
              appointment_date: patient?.date_ || selectedDate || "",
              date_: patient?.date_ || selectedDate || "",
              appointment_time: patient?.time_ || "",
              form_url: formUrl,
              phone: contactInfo.phone?.replace(/\D/g, "") || "",
              alt_phone: altPhoneFor(patient, contactInfo.phone, { digitsOnly: true }),
              email: contactInfo.email || "",
              message: formattedMessage,
              twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
              zoho_email: settings.ZOHO_EMAIL || "",
              zoho_password: settings.ZOHO_PASSWORD || "",
              user_id: userId,
              contact_methods: contactMethods,
              confirm_bad_number: badNumberGuard.confirmed(contactInfo.phone || ""),
              schema: supabaseSchema, // Include current schema from supabase client
              split: reminderSplitSms,
              character_limit: customSmsCharacterLimit,
              ...(scheduledForISO
                ? {
                    // Backend will enqueue instead of sending immediately
                    scheduled_for: scheduledForISO,
                    // YYYY-MM-DD in PST — used by the backend as the dedup key
                    // so the same patient can have multiple scheduled reminders
                    // as long as their send dates differ.
                    scheduled_date: schedEntry.date,
                  }
                : {}),
            },
            fpath: "reminder_sms",
            // Top-level schema required: backend maps this to df1._schema for store_message (df1.schema alone is ignored).
            schema: supabaseSchema,
          };

          // For scheduled reminders, avoid storing secrets in the scheduled payload
          if (scheduledForISO) {
            requestData.df1.twilio_phone = "";
            requestData.df1.zoho_email = "";
            requestData.df1.zoho_password = "";
          }

          debugLog(
            `[REMINDER] Sending reminder to patient ${patientId} (scheduled for ${schedEntry.date || "immediate"}):`,
            requestData,
          );

          try {
            const sendFormsUrl = getSendFormsUrl();
            const response = await fetch(sendFormsUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              credentials: "include",
              body: JSON.stringify(requestData),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(
                errorData.error || errorData.message || "Failed to send reminder",
              );
            }

            const responseData = await response.json();
            debugLog(
              `[REMINDER] Response for patient ${patientId}:`,
              responseData,
            );

            // Check if reminder was skipped (duplicate)
            if (responseData.skipped || responseData.status === "skipped") {
              skippedCount++;
              skippedPatients.push({
                name: patientName,
                reason:
                  responseData.reason ||
                  `Already scheduled/sent for ${schedEntry.date || "this date"}`,
              });
              debugLog(
                `[REMINDER] Skipped patient ${patientName} - ${responseData.reason}`,
              );
              continue;
            }

            successCount++;
            successfulPatients.push({
              name: patientName,
              ...(scheduledForISO
                ? {
                    scheduledDate: formatScheduledDateDisplay(scheduledForISO),
                  }
                : {}),
            });
          } catch (error) {
            console.error(
              `Error sending reminder to patient ${patientId}:`,
              error,
            );
            errorCount++;
            failedPatients.push({
              name: patientName,
              reason: error.message,
            });
            errors.push(`${patientName}: ${error.message}`);
          }
        }
      }

      // Store report data
      const reportData = {
        successful: successfulPatients,
        skipped: skippedPatients,
        failed: failedPatients,
      };
      setReminderReport(reportData);

      // Show summary results
      if (successCount > 0 && errorCount === 0 && skippedCount === 0) {
        const scheduledDates =
          manualScheduleReminder && manualScheduledReminders.length
            ? `${successCount} reminder(s) across ${manualScheduledReminders.length} date(s)`
            : `${successCount}`;
        toast({
          title: manualScheduleReminder
            ? "Reminders Scheduled Successfully"
            : "Reminders Sent Successfully",
          description: manualScheduleReminder
            ? `Scheduled ${scheduledDates} for the selected patients.`
            : `Reminders sent to all ${successCount} selected patients.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial Success",
          description: manualScheduleReminder
            ? `Scheduled reminders for ${successCount} patients. ${errorCount} failed: ${errors.join(
                ", ",
              )}`
            : `Reminders sent to ${successCount} patients. ${errorCount} failed: ${errors.join(
                ", ",
              )}`,
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else if (errorCount > 0) {
        // Only show a failure toast if there were actual send errors.
        // If everything was skipped (e.g. no contact info), just show the report modal.
        toast({
          title: manualScheduleReminder
            ? "Failed to Schedule Some Reminders"
            : "Failed to Send Some Reminders",
          description: `All ${errorCount} patients failed: ${errors.join(
            ", ",
          )}`,
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      }

      // Refresh the reminder status
      await fetchPatientsWithoutForms();
      // Refresh pending scheduled list (if we scheduled anything) so new rows show
      if (manualScheduleReminder) {
        await fetchPendingScheduledReminders();
        setTimeout(() => fetchPendingScheduledReminders(), 600);
      }

      // Close the reminder modal first
      onReminderModalClose();

      // Open report modal after state update
      // Using setTimeout to ensure the reminder modal closes first
      setTimeout(() => {
        debugLog(
          "[REMINDER REPORT] Opening report modal with data:",
          reportData,
        );
        onReminderReportModalOpen();
      }, 150);
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast({
        title: "Send Failed",
        description:
          error.message || "An error occurred while sending the reminders.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingReminders(false);
    }
  };

  // Fetch all reports (fax and non-fax) for the inbox.
  // `light`: the page-load call only feeds the header badge (faxCounts), so it
  // skips the orphan check (one Storage listing per folder) and the
  // assigned-tasks request. Both render only inside the inbox, whose open
  // effect makes the full call.
  const fetchAllFaxes = async ({ light = false } = {}) => {
    setLoadingFaxes(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Always fetch all reports (both fax and non-fax) - filtering happens on frontend
      // Include schema parameter to filter faxes by clinic
      // `schema` stays the VIEWING schema (what the chips compare against);
      // `schemas` is everything the picker has ticked. The backend merges them
      // and collapses a fax that reached several schemas into one entry.
      const url =
        `${baseUrl}/api/fax/get-all-faxes/?schema=${encodeURIComponent(userSchema)}` +
        `&schemas=${encodeURIComponent((faxInboxSchemasRef.current || []).join(","))}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch reports");
      }

      const data = await response.json();
      const faxes = data.faxes || [];
      setAllFaxes(faxes);
      if (light) return;

      // Check for orphaned files (entries whose files no longer exist in storage)
      checkOrphanedFiles(faxes);

      // Also fetch tasks assigned to current user
      if (currentUserId) {
        await fetchMyAssignedTasks();
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast({
        title: "Error",
        description: "Failed to fetch reports",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingFaxes(false);
    }
  };

  // Check which fax entries have files that no longer exist in storage
  const checkOrphanedFiles = async (faxes) => {
    if (!faxes || faxes.length === 0) return;
    try {
      const supabase = await getSupabase();
      const orphaned = new Set();

      // Check files in batches by folder to minimize API calls
      const byFolder = {};
      for (const fax of faxes) {
        if (!fax.file_path) continue;
        const lastSlash = fax.file_path.lastIndexOf("/");
        const folder =
          lastSlash >= 0 ? fax.file_path.substring(0, lastSlash) : "";
        const fileName =
          lastSlash >= 0
            ? fax.file_path.substring(lastSlash + 1)
            : fax.file_path;
        if (!byFolder[folder]) byFolder[folder] = [];
        byFolder[folder].push({ fileName, fullPath: fax.file_path });
      }

      // Normalize names so URL-encoding differences (e.g. "%20" vs a space in
      // file names containing spaces/commas) don't cause false mismatches
      // between the stored path and what list() returns.
      const normalizeName = (s) => {
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      };

      for (const [folder, files] of Object.entries(byFolder)) {
        let listing = null;
        try {
          const { data, error } = await supabase.storage
            .from(bucketName_files)
            .list(folder, { limit: 10000 });
          if (!error) listing = data;
        } catch {
          listing = null;
        }

        // Fail SAFE: only flag a file as orphaned when we have a successful,
        // NON-EMPTY listing for its folder AND the file is genuinely absent.
        // A null / errored / empty listing is inconclusive — most often it just
        // means this client lacks storage LIST permission (storage RLS), NOT
        // that the file was deleted. Flagging on an inconclusive listing is
        // what made files that open fine via "View File" show
        // "File ... not found" across the whole inbox.
        if (!Array.isArray(listing) || listing.length === 0) {
          continue;
        }

        const existingNames = new Set();
        for (const item of listing) {
          if (!item || !item.name) continue;
          existingNames.add(item.name);
          existingNames.add(normalizeName(item.name));
        }
        for (const f of files) {
          if (
            !existingNames.has(f.fileName) &&
            !existingNames.has(normalizeName(f.fileName))
          ) {
            orphaned.add(f.fullPath);
          }
        }
      }

      if (orphaned.size > 0) {
        console.log(
          `Found ${orphaned.size} orphaned fax entries (files no longer in storage)`,
        );
      }
      setOrphanedFilePaths(orphaned);
    } catch (err) {
      console.warn("Error checking for orphaned files:", err);
    }
  };

  // Delete an orphaned fax entry (cache row only, file is already gone)
  const deleteOrphanedFax = async (fax) => {
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/delete/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_path: fax.file_path }),
      });

      if (response.ok) {
        setAllFaxes((prev) =>
          prev.filter((f) => f.file_path !== fax.file_path),
        );
        setOrphanedFilePaths((prev) => {
          const next = new Set(prev);
          next.delete(fax.file_path);
          return next;
        });
        toast({
          title: "Deleted",
          description: "Orphaned entry removed from fax inbox.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting orphaned fax:", error);
    }
  };

  // Fetch tasks assigned to current user
  const fetchMyAssignedTasks = async () => {
    if (!currentUserId) return;

    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Include schema parameter to filter by clinic
      const url = `${baseUrl}/api/fax/get-all-faxes/?assigned_to_user_id=${currentUserId}&schema=${encodeURIComponent(userSchema)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch assigned tasks");
      }

      const data = await response.json();
      setMyAssignedTasks(data.faxes || []);
    } catch (error) {
      console.error("Error fetching assigned tasks:", error);
    }
  };

  // Fetch sent faxes history
  const fetchSentFaxes = async () => {
    setLoadingSentFaxes(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Include schema parameter to filter sent faxes by clinic
      const url = `${baseUrl}/api/fax/sent/?limit=100&schema=${encodeURIComponent(userSchema)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch sent faxes");
      }

      const data = await response.json();
      setSentFaxes(data.data || []);
    } catch (error) {
      console.error("Error fetching sent faxes:", error);
      toast({
        title: "Error",
        description: "Failed to fetch sent fax history",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSentFaxes(false);
    }
  };

  // Handle refresh button click - refreshes data based on active tab
  const handleRefreshInbox = async () => {
    try {
      // Always refresh all faxes/reports
      await fetchAllFaxes();

      // If on Tasks tab, also refresh assigned tasks
      if (activeFaxTab === 1 && currentUserId) {
        await fetchMyAssignedTasks();
      }

      // If on Sent tab, refresh sent faxes
      if (activeFaxTab === 4) {
        await fetchSentFaxes();
      }

      toast({
        title: "Refreshed",
        description: "Scanning for new faxes/reports/tasks...",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error refreshing inbox:", error);
      toast({
        title: "Error",
        description: "Failed to refresh inbox",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Fetch spam faxes
  const fetchSpamFaxes = async () => {
    setLoadingSpamFaxes(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Same schemas as the inbox list: a fax marked spam in another selected
      // schema has to show up here to be restored.
      const url =
        `${baseUrl}/api/fax/spam/?schema=${encodeURIComponent(userSchema)}` +
        `&schemas=${encodeURIComponent((faxInboxSchemasRef.current || []).join(","))}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch spam faxes");
      }

      const data = await response.json();
      setSpamFaxes(data.spam_faxes || []);
    } catch (error) {
      console.error("Error fetching spam faxes:", error);
      toast({
        title: "Error",
        description: "Failed to fetch spam faxes",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSpamFaxes(false);
    }
  };

  // Mark a fax as spam
  const markFaxAsSpam = async (fax) => {
    setMarkingAsSpam(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/mark-as-spam/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_path: fax.file_path }),
      });

      if (!response.ok) {
        throw new Error("Failed to mark fax as spam");
      }

      // Remove from inbox and add to spam
      setAllFaxes((prev) => prev.filter((f) => f.file_path !== fax.file_path));
      setSpamFaxes((prev) => [...prev, { ...fax, is_spam: true }]);

      toast({
        title: "Marked as Spam",
        description: "Fax has been moved to spam folder",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error marking fax as spam:", error);
      toast({
        title: "Error",
        description: "Failed to mark fax as spam",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMarkingAsSpam(false);
    }
  };

  // Mark a fax as not spam (move to inbox)
  const markFaxAsNotSpam = async (fax) => {
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/mark-as-not-spam/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_path: fax.file_path }),
      });

      if (!response.ok) {
        throw new Error("Failed to move fax to inbox");
      }

      // Remove from spam and refresh inbox
      setSpamFaxes((prev) => prev.filter((f) => f.file_path !== fax.file_path));
      setSelectedSpamFaxes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fax.file_path);
        return newSet;
      });

      // Add to inbox
      setAllFaxes((prev) => [...prev, { ...fax, is_spam: false }]);

      toast({
        title: "Moved to Inbox",
        description: "Fax has been moved back to inbox",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error moving fax to inbox:", error);
      toast({
        title: "Error",
        description: "Failed to move fax to inbox",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Delete a single spam fax
  const deleteSpamFax = async (fax) => {
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/mass-delete-spam/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_paths: [fax.file_path] }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete spam fax");
      }

      // Remove from spam list
      setSpamFaxes((prev) => prev.filter((f) => f.file_path !== fax.file_path));
      setSelectedSpamFaxes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fax.file_path);
        return newSet;
      });

      toast({
        title: "Deleted",
        description: "Spam fax has been deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting spam fax:", error);
      toast({
        title: "Error",
        description: "Failed to delete spam fax",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Mass delete selected spam faxes
  const massDeleteSpamFaxes = async () => {
    if (selectedSpamFaxes.size === 0) return;

    setDeletingSpamFaxes(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/mass-delete-spam/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_paths: Array.from(selectedSpamFaxes) }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete spam faxes");
      }

      const data = await response.json();

      // Remove deleted faxes from spam list
      setSpamFaxes((prev) =>
        prev.filter((f) => !selectedSpamFaxes.has(f.file_path)),
      );
      setSelectedSpamFaxes(new Set());

      toast({
        title: "Deleted",
        description:
          data.message || `Deleted ${data.deleted_count} spam fax(es)`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting spam faxes:", error);
      toast({
        title: "Error",
        description: "Failed to delete spam faxes",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingSpamFaxes(false);
    }
  };

  // Toggle spam fax selection
  const toggleSpamFaxSelection = (filePath) => {
    setSelectedSpamFaxes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filePath)) {
        newSet.delete(filePath);
      } else {
        newSet.add(filePath);
      }
      return newSet;
    });
  };

  // Select all spam faxes
  const selectAllSpamFaxes = () => {
    setSelectedSpamFaxes(new Set(spamFaxes.map((f) => f.file_path)));
  };

  // Clear spam fax selection
  const clearSpamSelection = () => {
    setSelectedSpamFaxes(new Set());
  };

  // Mass move selected spam faxes to inbox
  const massMoveSpamToInbox = async () => {
    if (selectedSpamFaxes.size === 0) return;

    setMovingSpamToInbox(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      let movedCount = 0;
      let errorCount = 0;
      const filePaths = Array.from(selectedSpamFaxes);

      // Move each fax to inbox
      for (const filePath of filePaths) {
        try {
          const response = await fetch(`${baseUrl}/api/fax/mark-as-not-spam/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ file_path: filePath }),
          });

          if (response.ok) {
            movedCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error moving fax ${filePath} to inbox:`, error);
          errorCount++;
        }
      }

      // Get the faxes that were moved
      const movedFaxes = spamFaxes.filter((f) =>
        selectedSpamFaxes.has(f.file_path),
      );

      // Update state: remove from spam, add to inbox
      setSpamFaxes((prev) =>
        prev.filter((f) => !selectedSpamFaxes.has(f.file_path)),
      );
      setAllFaxes((prev) => [
        ...prev,
        ...movedFaxes.map((f) => ({ ...f, is_spam: false })),
      ]);
      setSelectedSpamFaxes(new Set());

      if (errorCount > 0) {
        toast({
          title: "Partially Moved",
          description: `Moved ${movedCount} fax(es) to inbox, ${errorCount} failed`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Moved to Inbox",
          description: `Successfully moved ${movedCount} fax(es) to inbox`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error moving spam faxes to inbox:", error);
      toast({
        title: "Error",
        description: "Failed to move faxes to inbox",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMovingSpamToInbox(false);
    }
  };

  // Group spam faxes by date (similar to regular faxes)
  const groupedSpamFaxesByDate = useMemo(() => {
    const groupedByDate = {};
    spamFaxes.forEach((fax) => {
      const createdAt = fax.created_at ? new Date(fax.created_at) : new Date();
      const dateKey = createdAt.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(fax);
    });

    // Sort dates descending
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
      return new Date(b) - new Date(a);
    });

    return { groupedByDate, sortedDates };
  }, [spamFaxes]);

  // Check selected faxes for spam
  const checkSelectedFaxesForSpam = async () => {
    if (selectedFaxes.size === 0) return;

    // Filter to only include unmatched faxes (not reports or matched faxes)
    const unmatchedFaxPaths = allFaxes
      .filter(
        (f) =>
          selectedFaxes.has(f.file_path) && f.is_faxed_report && f.is_unmatched,
      )
      .map((f) => f.file_path);

    if (unmatchedFaxPaths.length === 0) {
      toast({
        title: "No Unmatched Faxes Selected",
        description:
          "Spam check only applies to unmatched faxes. Please select unmatched faxes to check.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setCheckingForSpam(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // First, re-extract text/metadata for all unmatched faxes to get fresh OCR
      toast({
        title: "Re-extracting Text",
        description: `Running OCR on ${unmatchedFaxPaths.length} fax(es) before spam check...`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      const extractResponse = await fetch(
        `${baseUrl}/api/fax/extract-text-metadata/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths: unmatchedFaxPaths }),
        },
      );

      if (!extractResponse.ok) {
        console.warn(
          "OCR extraction failed, proceeding with existing text for spam check",
        );
      } else {
        const extractData = await extractResponse.json();
        console.log("OCR extraction complete:", extractData);
      }

      // Now check for spam using the freshly extracted text
      const response = await fetch(`${baseUrl}/api/fax/check-for-spam/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_paths: unmatchedFaxPaths }),
      });

      if (!response.ok) {
        throw new Error("Failed to check faxes for spam");
      }

      const data = await response.json();

      if (data.spam_count > 0) {
        // Get the file paths of spam faxes
        const spamFilePaths = data.results
          .filter((r) => r.is_spam)
          .map((r) => r.file_path);

        // Move spam faxes from inbox to spam list
        const spamFaxesToMove = allFaxes.filter((f) =>
          spamFilePaths.includes(f.file_path),
        );
        setSpamFaxes((prev) => [
          ...prev,
          ...spamFaxesToMove.map((f) => ({ ...f, is_spam: true })),
        ]);
        setAllFaxes((prev) =>
          prev.filter((f) => !spamFilePaths.includes(f.file_path)),
        );

        // Clear selection
        setSelectedFaxes(new Set());

        toast({
          title: "Spam Check Complete",
          description: `Found ${data.spam_count} spam fax(es) out of ${data.checked_count} checked. Moved to Spam folder.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Spam Check Complete",
          description: `Checked ${data.checked_count} fax(es), no spam detected.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error checking faxes for spam:", error);
      toast({
        title: "Error",
        description: "Failed to check faxes for spam",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckingForSpam(false);
    }
  };

  // Handle upload to EHR task - downloads files to user-selected folder (single fax)
  const handleUploadToEHR = async (fax) => {
    if (!fax) return;

    const faxesToDownload = [fax];
    const taskKey = `uploadToEHR_${fax.file_path}`;
    setProcessingTasks({ ...processingTasks, [taskKey]: true });
    try {
      // Check if File System Access API is available
      if (!window.showDirectoryPicker) {
        toast({
          title: "Not Supported",
          description:
            "Folder selection is not supported in this browser. Files will be downloaded to default download folder.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      let directoryHandle = null;
      if (window.showDirectoryPicker) {
        try {
          directoryHandle = await window.showDirectoryPicker();
        } catch (err) {
          if (err.name !== "AbortError") {
            throw err;
          }
          // User cancelled folder selection
          setProcessingTasks({ ...processingTasks, uploadToEHR: false });
          return;
        }
      }

      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      let downloadedCount = 0;
      let errorCount = 0;

      for (const fax of faxesToDownload) {
        try {
          const filePath = fax.file_path || "";

          // Get the file URL
          const response = await fetch(
            `${baseUrl}/api/fax/get-view-url/?file_path=${encodeURIComponent(filePath)}`,
            { headers: await authHeader() },
          );

          let fileUrl;
          if (response.ok) {
            const data = await response.json();
            fileUrl = data.url;
          } else {
            // Fallback to public URL (using supabaseUrl from env.js)
            fileUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
          }

          // Fetch the file
          const fileResponse = await fetch(fileUrl);
          if (!fileResponse.ok) {
            throw new Error(`Failed to fetch file: ${filePath}`);
          }

          const blob = await fileResponse.blob();
          const filename =
            fax.filename || filePath.split("/").pop() || "file.pdf";

          if (directoryHandle) {
            // Use File System Access API to save to selected folder
            const fileHandle = await directoryHandle.getFileHandle(filename, {
              create: true,
            });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
          } else {
            // Fallback: download to default download folder
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }

          downloadedCount++;
        } catch (error) {
          console.error(`Error downloading file ${fax.file_path}:`, error);
          errorCount++;
        }
      }

      // Mark as uploaded in backend
      const file_paths = faxesToDownload.map((f) => f.file_path);
      try {
        const response = await fetch(`${baseUrl}/api/fax/upload-to-ehr/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths }),
        });

        if (response.ok) {
          const data = await response.json();
          toast({
            title: "Success",
            description: `Downloaded ${downloadedCount} file(s)${errorCount > 0 ? ` (${errorCount} failed)` : ""}`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error marking as uploaded:", error);
      }

      // Refresh faxes to update status
      await fetchAllFaxes();
      // Update the fax in taskFaxes
      setTaskFaxes((prev) =>
        prev.map((f) =>
          f.file_path === fax.file_path ? { ...f, uploaded_to_ehr: true } : f,
        ),
      );
    } catch (error) {
      console.error("Error uploading to EHR:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to download files",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingTasks({ ...processingTasks, [taskKey]: false });
    }
  };

  // Handle extract text and metadata task (single fax)
  const handleExtractTextMetadata = async (fax) => {
    if (!fax) return;

    const taskKey = `extractText_${fax.file_path}`;
    setProcessingTasks({ ...processingTasks, [taskKey]: true });
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const file_paths = [fax.file_path];
      const response = await fetch(
        `${baseUrl}/api/fax/extract-text-metadata/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths }),
        },
      );

      if (!response.ok) {
        if (response.status === 502 || response.status === 503) {
          throw new Error(
            "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
          );
        }
        let errorMessage = "Failed to extract text and metadata";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(
          "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
        );
      }
      console.log("Extraction response:", data);

      // Check if any files were truncated
      if (
        data.has_truncated_files &&
        data.truncated_files &&
        data.truncated_files.length > 0
      ) {
        const truncatedFile = data.truncated_files[0]; // Handle first truncated file
        console.log("Document was truncated:", truncatedFile);

        const truncationData = {
          file_path: truncatedFile.file_path,
          filename: truncatedFile.filename,
          total_pages: truncatedFile.total_pages,
          pages_processed: truncatedFile.pages_processed,
          remaining_pages: truncatedFile.remaining_pages,
        };

        // Save to localStorage so it persists until user makes a choice
        savePendingTruncation(truncatedFile.file_path, truncationData);

        setTruncationInfo(truncationData);
        onTruncationModalOpen();

        // Still show success but mention truncation
        toast({
          title: "Extraction Complete",
          description: `Processed first ${truncatedFile.pages_processed} of ${truncatedFile.total_pages} pages. ${truncatedFile.remaining_pages} pages remaining.`,
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description:
            data.message ||
            `Extracted text and metadata for ${data.updated_count} fax(es)`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh faxes to update status
      await fetchAllFaxes();
      // Update the fax in taskFaxes
      setTaskFaxes((prev) =>
        prev.map((f) =>
          f.file_path === fax.file_path
            ? { ...f, extracted_text_metadata: true }
            : f,
        ),
      );
    } catch (error) {
      console.error("Error extracting text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingTasks({ ...processingTasks, [taskKey]: false });
    }
  };

  // Handle continue extraction for truncated documents
  const handleContinueExtraction = async () => {
    if (!truncationInfo) return;

    setIsContinuingExtraction(true);
    setContinueProgress(null);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // The server OCRs a batch of pages per request; call until none remain.
      const data = await continueOcrExtractionInBatches(async () => {
        const response = await fetch(
          `${baseUrl}/api/fax/continue-ocr-extraction/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ file_path: truncationInfo.file_path }),
          },
        );

        if (!response.ok) {
          if (response.status === 502 || response.status === 503) {
            throw new Error(
              "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
            );
          }
          let errorMessage = "Failed to continue extraction";
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
          }
          throw new Error(errorMessage);
        }

        try {
          return await response.json();
        } catch (parseError) {
          throw new Error(
            "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
          );
        }
      }, { onProgress: setContinueProgress });

      // Pages left (the OCR service stopped responding mid-way): keep the
      // pending entry so the prompt comes back, and say what was saved.
      const incomplete = continueOcrIncompleteToast(data);
      if (incomplete) {
        // Keep the entry, with the counts that are left now.
        savePendingTruncation(
          truncationInfo.file_path,
          continueOcrUpdatedTruncation(truncationInfo, data),
        );
        toast({ ...incomplete, duration: 8000, isClosable: true });
      } else {
        // Clear from localStorage since user made a choice
        clearPendingTruncation(truncationInfo.file_path);

        toast({
          title: "Success",
          description: data.message || `Successfully processed remaining pages`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh faxes to update status
      await fetchAllFaxes();

      onTruncationModalClose();
      setTruncationInfo(null);
    } catch (error) {
      console.error("Error continuing extraction:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to continue extraction",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsContinuingExtraction(false);
      setContinueProgress(null);
    }
  };

  // Handle declining to continue extraction
  const handleDeclineContinueExtraction = () => {
    if (truncationInfo) {
      // Clear from pending and mark as declined so we don't show the modal again
      clearPendingTruncation(truncationInfo.file_path);
      saveDeclinedTruncation(truncationInfo.file_path);
    }
    onTruncationModalClose();
    setTruncationInfo(null);
    toast({
      title: "Extraction Complete",
      description: "Document was processed with the first 10 pages only.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  // Handle viewing extracted text from truncation modal
  const handleViewTextFromTruncationModal = async () => {
    if (!truncationInfo) return;

    try {
      const supabase = await getSupabase();
      // The row lives in the schema the file's path names, not necessarily the
      // viewer's active one (cross-schema providers, schema-group fan-out).
      const { data, error } = await readCacheRow(
        supabase, truncationInfo.file_path, "full_text, filename", supabaseSchema);

      if (error && !data) {
        throw new Error(error.message || "Failed to fetch extracted text");
      }

      if (!data || !data.full_text) {
        toast({
          title: "No Text Available",
          description:
            "This file does not have extracted text in the database.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setViewingTextData({
        file_path: truncationInfo.file_path,
        full_text: data.full_text,
        filename: data.filename || truncationInfo.filename || "Unknown",
      });
      // Keep truncation modal open, just open the view text modal alongside
      onViewTextModalOpen();
    } catch (error) {
      console.error("Error fetching extracted text:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch extracted text",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle view extracted text
  const handleViewExtractedText = async (fax) => {
    if (!fax || !fax.file_path) return;

    // Check if user has already declined truncation for this file
    if (hasDeclinedTruncation(fax.file_path)) {
      console.log("User already declined truncation for file:", fax.file_path);
      // Skip truncation modal, go straight to viewing text
    } else {
      // Check if there's a pending truncation for this file
      const pendingTruncations = getPendingTruncations();
      const pendingTruncation = pendingTruncations[fax.file_path];

      if (pendingTruncation) {
        console.log(
          "Found pending truncation for file:",
          fax.file_path,
          pendingTruncation,
        );
        // Show truncation modal first
        setTruncationInfo(pendingTruncation);
        onTruncationModalOpen();
        return; // Don't open view text modal until user makes a choice
      }
    }

    try {
      // Fetch the full_text and metadata from the schema that owns the file. An
      // unqualified read used the viewer's ACTIVE schema, so a fax filed under
      // another clinic's schema failed with "Cannot coerce the result to a
      // single JSON object" (an HTTP 406 from .single() on zero rows).
      const supabase = await getSupabase();
      const { data, error } = await readCacheRow(
        supabase, fax.file_path, "full_text, filename, metadata", supabaseSchema);

      if (error && !data) {
        throw new Error(error.message || "Failed to fetch extracted text");
      }

      if (!data || !data.full_text) {
        toast({
          title: "No Text Available",
          description:
            "This file does not have extracted text in the database.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Check if metadata has truncation info (for files extracted before localStorage was implemented)
      // But skip if user has already declined
      if (
        data.metadata?.truncation_info?.was_truncated &&
        !hasDeclinedTruncation(fax.file_path)
      ) {
        const truncInfo = data.metadata.truncation_info;
        const truncationData = {
          file_path: fax.file_path,
          filename: data.filename || fax.filename || "Unknown",
          total_pages: truncInfo.total_pages,
          pages_processed: truncInfo.pages_processed,
          remaining_pages: truncInfo.remaining_pages,
        };
        console.log("Found truncation info in metadata:", truncationData);
        // Save to localStorage and show modal
        savePendingTruncation(fax.file_path, truncationData);
        setTruncationInfo(truncationData);
        onTruncationModalOpen();
        return;
      }

      setViewingTextData({
        file_path: fax.file_path,
        filename: data.filename || fax.filename || "Unknown",
        full_text: data.full_text,
      });
      onViewTextModalOpen();
    } catch (error) {
      console.error("Error fetching extracted text:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch extracted text",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle re-extract text from the extracted text modal
  const handleReExtractText = async () => {
    if (!viewingTextData?.file_path) return;

    setIsReExtractingText(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const file_paths = [viewingTextData.file_path];
      const response = await fetch(
        `${baseUrl}/api/fax/extract-text-metadata/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths }),
        },
      );

      if (!response.ok) {
        if (response.status === 502 || response.status === 503) {
          throw new Error(
            "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
          );
        }
        let errorMessage = "Failed to extract text and metadata";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(
          "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
        );
      }
      console.log("Re-extraction response:", data);

      // Check if any files were truncated
      if (
        data.has_truncated_files &&
        data.truncated_files &&
        data.truncated_files.length > 0
      ) {
        const truncatedFile = data.truncated_files[0];
        console.log("Re-extracted document was truncated:", truncatedFile);

        const truncationData = {
          file_path: truncatedFile.file_path,
          filename: truncatedFile.filename,
          total_pages: truncatedFile.total_pages,
          pages_processed: truncatedFile.pages_processed,
          remaining_pages: truncatedFile.remaining_pages,
        };

        // Save to localStorage so it persists until user makes a choice
        savePendingTruncation(truncatedFile.file_path, truncationData);

        setTruncationInfo(truncationData);
        onTruncationModalOpen();

        toast({
          title: "Extraction Complete",
          description: `Processed first ${truncatedFile.pages_processed} of ${truncatedFile.total_pages} pages. ${truncatedFile.remaining_pages} pages remaining.`,
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description:
            data.message || "Re-extracted text and metadata successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh the extracted text in the modal (from the file's own schema)
      const supabase = await getSupabase();
      const { data: refreshedData, error: refreshError } = await readCacheRow(
        supabase, viewingTextData.file_path, "full_text, filename", supabaseSchema);

      if (!refreshError && refreshedData) {
        setViewingTextData({
          ...viewingTextData,
          full_text: refreshedData.full_text,
          filename: refreshedData.filename || viewingTextData.filename,
        });
      }

      // Refresh faxes list
      await fetchAllFaxes();
    } catch (error) {
      console.error("Error re-extracting text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to re-extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReExtractingText(false);
    }
  };

  // Handle file upload for send fax
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file size (max 50MB for faxes)
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File Too Large",
        description: `File size must be less than 50MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSendFaxAttachmentsExpanded(true);
    setUploadingFile(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const supabase = await getSupabase();

      // Generate unique file path - include schema for isolation
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      // Include schema in path if not 'public' to isolate files by clinic
      const filePath =
        userSchema && userSchema !== "public"
          ? `New/Reports/${userSchema}/Uploaded/${timestamp}_${sanitizedFileName}`
          : `New/Reports/Uploaded/${timestamp}_${sanitizedFileName}`;

      // Determine content type
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const contentType =
        fileExtension === "pdf"
          ? "application/pdf"
          : fileExtension === "png"
            ? "image/png"
            : fileExtension === "jpg" || fileExtension === "jpeg"
              ? "image/jpeg"
              : fileExtension === "tiff" || fileExtension === "tif"
                ? "image/tiff"
                : file.type || "application/octet-stream";

      // Upload file to Supabase storage
      debugLog(
        `[UPLOAD] Uploading file to bucket '${bucketName_files || "files"}', path: ${filePath}`,
      );

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName_files || "files")
        .upload(filePath, file, {
          contentType: contentType,
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error(`[UPLOAD] Upload failed:`, uploadError);
        throw new Error(uploadError.message || `Failed to upload ${file.name}`);
      }

      debugLog(`[UPLOAD] Upload response:`, uploadData);

      // Verify the file actually exists in storage
      const { data: verifyData, error: verifyError } = await supabase.storage
        .from(bucketName_files || "files")
        .list(filePath.substring(0, filePath.lastIndexOf("/")), {
          search: filePath.split("/").pop(),
        });

      if (verifyError || !verifyData || verifyData.length === 0) {
        console.error(
          `[UPLOAD] File verification failed - file may not exist:`,
          verifyError,
        );
        throw new Error(
          `Upload appeared to succeed but file not found in storage. Check storage bucket policies.`,
        );
      }

      debugLog(`[UPLOAD] File verified in storage:`, verifyData);

      // Set the uploaded file as selected
      setUploadedFile({
        file_path: filePath,
        filename: file.name,
        size: file.size,
        type: file.type,
      });

      setSelectedFaxForSending({
        file_path: filePath,
        filename: file.name,
      });

      setUploadProgress(100);

      toast({
        title: "File Uploaded Successfully",
        description: `${file.name} has been uploaded and is ready to send.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadError(error.message);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload file",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle send fax click - switches to Send tab with selected fax
  const handleSendFaxClick = (fax) => {
    if (!fax || !fax.file_path) {
      toast({
        title: "Error",
        description: "Invalid fax data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // Set the selected fax for sending
    setSelectedFaxForSending(fax);
    // Clear any uploaded file since we're selecting from inbox
    setUploadedFile(null);
    // Switch to the Send tab (index 3: Inbox=0, Tasks=1, Search=2, Send=3, Sent=4)
    setActiveFaxTab(3);
    toast({
      title: "File Selected",
      description: `Selected "${fax.filename || "file"}" for sending. Fill in the recipient details below.`,
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle send fax submission
  const handleSendFax = async () => {
    if (!selectedFaxForSending || !selectedFaxForSending.file_path) {
      toast({
        title: "Error",
        description: "No fax selected",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!sendFaxForm.fax_number.trim()) {
      toast({
        title: "Error",
        description: "Recipient fax number is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSendingFax(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Get current user info from Supabase session
      let sentByUser = null;
      let sentByUserId = null;
      try {
        const supabaseClient = await getSupabase();
        const {
          data: { session },
        } = await supabaseClient.auth.getSession();
        if (session?.user) {
          sentByUserId = session.user.id;
          // Try to get name from users table first
          const { data: userData, error: userError } = await supabasePublic()
            .from("users")
            .select("name, email")
            .eq("id", session.user.id)
            .single();

          debugLog("User data query result:", {
            userData,
            userError,
            userId: session.user.id,
          });

          if (userError) {
            console.warn("Error fetching user name:", userError);
            sentByUser = session.user.email;
          } else {
            sentByUser =
              userData?.name || userData?.email || session.user.email;
            debugLog(
              "Using sent_by:",
              sentByUser,
              "from userData.name:",
              userData?.name,
            );
          }
        }
      } catch (e) {
        console.warn("Could not get user session for sent_by:", e);
      }

      const response = await fetch(`${baseUrl}/api/fax/send/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_path: selectedFaxForSending.file_path,
          fax_number: sendFaxForm.fax_number.trim(),
          recipient_name: sendFaxForm.recipient_name.trim(),
          sender_name: sendFaxForm.sender_name.trim(),
          sender_fax: sendFaxForm.sender_fax.trim(),
          // Only include email if provided (it's optional and must be authorized in SRFax)
          ...(sendFaxForm.sender_email.trim() && {
            sender_email: sendFaxForm.sender_email.trim(),
          }),
          subject: sendFaxForm.subject.trim(),
          cover_page: sendFaxForm.cover_page,
          cover_page_text: sendFaxForm.cover_page_text.trim(),
          // Pass user info for tracking who sent the fax
          sent_by: sentByUser,
          sent_by_user_id: sentByUserId,
          schema: userSchema, // Include schema to isolate sent faxes by clinic
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || "Failed to send fax";

        // Provide helpful message if email is not authorized
        if (errorMessage.includes("Invalid Senders Email Address")) {
          throw new Error(
            `${errorMessage}\n\nTo fix this:\n1. Log into your SRFax account dashboard\n2. Go to Account Settings\n3. Authorize the email address: ${sendFaxForm.sender_email}\n\nOr try selecting a different email from the dropdown that is already authorized.`,
          );
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Fax Sent Successfully",
          description: `Fax has been queued for sending. Transaction ID: ${data.fax_id || "N/A"}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setSelectedFaxForSending(null);
        setUploadedFile(null); // Clear uploaded file after successful send
        // Reset form with default email from srfaxAuthEmails
        setSendFaxForm({
          fax_number: "1", // Default to "1" for US country code
          recipient_name: "",
          sender_name: "",
          sender_fax: "",
          sender_email: srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : "", // Default to first authorized email
          subject: "",
          cover_page: false,
          cover_page_text: "",
        });
        // Refresh sent faxes list
        fetchSentFaxes();
      } else {
        throw new Error(data.error || "Failed to send fax");
      }
    } catch (error) {
      console.error("Error sending fax:", error);
      toast({
        title: "Send Fax Failed",
        description: error.message || "An error occurred while sending the fax",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingFax(false);
    }
  };

  // Handle assign task - opens modal first (single fax)
  const handleAssignTaskClick = (fax) => {
    if (!fax) return;
    setSelectedFaxForTask(fax); // Store the fax for assignment
    setSelectedUserForTask(null);
    setUserSearchQuery("");
    setUserSearchResults([]);
    setShowUserDropdown(false);
    onAssignTaskModalOpen();
  };

  // Reset dropdown state when modal closes
  useEffect(() => {
    if (!isAssignTaskModalOpen) {
      setShowUserDropdown(false);
      setUserSearchQuery("");
      setUserSearchResults([]);
      setSelectedUserForTask(null);
    }
  }, [isAssignTaskModalOpen]);

  // Handle assign task confirmation
  const handleAssignTask = async () => {
    if (!selectedUserForTask || !selectedFaxForTask) {
      toast({
        title: "Error",
        description: "Please select a user",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const taskKey = `assignTask_${selectedFaxForTask.file_path}`;
    setProcessingTasks({ ...processingTasks, [taskKey]: true });
    onAssignTaskModalClose();

    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const file_paths = [selectedFaxForTask.file_path];
      const response = await fetch(`${baseUrl}/api/fax/assign-task/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_paths, user_id: selectedUserForTask.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign task");
      }

      const data = await response.json();

      // Check if assignment was successful
      if (data.updated_count === 0) {
        toast({
          title: "Warning",
          description:
            "No records were updated. The file may not exist or may already be assigned.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description:
            data.message ||
            `Assigned ${data.updated_count} fax task(s) to ${selectedUserForTask.name || selectedUserForTask.email}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Wait a bit for database to update, then refresh faxes to update status
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Always refresh assigned tasks if we have a current user, regardless of who we assigned to
      // This ensures the list is up-to-date
      if (currentUserId) {
        await fetchMyAssignedTasks();
      }

      // Refresh all faxes to update the main list
      await fetchAllFaxes();

      // Remove the fax from taskFaxes since it's now assigned to someone else
      // (taskFaxes are unassigned tasks, so once assigned, they should be removed)
      setTaskFaxes((prev) =>
        prev.filter((f) => f.file_path !== selectedFaxForTask.file_path),
      );
      setSelectedUserForTask(null);
      setSelectedFaxForTask(null);
    } catch (error) {
      console.error("Error assigning task:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign task",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingTasks({ ...processingTasks, [taskKey]: false });
    }
  };

  // Handle unassign task
  const handleUnassignTask = async (fax) => {
    if (!fax || !fax.file_path) {
      toast({
        title: "Error",
        description: "Invalid fax data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const taskKey = `unassignTask_${fax.file_path}`;
    setProcessingTasks({ ...processingTasks, [taskKey]: true });

    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/unassign-task/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_path: fax.file_path }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to unassign task");
      }

      const data = await response.json();
      toast({
        title: "Success",
        description: data.message || `Unassigned fax task`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Refresh faxes to update status
      await fetchAllFaxes();
      // Refresh assigned tasks
      await fetchMyAssignedTasks();
    } catch (error) {
      console.error("Error unassigning task:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to unassign task",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingTasks({ ...processingTasks, [taskKey]: false });
    }
  };

  // Handle search faxes
  const handleSearchFaxes = async () => {
    setSearchingFaxes(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/search/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Read the box itself: it is uncontrolled, and the Search button sets
          // state and calls this in the same tick, so `searchQuery` was still the
          // PREVIOUS text -- the first search always went out with no query.
          query: searchQueryRef.current?.value ?? searchQuery,
          patient_name: searchPatientName,
          report_type: searchReportType,
          report_type_filter: searchReportTypeFilter, // 'all', 'fax', 'reports'
          schema: userSchema,
          schemas: faxInboxSchemasRef.current || [], // same schemas as the inbox list
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to search faxes");
      }

      const data = await response.json();
      setSearchResults(data.results || []);

      if (data.results && data.results.length === 0) {
        toast({
          title: "No Results",
          description: "No reports found matching your search criteria",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error searching faxes:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to search faxes",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSearchingFaxes(false);
    }
  };

  // Assign a fax to a patient
  // Schema a patient row came from (rows are merged from every selected
  // provider schema and tagged `_schema`); the viewer's schema otherwise.
  const patientSchemaFor = (patientId) =>
    patients.find((p) => p.id === patientId)?._schema || userSchema;

  const assignFaxToPatient = async (fax, patientId) => {
    setPendingFocus({ doc: faxDocumentKey(fax), since: allFaxes });
    setAssigningFax(true);
    try {
      // Get filename and file_path first (needed for fallback)
      const filename = fax.filename || "";
      const filePath = fax.file_path || "";

      // Try to get fax_id directly from the fax object (if stored in database)
      let faxId = fax.fax_id || null;

      if (!faxId) {
        // Extract fax_id from filename or file_path
        // Format examples:
        // - Fax_2025-12-25_03-59-19_814ea6456144e95d7e0283e1d3c1f725ac3645ad.pdf
        // - Fax_20251225191903_5636-1_1|1593487422.pdf

        // Try filename first
        if (filename.includes("Fax_")) {
          const parts = filename.split("_");
          if (parts.length >= 4) {
            // Format: Fax_DATE_TIME_FAXID.ext
            // Get the last part (FAXID.ext) and remove extension
            faxId = parts[parts.length - 1].split(".")[0];
          } else if (parts.length >= 3) {
            // Fallback: if only 3 parts, assume format is Fax_TIMESTAMP_FAXID.ext
            faxId = parts[2].split(".")[0];
          }
        }

        // If still no faxId, try to extract from file_path (which may contain original filename)
        if (!faxId && filePath) {
          const pathParts = filePath.split("/");
          const fileName = pathParts[pathParts.length - 1];
          if (fileName.includes("Fax_")) {
            const parts = fileName.split("_");
            if (parts.length >= 4) {
              faxId = parts[parts.length - 1].split(".")[0];
            } else if (parts.length >= 3) {
              faxId = parts[2].split(".")[0];
            }
          }

          // Also check entire file_path for hex strings (in case filename was renamed but path has original)
          if (!faxId) {
            const hexMatch = filePath.match(/[a-f0-9]{40,64}/i);
            if (hexMatch) {
              faxId = hexMatch[0];
            }
          }
        }

        // If still no faxId, try to extract the long hash-like ID (40-64 chars) from filename
        if (!faxId && filename) {
          // Look for a 40-64 character hex string (common fax ID formats)
          const hexMatch = filename.match(/[a-f0-9]{40,64}/i);
          if (hexMatch) {
            faxId = hexMatch[0];
          }
        }

        // Last resort: try to extract any alphanumeric string after the last underscore
        if (!faxId && filename) {
          const underscoreMatch = filename.match(
            /_([a-zA-Z0-9]+)(?:\.[^.]+)?$/,
          );
          if (underscoreMatch && underscoreMatch[1].length >= 10) {
            faxId = underscoreMatch[1];
          }
        }

        // Final fallback: if we have file_path, try to use it directly with the backend
        // The backend can look up the fax_id from the database using file_path
        if (!faxId && filePath) {
          // We'll need to pass file_path to backend and let it handle the lookup
          // For now, try to extract any potential ID from the path structure
          const pathIdMatch =
            filePath.match(/\/([a-f0-9]{20,64})\//i) ||
            filePath.match(/_([a-f0-9]{20,64})/i);
          if (pathIdMatch) {
            faxId = pathIdMatch[1];
          }
        }
      }

      // If we still don't have faxId but have file_path, send file_path instead
      // The backend can look up the fax using file_path
      // The PATIENT's schema, not the viewer's: the picker lists patients from
      // every selected schema, and the backend looks the patient up (and files
      // the fax) in the schema it is given.
      const patientSchema = patientSchemaFor(patientId);
      const requestBody = {
        patient_id: patientId,
        move_file: true,
        schema: patientSchema,
        ...(currentUserName && { assigned_by_name: currentUserName }),
      };

      debugLog(
        `📋 Assigning fax to patient - Patient ID: ${patientId}, Schema: ${patientSchema}, Fax ID: ${
          faxId || "N/A"
        }, File Path: ${filePath || "N/A"}`,
      );

      if (faxId) {
        requestBody.fax_id = faxId;
      } else if (filePath) {
        requestBody.file_path = filePath;
      } else {
        console.error("Fax object:", fax);
        throw new Error(
          `Could not extract fax ID from filename: ${
            fax.filename || "N/A"
          }. Please check the fax record.`,
        );
      }

      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/assign-unmatched-fax/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign fax");
      }

      toast({
        title: "Success",
        description: "Fax assigned to patient successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Refresh fax list
      await fetchAllFaxes();
      setSelectedFaxForAssignment(null);
      setSelectedPatientForAssignment(null);
    } catch (error) {
      console.error("Error assigning fax:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign fax to patient",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAssigningFax(false);
    }
  };

  // Change fax classification function
  const changeFaxClassification = async () => {
    if (!selectedFaxForClassification || !newFaxClassification) {
      toast({
        title: "Error",
        description: "Please select a classification",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setChangingFaxClassification(true);
    try {
      const supabase = await getSupabase();
      const filePath = selectedFaxForClassification.file_path;
      const fileName = selectedFaxForClassification.filename || "";

      // Get existing classification data to preserve other fields -- from the
      // schema that owns the file, and write back to that same schema. An
      // unqualified upsert landed in the viewer's ACTIVE schema: the real row
      // never changed and a stray, text-less duplicate appeared there instead.
      const { data: existingData, schema: rowSchema } = await readCacheRow(
        supabase, filePath, "full_text, summary, is_faxed_report", supabaseSchema);
      const writeSchema = rowSchema || schemaFromReportsPath(filePath);

      // Update or insert classification
      const { error: updateError } = await clientForSchema(supabase, writeSchema, supabaseSchema)
        .from("file_classification_cache")
        .upsert(
          {
            file_path: filePath,
            filename: fileName,
            classification: newFaxClassification,
            full_text: existingData?.full_text || null,
            summary: existingData?.summary || null,
            is_faxed_report:
              existingData?.is_faxed_report !== undefined
                ? existingData.is_faxed_report
                : true,
          },
          {
            onConflict: "file_path",
          },
        );

      if (updateError) {
        throw new Error(
          `Failed to update classification: ${updateError.message}`,
        );
      }

      toast({
        title: "Classification Updated",
        description: `Fax classification has been changed to ${newFaxClassification}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onChangeFaxClassificationClose();
      setSelectedFaxForClassification(null);
      setNewFaxClassification("");

      // Refresh fax list
      await fetchAllFaxes();
    } catch (error) {
      console.error("Error changing fax classification:", error);
      toast({
        title: "Update Failed",
        description:
          error.message ||
          "An error occurred while updating the classification.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChangingFaxClassification(false);
    }
  };

  // Rename fax function
  const renameFax = async () => {
    if (!selectedFaxForRename || !newFaxName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid filename",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setRenamingFax(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/rename/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_path: selectedFaxForRename.file_path,
          new_filename: newFaxName.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to rename fax");
      }

      const data = await response.json();
      toast({
        title: "Fax Renamed",
        description: `Fax has been renamed to ${data.new_filename}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onRenameFaxClose();
      setSelectedFaxForRename(null);
      setNewFaxName("");
      fetchAllFaxes(); // Refresh the list
    } catch (error) {
      console.error("Error renaming fax:", error);
      toast({
        title: "Rename Failed",
        description:
          error.message || "An error occurred while renaming the fax.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRenamingFax(false);
    }
  };

  // Load reports when inbox opens (refresh) - wait for userSchema to be loaded
  useEffect(() => {
    if (isFaxInboxOpen && userSchemaLoaded) {
      fetchAllFaxes();
    }
  }, [isFaxInboxOpen, currentUserId, userSchemaLoaded, userSchema]);

  // Fetch fax number when inbox opens (per-schema, falls back to env default)
  useEffect(() => {
    if (isFaxInboxOpen && userSchemaLoaded) {
      const fetchFaxNumber = async () => {
        try {
          const hostname = window.location.hostname;
          const baseUrl =
            hostname === "localhost" || hostname === "127.0.0.1"
              ? BASE_URLS.local.backend
              : BASE_URLS.render.backend;
          const response = await fetch(
            `${baseUrl}/api/fax/number/?schema=${encodeURIComponent(userSchema)}`,
          );
          if (response.ok) {
            const data = await response.json();
            setFaxNumber(data.fax_number || "");
          }
        } catch (error) {
          console.error("Error fetching fax number:", error);
        }
      };
      fetchFaxNumber();
    }
  }, [isFaxInboxOpen, userSchemaLoaded, userSchema]);

  // Adjust Select dropdown icon position
  useEffect(() => {
    if (isFaxInboxOpen && activeFaxTab === 2) {
      // Wait for DOM to render
      const timer = setTimeout(() => {
        const selectWrapper = document.querySelector(
          ".report-type-select-wrapper",
        );
        if (selectWrapper) {
          const icon =
            selectWrapper.querySelector("[data-icon]") ||
            selectWrapper.querySelector("svg");
          if (icon) {
            icon.style.top = "25%";
            icon.style.transform = "translateY(-50%)";
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFaxInboxOpen, activeFaxTab]);

  // Fetch spam faxes when spam tab is selected
  useEffect(() => {
    if (isFaxInboxOpen && activeFaxTab === 5 && spamFaxes.length === 0) {
      fetchSpamFaxes();
    }
  }, [isFaxInboxOpen, activeFaxTab]);

  // Search users for task assignment
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const searchUsers = async (query) => {
    if (!query || query.trim().length < 1) {
      setUserSearchResults([]);
      return;
    }

    setSearchingUsers(true);
    try {
      // The backend searches the staff of the signed-in user's own clinics,
      // with their token (Plan 4 Task 11).
      const data = await searchStaffUsers(query);
      setUserSearchResults(data.users || []);
    } catch (error) {
      console.error("Error searching users:", error);
      setUserSearchResults([]);
      toast({
        title: "Error",
        description: error.message || "Failed to search users",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSearchingUsers(false);
    }
  };

  // Debounce user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchUsers(userSearchQuery);
      } else {
        setUserSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // Delete fax function
  const deleteFax = async () => {
    if (!faxToDelete) return;

    setDeletingFax(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/delete/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_path: faxToDelete.file_path,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete fax");
      }

      toast({
        title: "Fax Deleted",
        description: `Fax ${faxToDelete.filename} has been deleted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onDeleteFaxClose();
      setFaxToDelete(null);
      fetchAllFaxes(); // Refresh the list
    } catch (error) {
      console.error("Error deleting fax:", error);
      toast({
        title: "Delete Failed",
        description:
          error.message || "An error occurred while deleting the fax.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingFax(false);
    }
  };

  // Mass assign faxes function
  const massAssignFaxes = async (patientId) => {
    if (selectedFaxes.size === 0) {
      toast({
        title: "No Selection",
        description: "Please select at least one fax to assign.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setMassAssigning(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // One fax at a time through the same endpoint as a single assign: it
      // looks the patient up in THEIR schema, files under that schema's path,
      // updates the chart's reports pointer and dual-files the roster copy.
      // /api/fax/mass-assign/ was a public-only copy of that logic, so every
      // patient outside public came back "Patient not found".
      const filePaths = Array.from(selectedFaxes);
      const patientSchema = patientSchemaFor(patientId);
      let assigned = 0;
      const failures = [];
      for (const filePath of filePaths) {
        const response = await fetch(`${baseUrl}/api/fax/assign-unmatched-fax/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_path: filePath,
            patient_id: patientId,
            move_file: true,
            schema: patientSchema,
            ...(currentUserName && { assigned_by_name: currentUserName }),
          }),
        });
        if (response.ok) {
          assigned += 1;
        } else {
          const errorData = await response.json().catch(() => ({}));
          failures.push(`${filePath.split("/").pop()}: ${errorData.error || response.status}`);
        }
      }

      if (assigned === 0) {
        throw new Error(failures[0] || "Failed to assign faxes");
      }
      toast({
        title: "Faxes Assigned",
        description:
          `Successfully assigned ${assigned} fax(es).` +
          (failures.length ? ` ${failures.length} failed: ${failures.join("; ")}` : ""),
        status: failures.length ? "warning" : "success",
        duration: failures.length ? 8000 : 5000,
        isClosable: true,
      });

      onMassAssignClose();
      setSelectedFaxes(new Set());
      fetchAllFaxes(); // Refresh the list
    } catch (error) {
      console.error("Error mass assigning faxes:", error);
      toast({
        title: "Assignment Failed",
        description:
          error.message || "An error occurred while assigning faxes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMassAssigning(false);
    }
  };

  // Mass delete faxes function
  const massDeleteFaxes = async () => {
    if (selectedFaxes.size === 0) {
      toast({
        title: "No Selection",
        description: "Please select at least one fax to delete.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setMassDeleting(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const filePaths = Array.from(selectedFaxes);
      const response = await fetch(`${baseUrl}/api/fax/mass-delete/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_paths: filePaths,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete faxes");
      }

      const data = await response.json();
      toast({
        title: "Faxes Deleted",
        description:
          data.message || `Successfully deleted ${data.deleted_count} fax(es).`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onMassDeleteClose();
      setSelectedFaxes(new Set());
      fetchAllFaxes(); // Refresh the list
    } catch (error) {
      console.error("Error mass deleting faxes:", error);
      toast({
        title: "Delete Failed",
        description: error.message || "An error occurred while deleting faxes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMassDeleting(false);
    }
  };

  // Toggle fax selection
  const handleMatchSingleFax = async (fax) => {
    // Remember it BEFORE the call: matching renames the file, so the row has a
    // different path by the time the list comes back.
    setPendingFocus({ doc: faxDocumentKey(fax), since: allFaxes });
    if (!fax.is_unmatched) {
      toast({
        title: "Fax already matched",
        description: "This fax is already matched to a patient.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setMatchingFaxes(fax.file_path);
    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    try {
      beginActivity("fax_matching");
      const response = await fetch(
        `${baseUrl}/api/fax/match/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_paths: [fax.file_path],
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        const results = data.results || [];
        const matchedResult = results.find((r) => r.status === "matched");
        const needsConfirmation = results.find(
          (r) => r.status === "needs_confirmation",
        );
        const failedResult = results.find(
          (r) => r.status === "no_match" || r.status === "error",
        );

        // If fax needs confirmation, show modal
        if (needsConfirmation) {
          setConfirmationModal({
            file_path: needsConfirmation.file_path,
            extracted_name: needsConfirmation.extracted_name,
            matched_patient: needsConfirmation.matched_patient,
            remainingConfirmations: [], // Single fax, no remaining
          });
          onConfirmMatchOpen();
        } else if (matchedResult) {
          // Successfully matched
          const patientName = matchedResult.patient_name || "Unknown Patient";
          const scheduledDate =
            matchedResult.scheduled_date_display ||
            matchedResult.scheduled_date ||
            "Unknown Date";

          toast({
            title: "Match Successful",
            description: `Matched to ${patientName} (Scheduled: ${scheduledDate})`,
            status: "success",
            duration: 6000,
            isClosable: true,
          });

          // Refresh fax list
          await fetchAllFaxes();
        } else if (failedResult) {
          // No match found
          toast({
            title: "No Match Found",
            description: "Could not find a matching patient for this fax.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        throw new Error(data.error || data.message || "Failed to match fax");
      }
    } catch (error) {
      console.error("Error matching fax:", error);
      toast({
        title: "Matching Failed",
        description:
          error.message || "An error occurred while matching the fax.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMatchingFaxes(null);
      endActivity("fax_matching");
    }
  };

  // A sibling schema holds someone with this patient's name but a different
  // date of birth. The backend refuses to guess and asks here instead; either
  // answer clears the question so it is not re-asked on every refresh.
  const handleResolveIdentity = async (question, action) => {
    setPendingFocus({
      doc: faxDocumentKey({ file_path: question.file_path }),
      since: allFaxes,
    });
    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;
    try {
      const response = await fetch(`${baseUrl}/api/fax/confirm-match/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_path: question.file_path,
          action,
          schema: question.schema,
          table: question.table,
          row_id: question.row_id,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to resolve");
      toast({
        title: action === "confirm_identity" ? "Filed" : "Left unfiled",
        description:
          action === "confirm_identity"
            ? `Filed into ${question.schema}.`
            : `${question.schema} will not be asked about again for this fax.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchAllFaxes();
    } catch (error) {
      console.error("Error resolving identity question:", error);
      toast({
        title: "Could not resolve",
        description: error.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmMatch = async (action) => {
    if (!confirmationModal) return;
    setPendingFocus({
      doc: faxDocumentKey({ file_path: confirmationModal.file_path }),
      since: allFaxes,
    });

    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    try {
      if (action === "confirm") {
        const response = await fetch(`${baseUrl}/api/fax/confirm-match/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_path: confirmationModal.file_path,
            patient_id: confirmationModal.matched_patient.id,
            action: "confirm",
            schema: confirmationModal.matched_patient?.schema || "public",
          }),
        });

        const data = await response.json();

        if (response.ok) {
          toast({
            title: "Match Confirmed",
            description: `Matched to ${data.patient_name} (Scheduled: ${data.scheduled_date_display})`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          throw new Error(data.error || "Failed to confirm match");
        }
      } else if (action === "leave_unmatched") {
        const response = await fetch(`${baseUrl}/api/fax/confirm-match/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_path: confirmationModal.file_path,
            action: "leave_unmatched",
            schema: confirmationModal.matched_patient?.schema || "public",
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to leave unmatched");
        }

        toast({
          title: "Left Unmatched",
          description: "Fax has been left unmatched as requested.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } else if (action === "try_again") {
        // Just close the modal and let user try matching again
        onConfirmMatchClose();
        setConfirmationModal(null);
        return;
      }

      // Check if there are more confirmations pending
      if (
        confirmationModal.remainingConfirmations &&
        confirmationModal.remainingConfirmations.length > 0
      ) {
        const nextConfirmation = confirmationModal.remainingConfirmations[0];
        setConfirmationModal({
          file_path: nextConfirmation.file_path,
          extracted_name: nextConfirmation.extracted_name,
          matched_patient: nextConfirmation.matched_patient,
          remainingConfirmations:
            confirmationModal.remainingConfirmations.slice(1),
        });
        // Modal stays open for next confirmation
      } else {
        // No more confirmations, close modal and refresh
        onConfirmMatchClose();
        setConfirmationModal(null);
        await fetchAllFaxes();
        clearSelection();
      }
    } catch (error) {
      console.error("Error handling confirmation:", error);
      toast({
        title: "Error",
        description: error.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openFaxViewFromModal = async () => {
    if (!confirmationModal) return;

    try {
      const filePath = confirmationModal.file_path;
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/fax/get-view-url/?file_path=${encodeURIComponent(
          filePath,
        )}`,
        { headers: await authHeader() },
      );

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, "_blank");
      } else {
        // Fallback to public URL if API fails (using supabaseUrl from env.js)
        const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
        window.open(storageUrl, "_blank");
      }
    } catch (error) {
      console.error("Error getting fax view URL:", error);
      // Fallback to public URL (using supabaseUrl from env.js)
      const filePath = confirmationModal.file_path;
      const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
      window.open(storageUrl, "_blank");
    }
  };

  // Unmatch takes the fax off EVERY chart it was filed onto — the visit folder
  // and the roster folder in this schema, and the same pair in every other
  // schema of the clinic group (the "Filed to:" badges on the row). The backend
  // removes the whole family and leaves one copy in Unmatched; the toast names
  // the schemas it actually cleared so a fan-out isn't silently undone.
  const handleUnmatchFax = async (fax) => {
    setPendingFocus({ doc: faxDocumentKey(fax), since: allFaxes });
    if (fax.is_unmatched) {
      toast({
        title: "Fax already unmatched",
        description: "This fax is already unmatched.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    try {
      const response = await fetch(`${baseUrl}/api/fax/unmatch/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_path: fax.file_path,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const schemas = data.unmatched_schemas || [];
        const failed = (data.failed_paths || []).length;
        const where =
          schemas.length > 1
            ? `Removed from ${schemas.join(", ")}.`
            : "Removed from the patient's chart.";
        toast({
          title: "Fax Unmatched",
          description:
            `${where} It is back in Unmatched.` +
            (failed ? ` ${failed} copy/copies could not be removed.` : ""),
          status: failed ? "warning" : "success",
          duration: 6000,
          isClosable: true,
        });

        // Refresh fax list
        await fetchAllFaxes();
      } else {
        throw new Error(data.error || "Failed to unmatch fax");
      }
    } catch (error) {
      console.error("Error unmatching fax:", error);
      toast({
        title: "Unmatch Failed",
        description:
          error.message || "An error occurred while unmatching the fax.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ── Rematch (unmatch + run auto-match again) ──────────────────────────────
  // Lean unmatch call used by the rematch flows. Unlike handleUnmatchFax it
  // does NOT toast or refresh — it just takes the fax off every chart it was
  // filed onto and returns the path of the pending copy left behind, so we can
  // immediately re-run matching on it.
  const unmatchFaxRequest = async (filePath, baseUrl) => {
    const response = await fetch(`${baseUrl}/api/fax/unmatch/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ file_path: filePath }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to unmatch fax");
    // Backend returns the new pending path; fall back to the same construction
    // it uses — the fax comes back to ITS OWN schema's Pending_Assignment
    // folder, not the public one, or the inbox scoped to that schema would
    // stop showing it.
    const schema = parseFaxReportPath(filePath)?.schema || "public";
    const root = schema === "public" ? "New/Reports" : `New/Reports/${schema}`;
    return (
      data.new_file_path ||
      `${root}/Pending_Assignment/${filePath.split("/").pop()}`
    );
  };

  // Single-row Rematch: unmatch this fax, then re-run the normal single-fax
  // match on the now-unmatched copy. Only meaningful for an already-matched
  // fax. Reusing handleMatchSingleFax keeps the "needs confirmation" modal
  // behaving exactly as it does for a normal Match.
  const handleRematchFax = async (fax) => {
    if (fax.is_unmatched) {
      toast({
        title: "Already unmatched",
        description: "Use Match — this fax isn't matched to anyone yet.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    setRematchingFax(fax.file_path);
    setPendingFocus({ doc: faxDocumentKey(fax), since: allFaxes });
    try {
      const newPath = await unmatchFaxRequest(fax.file_path, baseUrl);
      // Re-run the standard single-fax match on the freshly-unmatched copy.
      await handleMatchSingleFax({ file_path: newPath, is_unmatched: true });
    } catch (error) {
      console.error("Error rematching fax:", error);
      toast({
        title: "Rematch Failed",
        description:
          error.message || "An error occurred while rematching the fax.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRematchingFax(null);
      // Make sure the list reflects the final state — re-matched, or back in
      // Unmatched if no confident match was found / a confirmation is pending.
      await fetchAllFaxes();
    }
  };

  // Bulk Match / Rematch. Does the right thing for whatever is selected: an
  // unmatched fax just needs matching, a matched one needs unmatching first.
  // It used to skip unmatched faxes outright, so selecting every unmatched fax
  // offered an action that refused all of them. No per-fax confirmation modals
  // — any fax the matcher isn't confident about is left in Unmatched and
  // summarized.
  const handleBulkRematch = async () => {
    const selected = filteredFaxesList.filter((f) =>
      selectedFaxes.has(f.file_path),
    );
    const { toMatch, toRematch } = partitionForBulkMatch(
      selected, faxDocumentKey,
    );
    const matched = toRematch;

    if (matched.length === 0 && toMatch.length === 0) {
      toast({
        title: "Nothing selected",
        description: "Select at least one fax to match or rematch.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    setBulkRematching(true);
    try {
      // 1) Unmatch each matched fax, collecting the new Pending_Assignment paths.
      // Already unmatched: nothing to undo, they go straight into the batch.
      const newPaths = toMatch.map((f) => f.file_path).filter(Boolean);
      let unmatchFailures = 0;
      for (const f of matched) {
        try {
          newPaths.push(await unmatchFaxRequest(f.file_path, baseUrl));
        } catch (e) {
          unmatchFailures += 1;
          console.error("Bulk rematch: unmatch failed for", f.file_path, e);
        }
      }

      // 2) Re-run auto-match on all of them at once.
      let matchedCount = 0;
      let needsReview = 0;
      let noMatch = 0;
      if (newPaths.length > 0) {
        beginActivity("fax_matching");
        const response = await fetch(
          `${baseUrl}/api/fax/match/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ file_paths: newPaths }),
          },
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || data.message || "Failed to match faxes");
        }
        for (const r of data.results || []) {
          if (r.status === "matched") matchedCount += 1;
          else if (r.status === "needs_confirmation") needsReview += 1;
          else noMatch += 1; // no_match / error / already_matched
        }
      }

      // 3) Summarize what happened.
      // "filed", not "rematched": the batch can hold both, and the ones that
      // were already unmatched were matched rather than re-matched.
      const parts = [`${matchedCount} filed`];
      if (needsReview) parts.push(`${needsReview} needs review`);
      if (noMatch) parts.push(`${noMatch} no match`);
      if (unmatchFailures) parts.push(`${unmatchFailures} unmatch failed`);
      toast({
        title: matched.length ? "Rematch complete" : "Match complete",
        description: parts.join(" · "),
        status: matchedCount > 0 ? "success" : "warning",
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error during bulk rematch:", error);
      toast({
        title: matched.length ? "Bulk Rematch Failed" : "Bulk Match Failed",
        description:
          error.message || "An error occurred while rematching faxes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBulkRematching(false);
      setSelectedFaxes(new Set());
      await fetchAllFaxes();
      endActivity("fax_matching");
    }
  };

  const toggleFaxSelection = (filePath) => {
    const newSelected = new Set(selectedFaxes);
    if (newSelected.has(filePath)) {
      newSelected.delete(filePath);
    } else {
      newSelected.add(filePath);
    }
    setSelectedFaxes(newSelected);
  };

  // Select all visible reports
  const selectAllFaxes = () => {
    // Use memoized filteredFaxesList instead of recalculating
    const allPaths = new Set(
      filteredFaxesList.map((f) => f.file_path).filter(Boolean),
    );
    setSelectedFaxes(allPaths);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedFaxes(new Set());
  };

  // Enhanced function to determine the appropriate button text based on patient type and forms status
  const getSendFormsButtonText = async () => {
    if (!selectedPatient) {
      return "Send Forms";
    }

    const patientType = findPatientField(selectedPatient, "type");
    const formsIndicator = selectedPatient.forms_ind;

    // Use enhanced extractAllSubmissionLinks to check for filled forms only
    // This now only returns forms that contain /submissions/ or /submission/ in URL
    const submissionLinks = await extractAllSubmissionLinks(selectedPatient.id);
    const hasFilledSubmissionLinks =
      submissionLinks && submissionLinks.length > 0;

    // Also check submissions_array directly for filled forms
    const submissionsArray = selectedPatient.submissions_array || [];
    let hasFilledSubmissionsArray = false;

    if (Array.isArray(submissionsArray)) {
      hasFilledSubmissionsArray = submissionsArray.some((submission) => {
        if (submission && typeof submission === "object" && submission.url) {
          // Use the same detection logic as the backend
          const url = submission.url;
          return url.includes("/submissions/") || url.includes("/submission/");
        }
        return false;
      });
    }

    // Use case-insensitive comparison to handle variations in patient type
    const normalizedPatientType = patientType
      ? patientType.toUpperCase().trim()
      : "";

    debugLog(`[BUTTON TEXT] Patient ${selectedPatient.id} analysis:`, {
      patientType: normalizedPatientType,
      hasFilledSubmissionLinks,
      hasFilledSubmissionsArray,
      formsIndicator,
      submissionCount: submissionLinks.length,
    });

    // If patient has filled forms, indicate completion status
    if (
      hasFilledSubmissionLinks ||
      hasFilledSubmissionsArray ||
      formsIndicator
    ) {
      return "Send Forms";
    }

    // Default text for patients without filled forms
    return "Send Forms";
  };

  // Generate preview data for send forms
  const generateFormsPreview = async () => {
    // Fetch fresh user settings first to ensure we have the latest form URLs
    const settings = await fetchUserSettings();
    console.log("[Preview] Fresh settings loaded:", settings);
    console.log("[Preview] NP_FORM_URL in settings:", settings?.NP_FORM_URL);
    // Update local state so UI reflects latest values
    setUserSettings(settings);

    const selectedPatientIds = Object.keys(selectedRows).filter(
      (key) => selectedRows[key],
    );

    if (selectedPatientIds.length === 0) {
      return null;
    }

    const selectedPatientsList = patients.filter((p) =>
      selectedPatientIds.includes(p.id),
    );

    // Get form type names
    const formTypeNames = {
      NP: "New Pulmonary Patient (NP)",
      NS: "New Sleep Patient (NS)",
      CL1: "Clinical Form 1 (CL1)",
      CL2: "Clinical Form 2 (CL2)",
      FFP: "Follow-up Form (FFP)",
      FFS: "Follow-up Form (FFS)",
    };

    const formTypes = formType.map((type) => formTypeNames[type] || type);

    // Get contact methods
    const methods = [];
    if (contactMethods.email) methods.push("Email");
    if (contactMethods.phone) methods.push("SMS/Phone");

    // Get patients list with contact info
    const patientsList = selectedPatientIds.map((patientId) => {
      const patientData = patients.find((p) => p.id === patientId);
      const contactInfo = selectedPatientsContact[patientId] || {};
      const patientName =
        patientData?.[prompts[0][0]] || patientData?.name || "Unknown Patient";

      return {
        id: patientId,
        name: patientName,
        email: contactInfo.email || "Not set",
        phone: contactInfo.phone || "Not set",
        type: findPatientField(patientData, "type") || "Unknown",
      };
    });

    // Get normalized form types
    const normalizedTypes = normalizeFormTypes(formType);
    console.log("[Preview] Form type:", formType);
    console.log("[Preview] Normalized:", normalizedTypes);

    // Get form URLs for each form type independently
    const formUrlsByType = {};
    const missingTypes = [];
    for (const type of normalizedTypes) {
      const urls = createFormUrls([type], settings);
      formUrlsByType[type] = urls;
      if (urls.length === 0) {
        missingTypes.push(type);
      }
    }
    console.log("[Preview] Form URLs by type:", formUrlsByType);
    console.log("[Preview] Missing types:", missingTypes);
    console.log("[Preview] Settings used:", settings);

    // Get actual message from backend API
    let actualMessage = "Select a form type to see preview";
    let actualMessageError = null;

    // Get first patient's data for the preview
    const firstPatient = patientsList[0];
    const firstPatientData =
      selectedPatientIds.length > 0
        ? currentScheduleData[selectedPatientIds[0]] ||
          patients.find((p) => p.id === selectedPatientIds[0])
        : null;
    const patientType =
      findPatientField(firstPatientData, "type") || normalizedTypes[0] || "NP";
    const appointmentDate = firstPatientData?.date_ || selectedDate || "";

    // Check if this is an FFP/FFS patient type that needs dynamic form generation
    const isFFPType =
      patientType.toUpperCase().includes("FFP") ||
      patientType.toUpperCase().includes("FFS");

    // If FFP/FFS patient and no static form URLs, generate form dynamically
    let generatedFormUrl = null;
    if (
      isFFPType &&
      (!formUrlsByType[patientType.toUpperCase()] ||
        formUrlsByType[patientType.toUpperCase()].length === 0)
    ) {
      console.log(
        "[Preview] FFP/FFS patient detected - generating form dynamically",
      );

      try {
        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;

        // Get patient name
        let patientName = firstPatient?.name || "Patient";

        // Call the form generation API
        const response = await fetch(`${baseUrl}/api/generate-form/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uuid: firstPatientData?.id,
            patient_name: patientName,
            year: new Date().getFullYear().toString(),
            date: new Date().toISOString().split("T")[0],
            model: selectedLLMModel,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.form_data) {
            generatedFormUrl =
              result.form_data.form_link ||
              result.form_data.metadata?.form_link;
            console.log("[Preview] Generated form URL:", generatedFormUrl);
          }
        } else {
          console.error("[Preview] Form generation failed:", response.status);
        }
      } catch (error) {
        console.error("[Preview] Error generating form:", error);
      }
    }

    // Find all form URLs for preview - handle multiple form types
    // When multiple form types are selected, combine all URLs to use multi-form template
    let previewType = null;
    let previewUrl = null;

    // Get all available URLs from all selected form types
    const allFormUrls = [];
    for (const type of normalizedTypes) {
      const urls = formUrlsByType[type];
      if (urls && urls.length > 0) {
        allFormUrls.push(...urls);
        if (!previewType) {
          previewType = type;
        }
      }
    }

    // If we have multiple form types selected, combine all URLs into comma-separated string
    // This will trigger the backend to use the CL_template for multiple forms
    if (normalizedTypes.length > 1 && allFormUrls.length > 0) {
      previewUrl = allFormUrls.join(",");
      console.log(
        "[Preview] Multiple forms selected, combined URLs:",
        previewUrl,
      );
    } else if (allFormUrls.length > 0) {
      // Single form type - use the first URL as before
      previewUrl = allFormUrls[0];
    }

    // If no static URL found but this is FFP type, use generated URL
    if (!previewUrl && generatedFormUrl) {
      previewType = patientType.toUpperCase();
      previewUrl = generatedFormUrl;
    }

    // Show warning about missing types (but don't block operation)
    let warningMessage = null;
    if (
      missingTypes.length > 0 &&
      missingTypes.length < normalizedTypes.length &&
      previewUrl
    ) {
      // Some types have URLs, some don't - show warning but allow preview
      warningMessage = `Note: No URL configured for ${missingTypes.join(", ")}. These forms will not be sent.`;
    } else if (
      missingTypes.length > 0 &&
      missingTypes.length === normalizedTypes.length &&
      !generatedFormUrl
    ) {
      // ALL types missing - show error
      warningMessage = `No form URL configured for ${missingTypes.join(", ")}. Please configure ${missingTypes.join("_")}_FORM_URL in settings.`;
    }

    if (warningMessage && !previewUrl) {
      actualMessageError = warningMessage;
    } else if (previewUrl) {
      // Add warning to the message if exists (for display)
      const displayWarning = warningMessage ? `${warningMessage}\n\n` : "";
      try {
        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;

        console.log("[Preview] Backend URL:", baseUrl);
        console.log("[Preview] Patient type:", patientType);
        console.log("[Preview] Appointment date:", appointmentDate);
        console.log("[Preview] Patient name:", firstPatient?.name);

        // Call the preview API for the first available form URL
        console.log("[Preview] Preview type:", previewType);
        console.log("[Preview] Calling API with URL:", previewUrl);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${baseUrl}/twilio/preview-form-message/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              patient_name: firstPatient?.name || "Patient",
              patient_type: previewType || patientType,
              form_url: previewUrl,
              appointment_date: appointmentDate,
            }),
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);
        console.log("[Preview] Response status:", response.status);

        if (response.ok) {
          const data = await response.json();
          console.log("[Preview] Response data:", data);
          actualMessage = data.message;
        } else {
          const errorText = await response.text();
          console.error("[Preview] Error response:", errorText);
          actualMessageError = `Server error: ${response.status}`;
        }
      } catch (error) {
        console.error("[Preview] Error fetching message preview:", error);
        if (error.name === "AbortError") {
          actualMessageError = "Request timed out. Please try again.";
        } else {
          actualMessageError =
            "Could not load message preview: " + error.message;
        }
      }
    }

    // Show the warning message along with the preview if we have one
    if (warningMessage && previewUrl && actualMessage) {
      actualMessage = `${warningMessage}\n\n${actualMessage}`;
    }

    const previewData = {
      patientCount: selectedPatientIds.length,
      formTypes: formTypes,
      contactMethods: methods,
      splitMessage: splitMessage,
      patients: patientsList,
      sampleMessage: actualMessage,
      sampleMessageError: actualMessageError,
      // Include all URLs from formUrlsByType plus generated form URL for FFP patients
      formUrls: Object.values(formUrlsByType).flat(),
      generatedFormUrl: generatedFormUrl,
      missingFormTypes: missingTypes,
    };

    setFormsPreviewData(previewData);
    setShowFormsPreview(true);
  };

  const handleFormTypeChange = (type) => (e) => {
    const isChecked = e.target.checked;

    // Use fuzzy matching to ensure we're working with the correct form type
    const normalizedType = fuzzyMatchFormType(type);
    if (!normalizedType) {
      console.warn(`[FUZZY MAPPING] Could not match form type: ${type}`);
      toast({
        title: "Invalid Form Type",
        description: `Could not recognize form type: ${type}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (isChecked) {
      if (formType.length < 3) {
        setFormType([...formType, normalizedType]);
        debugLog(
          `[FUZZY MAPPING] Added form type: ${type} -> ${normalizedType}`,
        );
      } else {
        toast({
          title: "Maximum Forms Selected",
          description: "You can only select up to 2 forms at a time.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      setFormType(formType.filter((t) => t !== normalizedType));
      debugLog(
        `[FUZZY MAPPING] Removed form type: ${type} -> ${normalizedType}`,
      );
    }
  };

  const handleDuplicateSave = async (newPatientData) => {
    const supabase = await getSupabase();
    try {
      // Map form data keys to database column names
      const patientDataToInsert = {
        [prompts[0][0]]: newPatientData.name, // This will now be "LastName, FirstName"
        [prompts[1][0]]: newPatientData.Email, // Use correct email field (index 1)
        [prompts[2][0]]: newPatientData["Phone Number"], // Use correct phone field (index 2)
        EHR_ID: newPatientData["Class Id"],
        type: newPatientData.Type,
        date_: newPatientData["Scheduled Date"],
        time_: newPatientData["Scheduled Time"],
        forms_ind: newPatientData.Forms,
      };

      // Ensure required fields
      if (!patientDataToInsert.name) {
        throw new Error("Name is a required field.");
      }
      // Use fuzzy field mapping to validate email field
      const emailValue = findPatientField(patientDataToInsert, "email");
      if (!emailValue) {
        throw new Error("Email is a required field.");
      }

      const { data, error } = await supabase
        .from(tableName)
        .insert([patientDataToInsert])
        .select()
        .single();

      if (error) {
        console.error("Supabase error details:", error);
        throw error;
      }

      setPatients([...patients, data]);
      toast({
        title: "Success",
        description: "Patient duplicated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsDuplicateModalOpen(false);
    } catch (error) {
      console.error("Error duplicating patient:", error);
      toast({
        title: "Error",
        description: `Failed to duplicate patient: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Helper function to automatically generate and save a form for FFP patients
  const autoGenerateAndSaveForm = async (patient) => {
    try {
      debugLog(
        `[AUTO-GENERATE] Starting form generation for patient: ${patient.id}`,
      );

      // Get current date for form generation
      const today = new Date();
      const year = today.getFullYear().toString();
      const date = today.toISOString().split("T")[0]; // YYYY-MM-DD format

      // Handle patient name extraction with better fallbacks
      let patientName = "";
      if (patient.name) {
        patientName = patient.name;
      } else if (patient.LastName && patient.FirstName) {
        patientName = `${patient.LastName}, ${patient.FirstName}`;
      } else if (patient.LastName) {
        patientName = patient.LastName;
      } else if (patient.FirstName) {
        patientName = patient.FirstName;
      } else {
        // Use fuzzy field mapping to extract name
        const extractedInfo = extractPatientInfo(patient);
        patientName =
          extractedInfo.name || `Patient ${patient.id.substring(0, 8)}`;
      }

      debugLog(`[AUTO-GENERATE] Form generation parameters:`, {
        uuid: patient.id,
        patient_name: patientName,
        year: year,
        date: date,
        model: selectedLLMModel,
      });

      // Call the backend API to generate form
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/generate-form/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: patient.id,
          patient_name: patientName,
          year: year,
          date: date,
          model: selectedLLMModel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate form");
      }

      const result = await response.json();
      debugLog(`[AUTO-GENERATE] Form generation successful:`, result);

      if (result.success && result.form_data) {
        // Store the complete form data including form_id and form_link for later use
        const completeFormData = {
          ...result.form_data,
          form_id:
            result.form_data?.form_id || result.form_data?.metadata?.form_id,
          form_link:
            result.form_data?.form_link ||
            result.form_data?.metadata?.form_link,
          local_path:
            result.form_data?.local_path ||
            result.form_data?.metadata?.local_path,
        };

        // Create a result object that matches what processGeneratedFormResponse expects
        const resultData = {
          form_id: completeFormData.form_id,
          form_link: completeFormData.form_link,
          local_path: completeFormData.local_path,
          success: true,
          // Also include form_data structure for backward compatibility
          form_data: {
            metadata: {
              form_id: completeFormData.form_id,
              form_link: completeFormData.form_link,
              local_path: completeFormData.local_path,
              storage_path: completeFormData.local_path,
            },
          },
        };

        // Save the form to patient's submissions
        const addedToSubmissions = await processGeneratedFormResponse(
          patient.id,
          resultData,
        );
        debugLog(
          `[AUTO-GENERATE] Form saved to submissions:`,
          addedToSubmissions,
        );

        return completeFormData;
      } else {
        throw new Error(
          result.error || "Unknown error occurred during form generation",
        );
      }
    } catch (error) {
      console.error(
        `[AUTO-GENERATE] Error generating form for patient ${patient.id}:`,
        error,
      );
      throw error;
    }
  };

  const sendForms = async () => {
    const selectedPatientIds = Object.keys(selectedRows).filter(
      (key) => selectedRows[key],
    );
    if (selectedPatientIds.length === 0) {
      toast({
        title: "No Patients Selected",
        description: "Please select at least one patient to send forms to.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Skip form type validation for FFP/FFS patients as they use existing follow-up forms
    if (formType.length === 0) {
      toast({
        title: "No Form Type Selected",
        description:
          "Please select at least one form type (NP, NS, CL1, or CL2).",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSendingForms(true);
    try {
      // Fetch fresh user settings first to ensure we have the latest form URLs
      const settings = await fetchUserSettings();
      debugLog("Using fresh settings:", settings);
      debugLog("NP_FORM_URL in settings:", settings?.NP_FORM_URL);
      // Update local state so UI reflects latest values
      setUserSettings(settings);

      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      // For non-FF patients, use the standard form URL creation
      debugLog("[FUZZY MAPPING] Original form types:", formType);
      const normalizedFormTypes = normalizeFormTypes(formType);
      debugLog("[FUZZY MAPPING] Normalized form types:", normalizedFormTypes);

      // Get form URLs for each form type independently
      const formUrlsByType = {};
      const missingTypes = [];
      for (const type of normalizedFormTypes) {
        const urls = createFormUrls([type], settings);
        formUrlsByType[type] = urls;
        if (urls.length === 0) {
          missingTypes.push(type);
        }
      }
      // Flatten to get all URLs
      const formUrls = Object.values(formUrlsByType).flat();
      debugLog("[FUZZY MAPPING] Form URLs by type:", formUrlsByType);
      debugLog("[FUZZY MAPPING] All form URLs:", formUrls);
      debugLog("[FUZZY MAPPING] Missing types:", missingTypes);
      debugLog("[FUZZY MAPPING] Settings used:", settings);

      // Check if any selected form type is FFP or FFS
      const isFollowUp = normalizedFormTypes.some(
        (type) => type === "FFP" || type === "FFS",
      );

      // Allow sending if: FFP/FFS selected OR at least one form type has a URL
      // Independent: only block if ALL selected types are missing URLs
      if (missingTypes.length === normalizedFormTypes.length && !isFollowUp) {
        toast({
          title: "No Form URLs Available",
          description: `No URL configured for ${missingTypes.join(", ")}. Please configure ${missingTypes.join("_")}_FORM_URL in settings.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      debugLog("[FORM SELECTION] Selected form types:", formType);
      debugLog("[FORM SELECTION] Final form URLs to be sent:", formUrls);
      debugLog("[FORM SELECTION] User settings:", settings);
      debugLog("[FORM SELECTION] Selected patients:", selectedPatientIds);
      debugLog("[FORM SELECTION] Contact info:", selectedPatientsContact);
      debugLog("[FORM SELECTION] Schedule data:", currentScheduleData);

      // Check if we have at least one contact method
      if (!contactMethods.phone && !contactMethods.email) {
        toast({
          title: "No Contact Method Selected",
          description:
            "Please select at least one contact method (phone or email).",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Check if we have the necessary credentials for the selected contact methods
      if (contactMethods.phone && !settings.TWILIO_PHONE_NUMBER) {
        toast({
          title: "Missing Twilio Configuration",
          description: "Please configure your Twilio phone number in settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (
        contactMethods.email &&
        (!settings.ZOHO_EMAIL || !settings.ZOHO_PASSWORD)
      ) {
        toast({
          title: "Missing Email Configuration",
          description:
            "Please configure your Zoho email and password in settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const badNumberGuard = await confirmBadNumbers(
        contactMethods.phone
          ? selectedPatientIds.map((patientId) => {
              const patient =
                currentScheduleData[patientId] || patients.find((p) => p.id === patientId);
              const phone = selectedPatientsContact[patientId]?.phone;
              return {
                phones: [phone, patient ? altPhoneFor(patient, phone) : ""].filter(Boolean),
                name: patient?.[prompts[0][0]] || patient?.name || patient?.[colid2] || "",
              };
            })
          : [],
      );
      if (!badNumberGuard) return;

      // Send forms to all selected patients
      setSendProgress({ current: 0, total: selectedPatientIds.length });
      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const successfulPatients = [];
      const skippedPatients = [];
      const failedPatients = [];

      for (const patientId of selectedPatientIds) {
        processedCount++;
        setSendProgress((prev) => ({ ...prev, current: processedCount }));
        // Prioritize scheduleData if available, otherwise search in patients array
        let patient = null;
        if (currentScheduleData[patientId]) {
          patient = currentScheduleData[patientId];
          debugLog(`[SYNC] Using scheduleData for sending: ${patientId}`);
        } else {
          patient = patients.find((p) => p.id === patientId);
          if (patient) {
            debugLog(`[SYNC] Using patients array for sending: ${patientId}`);
          } else {
            debugLog(
              `[SYNC] WARNING: No patient data found for sending: ${patientId}`,
            );
          }
        }

        const contactInfo = selectedPatientsContact[patientId];

        if (!patient || !contactInfo) {
          errorCount++;
          failedPatients.push({
            name: "Unknown Patient",
            reason: `Missing data for patient ID: ${patientId}`,
          });
          continue;
        }

        const patientName =
          patient?.[prompts[0][0]] ||
          patient?.name ||
          patient?.[colid2] ||
          "Unknown Patient";

        // Skip patient only if they have NONE of the required contact methods
        // If both email and phone are selected, patient needs at least one
        const hasEmail = contactInfo?.email?.trim() || false;
        const hasPhone = contactInfo?.phone?.trim() || false;

        const emailRequired = contactMethods.email;
        const phoneRequired = contactMethods.phone;

        // Skip only if patient has no contact info for the required methods
        const missingEmail = emailRequired && !hasEmail;
        const missingPhone = phoneRequired && !hasPhone;

        // Skip if ALL required contact methods are missing
        if (missingEmail && missingPhone) {
          skippedCount++;
          skippedPatients.push({
            name: patientName,
            reason: "Email/phone not found",
          });
          debugLog(
            `[SKIP] Skipping patient ${patientId} - missing all required contact methods`,
          );
          continue;
        }

        // "Skip" in the bad-number pop-up drops the text; email still goes.
        let patientContactMethods = contactMethods;
        if (phoneRequired && hasPhone && badNumberGuard.skipped(contactInfo.phone)) {
          if (!(emailRequired && hasEmail)) {
            skippedCount++;
            skippedPatients.push({ name: patientName, reason: "Known bad number" });
            continue;
          }
          patientContactMethods = { ...contactMethods, phone: false };
        }

        let patientFormUrls = [...formUrls];
        if (isFollowUp) {
          debugLog(
            `[FFP/FFS] Retrieving existing follow-up forms for patient ${patientId}`,
          );
          try {
            const submissionLinks = await extractAllSubmissionLinks(patientId);
            if (submissionLinks && submissionLinks.length > 0) {
              // extractAllSubmissionLinks returns array of strings, with older ones usually first
              // If we are strictly sending a follow-up form (FFP/FFS), we likely want only the LATEST generated form.
              // If the user selected other forms (NP/NS) as well, we append.

              let linksToAdd = [];

              if (formUrls.length === 0) {
                // No other forms selected, so user intends to send "The Follow-up Form".
                // Take the latest one (last in array).
                const latestLink = submissionLinks[submissionLinks.length - 1];
                linksToAdd = [latestLink];
                debugLog(`[FFP/FFS] Selected latest form: ${latestLink}`);
              } else {
                // User selected mixed forms (e.g. NP + FFP).
                // In this case, maybe they want all history? Or just the latest?
                // Let's assume just the latest to be safe and avoid "wrong template" issues (comma separation).
                // Actually, if they mix, they probably want the latest FFP + the static NP.
                const latestLink = submissionLinks[submissionLinks.length - 1];
                linksToAdd = [latestLink];
              }

              // Filter out duplicates just in case
              const uniqueLinks = linksToAdd.filter(
                (link) => !patientFormUrls.includes(link),
              );
              patientFormUrls = [...patientFormUrls, ...uniqueLinks];
            } else {
              console.warn(
                `[FFP/FFS] No generated follow-up form found for patient ${patientId}. Auto-generating form...`,
              );

              const pName =
                patient?.[prompts[0][0]] ||
                patient?.name ||
                patient?.[colid2] ||
                "Unknown Patient";

              try {
                // Auto-generate form for FFP patient
                const generatedFormData =
                  await autoGenerateAndSaveForm(patient);

                if (generatedFormData && generatedFormData.form_link) {
                  // Add the auto-generated form to patientFormUrls
                  const uniqueLinks = [generatedFormData.form_link].filter(
                    (link) => !patientFormUrls.includes(link),
                  );
                  patientFormUrls = [...patientFormUrls, ...uniqueLinks];
                  debugLog(
                    `[FFP/FFS] Auto-generated form for ${pName}: ${generatedFormData.form_link}`,
                  );
                } else {
                  throw new Error("Auto-generated form missing form_link");
                }
              } catch (autoGenError) {
                console.error(
                  `[FFP/FFS] Auto-generation failed for patient ${patientId}:`,
                  autoGenError,
                );
                errorCount++;
                failedPatients.push({
                  name: pName,
                  reason: `Failed to auto-generate follow-up form: ${autoGenError.message}`,
                });
                continue;
              }
            }
          } catch (err) {
            console.error(
              `[FFP/FFS] Error retrieving follow-up forms for patient ${patientId}:`,
              err,
            );
            errorCount++;
            const pName =
              patient?.[prompts[0][0]] ||
              patient?.name ||
              patient?.[colid2] ||
              "Unknown Patient";
            failedPatients.push({
              name: pName,
              reason: `Error retrieving follow-up form: ${err.message || err}`,
            });
            continue;
          }
        }

        const requestData = {
          df1: {
            name: patient?.[prompts[0][0]] || patient?.[colid2] || "",
            date_: patient?.date_ || "",
            appointment_date: patient?.date_ || "",
            appointment_time: patient?.time_ || "",
            form_type: normalizedFormTypes[0] || "",
            form_url: patientFormUrls.join(","),
            phone:
              contactMethods.phone && contactInfo.phone?.trim()
                ? contactInfo.phone.replace(/\D/g, "")
                : "",
            alt_phone:
              contactMethods.phone && contactInfo.phone?.trim()
                ? altPhoneFor(patient, contactInfo.phone, { digitsOnly: true })
                : "",
            Email:
              contactMethods.email && contactInfo.email?.trim()
                ? contactInfo.email
                : "",
            zoho_email: settings.ZOHO_EMAIL || "",
            zoho_password: settings.ZOHO_PASSWORD || "",
            twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
            split: splitMessage,
            character_limit: customSmsCharacterLimit,
            user_id: userId,
            patient_id: patientId, // Add patient ID for updating forms_ind
            contact_methods: patientContactMethods, // Send contact methods to backend
            confirm_bad_number: badNumberGuard.confirmed(contactInfo.phone || ""),
          },
          fpath: "manual",
          schema: supabaseSchema, // For backend to store in correct schema
        };

        debugLog(
          `[FORM SELECTION] Sending form to patient ${patientId}:`,
          requestData,
        );

        try {
          const sendFormsUrl = getSendFormsUrl();
          const response = await fetch(sendFormsUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            credentials: "include",
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.error || errorData.message || "Failed to send forms",
            );
          }

          const responseData = await response.json();
          debugLog(`[DEBUG] Response for patient ${patientId}:`, responseData);
          successCount++;
          successfulPatients.push({
            name: patientName,
          });
        } catch (error) {
          console.error(`Error sending form to patient ${patientId}:`, error);
          errorCount++;
          failedPatients.push({
            name: patientName,
            reason: error.message,
          });
        }
      }

      // Store report data
      const reportData = {
        successful: successfulPatients,
        skipped: skippedPatients,
        failed: failedPatients,
      };
      setSendFormsReport(reportData);

      // Refresh reminder status if any forms were successfully sent
      if (successCount > 0) {
        try {
          await fetchPatientsWithoutForms();
        } catch (error) {
          console.error("Error refreshing reminder status:", error);
          // Don't show error to user, just log it
        }
      }

      // Close send form modal first
      onSendFormClose();

      // Open report modal after state update
      // Using setTimeout to ensure the send form modal closes first
      setTimeout(() => {
        debugLog("[REPORT] Opening report modal with data:", reportData);
        onReportModalOpen();
      }, 150);
    } catch (error) {
      console.error("Error sending forms:", error);
      toast({
        title: "Send Failed",
        description:
          error.message || "An error occurred while sending the form.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingForms(false);
    }
  };

  const columns = [
    { header: "Name", accessor: "name" },
    {
      header: "Forms URL",
      accessor: "forms_url",
      render: (row) => (
        <Button onClick={() => navigate(`/forms/${row.id}`)}>View Forms</Button>
      ),
    },
    {
      header: "Message",
      accessor: "message",
      render: (row) => (
        <Button onClick={() => navigate(`/messages/${row.id}`)}>Message</Button>
      ),
    },
    {
      header: "Reports",
      accessor: "reports",
      render: (row) => (
        <Button onClick={() => navigate(`/reports/${row.id}`)}>
          View Reports
        </Button>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <Modal
        isOpen={isPasswordOpen}
        onClose={() => navigate("/auth")}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handlePasswordSubmit}>
              Submit
            </Button>
            <Button variant="ghost" onClick={() => navigate("/auth")}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Center flexDirection="column" className="schedules-container">
      <Flex align="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold">
          Admin Page - Scheduling and tracking of appointments
        </Text>
        {/* Notification icon next to the title with Popover for unread messages */}
        <Popover placement="bottom-start" isLazy>
          <PopoverTrigger>
            <Box ml={4} position="relative">
              <IconButton
                icon={<BellIcon />}
                aria-label="Notifications"
                colorScheme={totalUnreadMessages > 0 ? "red" : "gray"}
                variant="ghost"
                size="lg"
                onClick={() => {
                  // Refresh unread messages count when opening popover
                  fetchTotalUnreadMessages();
                }}
                isLoading={loadingUnreadMessages}
              />
              {totalUnreadMessages > 0 && (
                <Badge
                  colorScheme="red"
                  position="absolute"
                  top="-2"
                  right="-2"
                  borderRadius="full"
                  minW="20px"
                  h="20px"
                  textAlign="center"
                  fontSize="xs"
                >
                  {totalUnreadMessages > 99 ? "99+" : totalUnreadMessages}
                </Badge>
              )}
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent maxH="400px" overflowY="auto" w="320px">
              <PopoverHeader fontWeight="bold" borderBottomWidth="1px">
                Unread Messages ({totalUnreadMessages})
              </PopoverHeader>
              <PopoverBody p={2}>
                {unreadMessagesList.length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={4}>
                    No unread messages
                  </Text>
                ) : (
                  unreadMessagesList.map((item) => (
                    <Box
                      key={item.patientId}
                      p={2}
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                      _hover={{ bg: "gray.50", cursor: "pointer" }}
                      onClick={() => {
                        setSelectedPatientForMessaging(item.patientData);
                        setIsMessagingModalOpen(true);
                      }}
                    >
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {item.patientName}
                        </Text>
                        <Badge colorScheme="red" borderRadius="full">
                          {item.unreadCount}
                        </Badge>
                      </Flex>
                      <Text fontSize="xs" color="gray.600" noOfLines={1}>
                        {item.messages[0]?.message || "No message content"}
                      </Text>
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        {item.messages[0]?.created_at &&
                          new Date(item.messages[0].created_at).toLocaleString()}
                      </Text>
                    </Box>
                  ))
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Flex>

      {/* Toolbar: one dropdown per category (components/admin/ActionMenu).
          Safety state (SMS blocked, unmatched faxes) rides on the triggers. */}
      <Flex
        wrap="wrap"
        gap={{ base: 1, sm: 2 }}
        my={4}
        justify="center"
        align="center"
      >
        <ActionMenu
          label="Patients"
          colorScheme="teal"
          sections={[
            {
              items: [
                {
                  label: "Add Patient",
                  onClick: () => setIsAddPatientModalOpen(true),
                },
                {
                  label: "Edit Patient",
                  isDisabled: Object.keys(selectedRows).length === 0,
                  hint:
                    Object.keys(selectedRows).length === 0
                      ? "Select a patient first"
                      : undefined,
                  onClick: () => setIsEditPatientModalOpen(true),
                },
                {
                  label: "Duplicate Patient",
                  isDisabled: Object.keys(selectedRows).length !== 1,
                  hint:
                    Object.keys(selectedRows).length !== 1
                      ? "Select exactly one patient"
                      : undefined,
                  onClick: handleDuplicatePatient,
                },
              ],
            },
            {
              items: [
                {
                  label: "Upload Files",
                  isDisabled: Object.keys(selectedRows).length === 0,
                  onClick: () =>
                    navigate(`/upload-files/${selectedPatient?.id}`),
                },
                {
                  label: "View Reports",
                  isDisabled: !selectedPatient,
                  // There is no /reports/:id page; open the grid's own View
                  // Reports window for the selected patient instead.
                  onClick: () =>
                    schedulesTableActionsRef.current?.openReports(selectedPatient),
                },
                {
                  label: "Settings",
                  isDisabled: Object.keys(selectedRows).length === 0,
                  onClick: handleOpenSettings,
                },
                {
                  label: "Patient Graph",
                  onClick: () => setIsBreakdownOpen(true),
                },
              ],
            },
          ]}
        />
        <ActionMenu
          label="Forms"
          colorScheme="purple"
          sections={[
            {
              items: [
                {
                  label: `${
                    Object.keys(selectedRows).filter((key) => selectedRows[key])
                      .length === 0
                      ? "Select All FFP and FFS patients"
                      : sendFormsButtonText
                  } (${
                    Object.keys(selectedRows).filter((key) => selectedRows[key])
                      .length
                  } selected)`,
                  onClick: handleSendFormsClick,
                },
                {
                  label: "Generate Forms",
                  onClick: () => setIsBulkFormModalOpen(true),
                },
              ],
            },
          ]}
        />
        <ActionMenu
          label="Messaging"
          colorScheme="blue"
          badge={!smsEnabled ? { text: "SMS off" } : undefined}
          sections={[
            {
              title: "Send",
              items: [
                {
                  label: `Send Reminders (${reminderStatus.patients_without_forms} pending)`,
                  onClick: handleSendRemindersClick,
                },
                { label: "Send SMS", onClick: handleSendSMSClick },
                {
                  label:
                    Object.keys(selectedRows).filter((key) => selectedRows[key])
                      .length === 0
                      ? "Confirmations"
                      : `Send Confirmation (${
                          Object.keys(selectedRows).filter(
                            (key) => selectedRows[key]
                          ).length
                        } selected)`,
                  onClick: handleConfirmationsClick,
                  isLoading: isSendingConfirmations,
                },
                {
                  label: "Send Followup",
                  onClick: handleSendFollowupClick,
                  isDisabled:
                    Object.keys(selectedRows).length !== 1 && !selectedPatient,
                },
              ],
            },
            {
              title: "Delivery",
              items: [
                { label: "SMS Status", onClick: handleSMSStatusClick },
                {
                  label: "Twilio Health",
                  onClick: () => setIsTwilioHealthModalOpen(true),
                },
                {
                  label: !hasSelectedPatients
                    ? "Do Not Text List"
                    : allSelectedOnDnt
                      ? "Remove from Do Not Text List"
                      : "Add to Do Not Text List",
                  onClick: !hasSelectedPatients
                    ? openDoNotTextModal
                    : allSelectedOnDnt
                      ? handleRemoveSelectedFromDoNotText
                      : handleAddSelectedToDoNotText,
                  isLoading: hasSelectedPatients && dntSubmitting,
                },
              ],
            },
          ]}
        />
        <ActionMenu
          label="Inbox"
          colorScheme="orange"
          badge={
            faxCounts.unmatched > 0
              ? { text: `${faxCounts.unmatched} unmatched` }
              : undefined
          }
          sections={[
            {
              items: [
                {
                  label: `Fax/Reports Inbox (${
                    faxCounts.unmatched > 0
                      ? `${faxCounts.unmatched} unmatched / `
                      : ""
                  }${faxCounts.fax + faxCounts.reports})`,
                  onClick: onFaxInboxOpen,
                },
                {
                  label: "Appointments",
                  onClick: () => setIsAppointmentInboxOpen(true),
                },
              ],
            },
          ]}
        />
        <ActionPanel
          label="Automation"
          badge={!smsEnabled ? { text: "SMS off" } : undefined}
        >
          {(close) => (
            <>
              {/* SMS Master Switch — blocks ALL outbound SMS for this schema */}
              <ToggleRow
                label="SMS"
                danger
                description={
                  smsEnabled
                    ? `Outbound SMS sends normally for ${supabaseSchema || "public"}`
                    : `All outbound SMS is blocked for ${supabaseSchema || "public"} (admin sends, scheduled, AI replies, webhooks, OTP, compliance)`
                }
                isChecked={smsEnabled}
                onChange={toggleSmsEnabledSetting}
                isDisabled={loadingSmsEnabledSetting}
              />
              <ToggleRow
                label="Auto SMS"
                description="Retry SMS that failed with 'exceeded message length' after 5 minutes. Click to see scheduled SMS."
                isChecked={autoSmsEnabled}
                onChange={toggleAutoSmsSetting}
                isDisabled={loadingAutoSmsSetting}
                onLabelClick={() => {
                  close();
                  onScheduledSMSModalOpen();
                }}
              />
              <ToggleRow
                label="Auto-Reminders"
                colorScheme="green"
                description="Remind patients by email/phone 1 day before appointments. Click for settings."
                isChecked={autoRemindersEnabled}
                onChange={toggleAutoRemindersSetting}
                isDisabled={loadingAutoRemindersSetting}
                onLabelClick={() => {
                  close();
                  onAutoReminderSettingsOpen();
                }}
              />
              <ToggleRow
                label="AI Chat"
                colorScheme="green"
                description="Patients can text 'AI assistant' to get automated responses"
                isChecked={aiChatEnabled}
                onChange={toggleAiChatSetting}
                isDisabled={loadingAiChatSetting}
              />
              <ToggleRow
                label="Compliance"
                colorScheme="orange"
                description="Scan for med non-compliance and care gaps. Click for the quality dashboard."
                isChecked={complianceEnabled}
                onChange={toggleComplianceSetting}
                isDisabled={loadingComplianceSetting}
                onLabelClick={() => {
                  close();
                  onComplianceDashboardOpen();
                }}
                onSettingsClick={() => {
                  close();
                  onComplianceSettingsOpen();
                }}
              />
              {/* Upgrade CTA — controls the "Upgrade to Pro" button on the Schedules page */}
              <ToggleRow
                label="Upgrade button"
                colorScheme="yellow"
                description={`"Upgrade to Pro" on the Schedules page for ${supabaseSchema || "public"}`}
                isChecked={upgradeEnabled}
                onChange={toggleUpgradeSetting}
                isDisabled={loadingUpgradeSetting}
                settingsLabel="Edit checkout link"
                onSettingsClick={() => {
                  close();
                  setUpgradeLinkDraft(upgradeLink);
                  onUpgradeLinkOpen();
                }}
              />
              <Divider my={2} />
              <Button
                size="sm"
                variant="ghost"
                w="100%"
                justifyContent="flex-start"
                onClick={() => {
                  close();
                  setIsLLMModelModalOpen(true);
                }}
              >
                LLM Model…
              </Button>
            </>
          )}
        </ActionPanel>
        {/* Same providers picker as Schedules/Patients — it selects which
            providers' schemas the patient list below is pulled from. Its
            Tailwind classes are scoped to `#clinic-root` (see
            tailwind.config.js `important`), and this Chakra page has no such
            ancestor, so it gets its own wrapper — utilities apply to
            descendants only, which is what the dropdown needs. */}
        <Flex id="clinic-root" align="center">
          <ProvidersDropdown userRole={userRole} />
        </Flex>
      </Flex>

      <Modal isOpen={isUpgradeLinkOpen} onClose={onUpgradeLinkClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Upgrade checkout link</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Stripe checkout link the Upgrade button opens for{" "}
              {supabaseSchema || "public"}. Leave blank to use the global
              default ({UPGRADE_DEFAULTS.link}).
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Checkout URL</FormLabel>
              <Input
                size="sm"
                placeholder={UPGRADE_DEFAULTS.link}
                value={upgradeLinkDraft}
                onChange={(e) => setUpgradeLinkDraft(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={onUpgradeLinkClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="yellow"
              isLoading={loadingUpgradeSetting}
              onClick={async () => {
                await saveUpgradeLink(upgradeLinkDraft);
                onUpgradeLinkClose();
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <PatientTypeBreakdownDrawer
        isOpen={isBreakdownOpen}
        onClose={() => setIsBreakdownOpen(false)}
        patients={patients}
        selectedDate={selectedDate}
      />

      <SchedulesTable
        showCheckboxes
        columns={columns}
        data={patients}
        onSelectedRowChange={handleSelectedRowChange}
        setSchedules={setPatients}
        isAdminPage={true}
        externalSelectedRows={selectedRows}
        smsCache={smsCache}
        actionsRef={schedulesTableActionsRef}
      />

      <AddPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={() => setIsAddPatientModalOpen(false)}
        onAddPatient={async (newPatient) => {
          setPatients([...patients, newPatient]);
          setSelectedPatient(newPatient);
          setIsAddPatientModalOpen(false);
          // Send welcome first (waits for all parts), then forms
          await handleWelcomeSending(newPatient, toast);
          await handleFormSending(newPatient, toast);
        }}
      />

      <EditPatientModal
        isOpen={isEditPatientModalOpen}
        onClose={() => setIsEditPatientModalOpen(false)}
        patientData={selectedPatient}
        onUpdatePatient={(updated) =>
          setPatients(patients.map((p) => (p.id === updated.id ? updated : p)))
        }
      />

      <ConfirmationChannelDialog
        isOpen={Boolean(confirmationChannelSummary)}
        onClose={() => setConfirmationChannelSummary(null)}
        summary={confirmationChannelSummary}
        onChoose={sendConfirmations}
      />

      <AppointmentConfirmationModal
        isOpen={isConfirmationHubOpen}
        onClose={() => setIsConfirmationHubOpen(false)}
        schema={supabaseSchema || "public"}
        patients={patients}
        onSelectAllUnconfirmed={handleSelectAllUnconfirmed}
        date={selectedDate}
        userEmail={currentUserEmail}
        providerSchemas={
          selectedProviders && selectedProviders.length
            ? selectedProviders
            : [homeSchema || supabaseSchema || "public"]
        }
        onStatusesChanged={async () => {
          await fetchPatients();
          window.dispatchEvent(new CustomEvent("patientDataRefresh"));
        }}
      />

      <DuplicatePatientModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        patientData={selectedPatient}
        onDuplicatePatient={handleDuplicateSave}
      />
      <LLMModelSelectionModal
        isOpen={isLLMModelModalOpen}
        onClose={() => setIsLLMModelModalOpen(false)}
        currentModel={selectedLLMModel}
        onSave={handleLLMModelSave}
      />

      <BulkFormGenerationModal
        isOpen={isBulkFormModalOpen}
        onClose={() => setIsBulkFormModalOpen(false)}
        date={selectedDate}
        onRefresh={fetchPatients}
        selectedLLMModel={selectedLLMModel}
      />

      {/* Do Not Text List Modal (add + remove) */}
      <Modal
        isOpen={isAddDNTModalOpen}
        onClose={() => {
          setIsAddDNTModalOpen(false);
          setDntPhoneInput("");
        }}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Do Not Text List</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Search or add phone number</FormLabel>
              <HStack>
                <Input
                  placeholder="e.g. 6265551234 or +1 626-555-1234"
                  value={dntPhoneInput}
                  onChange={(e) => setDntPhoneInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !dntInputOnList && handleAddDoNotText()
                  }
                />
                <Button
                  colorScheme="orange"
                  onClick={handleAddDoNotText}
                  isLoading={dntSubmitting}
                  isDisabled={dntInputOnList}
                  flexShrink={0}
                >
                  {dntInputOnList ? "Already on list" : "Add"}
                </Button>
              </HStack>
            </FormControl>

            <Divider my={4} />

            <Text fontWeight="semibold" mb={2}>
              {dntSearchDigits
                ? `Matching (${dntFilteredEntries.length} of ${dntEntries.length})`
                : `Current list (${dntEntries.length})`}
            </Text>
            {dntLoading ? (
              <Center py={6}>
                <Spinner />
              </Center>
            ) : dntEntries.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No phone numbers on the Do Not Text list.
              </Text>
            ) : dntFilteredEntries.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No matching numbers on the Do Not Text list.
              </Text>
            ) : (
              <VStack
                align="stretch"
                spacing={1}
                maxH="300px"
                overflowY="auto"
              >
                {dntFilteredEntries.map((entry) => (
                  <Flex
                    key={entry.id ?? entry.phone_number}
                    align="center"
                    justify="space-between"
                    px={2}
                    py={1}
                    borderWidth="1px"
                    borderRadius="md"
                  >
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {entry.phone_number}
                      </Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {entry.reason || "—"}
                        {entry.created_at
                          ? ` · ${new Date(entry.created_at).toLocaleDateString()}`
                          : ""}
                      </Text>
                    </Box>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      leftIcon={<DeleteIcon />}
                      onClick={() => handleRemoveDoNotText(entry.phone_number)}
                      isLoading={dntRemovingPhone === entry.phone_number}
                      isDisabled={
                        dntRemovingPhone !== null &&
                        dntRemovingPhone !== entry.phone_number
                      }
                      flexShrink={0}
                      ml={2}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsAddDNTModalOpen(false);
                setDntPhoneInput("");
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMS Modal */}
      <Modal
        isOpen={isSendSMSModalOpen}
        onClose={() => {
          setIsSendSMSModalOpen(false);
          setSmsMessage("");
          setSmsPatientTypeFilter([]);
          setSmsSelectedPatients({});
        }}
        isCentered
        size="xl"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Send SMS Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                Select patients from the schedule below and write a custom
                message to send SMS.
              </AlertDescription>
            </Alert>

            {/* Patient Selection - All Patients from Schedule */}
            <Box mb={4}>
              <Text fontWeight="bold" fontSize="sm" mb={2} color="purple.600">
                Select Patients ({patients.length} patients in schedule)
              </Text>

              {/* Patient Type Filter */}
              <Flex wrap="wrap" gap={2} mb={3} align="center">
                {patientTypes.map((type) => (
                  <Checkbox
                    key={type}
                    colorScheme="purple"
                    isChecked={smsPatientTypeFilter.includes(type)}
                    onChange={(e) =>
                      handleSmsPatientTypeChange(type, e.target.checked)
                    }
                    size="sm"
                  >
                    <HStack spacing={1}>
                      <Text fontSize="xs">{type}</Text>
                      <Badge colorScheme="purple" fontSize="xs">
                        {totalPatientsByType[type] || 0}
                      </Badge>
                    </HStack>
                  </Checkbox>
                ))}
                {smsPatientTypeFilter.length > 0 && (
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="gray"
                    onClick={() => {
                      setSmsPatientTypeFilter([]);
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Flex>

              {/* All Patients List */}
              <Box
                p={3}
                bg="gray.50"
                borderRadius="md"
                border="1px"
                borderColor="gray.200"
                maxH="300px"
                overflowY="auto"
              >
                <VStack spacing={1} align="stretch">
                  {/* Select All - shows filtered count */}
                  {(() => {
                    const filteredPatients = patients.filter((patient) => {
                      if (smsPatientTypeFilter.length === 0) return true;
                      const patientType = findPatientField(
                        patient,
                        "type",
                      )?.toUpperCase();
                      return smsPatientTypeFilter.includes(patientType);
                    });
                    const filteredCount = filteredPatients.length;
                    const allFilteredSelected =
                      filteredCount > 0 &&
                      filteredPatients.every((p) => smsSelectedPatients[p.id]);
                    const someFilteredSelected =
                      filteredPatients.some((p) => smsSelectedPatients[p.id]) &&
                      !allFilteredSelected;

                    return (
                      <Flex
                        justify="space-between"
                        align="center"
                        mb={2}
                        pb={2}
                        borderBottom="1px"
                        borderColor="gray.200"
                      >
                        <Checkbox
                          colorScheme="purple"
                          size="md"
                          isChecked={allFilteredSelected}
                          isIndeterminate={someFilteredSelected}
                          onChange={(e) => {
                            const newSelection = { ...smsSelectedPatients };
                            if (e.target.checked) {
                              filteredPatients.forEach((p) => {
                                newSelection[p.id] = true;
                              });
                            } else {
                              filteredPatients.forEach((p) => {
                                delete newSelection[p.id];
                              });
                            }
                            setSmsSelectedPatients(newSelection);
                          }}
                        >
                          <Text fontWeight="medium" fontSize="sm">
                            Select All ({filteredCount})
                          </Text>
                        </Checkbox>
                      </Flex>
                    );
                  })()}

                  {/* Patient List */}
                  {patients
                    .filter((patient) => {
                      if (smsPatientTypeFilter.length === 0) return true;
                      const patientType = findPatientField(
                        patient,
                        "type",
                      )?.toUpperCase();
                      return smsPatientTypeFilter.includes(patientType);
                    })
                    .map((patient) => {
                      const patientName =
                        patient[prompts[0][0]] ||
                        patient.name ||
                        patient[colid2] ||
                        "Unknown Patient";
                      const patientPhone = rowPhones(patient)[0] || "";
                      const patientType =
                        findPatientField(patient, "type") || "";

                      return (
                        <Flex
                          key={patient.id}
                          justify="space-between"
                          align="center"
                          p={2}
                          bg="white"
                          borderRadius="md"
                        >
                          <Checkbox
                            size="sm"
                            colorScheme="green"
                            isChecked={!!smsSelectedPatients[patient.id]}
                            onChange={() => handleSmsPatientToggle(patient.id)}
                          >
                            <Box>
                              <Text fontSize="sm" noOfLines={1}>
                                {patientName}
                              </Text>
                              {patientPhone ? (
                                <PatientPhones row={patient} compact fontSize="xs" color="gray.500" />
                              ) : (
                                <Text fontSize="xs" color="gray.500">No phone</Text>
                              )}
                            </Box>
                          </Checkbox>
                          <Badge colorScheme="purple" fontSize="xs">
                            {patientType}
                          </Badge>
                        </Flex>
                      );
                    })}
                </VStack>
              </Box>

              {/* Selection Summary */}
              <HStack mt={2}>
                <Badge colorScheme="green" fontSize="sm">
                  {
                    Object.keys(smsSelectedPatients).filter(
                      (id) => smsSelectedPatients[id],
                    ).length
                  }{" "}
                  patients selected
                </Badge>
                {Object.keys(smsSelectedPatients).filter(
                  (id) => smsSelectedPatients[id],
                ).length > 0 && (
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="red"
                    onClick={() => setSmsSelectedPatients({})}
                  >
                    Clear Selection
                  </Button>
                )}
              </HStack>
            </Box>

            <FormControl>
              <FormLabel>Message</FormLabel>
              <Input
                as="textarea"
                rows={6}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Enter your custom SMS message here..."
              />
              <Flex justify="space-between" align="center" mt={1}>
                <Text
                  fontSize="sm"
                  color={
                    smsMessage.length > customSmsCharacterLimit
                      ? "red.500"
                      : "gray.500"
                  }
                >
                  {smsMessage.length} / {customSmsCharacterLimit} characters
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => {
                    const newLimit = prompt(
                      "Enter custom SMS character limit (default: 1000):",
                      customSmsCharacterLimit,
                    );
                    if (newLimit !== null) {
                      const val = parseInt(newLimit, 10);
                      if (val > 0 && val <= 10000) {
                        setCustomSmsCharacterLimit(val);
                        saveCustomSmsCharacterLimit(val);
                      } else {
                        alert("Please enter a value between 1 and 10000");
                      }
                    }
                  }}
                >
                  Change Limit
                </Button>
              </Flex>
              <Checkbox
                mt={2}
                isChecked={splitMessage}
                onChange={(e) => setSplitMessage(e.target.checked)}
              >
                Split long messages into multiple parts
              </Checkbox>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Tooltip
              label="Send a pre-configured welcome message to introduce patients to the messaging system"
              hasArrow
            >
              <Button
                colorScheme="green"
                mr={3}
                onClick={handleSendWelcome}
                isDisabled={isSendingSMS || isSendingWelcome}
                isLoading={isSendingWelcome}
                loadingText="Sending Welcome..."
              >
                Send Welcome
              </Button>
            </Tooltip>
            <Button
              colorScheme="cyan"
              mr={3}
              onClick={handleSendSMS}
              isDisabled={
                !smsMessage.trim() ||
                isSendingSMS ||
                isSendingWelcome ||
                Object.keys(smsSelectedPatients).filter(
                  (id) => smsSelectedPatients[id],
                ).length === 0
              }
              isLoading={isSendingSMS}
              loadingText="Sending..."
            >
              Send Custom SMS
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setIsSendSMSModalOpen(false);
                setSmsMessage("");
                setSmsSelectedPatients({});
                setSmsPatientTypeFilter([]);
              }}
              isDisabled={isSendingSMS}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMS Status Modal */}
      <Modal
        isOpen={isSMSStatusModalOpen}
        onClose={() => setIsSMSStatusModalOpen(false)}
        isCentered
        size={activeSMSTab >= 2 ? "3xl" : "2xl"}
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            <Flex justify="space-between" align="center" w="100%" pr={8} gap={2} wrap="wrap">
              <Text>SMS Status - Twilio Health</Text>
              <HStack spacing={2}>
                <Tooltip
                  label="Re-check every number now (good, bad, failed, not texted) for the selected providers"
                  hasArrow
                >
                  <Button
                    size="sm"
                    colorScheme="teal"
                    variant="outline"
                    leftIcon={<RepeatIcon />}
                    onClick={handleRebuildNumberHealth}
                    isLoading={rebuildingNumberHealth}
                    loadingText="Refreshing"
                  >
                    Refresh DB
                  </Button>
                </Tooltip>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                    setIsSMSStatusModalOpen(false);
                    setIsTwilioHealthModalOpen(true);
                  }}
                >
                  View Full Details
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <NumberHealthChanges
              result={numberHealthResult}
              homeSchema={homeSchema}
              onClose={() => setNumberHealthResult(null)}
            />
            <Box mb={3}>
              <Text as="span" fontSize="sm" color="gray.600" mr={2}>
                Method for &quot;By Appointment Date&quot; tab:
              </Text>
              <Badge
                colorScheme={by_appointment_date_method ? "green" : "blue"}
                fontSize="xs"
              >
                {by_appointment_date_method ? "simple" : "complex"}
              </Badge>
            </Box>
            {loadingSMSStatus && activeSMSTab === 0 ? (
              <Center py={8}>
                <Spinner size="xl" />
                <Text ml={4}>Loading SMS status...</Text>
              </Center>
            ) : loadingSMSByAppointmentDate && activeSMSTab === 1 ? (
              <Center py={8}>
                <Spinner size="xl" />
                <Text ml={4}>Loading SMS by appointment date...</Text>
              </Center>
            ) : (
              <Tabs
                index={activeSMSTab}
                onChange={setActiveSMSTab}
                variant="soft-rounded"
                colorScheme="teal"
                size="sm"
              >
                {/* One line per label: wrapped labels made the selected pill
                    two or three lines tall. Scrolls sideways if still too narrow. */}
                <TabList
                  mb={4}
                  gap={1}
                  overflowX="auto"
                  sx={{
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    "& > button": { whiteSpace: "nowrap", flexShrink: 0, px: 3 },
                  }}
                >
                  <Tab
                    onClick={() => {
                      if (activeSMSTab !== 0) {
                        fetchSMSHealthStatus(selectedSMSDate);
                      }
                    }}
                  >
                    By Created Date
                  </Tab>
                  <Tab
                    onClick={() => {
                      if (activeSMSTab !== 1) {
                        const y = appointmentCalendarMonth.getFullYear();
                        const m = appointmentCalendarMonth.getMonth();
                        fetchSMSCountsForAppointmentMonth(y, m);
                        fetchSMSByAppointmentDate(selectedAppointmentDate);
                      }
                    }}
                  >
                    By Appointment Date
                  </Tab>
                  <Tab>Failed numbers</Tab>
                  <Tab>Good numbers</Tab>
                  <Tab>Not texted</Tab>
                </TabList>
                <TabPanels>
                  {/* Tab 1: By Created Date (existing content) */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      {/* Date Selector with Calendar */}
                      <Box>
                        <Flex justify="space-between" align="center" mb={3}>
                          <Text fontWeight="bold">Select Date</Text>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous month"
                              icon={<ChevronLeftIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newMonth = new Date(calendarMonth);
                                newMonth.setMonth(newMonth.getMonth() - 1);
                                setCalendarMonth(newMonth);
                                fetchFailedSMSCountsForMonth(
                                  newMonth.getFullYear(),
                                  newMonth.getMonth(),
                                );
                              }}
                            />
                            <Text
                              fontWeight="bold"
                              minW="120px"
                              textAlign="center"
                            >
                              {calendarMonth.toLocaleDateString("en-US", {
                                month: "long",
                                year: "numeric",
                              })}
                            </Text>
                            <IconButton
                              aria-label="Next month"
                              icon={<ChevronRightIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newMonth = new Date(calendarMonth);
                                newMonth.setMonth(newMonth.getMonth() + 1);
                                setCalendarMonth(newMonth);
                                fetchFailedSMSCountsForMonth(
                                  newMonth.getFullYear(),
                                  newMonth.getMonth(),
                                );
                              }}
                            />
                          </HStack>
                        </Flex>

                        {loadingCalendarData ? (
                          <Center py={8}>
                            <Spinner size="md" />
                            <Text ml={3}>Loading calendar...</Text>
                          </Center>
                        ) : (
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            overflow="hidden"
                          >
                            {/* Calendar Header */}
                            <Grid
                              templateColumns="repeat(7, 1fr)"
                              bg="gray.100"
                              p={2}
                            >
                              {[
                                "Sun",
                                "Mon",
                                "Tue",
                                "Wed",
                                "Thu",
                                "Fri",
                                "Sat",
                              ].map((day) => (
                                <Text
                                  key={day}
                                  fontWeight="bold"
                                  fontSize="xs"
                                  textAlign="center"
                                  color="gray.600"
                                >
                                  {day}
                                </Text>
                              ))}
                            </Grid>

                            {/* Calendar Days */}
                            <Grid
                              templateColumns="repeat(7, 1fr)"
                              gap={1}
                              p={2}
                            >
                              {(() => {
                                const year = calendarMonth.getFullYear();
                                const month = calendarMonth.getMonth();
                                const firstDayOfMonth = new Date(
                                  year,
                                  month,
                                  1,
                                ).getDay();
                                const daysInMonth = new Date(
                                  year,
                                  month + 1,
                                  0,
                                ).getDate();
                                const today = new Date();

                                const calendarDays = [];

                                // Empty cells for days before the first day of the month
                                for (let i = 0; i < firstDayOfMonth; i++) {
                                  calendarDays.push(<Box key={`empty-${i}`} />);
                                }

                                // Days of the month
                                for (let day = 1; day <= daysInMonth; day++) {
                                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                  const failedCount =
                                    failedSMSCountsByDate[dateStr] || 0;
                                  const totalCount =
                                    totalSMSCountsByDate[dateStr] || 0;
                                  const isSelected =
                                    dateStr === selectedSMSDate;
                                  const isToday =
                                    dateStr ===
                                    today.toISOString().split("T")[0];

                                  calendarDays.push(
                                    <Box
                                      key={dateStr}
                                      p={2}
                                      textAlign="center"
                                      cursor="pointer"
                                      borderRadius="md"
                                      bg={
                                        isSelected
                                          ? "blue.500"
                                          : isToday
                                            ? "blue.50"
                                            : "white"
                                      }
                                      color={isSelected ? "white" : "gray.800"}
                                      borderWidth="1px"
                                      borderColor={
                                        isSelected
                                          ? "blue.500"
                                          : isToday
                                            ? "blue.300"
                                            : "gray.200"
                                      }
                                      position="relative"
                                      onClick={() =>
                                        handleSMSDateChange(dateStr)
                                      }
                                      _hover={{
                                        bg: isSelected ? "blue.600" : "gray.50",
                                      }}
                                      transition="all 0.2s"
                                    >
                                      <Text
                                        fontWeight={
                                          isSelected ? "bold" : "normal"
                                        }
                                      >
                                        {day}
                                      </Text>
                                      {totalCount > 0 && (
                                        <>
                                          {/* Total count badge - green */}
                                          <Badge
                                            position="absolute"
                                            bottom="-2px"
                                            right="-2px"
                                            colorScheme="green"
                                            fontSize="xs"
                                            minW="18px"
                                            h="18px"
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            zIndex={1}
                                          >
                                            {totalCount}
                                          </Badge>
                                          {/* Failed count badge - red */}
                                          {failedCount > 0 && (
                                            <Badge
                                              position="absolute"
                                              top="-2px"
                                              right="-2px"
                                              colorScheme="red"
                                              fontSize="xs"
                                              minW="18px"
                                              h="18px"
                                              borderRadius="full"
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              zIndex={2}
                                            >
                                              {failedCount}
                                            </Badge>
                                          )}
                                        </>
                                      )}
                                    </Box>,
                                  );
                                }

                                return calendarDays;
                              })()}
                            </Grid>
                          </Box>
                        )}

                        {/* Legend */}
                        {(Object.keys(failedSMSCountsByDate).length > 0 ||
                          Object.keys(totalSMSCountsByDate).length > 0) && (
                          <Flex mt={3} align="center" gap={4} flexWrap="wrap">
                            <Flex align="center" gap={2}>
                              <Box
                                w="4"
                                h="4"
                                bg="blue.500"
                                borderRadius="md"
                              />
                              <Text fontSize="xs">Selected</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Box
                                w="4"
                                h="4"
                                bg="blue.50"
                                borderWidth="1px"
                                borderColor="blue.300"
                                borderRadius="md"
                              />
                              <Text fontSize="xs">Today</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Badge
                                colorScheme="green"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                #
                              </Badge>
                              <Text fontSize="xs">Total SMS</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Badge
                                colorScheme="red"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                #
                              </Badge>
                              <Text fontSize="xs">Failed</Text>
                            </Flex>
                          </Flex>
                        )}
                      </Box>

                      {/* Patient Selection - Checkboxes for each patient from schedule */}
                      <Box
                        p={4}
                        bg="purple.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="purple.200"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="md"
                          mb={3}
                          color="purple.700"
                        >
                          Select Patients
                        </Text>

                        <Text
                          fontWeight="medium"
                          fontSize="sm"
                          mb={3}
                          color="purple.600"
                        >
                          Patients for {formatDateString(selectedSMSDate)}:
                        </Text>

                        {/* Use patients from the schedule (selectedDate) */}
                        {patients && patients.length > 0 ? (
                          <>
                            <VStack
                              spacing={2}
                              align="stretch"
                              maxH="200px"
                              overflowY="auto"
                              bg="white"
                              p={2}
                              borderRadius="md"
                              border="1px"
                              borderColor="purple.200"
                            >
                              {patients.map((patient) => {
                                // Extract patient name using existing helper
                                const extractedInfo =
                                  extractPatientInfo(patient);
                                const patientName =
                                  extractedInfo.name ||
                                  patient[prompts[0][0]] ||
                                  patient.name ||
                                  patient[colid2] ||
                                  "Unknown Patient";

                                // Count messages for this patient on the selected date
                                const messageCount = allSMSForDate.filter(
                                  (m) => {
                                    const msgDate =
                                      m.date_ || m.appointment_date;
                                    const msgPatientId =
                                      m.patient_id ||
                                      m.df1?.patient_id ||
                                      m.name;
                                    return (
                                      msgDate === selectedSMSDate &&
                                      msgPatientId === patient.id
                                    );
                                  },
                                ).length;

                                return (
                                  <Checkbox
                                    key={patient.id}
                                    isChecked={
                                      selectedSMSPatient === patient.id
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedSMSPatient(patient.id);
                                      } else {
                                        setSelectedSMSPatient("");
                                      }
                                    }}
                                    colorScheme="purple"
                                  >
                                    <Flex
                                      justify="space-between"
                                      align="center"
                                      w="full"
                                    >
                                      <Text fontSize="sm" fontWeight="medium">
                                        {patientName}
                                      </Text>
                                      <Badge colorScheme="purple" fontSize="xs">
                                        {messageCount} SMS
                                      </Badge>
                                    </Flex>
                                  </Checkbox>
                                );
                              })}
                            </VStack>

                            {/* Selected patient indicator */}
                            {selectedSMSPatient && (
                              <HStack mt={3} spacing={2}>
                                <Badge colorScheme="teal" fontSize="xs">
                                  Selected:{" "}
                                  {(() => {
                                    const patient = patients.find(
                                      (p) => p.id === selectedSMSPatient,
                                    );
                                    if (patient) {
                                      const extractedInfo =
                                        extractPatientInfo(patient);
                                      return (
                                        extractedInfo.name ||
                                        patient[prompts[0][0]] ||
                                        patient.name ||
                                        patient[colid2] ||
                                        "Selected"
                                      );
                                    }
                                    return "Selected";
                                  })()}
                                </Badge>
                                <Button
                                  size="xs"
                                  variant="link"
                                  colorScheme="red"
                                  onClick={() => setSelectedSMSPatient("")}
                                >
                                  Clear Selection
                                </Button>
                              </HStack>
                            )}

                            {/* Select First button */}
                            <HStack mt={3} spacing={2}>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="purple"
                                onClick={() => {
                                  // Select first patient as default
                                  if (patients.length > 0) {
                                    setSelectedSMSPatient(patients[0].id);
                                  }
                                }}
                              >
                                Select First
                              </Button>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="gray"
                                onClick={() => setSelectedSMSPatient("")}
                              >
                                Clear All
                              </Button>
                            </HStack>
                          </>
                        ) : (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            fontStyle="italic"
                          >
                            No patients found for this date. Select a date with
                            patients in the schedule.
                          </Text>
                        )}
                      </Box>

                      {/* SMS Messages for Selected Patient - Categorized by Appointment */}
                      {selectedSMSPatient && (
                        <Box
                          p={4}
                          bg="green.50"
                          borderRadius="md"
                          border="1px"
                          borderColor="green.200"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="md"
                            mb={3}
                            color="green.700"
                          >
                            SMS Messages for{" "}
                            {(() => {
                              const patient = patients.find(
                                (p) => p.id === selectedSMSPatient,
                              );
                              if (patient) {
                                const extractedInfo =
                                  extractPatientInfo(patient);
                                return (
                                  extractedInfo.name ||
                                  patient[prompts[0][0]] ||
                                  patient.name ||
                                  patient[colid2] ||
                                  "Selected Patient"
                                );
                              }
                              return "Selected Patient";
                            })()}
                          </Text>

                          {/* Filter and categorize SMS for selected patient */}
                          {(() => {
                            // Get selected patient data
                            const selectedPatientData = patients.find(
                              (p) => p.id === selectedSMSPatient,
                            );

                            // Extract categorized messages structure
                            const categorized =
                              categorizedSMSMessages?.categorized || {};
                            const uncategorized =
                              categorizedSMSMessages?.uncategorized || [];
                            const appointmentDates = Object.keys(categorized);
                            const hasUncategorized = uncategorized.length > 0;
                            const hasAnyCategorized =
                              appointmentDates.length > 0;
                            const filteredMessages = uncategorized; // Use uncategorized as fallback list

                            // Get patient phone
                            const patientPhone = rowPhones(selectedPatientData)[0] || null;

                            // Calculate total messages from categorized and uncategorized
                            const totalCategorizedMessages = Object.values(
                              categorized,
                            ).reduce(
                              (sum, section) =>
                                sum + (section.messages?.length || 0),
                              0,
                            );
                            const totalMessages =
                              totalCategorizedMessages + uncategorized.length;

                            if (totalMessages === 0) {
                              return (
                                <Text
                                  fontSize="sm"
                                  color="gray.500"
                                  fontStyle="italic"
                                >
                                  No SMS messages found for this patient
                                </Text>
                              );
                            }

                            // Loading categorization
                            if (loadingSMSCategorization) {
                              return (
                                <Flex justify="center" align="center" py={4}>
                                  <Spinner size="sm" />
                                  <Text ml={2} fontSize="sm" color="gray.500">
                                    Categorizing messages by appointment...
                                  </Text>
                                </Flex>
                              );
                            }

                            return (
                              <VStack spacing={3} align="stretch">
                                {/* Refresh button */}
                                <HStack justify="flex-end">
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="green"
                                    onClick={async () => {
                                      if (patientPhone) {
                                        setLoadingSMSCategorization(true);
                                        const allPatientMessages =
                                          await fetchAllSMSForPatient(
                                            patientPhone,
                                            selectedPatientData,
                                          );
                                        console.log(
                                          "[SMS Status] Fetched",
                                          allPatientMessages.length,
                                          "messages for patient",
                                        );
                                        const result =
                                          await categorizeSMSMessagesByAppointment(
                                            allPatientMessages,
                                            patientPhone,
                                            selectedPatientData,
                                          );
                                        console.log(
                                          "[SMS Status] Categorized:",
                                          Object.keys(result.categorized || {})
                                            .length,
                                          "appointment sections,",
                                          (result.uncategorized || []).length,
                                          "uncategorized messages",
                                        );
                                        setCategorizedSMSMessages(result);
                                        // Auto-expand the closest appointment section
                                        const closestDate =
                                          findClosestAppointmentDate(
                                            result,
                                            selectedPatientData,
                                          );
                                        console.log(
                                          "[SMS Status] Closest appointment date:",
                                          closestDate,
                                        );
                                        if (closestDate) {
                                          setExpandedSMSAppointments(
                                            (prev) => ({
                                              ...prev,
                                              [closestDate]: true,
                                            }),
                                          );
                                        }
                                        setLoadingSMSCategorization(false);
                                      }
                                    }}
                                    isLoading={loadingSMSCategorization}
                                  >
                                    Refresh Categorization
                                  </Button>
                                </HStack>

                                {/* Find closest appointment date for highlighting */}
                                {(() => {
                                  const selectedPatientData = patients.find(
                                    (p) => p.id === selectedSMSPatient,
                                  );
                                  return findClosestAppointmentDate(
                                    categorizedSMSMessages,
                                    selectedPatientData,
                                  );
                                })()}

                                {/* Categorized by appointment date */}
                                {appointmentDates.map((appointmentDate) => {
                                  const section = categorized[appointmentDate];
                                  const messagesInSection =
                                    section.messages || [];
                                  const closestAppointmentDate = (() => {
                                    const selectedPatientData = patients.find(
                                      (p) => p.id === selectedSMSPatient,
                                    );
                                    return findClosestAppointmentDate(
                                      categorizedSMSMessages,
                                      selectedPatientData,
                                    );
                                  })();
                                  const isClosest =
                                    appointmentDate === closestAppointmentDate;

                                  return (
                                    <Box key={`sms-appt-${appointmentDate}`}>
                                      <Box
                                        bg={
                                          isClosest ? "blue.100" : "green.100"
                                        }
                                        borderWidth="2px"
                                        borderColor={
                                          isClosest ? "blue.500" : "green.300"
                                        }
                                        borderRadius="md"
                                        p={2}
                                        cursor="pointer"
                                        onClick={() => {
                                          setExpandedSMSAppointments(
                                            (prev) => ({
                                              ...prev,
                                              [appointmentDate]:
                                                !prev?.[appointmentDate],
                                            }),
                                          );
                                        }}
                                        _hover={{
                                          bg: isClosest
                                            ? "blue.200"
                                            : "green.200",
                                        }}
                                      >
                                        <HStack justify="space-between">
                                          <HStack spacing={2}>
                                            {isClosest && (
                                              <Badge
                                                colorScheme="blue"
                                                fontSize="xs"
                                              >
                                                Closest to Appointment
                                              </Badge>
                                            )}
                                            <Text
                                              fontWeight="semibold"
                                              fontSize="sm"
                                              color={
                                                isClosest
                                                  ? "blue.700"
                                                  : "green.700"
                                              }
                                            >
                                              {(() => {
                                                try {
                                                  const date = new Date(
                                                    appointmentDate +
                                                      "T12:00:00",
                                                  );
                                                  return date.toLocaleDateString(
                                                    "en-US",
                                                    {
                                                      weekday: "short",
                                                      month: "short",
                                                      day: "numeric",
                                                      year: "numeric",
                                                    },
                                                  );
                                                } catch (e) {
                                                  return appointmentDate;
                                                }
                                              })()}
                                            </Text>
                                            <Badge
                                              colorScheme="green"
                                              fontSize="xs"
                                            >
                                              {messagesInSection.length} message
                                              {messagesInSection.length !== 1
                                                ? "s"
                                                : ""}
                                            </Badge>
                                          </HStack>
                                          <Text fontSize="xs" color="green.600">
                                            {expandedSMSAppointments?.[
                                              appointmentDate
                                            ]
                                              ? "▼"
                                              : "▶"}
                                          </Text>
                                        </HStack>
                                      </Box>
                                      {expandedSMSAppointments?.[
                                        appointmentDate
                                      ] && (
                                        <Box
                                          mt={2}
                                          p={3}
                                          bg="white"
                                          borderRadius="md"
                                          borderWidth="1px"
                                          borderColor="green.200"
                                        >
                                          <VStack spacing={2} align="stretch">
                                            {messagesInSection.map(
                                              (msg, msgIdx) => {
                                                const isFailed =
                                                  msg.status === "failed" ||
                                                  msg.status ===
                                                    "undelivered" ||
                                                  (msg.error_code &&
                                                    msg.error_code !== "None" &&
                                                    msg.error_code !== null &&
                                                    msg.error_code !== "");

                                                return (
                                                  <Box
                                                    key={msgIdx}
                                                    p={2}
                                                    border="1px"
                                                    borderColor={
                                                      isFailed
                                                        ? "red.300"
                                                        : "green.200"
                                                    }
                                                    borderRadius="md"
                                                    bg={
                                                      isFailed
                                                        ? "red.50"
                                                        : "gray.50"
                                                    }
                                                  >
                                                    <Flex
                                                      justify="space-between"
                                                      mb={1}
                                                    >
                                                      <Badge
                                                        colorScheme={
                                                          msg.status ===
                                                            "sent" ||
                                                          msg.status ===
                                                            "delivered"
                                                            ? "green"
                                                            : msg.status ===
                                                                  "failed" ||
                                                                msg.status ===
                                                                  "undelivered"
                                                              ? "red"
                                                              : "yellow"
                                                        }
                                                        fontSize="xs"
                                                      >
                                                        {msg.status ||
                                                          "unknown"}
                                                      </Badge>
                                                      <Text
                                                        fontSize="xs"
                                                        color="gray.500"
                                                      >
                                                        {msg.created_at
                                                          ? new Date(
                                                              msg.created_at,
                                                            ).toLocaleString()
                                                          : "N/A"}
                                                      </Text>
                                                    </Flex>
                                                    <Text
                                                      fontSize="sm"
                                                      color="gray.600"
                                                    >
                                                      {msg.message?.substring(
                                                        0,
                                                        150,
                                                      ) || ""}
                                                      {msg.message?.length >
                                                        150 && "..."}
                                                    </Text>
                                                    {isFailed &&
                                                      msg.error_code && (
                                                        <Text
                                                          fontSize="xs"
                                                          color="red.600"
                                                          mt={1}
                                                        >
                                                          Error:{" "}
                                                          {msg.error_code} -{" "}
                                                          {msg.error_message ||
                                                            "Unknown error"}
                                                        </Text>
                                                      )}
                                                  </Box>
                                                );
                                              },
                                            )}
                                          </VStack>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}

                                {/* Uncategorized messages */}
                                {hasUncategorized && (
                                  <Box>
                                    <Box
                                      bg="gray.100"
                                      borderWidth="1px"
                                      borderColor="gray.300"
                                      borderRadius="md"
                                      p={2}
                                      cursor="pointer"
                                      onClick={() => {
                                        setExpandedSMSAppointments((prev) => ({
                                          ...prev,
                                          _uncategorized:
                                            !prev?.["_uncategorized"],
                                        }));
                                      }}
                                    >
                                      <HStack justify="space-between">
                                        <HStack spacing={2}>
                                          <Text
                                            fontWeight="semibold"
                                            fontSize="sm"
                                            color="gray.600"
                                          >
                                            Other Messages (No Matching
                                            Appointment)
                                          </Text>
                                          <Badge
                                            colorScheme="gray"
                                            fontSize="xs"
                                          >
                                            {uncategorized.length} message
                                            {uncategorized.length !== 1
                                              ? "s"
                                              : ""}
                                          </Badge>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">
                                          {expandedSMSAppointments?.[
                                            "_uncategorized"
                                          ]
                                            ? "▼"
                                            : "▶"}
                                        </Text>
                                      </HStack>
                                    </Box>
                                    {expandedSMSAppointments?.[
                                      "_uncategorized"
                                    ] && (
                                      <Box
                                        mt={2}
                                        p={3}
                                        bg="white"
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                      >
                                        <VStack spacing={2} align="stretch">
                                          {uncategorized.map((msg, msgIdx) => {
                                            const isFailed =
                                              msg.status === "failed" ||
                                              msg.status === "undelivered" ||
                                              (msg.error_code &&
                                                msg.error_code !== "None" &&
                                                msg.error_code !== null &&
                                                msg.error_code !== "");

                                            return (
                                              <Box
                                                key={msgIdx}
                                                p={2}
                                                border="1px"
                                                borderColor={
                                                  isFailed
                                                    ? "red.300"
                                                    : "gray.200"
                                                }
                                                borderRadius="md"
                                                bg={
                                                  isFailed
                                                    ? "red.50"
                                                    : "gray.50"
                                                }
                                              >
                                                <Flex
                                                  justify="space-between"
                                                  mb={1}
                                                >
                                                  <Badge
                                                    colorScheme={
                                                      msg.status === "sent" ||
                                                      msg.status === "delivered"
                                                        ? "green"
                                                        : msg.status ===
                                                              "failed" ||
                                                            msg.status ===
                                                              "undelivered"
                                                          ? "red"
                                                          : "yellow"
                                                    }
                                                    fontSize="xs"
                                                  >
                                                    {msg.status || "unknown"}
                                                  </Badge>
                                                  <Text
                                                    fontSize="xs"
                                                    color="gray.500"
                                                  >
                                                    {msg.created_at
                                                      ? new Date(
                                                          msg.created_at,
                                                        ).toLocaleString()
                                                      : "N/A"}
                                                  </Text>
                                                </Flex>
                                                <Text
                                                  fontSize="sm"
                                                  color="gray.600"
                                                >
                                                  {msg.message?.substring(
                                                    0,
                                                    150,
                                                  ) || ""}
                                                  {msg.message?.length > 150 &&
                                                    "..."}
                                                </Text>
                                                {isFailed && msg.error_code && (
                                                  <Text
                                                    fontSize="xs"
                                                    color="red.600"
                                                    mt={1}
                                                  >
                                                    Error: {msg.error_code} -{" "}
                                                    {msg.error_message ||
                                                      "Unknown error"}
                                                  </Text>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </VStack>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {/* Fallback: show flat list if no categorization happened */}
                                {!hasAnyCategorized && !hasUncategorized && (
                                  <VStack
                                    spacing={2}
                                    align="stretch"
                                    maxH="300px"
                                    overflowY="auto"
                                  >
                                    {filteredMessages.map((msg, idx) => {
                                      const isFailed =
                                        msg.status === "failed" ||
                                        msg.status === "undelivered" ||
                                        (msg.error_code &&
                                          msg.error_code !== "None" &&
                                          msg.error_code !== null &&
                                          msg.error_code !== "");

                                      return (
                                        <Box
                                          key={idx}
                                          p={3}
                                          border="1px"
                                          borderColor={
                                            isFailed ? "red.300" : "green.300"
                                          }
                                          borderRadius="md"
                                          bg={isFailed ? "red.50" : "white"}
                                        >
                                          <Flex justify="space-between" mb={1}>
                                            <Badge
                                              colorScheme={
                                                msg.status === "sent" ||
                                                msg.status === "delivered"
                                                  ? "green"
                                                  : msg.status === "failed" ||
                                                      msg.status ===
                                                        "undelivered"
                                                    ? "red"
                                                    : "yellow"
                                              }
                                            >
                                              {msg.status || "unknown"}
                                            </Badge>
                                            <Text
                                              fontSize="xs"
                                              color="gray.500"
                                            >
                                              {msg.created_at
                                                ? new Date(
                                                    msg.created_at,
                                                  ).toLocaleString()
                                                : "N/A"}
                                            </Text>
                                          </Flex>
                                          <Flex justify="space-between" mb={1}>
                                            <Text fontSize="sm">
                                              To:{" "}
                                              {editedSMS[msg.id]?.to ||
                                                msg.to ||
                                                "N/A"}
                                              <PhoneStatusBadge phone={editedSMS[msg.id]?.to || msg.to} compact ml={2} />
                                              {smsStatusSchemas.length > 1 && msg._schema && (
                                                <Badge ml={2} variant="outline" fontSize="xs">
                                                  {msg._schema}
                                                </Badge>
                                              )}
                                            </Text>
                                            {isFailed && (
                                              <Badge
                                                colorScheme="red"
                                                fontSize="xs"
                                              >
                                                Failed
                                              </Badge>
                                            )}
                                          </Flex>
                                          <Text fontSize="sm" color="gray.600">
                                            {editedSMS[msg.id]?.message
                                              ? editedSMS[msg.id].message
                                              : msg.message?.substring(
                                                  0,
                                                  100,
                                                ) || ""}
                                            {!editedSMS[msg.id]?.message &&
                                              msg.message?.length > 100 &&
                                              "..."}
                                          </Text>
                                          {isFailed && msg.error_code && (
                                            <Text
                                              fontSize="xs"
                                              color="red.600"
                                              mt={1}
                                            >
                                              Error: {msg.error_code} -{" "}
                                              {msg.error_message ||
                                                "Unknown error"}
                                            </Text>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                )}
                              </VStack>
                            );
                          })()}
                        </Box>
                      )}

                      {/* Health Status Overview */}
                      {smsHealthData && (
                        <>
                          <Box p={4} bg="gray.50" borderRadius="md">
                            <Flex justify="space-between" align="center" mb={3}>
                              <Text fontWeight="bold" fontSize="lg">
                                Health Status (24h Overview)
                              </Text>
                            </Flex>
                            <VStack spacing={2} align="stretch">
                              <Flex justify="space-between">
                                <Text>Status:</Text>
                                <Badge
                                  colorScheme={
                                    smsHealthData.status === "healthy"
                                      ? "green"
                                      : smsHealthData.status === "warning"
                                        ? "yellow"
                                        : "red"
                                  }
                                >
                                  {smsHealthData.status || "unknown"}
                                </Badge>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Account SID:</Text>
                                <Text fontSize="sm" fontFamily="mono">
                                  {smsHealthData.accountSid
                                    ? `${smsHealthData.accountSid.substring(0, 10)}...`
                                    : "N/A"}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Phone Number:</Text>
                                <Text fontSize="sm">
                                  {smsHealthData.phoneNumber || "N/A"}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Total Messages (24h):</Text>
                                <Text fontWeight="bold">
                                  {smsHealthData.messageCount || 0}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Failed Messages (24h):</Text>
                                <Text fontWeight="bold" color="red.500">
                                  {smsHealthData.errorCount || 0}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Success Rate (24h):</Text>
                                <Text fontWeight="bold" color="green.500">
                                  {smsHealthData.successRate || 100}%
                                </Text>
                              </Flex>
                              <Divider />
                              <Flex justify="space-between">
                                <Text>Total SMS for Selected Date:</Text>
                                <Text fontWeight="bold">
                                  {allSMSForDate.length}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Failed SMS for Selected Date:</Text>
                                <Text fontWeight="bold" color="red.500">
                                  {failedSMS.length}
                                </Text>
                              </Flex>
                              {allSMSForDate.length > 0 && (
                                <Flex justify="space-between">
                                  <Text>Success Rate (Selected Date):</Text>
                                  <Text fontWeight="bold" color="green.500">
                                    {Math.round(
                                      ((allSMSForDate.length -
                                        failedSMS.length) /
                                        allSMSForDate.length) *
                                        100,
                                    )}
                                    %
                                  </Text>
                                </Flex>
                              )}
                              {/* Sync Messages Button with Progress */}
                              <Box>
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  leftIcon={<RepeatIcon />}
                                  isLoading={
                                    syncingSMSMessages && !syncProgress
                                  }
                                  loadingText="Starting..."
                                  isDisabled={
                                    syncingSMSMessages && !!syncProgress
                                  }
                                  onClick={async () => {
                                    setSyncingSMSMessages(true);
                                    setSyncProgress(null);
                                    try {
                                      const result =
                                        await syncTwilioMessages(true);
                                      const syncId = result?.sync_id;

                                      if (syncId) {
                                        // Poll for progress
                                        const pollProgress = async () => {
                                          let attempts = 0;
                                          const maxAttempts = 120; // 2 minutes max

                                          while (attempts < maxAttempts) {
                                            const progress =
                                              await getSyncProgress(syncId);
                                            setSyncProgress(progress);

                                            if (
                                              progress?.is_complete ||
                                              progress?.error
                                            ) {
                                              break;
                                            }

                                            await new Promise((resolve) =>
                                              setTimeout(resolve, 1000),
                                            );
                                            attempts++;
                                          }
                                        };

                                        await pollProgress();
                                      }

                                      // Refresh the SMS health status after sync
                                      await fetchSMSHealthStatus(
                                        selectedSMSDate,
                                      );
                                      await fetchFailedSMSCountsForMonth(
                                        calendarMonth.getFullYear(),
                                        calendarMonth.getMonth(),
                                      );

                                      toast({
                                        title: "Messages Synced",
                                        description: syncProgress?.synced
                                          ? `Successfully synced ${syncProgress.synced} of ${syncProgress.total} messages`
                                          : "SMS messages have been synced from Twilio",
                                        status: "success",
                                        duration: 4000,
                                      });
                                    } catch (error) {
                                      console.error(
                                        "Error syncing messages:",
                                        error,
                                      );
                                      toast({
                                        title: "Sync Failed",
                                        description:
                                          error.message ||
                                          "Failed to sync messages",
                                        status: "error",
                                        duration: 5000,
                                      });
                                    } finally {
                                      setSyncingSMSMessages(false);
                                      setSyncProgress(null);
                                    }
                                  }}
                                >
                                  {syncProgress
                                    ? `${syncProgress.synced || 0}/${syncProgress.total || 0} Synced`
                                    : "Sync Messages"}
                                </Button>

                                {/* Progress Bar */}
                                {syncProgress && (
                                  <Box mt={2}>
                                    <Progress
                                      value={
                                        syncProgress.total > 0
                                          ? ((syncProgress.synced || 0) /
                                              syncProgress.total) *
                                            100
                                          : 0
                                      }
                                      size="sm"
                                      colorScheme="blue"
                                      borderRadius="full"
                                      hasStripe
                                      isAnimated
                                    />
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                      {syncProgress.synced || 0} of{" "}
                                      {syncProgress.total || 0} messages synced
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            </VStack>
                          </Box>

                          {/* Error Code Filter */}
                          {getUniqueErrorCodes().length > 0 && (
                            <Box
                              p={4}
                              bg="blue.50"
                              borderRadius="md"
                              border="1px"
                              borderColor="blue.200"
                            >
                              <Text fontWeight="bold" fontSize="md" mb={3}>
                                Filter by Error Code
                              </Text>
                              <VStack
                                spacing={2}
                                align="stretch"
                                maxH="200px"
                                overflowY="auto"
                              >
                                {getUniqueErrorCodes().map((errorCode) => {
                                  const count = allSMSForDate.filter(
                                    (msg) => msg.error_code === errorCode,
                                  ).length;
                                  // Get error description: first try mapped description, then error_message from database
                                  const mappedDescription =
                                    getTwilioErrorDescription(errorCode);
                                  const firstMessageWithCode =
                                    allSMSForDate.find(
                                      (msg) => msg.error_code === errorCode,
                                    );
                                  const dbErrorMessage =
                                    firstMessageWithCode?.error_message || "";
                                  // Use mapped description if available, otherwise use DB error message
                                  const errorDescription =
                                    mappedDescription || dbErrorMessage;
                                  return (
                                    <Box key={errorCode}>
                                      <Checkbox
                                        isChecked={selectedErrorCodes.includes(
                                          errorCode,
                                        )}
                                        onChange={() =>
                                          handleErrorCodeToggle(errorCode)
                                        }
                                        colorScheme="blue"
                                      >
                                        <Text fontSize="sm" fontWeight="medium">
                                          {errorCode} ({count})
                                        </Text>
                                      </Checkbox>
                                      {errorDescription && (
                                        <Box ml={6} mt={-1} mb={1}>
                                          <Text
                                            fontSize="xs"
                                            color="gray.600"
                                            fontWeight="medium"
                                          >
                                            Full Error Description:
                                          </Text>
                                          <Text
                                            fontSize="xs"
                                            color="gray.600"
                                            mt={0.5}
                                          >
                                            {errorDescription}
                                          </Text>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </VStack>
                              {selectedErrorCodes.length > 0 && (
                                <Button
                                  size="xs"
                                  variant="link"
                                  colorScheme="blue"
                                  mt={2}
                                  onClick={() => setSelectedErrorCodes([])}
                                >
                                  Clear Filters
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* All SMS Messages for Selected Date */}
                          <Box>
                            <Text fontWeight="bold" fontSize="lg" mb={3}>
                              All SMS Messages for{" "}
                              {formatDateString(selectedSMSDate)} (
                              {selectedErrorCodes.length > 0
                                ? `${getFilteredMessages().length} of ${allSMSForDate.length}`
                                : allSMSForDate.length}
                              )
                            </Text>
                            {getFilteredMessages() &&
                            getFilteredMessages().length > 0 ? (
                              <VStack
                                spacing={2}
                                align="stretch"
                                maxH="400px"
                                overflowY="auto"
                              >
                                {getFilteredMessages().map((msg, idx) => {
                                  const isFailed =
                                    msg.status === "failed" ||
                                    msg.status === "undelivered" ||
                                    (msg.error_code &&
                                      msg.error_code !== "None" &&
                                      msg.error_code !== null &&
                                      msg.error_code !== "");

                                  return (
                                    <Box
                                      key={idx}
                                      p={3}
                                      border="1px"
                                      borderColor={
                                        isFailed ? "red.200" : "gray.200"
                                      }
                                      borderRadius="md"
                                      bg={isFailed ? "red.50" : "white"}
                                    >
                                      {/* Checkbox for selecting SMS to resend - only show for failed SMS */}
                                      {isFailed && (
                                        <Flex justify="flex-start" mb={2}>
                                          <Checkbox
                                            isChecked={
                                              selectedSMSForResend[msg.id] ||
                                              false
                                            }
                                            onChange={(e) => {
                                              setSelectedSMSForResend(
                                                (prev) => ({
                                                  ...prev,
                                                  [msg.id]: e.target.checked,
                                                }),
                                              );
                                            }}
                                            colorScheme="red"
                                          >
                                            <Text
                                              fontSize="sm"
                                              fontWeight="bold"
                                            >
                                              Select to resend
                                            </Text>
                                          </Checkbox>
                                        </Flex>
                                      )}
                                      <Flex justify="space-between" mb={1}>
                                        <Badge
                                          colorScheme={
                                            msg.status === "sent" ||
                                            msg.status === "delivered"
                                              ? "green"
                                              : msg.status === "failed" ||
                                                  msg.status === "undelivered"
                                                ? "red"
                                                : "yellow"
                                          }
                                        >
                                          {msg.status || "unknown"}
                                        </Badge>
                                        <Text fontSize="xs" color="gray.500">
                                          {msg.created_at
                                            ? new Date(
                                                msg.created_at,
                                              ).toLocaleString()
                                            : "N/A"}
                                        </Text>
                                      </Flex>
                                      <Flex
                                        justify="space-between"
                                        align="center"
                                        mb={1}
                                      >
                                        <Text fontSize="sm" fontWeight="bold">
                                          To:{" "}
                                          {editedSMS[msg.id]?.to ||
                                            msg.to ||
                                            "N/A"}
                                          <PhoneStatusBadge phone={editedSMS[msg.id]?.to || msg.to} compact ml={2} />
                                          {editedSMS[msg.id]?.to &&
                                            editedSMS[msg.id].to !== msg.to && (
                                              <Badge
                                                ml={2}
                                                colorScheme="blue"
                                                fontSize="xs"
                                              >
                                                Edited
                                              </Badge>
                                            )}
                                          {smsStatusSchemas.length > 1 && msg._schema && (
                                            <Badge ml={2} variant="outline" fontSize="xs">
                                              {msg._schema}
                                            </Badge>
                                          )}
                                        </Text>
                                        {isFailed && (
                                          <HStack spacing={2}>
                                            {splitSMS[msg.id] && (
                                              <Badge
                                                colorScheme="green"
                                                fontSize="xs"
                                              >
                                                Split
                                              </Badge>
                                            )}
                                            <IconButton
                                              aria-label="Edit SMS"
                                              icon={<EditIcon />}
                                              size="xs"
                                              colorScheme="blue"
                                              variant="ghost"
                                              onClick={() => handleEditSMS(msg)}
                                            />
                                          </HStack>
                                        )}
                                      </Flex>
                                      {isFailed && (
                                        <Text
                                          fontSize="xs"
                                          color="red.600"
                                          mt={1}
                                          cursor="pointer"
                                          textDecoration="underline"
                                          onClick={async () => {
                                            // Store the entire message object for full error log
                                            setSelectedError(msg);
                                            setTwilioErrorDetails(null);

                                            // If message_sid exists, fetch full details from Twilio
                                            if (msg.message_sid) {
                                              const twilioDetails =
                                                await fetchTwilioMessageDetails(
                                                  msg.message_sid,
                                                );
                                              if (twilioDetails) {
                                                setTwilioErrorDetails(
                                                  twilioDetails,
                                                );
                                              }
                                            }

                                            onErrorDialogOpen();
                                          }}
                                          _hover={{ color: "red.700" }}
                                        >
                                          Error:{" "}
                                          {msg.error_code ||
                                            msg.error_message ||
                                            "Unknown error"}{" "}
                                          (Click to view full details)
                                        </Text>
                                      )}
                                      <Text
                                        fontSize="sm"
                                        color="gray.600"
                                        mt={1}
                                      >
                                        {editedSMS[msg.id]?.message
                                          ? editedSMS[msg.id].message
                                          : msg.message?.substring(0, 150) ||
                                            ""}
                                        {!editedSMS[msg.id]?.message &&
                                          msg.message?.length > 150 &&
                                          "..."}
                                        {editedSMS[msg.id]?.message &&
                                          editedSMS[msg.id].message !==
                                            msg.message && (
                                            <Badge
                                              ml={2}
                                              colorScheme="blue"
                                              fontSize="xs"
                                            >
                                              Edited
                                            </Badge>
                                          )}
                                      </Text>
                                    </Box>
                                  );
                                })}
                              </VStack>
                            ) : (
                              <Text color="gray.500" fontStyle="italic">
                                {selectedErrorCodes.length > 0
                                  ? "No SMS messages found matching the selected error code filters"
                                  : "No SMS messages found for this date"}
                              </Text>
                            )}
                          </Box>
                        </>
                      )}

                      {/* Resend Failed SMS Button */}
                      <Box pt={4}>
                        {/* Select All / Deselect All Button */}
                        <Flex justify="space-between" align="center" mb={3}>
                          <Text fontSize="sm" fontWeight="bold">
                            Selected:{" "}
                            {
                              Object.keys(selectedSMSForResend).filter(
                                (id) => selectedSMSForResend[id],
                              ).length
                            }{" "}
                            of {getFilteredFailedSMS().length} failed SMS
                          </Text>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                              onClick={() => {
                                const allSelected = {};
                                getFilteredFailedSMS().forEach((msg) => {
                                  if (msg.id) {
                                    allSelected[msg.id] = true;
                                  }
                                });
                                setSelectedSMSForResend(allSelected);
                              }}
                            >
                              Select All
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="gray"
                              onClick={() => setSelectedSMSForResend({})}
                            >
                              Deselect All
                            </Button>
                          </HStack>
                        </Flex>

                        <FormControl mb={3}>
                          <Checkbox
                            isChecked={splitAllMessages}
                            onChange={(e) =>
                              setSplitAllMessages(e.target.checked)
                            }
                            colorScheme="blue"
                          >
                            <Text fontSize="sm">
                              Split all long messages into multiple parts when
                              resending
                            </Text>
                          </Checkbox>
                          <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                            This will override individual message split
                            settings. Messages longer than your configured limit
                            ({customSmsCharacterLimit} chars, Change Limit) will
                            be split.
                          </Text>
                        </FormControl>
                        <Button
                          colorScheme="red"
                          onClick={handleResendAllFailedSMS}
                          isDisabled={
                            Object.keys(selectedSMSForResend).filter(
                              (id) => selectedSMSForResend[id],
                            ).length === 0 || resendingFailedSMS
                          }
                          isLoading={resendingFailedSMS}
                          loadingText="Resending..."
                          width="100%"
                        >
                          Resend Selected SMS (
                          {
                            Object.keys(selectedSMSForResend).filter(
                              (id) => selectedSMSForResend[id],
                            ).length
                          }
                          )
                          {splitAllMessages && (
                            <Badge ml={2} colorScheme="blue" fontSize="xs">
                              Split Enabled
                            </Badge>
                          )}
                        </Button>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* Tab 2: By Appointment Date */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      {/* Calendar Selector for Appointment Date */}
                      <Box>
                        <Flex justify="space-between" align="center" mb={3}>
                          <Text fontWeight="bold">Select Appointment Date</Text>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous month"
                              icon={<ChevronLeftIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newMonth = new Date(
                                  appointmentCalendarMonth,
                                );
                                newMonth.setMonth(newMonth.getMonth() - 1);
                                setAppointmentCalendarMonth(newMonth);
                                setCurrentAppointmentMonth(newMonth);
                                setPreloadedAppointmentMonth(null);
                                fetchSMSCountsForAppointmentMonth(
                                  newMonth.getFullYear(),
                                  newMonth.getMonth(),
                                );
                              }}
                            />
                            <Text
                              fontWeight="bold"
                              minW="120px"
                              textAlign="center"
                            >
                              {appointmentCalendarMonth.toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                  year: "numeric",
                                },
                              )}
                            </Text>
                            <IconButton
                              aria-label="Next month"
                              icon={<ChevronRightIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newMonth = new Date(
                                  appointmentCalendarMonth,
                                );
                                newMonth.setMonth(newMonth.getMonth() + 1);
                                setAppointmentCalendarMonth(newMonth);
                                setCurrentAppointmentMonth(newMonth);
                                setPreloadedAppointmentMonth(null);
                                fetchSMSCountsForAppointmentMonth(
                                  newMonth.getFullYear(),
                                  newMonth.getMonth(),
                                );
                              }}
                            />
                          </HStack>
                        </Flex>

                        {loadingAppointmentCalendarData ? (
                          <Center py={8}>
                            <Spinner size="md" />
                            <Text ml={3}>Loading calendar...</Text>
                          </Center>
                        ) : (
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            overflow="hidden"
                          >
                            {/* Calendar Header */}
                            <Grid
                              templateColumns="repeat(7, 1fr)"
                              bg="orange.100"
                              p={2}
                            >
                              {[
                                "Sun",
                                "Mon",
                                "Tue",
                                "Wed",
                                "Thu",
                                "Fri",
                                "Sat",
                              ].map((day) => (
                                <Text
                                  key={day}
                                  fontWeight="bold"
                                  fontSize="xs"
                                  textAlign="center"
                                  color="gray.600"
                                >
                                  {day}
                                </Text>
                              ))}
                            </Grid>

                            {/* Calendar Days */}
                            <Grid
                              templateColumns="repeat(7, 1fr)"
                              gap={1}
                              p={2}
                            >
                              {(() => {
                                const year =
                                  appointmentCalendarMonth.getFullYear();
                                const month =
                                  appointmentCalendarMonth.getMonth();
                                const firstDayOfMonth = new Date(
                                  year,
                                  month,
                                  1,
                                ).getDay();
                                const daysInMonth = new Date(
                                  year,
                                  month + 1,
                                  0,
                                ).getDate();
                                const today = new Date();

                                const calendarDays = [];

                                // Empty cells for days before the first day of the month
                                for (let i = 0; i < firstDayOfMonth; i++) {
                                  calendarDays.push(<Box key={`empty-${i}`} />);
                                }

                                // Days of the month
                                for (let day = 1; day <= daysInMonth; day++) {
                                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                  // For the selected date, use the actual categorized counts from fetchSMSByAppointmentDate
                                  const isSelected =
                                    dateStr === selectedAppointmentDate;
                                  const isToday =
                                    dateStr ===
                                    today.toISOString().split("T")[0];

                                  // Use categorized counts for selected date, otherwise use pre-computed counts
                                  let failedCount;
                                  let totalCount;
                                  if (isSelected) {
                                    totalCount =
                                      allSMSForAppointmentDate.length || 0;
                                    failedCount =
                                      failedSMSForAppointmentDate.length || 0;
                                  } else {
                                    failedCount =
                                      failedSMSCountsByAppointmentDate[
                                        dateStr
                                      ] || 0;
                                    totalCount =
                                      totalSMSCountsByAppointmentDate[
                                        dateStr
                                      ] || 0;
                                  }

                                  calendarDays.push(
                                    <Box
                                      key={dateStr}
                                      p={2}
                                      textAlign="center"
                                      cursor="pointer"
                                      borderRadius="md"
                                      bg={
                                        isSelected
                                          ? "orange.500"
                                          : isToday
                                            ? "orange.50"
                                            : "white"
                                      }
                                      color={isSelected ? "white" : "gray.800"}
                                      borderWidth="1px"
                                      borderColor={
                                        isSelected
                                          ? "orange.500"
                                          : isToday
                                            ? "orange.300"
                                            : "gray.200"
                                      }
                                      position="relative"
                                      onClick={() => {
                                        setSelectedAppointmentDate(dateStr);
                                        fetchSMSByAppointmentDate(dateStr);
                                      }}
                                      _hover={{
                                        bg: isSelected
                                          ? "orange.600"
                                          : "gray.50",
                                      }}
                                      transition="all 0.2s"
                                    >
                                      <Text
                                        fontWeight={
                                          isSelected ? "bold" : "normal"
                                        }
                                      >
                                        {day}
                                      </Text>
                                      {totalCount > 0 && (
                                        <>
                                          {/* Total count badge - green */}
                                          <Badge
                                            position="absolute"
                                            bottom="-2px"
                                            right="-2px"
                                            colorScheme="green"
                                            fontSize="xs"
                                            minW="18px"
                                            h="18px"
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            zIndex={1}
                                          >
                                            {totalCount}
                                          </Badge>
                                          {/* Failed count badge - red */}
                                          {failedCount > 0 && (
                                            <Badge
                                              position="absolute"
                                              top="-2px"
                                              right="-2px"
                                              colorScheme="red"
                                              fontSize="xs"
                                              minW="18px"
                                              h="18px"
                                              borderRadius="full"
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              zIndex={2}
                                            >
                                              {failedCount}
                                            </Badge>
                                          )}
                                        </>
                                      )}
                                    </Box>,
                                  );
                                }

                                return calendarDays;
                              })()}
                            </Grid>
                          </Box>
                        )}

                        {/* Legend */}
                        {Object.keys(totalSMSCountsByAppointmentDate).length >
                          0 && (
                          <Flex mt={3} align="center" gap={4} flexWrap="wrap">
                            <Flex align="center" gap={2}>
                              <Box
                                w="4"
                                h="4"
                                bg="orange.500"
                                borderRadius="md"
                              />
                              <Text fontSize="xs">Selected</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Box
                                w="4"
                                h="4"
                                bg="orange.50"
                                borderWidth="1px"
                                borderColor="orange.300"
                                borderRadius="md"
                              />
                              <Text fontSize="xs">Today</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Badge
                                colorScheme="green"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                #
                              </Badge>
                              <Text fontSize="xs">Total SMS</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                              <Badge
                                colorScheme="red"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                #
                              </Badge>
                              <Text fontSize="xs">Failed</Text>
                            </Flex>
                          </Flex>
                        )}
                      </Box>

                      {/* Health Status Overview for Appointment Date */}
                      {allSMSForAppointmentDate.length > 0 && (
                        <>
                          <Box
                            p={4}
                            bg="orange.50"
                            borderRadius="md"
                            border="1px"
                            borderColor="orange.200"
                          >
                            <Text
                              fontWeight="bold"
                              fontSize="lg"
                              mb={3}
                              color="orange.700"
                            >
                              Health Status (Closest to Appointment Date)
                            </Text>
                            <VStack spacing={2} align="stretch">
                              <Flex justify="space-between">
                                <Text>Total SMS:</Text>
                                <Text fontWeight="bold">
                                  {allSMSForAppointmentDate.length}
                                </Text>
                              </Flex>
                              <Flex justify="space-between">
                                <Text>Failed SMS:</Text>
                                <Text fontWeight="bold" color="red.500">
                                  {failedSMSForAppointmentDate.length}
                                </Text>
                              </Flex>
                              {allSMSForAppointmentDate.length > 0 && (
                                <Flex justify="space-between">
                                  <Text>Success Rate:</Text>
                                  <Text fontWeight="bold" color="green.500">
                                    {Math.round(
                                      ((allSMSForAppointmentDate.length -
                                        failedSMSForAppointmentDate.length) /
                                        allSMSForAppointmentDate.length) *
                                        100,
                                    )}
                                    %
                                  </Text>
                                </Flex>
                              )}
                            </VStack>
                          </Box>

                          {/* Sync Messages Button with Progress - for Appointment Date */}
                          <Box
                            p={4}
                            bg="blue.50"
                            borderRadius="md"
                            border="1px"
                            borderColor="blue.200"
                          >
                            <Button
                              colorScheme="blue"
                              size="sm"
                              leftIcon={<RepeatIcon />}
                              isLoading={syncingSMSMessages && !syncProgress}
                              loadingText="Starting..."
                              isDisabled={syncingSMSMessages && !!syncProgress}
                              onClick={async () => {
                                setSyncingSMSMessages(true);
                                setSyncProgress(null);
                                try {
                                  const result = await syncTwilioMessages(true);
                                  const syncId = result?.sync_id;

                                  if (syncId) {
                                    // Poll for progress
                                    const pollProgress = async () => {
                                      let attempts = 0;
                                      const maxAttempts = 120; // 2 minutes max

                                      while (attempts < maxAttempts) {
                                        const progress =
                                          await getSyncProgress(syncId);
                                        setSyncProgress(progress);

                                        if (
                                          progress?.is_complete ||
                                          progress?.error
                                        ) {
                                          break;
                                        }

                                        await new Promise((resolve) =>
                                          setTimeout(resolve, 1000),
                                        );
                                        attempts++;
                                      }
                                    };

                                    await pollProgress();
                                  }

                                  // Refresh both views
                                  await fetchSMSHealthStatus(selectedSMSDate);
                                  await fetchFailedSMSCountsForMonth(
                                    calendarMonth.getFullYear(),
                                    calendarMonth.getMonth(),
                                  );
                                  await fetchSMSCountsForAppointmentMonth(
                                    appointmentCalendarMonth.getFullYear(),
                                    appointmentCalendarMonth.getMonth(),
                                  );
                                  await fetchSMSByAppointmentDate(
                                    selectedAppointmentDate,
                                  );

                                  toast({
                                    title: "Messages Synced",
                                    description: syncProgress?.synced
                                      ? `Successfully synced ${syncProgress.synced} of ${syncProgress.total} messages`
                                      : "SMS messages have been synced from Twilio",
                                    status: "success",
                                    duration: 4000,
                                  });
                                } catch (error) {
                                  console.error(
                                    "Error syncing messages:",
                                    error,
                                  );
                                  toast({
                                    title: "Sync Failed",
                                    description:
                                      error.message ||
                                      "Failed to sync messages",
                                    status: "error",
                                    duration: 5000,
                                  });
                                } finally {
                                  setSyncingSMSMessages(false);
                                  setSyncProgress(null);
                                }
                              }}
                            >
                              {syncProgress
                                ? `${syncProgress.synced || 0}/${syncProgress.total || 0} Synced`
                                : "Sync Messages"}
                            </Button>

                            {/* Progress Bar */}
                            {syncProgress && (
                              <Box mt={2}>
                                <Progress
                                  value={
                                    syncProgress.total > 0
                                      ? ((syncProgress.synced || 0) /
                                          syncProgress.total) *
                                        100
                                      : 0
                                  }
                                  size="sm"
                                  colorScheme="blue"
                                  borderRadius="full"
                                  hasStripe
                                  isAnimated
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  {syncProgress.synced || 0} of{" "}
                                  {syncProgress.total || 0} messages synced
                                </Text>
                              </Box>
                            )}
                          </Box>

                          {/* Error Code Filter for Appointment Date */}
                          {getAppointmentDateUniqueErrorCodes().length > 0 && (
                            <Box
                              p={4}
                              bg="orange.50"
                              borderRadius="md"
                              border="1px"
                              borderColor="orange.200"
                            >
                              <Text fontWeight="bold" fontSize="md" mb={3}>
                                Filter by Error Code
                              </Text>
                              <VStack
                                spacing={2}
                                align="stretch"
                                maxH="200px"
                                overflowY="auto"
                              >
                                {getAppointmentDateUniqueErrorCodes().map(
                                  (errorCode) => {
                                    const count =
                                      allSMSForAppointmentDate.filter(
                                        (msg) => msg.error_code === errorCode,
                                      ).length;
                                    const mappedDescription =
                                      getTwilioErrorDescription(errorCode);
                                    const firstMessageWithCode =
                                      allSMSForAppointmentDate.find(
                                        (msg) => msg.error_code === errorCode,
                                      );
                                    const dbErrorMessage =
                                      firstMessageWithCode?.error_message || "";
                                    const errorDescription =
                                      mappedDescription || dbErrorMessage;
                                    return (
                                      <Box key={errorCode}>
                                        <Checkbox
                                          isChecked={selectedAppointmentDateErrorCodes.includes(
                                            errorCode,
                                          )}
                                          onChange={() =>
                                            handleAppointmentDateErrorCodeToggle(
                                              errorCode,
                                            )
                                          }
                                          colorScheme="orange"
                                        >
                                          <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                          >
                                            {errorCode} ({count})
                                          </Text>
                                        </Checkbox>
                                        {errorDescription && (
                                          <Box ml={6} mt={-1} mb={1}>
                                            <Text
                                              fontSize="xs"
                                              color="gray.600"
                                              fontWeight="medium"
                                            >
                                              Full Error Description:
                                            </Text>
                                            <Text
                                              fontSize="xs"
                                              color="gray.600"
                                              mt={0.5}
                                            >
                                              {errorDescription}
                                            </Text>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  },
                                )}
                              </VStack>
                              {selectedAppointmentDateErrorCodes.length > 0 && (
                                <Button
                                  size="xs"
                                  variant="link"
                                  colorScheme="orange"
                                  mt={2}
                                  onClick={() =>
                                    setSelectedAppointmentDateErrorCodes([])
                                  }
                                >
                                  Clear Filters
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* All SMS Messages for Appointment Date (using categorization) */}
                          <Box>
                            <Text fontWeight="bold" fontSize="lg" mb={3}>
                              SMS Messages for{" "}
                              {formatDateString(selectedAppointmentDate)} (
                              {selectedAppointmentDateErrorCodes.length > 0
                                ? `${getFilteredAppointmentDateMessages().length} of ${allSMSForAppointmentDate.length}`
                                : allSMSForAppointmentDate.length}
                              )
                            </Text>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                              Messages are filtered to show only those closest
                              to the appointment date
                            </Text>
                            {getFilteredAppointmentDateMessages() &&
                            getFilteredAppointmentDateMessages().length > 0 ? (
                              <VStack
                                spacing={2}
                                align="stretch"
                                maxH="400px"
                                overflowY="auto"
                              >
                                {getFilteredAppointmentDateMessages().map(
                                  (msg, idx) => {
                                    const isFailed =
                                      msg.status === "failed" ||
                                      msg.status === "undelivered" ||
                                      (msg.error_code &&
                                        msg.error_code !== "None" &&
                                        msg.error_code !== null &&
                                        msg.error_code !== "");

                                    return (
                                      <Box
                                        key={idx}
                                        p={3}
                                        border="1px"
                                        borderColor={
                                          isFailed ? "red.200" : "gray.200"
                                        }
                                        borderRadius="md"
                                        bg={isFailed ? "red.50" : "white"}
                                      >
                                        {/* Checkbox for selecting SMS to resend - only show for failed SMS */}
                                        {isFailed && (
                                          <Flex justify="flex-start" mb={2}>
                                            <Checkbox
                                              isChecked={
                                                selectedAppointmentDateSMSForResend[
                                                  msg.id
                                                ] || false
                                              }
                                              onChange={(e) => {
                                                setSelectedAppointmentDateSMSForResend(
                                                  (prev) => ({
                                                    ...prev,
                                                    [msg.id]: e.target.checked,
                                                  }),
                                                );
                                              }}
                                              colorScheme="red"
                                            >
                                              <Text
                                                fontSize="sm"
                                                fontWeight="bold"
                                              >
                                                Select to resend
                                              </Text>
                                            </Checkbox>
                                          </Flex>
                                        )}
                                        <Flex justify="space-between" mb={1}>
                                          <Badge
                                            colorScheme={
                                              msg.status === "sent" ||
                                              msg.status === "delivered"
                                                ? "green"
                                                : msg.status === "failed" ||
                                                    msg.status === "undelivered"
                                                  ? "red"
                                                  : "yellow"
                                            }
                                          >
                                            {msg.status || "unknown"}
                                          </Badge>
                                          <Text fontSize="xs" color="gray.500">
                                            {msg.created_at
                                              ? new Date(
                                                  msg.created_at,
                                                ).toLocaleString()
                                              : "N/A"}
                                          </Text>
                                        </Flex>
                                        <Flex
                                          justify="space-between"
                                          align="center"
                                          mb={1}
                                        >
                                          <Text fontSize="sm" fontWeight="bold">
                                            To: {msg.to || "N/A"}
                                            <PhoneStatusBadge phone={msg.to} compact ml={2} />
                                            {smsStatusSchemas.length > 1 && msg._schema && (
                                              <Badge ml={2} variant="outline" fontSize="xs">
                                                {msg._schema}
                                              </Badge>
                                            )}
                                          </Text>
                                          {isFailed && (
                                            <Badge
                                              colorScheme="red"
                                              fontSize="xs"
                                            >
                                              Failed
                                            </Badge>
                                          )}
                                        </Flex>
                                        {isFailed && (
                                          <Text
                                            fontSize="xs"
                                            color="red.600"
                                            mt={1}
                                            cursor="pointer"
                                            textDecoration="underline"
                                            onClick={async () => {
                                              setSelectedError(msg);
                                              setTwilioErrorDetails(null);
                                              if (msg.message_sid) {
                                                const twilioDetails =
                                                  await fetchTwilioMessageDetails(
                                                    msg.message_sid,
                                                  );
                                                if (twilioDetails) {
                                                  setTwilioErrorDetails(
                                                    twilioDetails,
                                                  );
                                                }
                                              }
                                              onErrorDialogOpen();
                                            }}
                                            _hover={{ color: "red.700" }}
                                          >
                                            Error:{" "}
                                            {msg.error_code ||
                                              msg.error_message ||
                                              "Unknown error"}{" "}
                                            (Click to view full details)
                                          </Text>
                                        )}
                                        <Text
                                          fontSize="sm"
                                          color="gray.600"
                                          mt={1}
                                        >
                                          {msg.message?.substring(0, 150) || ""}
                                          {msg.message?.length > 150 && "..."}
                                        </Text>
                                        <Text
                                          fontSize="xs"
                                          color="gray.500"
                                          mt={1}
                                        >
                                          Appointment Date:{" "}
                                          {msg.appointment_date || "N/A"}
                                        </Text>
                                      </Box>
                                    );
                                  },
                                )}
                              </VStack>
                            ) : (
                              <Text color="gray.500" fontStyle="italic">
                                {selectedAppointmentDateErrorCodes.length > 0
                                  ? "No SMS messages found matching the selected error code filters"
                                  : "No SMS messages found for this appointment date"}
                              </Text>
                            )}
                          </Box>
                        </>
                      )}

                      {/* Empty state for no messages */}
                      {allSMSForAppointmentDate.length === 0 &&
                        !loadingSMSByAppointmentDate && (
                          <Box
                            p={4}
                            bg="gray.50"
                            borderRadius="md"
                            textAlign="center"
                          >
                            <Text color="gray.500" fontStyle="italic">
                              No SMS messages found for appointment date:{" "}
                              {formatDateString(selectedAppointmentDate)}
                            </Text>
                          </Box>
                        )}

                      {/* Resend Failed SMS Button for Appointment Date */}
                      {failedSMSForAppointmentDate.length > 0 && (
                        <Box pt={4}>
                          {/* Select All / Deselect All Button */}
                          <Flex justify="space-between" align="center" mb={3}>
                            <Text fontSize="sm" fontWeight="bold">
                              Selected:{" "}
                              {
                                Object.keys(
                                  selectedAppointmentDateSMSForResend,
                                ).filter(
                                  (id) =>
                                    selectedAppointmentDateSMSForResend[id],
                                ).length
                              }{" "}
                              of{" "}
                              {getFilteredFailedSMSForAppointmentDate().length}{" "}
                              failed SMS
                            </Text>
                            <HStack spacing={2}>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="orange"
                                onClick={() => {
                                  const allSelected = {};
                                  getFilteredFailedSMSForAppointmentDate().forEach(
                                    (msg) => {
                                      if (msg.id) {
                                        allSelected[msg.id] = true;
                                      }
                                    },
                                  );
                                  setSelectedAppointmentDateSMSForResend(
                                    allSelected,
                                  );
                                }}
                              >
                                Select All
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="gray"
                                onClick={() =>
                                  setSelectedAppointmentDateSMSForResend({})
                                }
                              >
                                Deselect All
                              </Button>
                            </HStack>
                          </Flex>
                          <FormControl mb={3}>
                            <Checkbox
                              isChecked={splitAllMessages}
                              onChange={(e) =>
                                setSplitAllMessages(e.target.checked)
                              }
                              colorScheme="blue"
                            >
                              <Text fontSize="sm">
                                Split message into parts when resending
                              </Text>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                              Messages longer than your configured limit (
                              {customSmsCharacterLimit} chars) will be split
                              into multiple SMS parts.
                            </Text>
                          </FormControl>
                          <Button
                            colorScheme="red"
                            onClick={handleResendAllFailedSMSForAppointmentDate}
                            isDisabled={
                              Object.keys(
                                selectedAppointmentDateSMSForResend,
                              ).filter(
                                (id) => selectedAppointmentDateSMSForResend[id],
                              ).length === 0 || resendingFailedSMS
                            }
                            isLoading={resendingFailedSMS}
                            loadingText="Resending..."
                            width="100%"
                          >
                            Resend Selected SMS (
                            {
                              Object.keys(
                                selectedAppointmentDateSMSForResend,
                              ).filter(
                                (id) => selectedAppointmentDateSMSForResend[id],
                              ).length
                            }
                            )
                          </Button>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Tab 3: Failed numbers (public.sms_number_health) */}
                  <TabPanel px={0}>
                    <FailedNumbersPanel
                      key={`${providersKey}#${numberHealthGen}`}
                      isActive={activeSMSTab === 2}
                      schemas={smsStatusSchemas}
                      homeSchema={homeSchema}
                    />
                  </TabPanel>

                  {/* Tab 4: Good numbers, each with its last SMS */}
                  <TabPanel px={0}>
                    <GoodNumbersPanel
                      key={`${providersKey}#${numberHealthGen}`}
                      isActive={activeSMSTab === 3}
                      schemas={smsStatusSchemas}
                      homeSchema={homeSchema}
                    />
                  </TabPanel>

                  {/* Tab 5: Patient numbers never texted */}
                  <TabPanel px={0}>
                    <NotTextedPanel
                      key={`${providersKey}#${numberHealthGen}`}
                      isActive={activeSMSTab === 4}
                      schemas={smsStatusSchemas}
                      homeSchema={homeSchema}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => setIsSMSStatusModalOpen(false)}
            >
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                void (async () => {
                  await fetchSMSHealthStatus(selectedSMSDate);
                  await fetchFailedSMSCountsForMonth(
                    calendarMonth.getFullYear(),
                    calendarMonth.getMonth(),
                  );
                  await fetchSMSCountsForAppointmentMonth(
                    appointmentCalendarMonth.getFullYear(),
                    appointmentCalendarMonth.getMonth(),
                  );
                  await fetchSMSByAppointmentDate(selectedAppointmentDate);
                })();
              }}
              isLoading={loadingSMSStatus || loadingSMSByAppointmentDate}
              ml={3}
            >
              Refresh
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Twilio Health Status Modal - Full Details */}
      <Modal
        isOpen={isTwilioHealthModalOpen}
        onClose={() => setIsTwilioHealthModalOpen(false)}
        isCentered
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent
          maxH="90vh"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader flexShrink={0}>Twilio Health Status</ModalHeader>
          <ModalCloseButton flexShrink={0} />
          <ModalBody overflowY="auto" overflowX="hidden" flex="1">
            <TwilioHealthStatus />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Error Details Dialog */}
      <AlertDialog
        isOpen={isErrorDialogOpen}
        onClose={() => {
          setTwilioErrorDetails(null);
          onErrorDialogClose();
        }}
        isCentered
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="600px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.600">
              SMS Error Details
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={3} align="stretch" maxH="70vh" overflowY="auto">
                {selectedError && (
                  <>
                    <Box>
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="red.600"
                        mb={3}
                      >
                        Complete Error Log
                      </Text>
                    </Box>

                    {/* Basic Information */}
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <Text
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.700"
                        mb={2}
                      >
                        Basic Information:
                      </Text>
                      {selectedError.to && (
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm" color="gray.600">
                            To:
                          </Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {selectedError.to}
                            <PhoneStatusBadge phone={selectedError.to} ml={2} />
                          </Text>
                        </Flex>
                      )}
                      {selectedError.from && (
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm" color="gray.600">
                            From:
                          </Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {selectedError.from}
                          </Text>
                        </Flex>
                      )}
                      {selectedError.created_at && (
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm" color="gray.600">
                            Date/Time:
                          </Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {new Date(
                              selectedError.created_at,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                      )}
                      {selectedError.status && (
                        <Flex justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Status:
                          </Text>
                          <Badge
                            colorScheme={
                              selectedError.status === "sent" ||
                              selectedError.status === "delivered"
                                ? "green"
                                : selectedError.status === "failed" ||
                                    selectedError.status === "undelivered"
                                  ? "red"
                                  : "yellow"
                            }
                          >
                            {selectedError.status}
                          </Badge>
                        </Flex>
                      )}
                    </Box>

                    {/* Loading Twilio Details */}
                    {loadingTwilioDetails && (
                      <Box
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="blue.200"
                      >
                        <Flex align="center">
                          <Spinner size="sm" mr={2} />
                          <Text fontSize="sm" color="blue.700">
                            Fetching full error details from Twilio...
                          </Text>
                        </Flex>
                      </Box>
                    )}

                    {/* Twilio Full Error Details */}
                    {twilioErrorDetails && (
                      <Box
                        p={4}
                        bg="red.50"
                        borderRadius="md"
                        border="2px"
                        borderColor="red.400"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="md"
                          color="red.700"
                          mb={3}
                        >
                          Complete Twilio Error Log:
                        </Text>

                        {/* Error Description - Most Prominent - Show FULL description */}
                        {(twilioErrorDetails.error_message ||
                          twilioErrorDetails.error_code) && (
                          <Box mb={3}>
                            <Text
                              fontWeight="bold"
                              fontSize="sm"
                              color="red.800"
                              mb={2}
                            >
                              Full Error Description:
                            </Text>
                            {twilioErrorDetails.error_message ? (
                              <Text
                                fontSize="md"
                                color="red.900"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                                p={3}
                                bg="white"
                                borderRadius="md"
                                border="2px"
                                borderColor="red.400"
                                fontWeight="medium"
                                lineHeight="1.6"
                                maxH="none"
                                overflowY="visible"
                              >
                                {twilioErrorDetails.error_message}
                              </Text>
                            ) : (
                              <Text
                                fontSize="md"
                                color="red.900"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                                p={3}
                                bg="yellow.50"
                                borderRadius="md"
                                border="2px"
                                borderColor="yellow.400"
                                fontWeight="medium"
                                lineHeight="1.6"
                                fontStyle="italic"
                              >
                                ⚠️ Error description not available from Twilio
                                for error code {twilioErrorDetails.error_code}.
                                Please refer to Twilio documentation for error
                                code {twilioErrorDetails.error_code}.
                              </Text>
                            )}
                            {twilioErrorDetails.error_description_source && (
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                mt={1}
                                fontStyle="italic"
                              >
                                (Description retrieved from error code lookup
                                table)
                              </Text>
                            )}
                          </Box>
                        )}

                        {/* Error Code */}
                        {twilioErrorDetails.error_code && (
                          <Box mb={3}>
                            <Text
                              fontWeight="bold"
                              fontSize="xs"
                              color="gray.700"
                              mb={1}
                            >
                              Error Code:
                            </Text>
                            <Text
                              fontFamily="mono"
                              fontSize="sm"
                              color="red.700"
                              p={2}
                              bg="white"
                              borderRadius="sm"
                              border="1px"
                              borderColor="red.300"
                            >
                              {twilioErrorDetails.error_code}
                            </Text>
                          </Box>
                        )}

                        {/* Show ALL error-related fields from Twilio */}
                        <Box
                          mt={4}
                          pt={3}
                          borderTop="1px"
                          borderColor="red.300"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="xs"
                            color="gray.700"
                            mb={2}
                          >
                            All Error Details from Twilio:
                          </Text>
                          <VStack spacing={2} align="stretch">
                            {Object.entries(twilioErrorDetails).map(
                              ([key, value]) => {
                                // Skip fields already displayed above and non-error fields
                                const skipFields = [
                                  "error_message",
                                  "error_code",
                                  "sid",
                                  "account_sid",
                                  "to",
                                  "from",
                                  "body",
                                  "date_sent",
                                  "date_created",
                                  "date_updated",
                                  "num_segments",
                                  "num_media",
                                  "direction",
                                  "price",
                                  "price_unit",
                                  "api_version",
                                  "uri",
                                  "status",
                                ];

                                if (
                                  skipFields.includes(key) ||
                                  value === null ||
                                  value === undefined ||
                                  value === ""
                                ) {
                                  return null;
                                }

                                // Show error-related or status-related fields
                                if (
                                  key.toLowerCase().includes("error") ||
                                  key.toLowerCase().includes("status") ||
                                  key.toLowerCase().includes("message") ||
                                  key.toLowerCase().includes("info") ||
                                  key.toLowerCase().includes("code")
                                ) {
                                  return (
                                    <Box key={key}>
                                      <Text
                                        fontWeight="bold"
                                        fontSize="xs"
                                        color="gray.600"
                                        mb={1}
                                      >
                                        {key
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (l) =>
                                            l.toUpperCase(),
                                          )}
                                        :
                                      </Text>
                                      <Text
                                        fontSize="sm"
                                        color="gray.800"
                                        whiteSpace="pre-wrap"
                                        wordBreak="break-word"
                                        fontFamily={
                                          typeof value === "object"
                                            ? "mono"
                                            : "inherit"
                                        }
                                        p={2}
                                        bg="white"
                                        borderRadius="sm"
                                        border="1px"
                                        borderColor="gray.300"
                                        maxH="200px"
                                        overflowY="auto"
                                      >
                                        {typeof value === "object" &&
                                        value !== null
                                          ? JSON.stringify(value, null, 2)
                                          : String(value)}
                                      </Text>
                                    </Box>
                                  );
                                }
                                return null;
                              },
                            )}
                          </VStack>
                        </Box>
                      </Box>
                    )}

                    {/* Fallback: Show database error only if Twilio details are not available */}
                    {!twilioErrorDetails &&
                      (selectedError.error_code ||
                        selectedError.error_message) && (
                        <Box
                          p={4}
                          bg="red.50"
                          borderRadius="md"
                          border="2px"
                          borderColor="red.400"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="md"
                            color="red.700"
                            mb={3}
                          >
                            Error Details:
                          </Text>

                          {selectedError.error_message && (
                            <Box mb={3}>
                              <Text
                                fontWeight="bold"
                                fontSize="sm"
                                color="red.800"
                                mb={2}
                              >
                                Error Description:
                              </Text>
                              <Text
                                fontSize="md"
                                color="red.900"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                                p={3}
                                bg="white"
                                borderRadius="md"
                                border="2px"
                                borderColor="red.400"
                                fontWeight="medium"
                                lineHeight="1.6"
                              >
                                {selectedError.error_message}
                              </Text>
                            </Box>
                          )}

                          {selectedError.error_code && (
                            <Box>
                              <Text
                                fontWeight="bold"
                                fontSize="xs"
                                color="gray.700"
                                mb={1}
                              >
                                Error Code:
                              </Text>
                              <Text
                                fontFamily="mono"
                                fontSize="sm"
                                color="red.700"
                                p={2}
                                bg="white"
                                borderRadius="sm"
                                border="1px"
                                borderColor="red.300"
                              >
                                {selectedError.error_code}
                              </Text>
                            </Box>
                          )}
                        </Box>
                      )}

                    {!twilioErrorDetails &&
                      !selectedError.error_code &&
                      !selectedError.error_message && (
                        <Box
                          p={3}
                          bg="yellow.50"
                          borderRadius="md"
                          border="1px"
                          borderColor="yellow.200"
                        >
                          <Text
                            color="yellow.800"
                            fontStyle="italic"
                            fontSize="sm"
                          >
                            ⚠️ No error code or error message available. This
                            may be a status-based error.
                          </Text>
                        </Box>
                      )}

                    {/* Additional Fields - Show all available fields */}
                    <Box mt={2}>
                      <Text
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.700"
                        mb={2}
                      >
                        All Available Fields:
                      </Text>
                      <Box
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        maxH="300px"
                        overflowY="auto"
                        border="1px"
                        borderColor="gray.200"
                      >
                        <VStack spacing={2} align="stretch">
                          {Object.entries(selectedError).map(([key, value]) => {
                            // Skip fields already displayed above
                            if (
                              [
                                "to",
                                "from",
                                "created_at",
                                "status",
                                "error_code",
                                "error_message",
                              ].includes(key)
                            ) {
                              return null;
                            }

                            return (
                              <Box key={key}>
                                <Text
                                  fontWeight="bold"
                                  fontSize="xs"
                                  color="gray.600"
                                  mb={1}
                                >
                                  {key}:
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.800"
                                  whiteSpace="pre-wrap"
                                  wordBreak="break-word"
                                  fontFamily={
                                    typeof value === "object"
                                      ? "mono"
                                      : "inherit"
                                  }
                                  p={1}
                                  bg="white"
                                  borderRadius="sm"
                                >
                                  {typeof value === "object" && value !== null
                                    ? JSON.stringify(value, null, 2)
                                    : String(value || "N/A")}
                                </Text>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    </Box>
                  </>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onErrorDialogClose} colorScheme="blue">
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit SMS Modal */}
      <Modal
        isOpen={isEditSMSModalOpen}
        onClose={handleCancelEditSMS}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit SMS Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingSMSId && editedSMS[editingSMSId] && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Phone Number (To)</FormLabel>
                  <Input
                    value={editedSMS[editingSMSId].to}
                    onChange={(e) => {
                      setEditedSMS((prev) => ({
                        ...prev,
                        [editingSMSId]: {
                          ...prev[editingSMSId],
                          to: e.target.value,
                        },
                      }));
                    }}
                    placeholder="Enter phone number"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    value={editedSMS[editingSMSId].message}
                    onChange={(e) => {
                      setEditedSMS((prev) => ({
                        ...prev,
                        [editingSMSId]: {
                          ...prev[editingSMSId],
                          message: e.target.value,
                        },
                      }));
                    }}
                    placeholder="Enter message"
                    rows={8}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Message length:{" "}
                    {(editedSMS[editingSMSId].message || "").length} characters
                  </Text>
                </FormControl>
                <FormControl>
                  <Checkbox
                    isChecked={splitSMS[editingSMSId] || false}
                    onChange={(e) => {
                      setSplitSMS((prev) => ({
                        ...prev,
                        [editingSMSId]: e.target.checked,
                      }));
                    }}
                    colorScheme="blue"
                  >
                    <Text fontSize="sm">
                      Split long message into multiple parts
                      {(editedSMS[editingSMSId].message || "").length >
                        customSmsCharacterLimit && (
                        <Badge ml={2} colorScheme="orange" fontSize="xs">
                          Recommended (over {customSmsCharacterLimit} chars)
                        </Badge>
                      )}
                    </Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                    If enabled, messages longer than your configured limit (
                    {customSmsCharacterLimit} chars) will be split into multiple
                    SMS parts.
                  </Text>
                </FormControl>
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.700">
                    <strong>Note:</strong> These changes will be used when you
                    click "Resend All Failed SMS". The original values are
                    preserved in the database.
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleCancelEditSMS} mr={3}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveEditedSMS}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontWeight="bold">Email Settings</Text>
              {["ZOHO_EMAIL", "ZOHO_PASSWORD", "TWILIO_PHONE_NUMBER"].map(
                (key) => (
                  <FormControl key={key}>
                    <FormLabel>{key}</FormLabel>
                    <Input
                      value={userSettings[key] || ""}
                      onChange={(e) =>
                        setUserSettings((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                ),
              )}

              <Text fontWeight="bold" mt={4}>
                Form URLs
              </Text>
              {FORM_CONFIG.map((form) => (
                <FormControl key={form.type}>
                  <FormLabel>
                    {form.label} ({form.type})
                  </FormLabel>
                  <Input
                    value={userSettings[`${form.type}_FORM_URL`] || ""}
                    onChange={(e) =>
                      setUserSettings((prev) => ({
                        ...prev,
                        [`${form.type}_FORM_URL`]: e.target.value,
                      }))
                    }
                    placeholder={form.defaultUrl}
                  />
                </FormControl>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={saveUserSettings}>
              Save
            </Button>
            <Button variant="ghost" onClick={onSettingsClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSendFormOpen} onClose={onSendFormClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Forms to Multiple Patients</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Contact Method</FormLabel>
              <VStack align="flex-start">
                <Checkbox
                  isChecked={contactMethods.email}
                  onChange={(e) =>
                    setContactMethods((prev) => ({
                      ...prev,
                      email: e.target.checked,
                    }))
                  }
                >
                  Email
                </Checkbox>
                <Checkbox
                  isChecked={contactMethods.phone}
                  onChange={(e) =>
                    setContactMethods((prev) => ({
                      ...prev,
                      phone: e.target.checked,
                    }))
                  }
                >
                  Phone
                </Checkbox>
                {contactMethods.phone && (
                  <Checkbox
                    isChecked={splitMessage}
                    onChange={(e) => setSplitMessage(e.target.checked)}
                  >
                    Split long messages into multiple parts
                  </Checkbox>
                )}
              </VStack>
            </FormControl>

            {/* Patient Contact Information */}
            <FormControl mb={4}>
              <FormLabel>Patient Contact Information</FormLabel>
              <VStack
                align="stretch"
                spacing={4}
                maxHeight="300px"
                overflowY="auto"
                overscrollBehavior="contain"
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
                sx={{
                  scrollBehavior: "smooth",
                  "&:hover": {
                    overflowY: "auto",
                  },
                }}
              >
                {Object.keys(selectedRows)
                  .filter((patientId) => selectedRows[patientId])
                  .map((patientId) => {
                    const patientData = patients.find(
                      (p) => p.id === patientId,
                    );
                    const contactInfo = selectedPatientsContact[patientId] || {
                      email: "",
                      phone: "",
                      name:
                        patientData?.[prompts[0][0]] ||
                        patientData?.name ||
                        "Unknown Patient",
                    };

                    return (
                      <Box
                        key={patientId}
                        p={3}
                        border="1px"
                        borderColor="gray.100"
                        borderRadius="md"
                        bg="gray.50"
                      >
                        <Text fontWeight="bold" mb={2}>
                          {contactInfo.name ||
                            patientData?.[prompts[0][0]] ||
                            patientData?.name ||
                            "Unknown Patient"}
                        </Text>
                        {contactMethods.email && (
                          <FormControl mb={2}>
                            <FormLabel fontSize="sm">Email Address</FormLabel>
                            <Input
                              placeholder="Enter email address"
                              value={contactInfo.email}
                              onChange={(e) => {
                                setSelectedPatientsContact((prev) => ({
                                  ...prev,
                                  [patientId]: {
                                    ...prev[patientId],
                                    email: e.target.value,
                                  },
                                }));
                              }}
                              type="email"
                              size="sm"
                            />
                          </FormControl>
                        )}
                        {contactMethods.phone && (
                          <FormControl mb={2}>
                            <FormLabel fontSize="sm">Phone Number</FormLabel>
                            <Input
                              placeholder="Enter phone number"
                              value={contactInfo.phone}
                              onChange={(e) => {
                                setSelectedPatientsContact((prev) => ({
                                  ...prev,
                                  [patientId]: {
                                    ...prev[patientId],
                                    phone: e.target.value,
                                  },
                                }));
                              }}
                              type="tel"
                              size="sm"
                            />
                          </FormControl>
                        )}
                      </Box>
                    );
                  })}
              </VStack>
            </FormControl>

            <FormControl>
              <FormLabel>Form Type</FormLabel>
              <VStack align="flex-start">
                {/* Show NP/NS for non-FF patients, FFS/FFP for FF patients */}
                {shouldShowFFPOptions && (
                  <Checkbox
                    isChecked={formType.includes(
                      findPatientField(
                        selectedPatient,
                        "type",
                      )?.toUpperCase() || "FFS",
                    )}
                    onChange={handleFormTypeChange(
                      findPatientField(
                        selectedPatient,
                        "type",
                      )?.toUpperCase() || "FFS",
                    )}
                  >
                    {findPatientField(selectedPatient, "type")?.toUpperCase() ||
                      "FFS"}{" "}
                    (Follow-up Form)
                  </Checkbox>
                )}
                <Checkbox
                  isChecked={formType.includes("NP")}
                  onChange={handleFormTypeChange("NP")}
                >
                  NP (New Pulmonary Patient)
                </Checkbox>
                <Checkbox
                  isChecked={formType.includes("NS")}
                  onChange={handleFormTypeChange("NS")}
                >
                  NS (New Sleep Patient)
                </Checkbox>
                <Checkbox
                  isChecked={formType.includes("CL1")}
                  onChange={handleFormTypeChange("CL1")}
                >
                  CL1 (Clinical Form 1)
                </Checkbox>
                <Checkbox
                  isChecked={formType.includes("CL2")}
                  onChange={handleFormTypeChange("CL2")}
                >
                  CL2 (Clinical Form 2)
                </Checkbox>
              </VStack>
            </FormControl>
            {isSendingForms && sendProgress.total > 1 && (
              <Box mt={4}>
                <Text fontSize="sm" mb={1}>
                  Sending forms... ({sendProgress.current} /{" "}
                  {sendProgress.total})
                </Text>
                <Progress
                  value={(sendProgress.current / sendProgress.total) * 100}
                  size="sm"
                  colorScheme="blue"
                  hasStripe
                  isAnimated
                />
              </Box>
            )}
            <HStack mt={6} justifyContent="space-between">
              <Button
                variant="outline"
                colorScheme="purple"
                onClick={generateFormsPreview}
                isDisabled={formType.length === 0}
              >
                Preview
              </Button>
              <HStack>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={sendForms}
                  isLoading={isSendingForms}
                  loadingText="Sending..."
                >
                  Send
                </Button>
                <Button variant="ghost" onClick={onSendFormClose}>
                  Cancel
                </Button>
              </HStack>
            </HStack>

            {/* Preview Section */}
            {showFormsPreview && formsPreviewData && (
              <Box
                mt={4}
                p={4}
                bg="gray.50"
                borderRadius="md"
                border="1px"
                borderColor="gray.200"
              >
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Preview - Forms to be Sent
                </Text>

                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Patients ({formsPreviewData.patientCount})
                    </Text>
                    <Box maxHeight="150px" overflowY="auto" mt={1}>
                      {formsPreviewData.patients.map((patient) => (
                        <Box
                          key={patient.id}
                          p={2}
                          bg="white"
                          borderRadius="md"
                          mb={1}
                          fontSize="sm"
                        >
                          <Text fontWeight="medium">{patient.name}</Text>
                          <Text color="gray.600">Type: {patient.type}</Text>
                          <Text color="gray.500" fontSize="xs">
                            Email: {patient.email} | Phone: {patient.phone}
                            <PhoneStatusBadge phone={patient.phone} compact ml={2} />
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Form Types
                    </Text>
                    <HStack mt={1} flexWrap="wrap">
                      {formsPreviewData.formTypes.map((formType, index) => (
                        <Badge key={index} colorScheme="blue" fontSize="sm">
                          {formType}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Contact Methods
                    </Text>
                    <HStack mt={1}>
                      {formsPreviewData.contactMethods.map((method, index) => (
                        <Badge key={index} colorScheme="green" fontSize="sm">
                          {method}
                        </Badge>
                      ))}
                      {formsPreviewData.splitMessage && (
                        <Badge colorScheme="orange" fontSize="sm">
                          Split Long Messages
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  <Divider />

                  <Box>
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color="gray.600"
                      mb={2}
                    >
                      Actual SMS Message to be Sent
                    </Text>
                    {formsPreviewData.sampleMessageError ? (
                      <Box
                        p={3}
                        bg="red.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="red.200"
                      >
                        <Text fontSize="sm" color="red.600">
                          {formsPreviewData.sampleMessageError}
                        </Text>
                      </Box>
                    ) : (
                      <>
                        <Box
                          p={3}
                          bg="white"
                          borderRadius="md"
                          border="1px"
                          borderColor="green.500"
                          maxHeight="250px"
                          overflowY="auto"
                        >
                          <Text
                            fontSize="xs"
                            whiteSpace="pre-wrap"
                            fontFamily="mono"
                          >
                            {formsPreviewData.sampleMessage}
                          </Text>
                        </Box>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Character count:{" "}
                          {formsPreviewData.sampleMessage?.length || 0}
                        </Text>
                      </>
                    )}
                    {formsPreviewData.formUrls &&
                      formsPreviewData.formUrls.length > 0 && (
                        <Box mt={3}>
                          <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            color="gray.600"
                          >
                            Form URLs that will be sent:
                          </Text>
                          {formsPreviewData.formUrls.map((url, index) => (
                            <Text
                              key={index}
                              fontSize="xs"
                              color="blue.600"
                              wordBreak="break-all"
                            >
                              {index + 1}. {url}
                            </Text>
                          ))}
                        </Box>
                      )}
                  </Box>
                </VStack>

                <Button
                  mt={3}
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowFormsPreview(false)}
                >
                  Close Preview
                </Button>
              </Box>
            )}

            {isFFPatient && (
              <>
                <Divider my={4} />
                <VStack spacing={4} align="stretch">
                  <Text fontSize="lg" fontWeight="bold">
                    Generate Patient Form
                  </Text>
                  {generating ? (
                    <VStack spacing={4}>
                      <Text>Generating form...</Text>
                      <Progress value={progress} width="100%" />
                      <Text fontSize="sm" color="gray.600">
                        {progress < 40 && "Initializing..."}
                        {progress >= 40 &&
                          progress < 60 &&
                          "Processing patient data..."}
                        {progress >= 60 &&
                          progress < 80 &&
                          "Generating questions..."}
                        {progress >= 80 &&
                          progress < 100 &&
                          "Finalizing form..."}
                        {progress >= 100 && "Complete!"}
                      </Text>
                    </VStack>
                  ) : generatedForm ? (
                    <VStack align="start" spacing={4}>
                      <Text fontWeight="bold">
                        Form Generated Successfully!
                      </Text>
                      <Text>
                        A follow-up form has been generated for{" "}
                        {selectedPatient?.name || "the patient"}.
                      </Text>
                      <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                        <Text fontSize="sm" color="gray.600">
                          Form contains {generatedForm.items?.length || 0}{" "}
                          questions
                        </Text>
                      </Box>
                    </VStack>
                  ) : (
                    <VStack align="start" spacing={4}>
                      <Text>
                        Generate a personalized follow-up form for{" "}
                        {selectedPatient?.name || "the patient"}
                        based on their clinical history.
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        This will create a customized questionnaire using AI
                        analysis of the patient's records.
                      </Text>
                    </VStack>
                  )}
                </VStack>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {isFFPatient && (
              <>
                <Box flexGrow={1} />
                {!generatedForm ? (
                  <Button
                    colorScheme="purple"
                    onClick={handleGenerateForm}
                    isLoading={generating}
                    isDisabled={!selectedPatient}
                  >
                    Generate Form
                  </Button>
                ) : (
                  <Button
                    colorScheme="teal"
                    onClick={handleSaveGeneratedForm}
                    isDisabled={generating}
                  >
                    Save Form to Patient
                  </Button>
                )}
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send Forms Report Modal */}
      <Modal
        isOpen={isReportModalOpen}
        onClose={onReportModalClose}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                Forms Sending Report
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {sendFormsReport.successful.length +
                  sendFormsReport.skipped.length +
                  sendFormsReport.failed.length}{" "}
                patient
                {sendFormsReport.successful.length +
                  sendFormsReport.skipped.length +
                  sendFormsReport.failed.length !==
                1
                  ? "s"
                  : ""}{" "}
                processed
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden">
            <VStack align="stretch" spacing={6}>
              {/* Summary Cards */}
              <HStack spacing={4} justify="space-around" mb={2}>
                {sendFormsReport.successful.length > 0 && (
                  <Box
                    bg="green.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {sendFormsReport.successful.length}
                    </Text>
                    <Text fontSize="xs" color="green.700">
                      Success
                    </Text>
                  </Box>
                )}
                {sendFormsReport.skipped.length > 0 && (
                  <Box
                    bg="orange.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="orange.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {sendFormsReport.skipped.length}
                    </Text>
                    <Text fontSize="xs" color="orange.700">
                      Skipped
                    </Text>
                  </Box>
                )}
                {sendFormsReport.failed.length > 0 && (
                  <Box
                    bg="red.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {sendFormsReport.failed.length}
                    </Text>
                    <Text fontSize="xs" color="red.700">
                      Failed
                    </Text>
                  </Box>
                )}
              </HStack>

              <Divider />

              {/* Success Section */}
              {sendFormsReport.successful.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <CheckCircleIcon color="green.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="green.600" fontSize="lg">
                      Successfully Sent ({sendFormsReport.successful.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="green.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    overscrollBehavior="contain"
                    borderLeft="4px solid"
                    borderColor="green.500"
                    sx={{
                      scrollBehavior: "smooth",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      {sendFormsReport.successful.map((patient, idx) => (
                        <Flex
                          key={idx}
                          align="center"
                          justify="space-between"
                          py={1}
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            ✓ {patient.name}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Skipped Section */}
              {sendFormsReport.skipped.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <WarningIcon color="orange.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="orange.600" fontSize="lg">
                      Skipped ({sendFormsReport.skipped.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="orange.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    overscrollBehavior="contain"
                    borderLeft="4px solid"
                    borderColor="orange.500"
                    sx={{
                      scrollBehavior: "smooth",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      {sendFormsReport.skipped.map((patient, idx) => (
                        <Box key={idx} py={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            ⊘ {patient.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" ml={4} mt={0.5}>
                            {patient.reason}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Failed Section */}
              {sendFormsReport.failed.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <CloseIcon color="red.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="red.600" fontSize="lg">
                      Failed ({sendFormsReport.failed.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="red.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    overscrollBehavior="contain"
                    borderLeft="4px solid"
                    borderColor="red.500"
                    sx={{
                      scrollBehavior: "smooth",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      {sendFormsReport.failed.map((patient, idx) => (
                        <Box key={idx} py={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            ✗ {patient.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" ml={4} mt={0.5}>
                            {patient.reason}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Empty State */}
              {sendFormsReport.successful.length === 0 &&
                sendFormsReport.skipped.length === 0 &&
                sendFormsReport.failed.length === 0 && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No report data available.
                  </Text>
                )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onReportModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reminder Report Modal */}
      <Modal
        isOpen={isReminderReportModalOpen}
        onClose={onReminderReportModalClose}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                {reminderReport.successful[0]?.scheduledDate
                  ? "Reminder Scheduling Report"
                  : "Reminder Sending Report"}
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {reminderReport.successful.length +
                  reminderReport.skipped.length +
                  reminderReport.failed.length}{" "}
                patient
                {reminderReport.successful.length +
                  reminderReport.skipped.length +
                  reminderReport.failed.length !==
                1
                  ? "s"
                  : ""}{" "}
                {reminderReport.successful[0]?.scheduledDate
                  ? "scheduled"
                  : "processed"}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden">
            <VStack align="stretch" spacing={6}>
              {/* Summary Cards */}
              <HStack spacing={4} justify="space-around" mb={2}>
                {reminderReport.successful.length > 0 && (
                  <Box
                    bg="green.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {reminderReport.successful.length}
                    </Text>
                    <Text fontSize="xs" color="green.700">
                      Success
                    </Text>
                  </Box>
                )}
                {reminderReport.skipped.length > 0 && (
                  <Box
                    bg="orange.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="orange.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {reminderReport.skipped.length}
                    </Text>
                    <Text fontSize="xs" color="orange.700">
                      Skipped
                    </Text>
                  </Box>
                )}
                {reminderReport.failed.length > 0 && (
                  <Box
                    bg="red.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.200"
                    textAlign="center"
                    flex={1}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {reminderReport.failed.length}
                    </Text>
                    <Text fontSize="xs" color="red.700">
                      Failed
                    </Text>
                  </Box>
                )}
              </HStack>

              <Divider />

              {/* Success Section */}
              {reminderReport.successful.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <CheckCircleIcon color="green.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="green.600" fontSize="lg">
                      {reminderReport.successful[0]?.scheduledDate
                        ? `Scheduled for`
                        : `Successfully Sent`}{" "}
                      ({reminderReport.successful.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="green.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    overscrollBehavior="contain"
                    borderLeft="4px solid"
                    borderColor="green.500"
                    sx={{
                      scrollBehavior: "smooth",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      {reminderReport.successful.map((patient, idx) => (
                        <Box key={idx} py={1}>
                          <Flex align="center" justify="space-between">
                            <Text fontSize="sm" fontWeight="medium">
                              ✓ {patient.name}
                            </Text>
                            {patient.scheduledDate && (
                              <Text
                                fontSize="xs"
                                color="green.600"
                                fontWeight="medium"
                              >
                                {patient.scheduledDate}
                              </Text>
                            )}
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Skipped Section */}
              {reminderReport.skipped.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <WarningIcon color="orange.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="orange.600" fontSize="lg">
                      Skipped ({reminderReport.skipped.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="orange.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    overscrollBehavior="contain"
                    borderLeft="4px solid"
                    borderColor="orange.500"
                    sx={{
                      scrollBehavior: "smooth",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      {reminderReport.skipped.map((patient, idx) => (
                        <Box key={idx} py={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            ⊘ {patient.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" ml={4} mt={0.5}>
                            {patient.reason}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Failed Section */}
              {reminderReport.failed.length > 0 && (
                <Box>
                  <Flex align="center" mb={3}>
                    <CloseIcon color="red.500" mr={2} boxSize={5} />
                    <Text fontWeight="bold" color="red.600" fontSize="lg">
                      Failed ({reminderReport.failed.length})
                    </Text>
                  </Flex>
                  <Box
                    pl={8}
                    pr={4}
                    py={3}
                    bg="red.50"
                    borderRadius="md"
                    maxH="250px"
                    overflowY="auto"
                    borderLeft="4px solid"
                    borderColor="red.500"
                  >
                    <VStack align="stretch" spacing={2}>
                      {reminderReport.failed.map((patient, idx) => (
                        <Box key={idx} py={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            ✗ {patient.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" ml={4} mt={0.5}>
                            {patient.reason}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Empty State */}
              {reminderReport.successful.length === 0 &&
                reminderReport.skipped.length === 0 &&
                reminderReport.failed.length === 0 && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No report data available.
                  </Text>
                )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onReminderReportModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Auto-Reminder Report Modal - Shows after triggering poll-and-schedule */}
      <Modal
        isOpen={isAutoReminderReportModalOpen}
        onClose={onAutoReminderReportModalClose}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" display="flex" flexDirection="column">
          <ModalHeader flexShrink={0}>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                Auto-Reminder Report
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {autoReminderReport.totalChecked || 0} patients processed
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex={1} overflow="hidden" pb={6}>
            {/* Summary Cards */}
            <HStack spacing={4} justify="space-around" mb={4} flexShrink={0}>
              <Box
                bg="green.50"
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="green.200"
                textAlign="center"
                flex={1}
              >
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {autoReminderReport.totalScheduled || 0}
                </Text>
                <Text fontSize="xs" color="green.700">
                  Scheduled
                </Text>
              </Box>
              <Box
                bg="orange.50"
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="orange.200"
                textAlign="center"
                flex={1}
              >
                <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                  {autoReminderReport.totalSkipped || 0}
                </Text>
                <Text fontSize="xs" color="orange.700">
                  Skipped
                </Text>
              </Box>
            </HStack>

            <Divider mb={4} flexShrink={0} />

            {/* Scrollable Content Area */}
            <Box
              flex={1}
              overflowY="auto"
              minH="150px"
              maxH="50vh"
              pr={2}
              style={{
                scrollBehavior: "smooth",
              }}
              css={{
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#a1a1a1",
                },
              }}
              onWheel={(e) => {
                e.stopPropagation();
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
              }}
            >
              {/* Scheduled Section */}
              {autoReminderReport.scheduled &&
                autoReminderReport.scheduled.length > 0 && (
                  <Box mb={4}>
                    <Flex align="center" mb={2}>
                      <CheckCircleIcon color="green.500" mr={2} boxSize={5} />
                      <Text fontWeight="bold" color="green.600" fontSize="lg">
                        Scheduled Reminders (
                        {autoReminderReport.scheduled.length})
                      </Text>
                    </Flex>
                    <Box
                      pl={4}
                      pr={4}
                      py={3}
                      bg="green.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderColor="green.500"
                    >
                      <VStack align="stretch" spacing={2}>
                        {autoReminderReport.scheduled.map((patient, idx) => (
                          <Box key={idx} py={1}>
                            <Flex
                              align="center"
                              justify="space-between"
                              flexWrap="wrap"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                ✓ {patient.name}
                              </Text>
                              <HStack spacing={2}>
                                {patient.type && (
                                  <Badge colorScheme="blue" fontSize="xs">
                                    {patient.type}
                                  </Badge>
                                )}
                                {patient.appointment_date && (
                                  <Text
                                    fontSize="xs"
                                    color="green.600"
                                    fontWeight="medium"
                                  >
                                    {patient.appointment_date}
                                  </Text>
                                )}
                              </HStack>
                            </Flex>
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              ml={4}
                              mt={0.5}
                            >
                              Contact: {patient.contact_method} | Scheduled for:{" "}
                              {patient.scheduled_for}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </Box>
                )}

              {/* Skipped Section */}
              {autoReminderReport.skipped &&
                autoReminderReport.skipped.length > 0 && (
                  <Box mb={4}>
                    <Flex align="center" mb={2}>
                      <WarningIcon color="orange.500" mr={2} boxSize={5} />
                      <Text fontWeight="bold" color="orange.600" fontSize="lg">
                        Skipped Patients ({autoReminderReport.skipped.length})
                      </Text>
                    </Flex>
                    <Box
                      pl={4}
                      pr={4}
                      py={3}
                      bg="orange.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderColor="orange.500"
                    >
                      <VStack align="stretch" spacing={2}>
                        {autoReminderReport.skipped.map((patient, idx) => (
                          <Box key={idx} py={1}>
                            <Flex
                              align="center"
                              justify="space-between"
                              flexWrap="wrap"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                ⊘ {patient.name}
                              </Text>
                              {patient.type && (
                                <Badge colorScheme="blue" fontSize="xs">
                                  {patient.type}
                                </Badge>
                              )}
                            </Flex>
                            <Text fontSize="xs" color="red.600" ml={4} mt={0.5}>
                              Reason: {patient.reason}
                            </Text>
                            {patient.appointment_date && (
                              <Text fontSize="xs" color="gray.500" ml={4}>
                                Appointment: {patient.appointment_date}
                              </Text>
                            )}
                            {patient.phone === "" && patient.email === "" && (
                              <Text fontSize="xs" color="gray.500" ml={4}>
                                No contact info on file
                              </Text>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </Box>
                )}

              {/* Empty State */}
              {(!autoReminderReport.scheduled ||
                autoReminderReport.scheduled.length === 0) &&
                (!autoReminderReport.skipped ||
                  autoReminderReport.skipped.length === 0) && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No auto-reminder report data available.
                  </Text>
                )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onAutoReminderReportModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Fax Contact Modal - Using standalone component for better typing performance */}
      <AddContactModal
        isOpen={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        onSave={handleAddContact}
        isSaving={savingContact}
      />

      {/* Edit Fax Contact Modal - View and edit contact details */}
      <EditContactModal
        isOpen={isEditContactOpen}
        onClose={() => {
          setIsEditContactOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSave={handleUpdateContact}
        onDelete={handleDeleteFromEditModal}
        isSaving={updatingContact}
      />

      {/* Appointment requests + availability, for this admin's own schema. */}
      <AppointmentInbox
        isOpen={isAppointmentInboxOpen}
        onClose={() => setIsAppointmentInboxOpen(false)}
        schema={userSchema}
      />

      {/* Fax Inbox Modal */}
      <Modal
        isOpen={isFaxInboxOpen}
        onClose={onFaxInboxClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader position="relative">
            <HStack spacing={4} align="baseline">
              <Text>Fax/Reports Inbox</Text>
              {/* Which schemas' faxes to show. Defaults to the whole clinic
                  group: an admin triaging the inbox is routing for the
                  practice, not just their own number. */}
              <FaxSchemaFilter onChange={handleRefreshInbox} />
              {faxNumber && (
                <Text
                  fontSize="sm"
                  color="white"
                  fontWeight="bold"
                  bg="blue.500"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  Fax #: {faxNumber}
                </Text>
              )}
            </HStack>
            {/* Refresh Button */}
            <IconButton
              position="absolute"
              right="40px"
              top="8px"
              icon={<RepeatIcon />}
              aria-label="Refresh inbox"
              onClick={handleRefreshInbox}
              isLoading={loadingFaxes}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              title="Refresh inbox to scan for new faxes/reports/tasks"
            />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            overflowX="hidden"
            sx={{
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              overscrollBehavior: "contain",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
            }}
          >
            <Tabs index={activeFaxTab} onChange={setActiveFaxTab}>
              <TabList borderBottom="none" mb={4}>
                <Tab
                  _selected={{
                    color: "blue.500",
                    borderBottom: "2px solid",
                    borderColor: "blue.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                >
                  Inbox
                </Tab>
                <Tab
                  _selected={{
                    color: "blue.500",
                    borderBottom: "2px solid",
                    borderColor: "blue.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                >
                  Tasks
                </Tab>
                <Tab
                  _selected={{
                    color: "blue.500",
                    borderBottom: "2px solid",
                    borderColor: "blue.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                >
                  Search
                </Tab>
                <Tab
                  _selected={{
                    color: "blue.500",
                    borderBottom: "2px solid",
                    borderColor: "blue.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                >
                  Send
                </Tab>
                <Tab
                  _selected={{
                    color: "blue.500",
                    borderBottom: "2px solid",
                    borderColor: "blue.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                  onClick={() => {
                    // Fetch sent faxes when clicking on the Sent tab
                    if (sentFaxes.length === 0) {
                      fetchSentFaxes();
                    }
                  }}
                >
                  Sent
                </Tab>
                <Tab
                  _selected={{
                    color: "red.500",
                    borderBottom: "2px solid",
                    borderColor: "red.500",
                  }}
                  borderBottom="2px solid transparent"
                  pb={2}
                  onClick={() => {
                    // Fetch spam faxes when clicking on the Spam tab
                    if (spamFaxes.length === 0) {
                      fetchSpamFaxes();
                    }
                  }}
                >
                  Spam {spamFaxes.length > 0 && `(${spamFaxes.length})`}
                </Tab>
              </TabList>
              <TabPanels>
                {/* Inbox Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    {/* Filter Tabs and Color Map - same row */}
                    <VStack spacing={3} align="stretch">
                      <HStack
                        spacing={2}
                        flexWrap="wrap"
                        justify="space-between"
                        align="flex-start"
                      >
                        <HStack spacing={2} flexWrap="wrap" flex="1">
                          <Button
                            size="sm"
                            colorScheme={
                              faxFilter === "all" && reportTypeFilter === "all"
                                ? "blue"
                                : "gray"
                            }
                            variant={
                              faxFilter === "all" && reportTypeFilter === "all"
                                ? "solid"
                                : "outline"
                            }
                            onClick={() => {
                              setFaxFilter("all");
                              setReportTypeFilter("all");
                            }}
                          >
                            All ({allFaxes.length})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              reportTypeFilter === "reports" ? "purple" : "gray"
                            }
                            variant={
                              reportTypeFilter === "reports"
                                ? "solid"
                                : "outline"
                            }
                            onClick={() => {
                              const newFilter =
                                reportTypeFilter === "reports"
                                  ? "all"
                                  : "reports";
                              setReportTypeFilter(newFilter);
                              // Reset matched/unmatched filter when changing report type filter
                              setFaxFilter("all");
                            }}
                          >
                            Reports ({faxCounts.reports})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              reportTypeFilter === "fax" ? "purple" : "gray"
                            }
                            variant={
                              reportTypeFilter === "fax" ? "solid" : "outline"
                            }
                            onClick={() => {
                              const newFilter =
                                reportTypeFilter === "fax" ? "all" : "fax";
                              setReportTypeFilter(newFilter);
                              // Reset matched/unmatched filter when changing report type filter
                              setFaxFilter("all");
                            }}
                          >
                            Fax ({faxCounts.fax})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              faxFilter === "unmatched" ? "red" : "gray"
                            }
                            variant={
                              faxFilter === "unmatched" ? "solid" : "outline"
                            }
                            onClick={() => {
                              setFaxFilter("unmatched");
                              setReportTypeFilter("fax");
                            }}
                          >
                            Unmatched ({faxCounts.unmatched})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              faxFilter === "matched" ? "green" : "gray"
                            }
                            variant={
                              faxFilter === "matched" ? "solid" : "outline"
                            }
                            onClick={() => {
                              setFaxFilter("matched");
                              setReportTypeFilter("fax");
                            }}
                          >
                            Matched ({faxCounts.matched})
                          </Button>
                        </HStack>
                        {/* Color Map - positioned on the right */}
                        <Box
                          p={2}
                          px={3}
                          bg="gray.50"
                          borderRadius="md"
                          border="1px"
                          borderColor="gray.200"
                          flexShrink={0}
                        >
                          <Text fontSize="xs" fontWeight="bold" mb={1}>
                            Color Map:
                          </Text>
                          <VStack spacing={1} align="flex-start">
                            {Object.entries(METADATA_COLORS)
                              .slice(0, 3)
                              .map(([category, color]) => {
                                const isHighlighted =
                                  isHighlightedCategory(category);
                                const bgColor = isHighlighted
                                  ? getMetadataBgColor(category)
                                  : "transparent";
                                return (
                                  <HStack key={category} spacing={1}>
                                    <Box
                                      w="12px"
                                      h="12px"
                                      bg={color}
                                      borderRadius="3px"
                                      border="1px solid"
                                      borderColor="gray.300"
                                      flexShrink={0}
                                    />
                                    <Text
                                      fontSize="11px"
                                      color={color}
                                      bg={bgColor}
                                      px={isHighlighted ? 1 : 0}
                                      borderRadius={isHighlighted ? "3px" : "0"}
                                      fontWeight={
                                        isHighlighted ? "600" : "normal"
                                      }
                                    >
                                      {category}
                                    </Text>
                                  </HStack>
                                );
                              })}
                            <HStack spacing={1}>
                              <Box
                                w="12px"
                                h="12px"
                                bg="#718096"
                                borderRadius="3px"
                                border="1px solid"
                                borderColor="gray.300"
                                flexShrink={0}
                              />
                              <Text fontSize="11px" color="#718096">
                                grey = unclassified
                              </Text>
                            </HStack>
                          </VStack>
                        </Box>
                      </HStack>
                      {selectedFaxes.size > 0 && (
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.600">
                            {selectedFaxes.size} selected
                          </Text>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={onMassAssignOpen}
                            isDisabled={
                              matchingFaxes !== null ||
                              massAssigning ||
                              massDeleting
                            }
                          >
                            Mass Assign ({selectedFaxes.size})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={onMassDeleteOpen}
                            isDisabled={
                              matchingFaxes !== null ||
                              massAssigning ||
                              massDeleting
                            }
                          >
                            Mass Delete ({selectedFaxes.size})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="purple"
                            onClick={handleBulkRematch}
                            isDisabled={
                              matchingFaxes !== null ||
                              massAssigning ||
                              massDeleting ||
                              bulkRematching
                            }
                            isLoading={bulkRematching}
                            loadingText="Matching..."
                            title="Match the selected unmatched faxes, and unmatch + re-run auto-match for the selected matched ones"
                          >
                            {bulkMatchAction.label}
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="orange"
                            onClick={checkSelectedFaxesForSpam}
                            isDisabled={
                              checkingForSpam || massAssigning || massDeleting
                            }
                            isLoading={checkingForSpam}
                            loadingText="Checking..."
                            title="Check selected faxes for spam content"
                          >
                            Check for Spam ({selectedFaxes.size})
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="gray"
                            bg="gray.200"
                            _hover={{ bg: "gray.300" }}
                            onClick={clearSelection}
                          >
                            Clear Selection
                          </Button>
                        </HStack>
                      )}
                      {selectedFaxes.size === 0 && (
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={selectAllFaxes}
                          >
                            Select All
                          </Button>
                        </HStack>
                      )}
                    </VStack>

                    {/* Reports List */}
                    {loadingFaxes ? (
                      <Center py={8}>
                        <Text>Loading reports...</Text>
                      </Center>
                    ) : (
                      <Box
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={4}
                      >
                        <VStack spacing={4} align="stretch">
                          {/* Use memoized groupedFaxesByDate for better performance */}
                          {groupedFaxesByDate.sortedDates.map((dateKey) => (
                            <Box key={dateKey} mb={4}>
                              <FaxDateHeader
                                dateKey={dateKey}
                                count={groupedFaxesByDate.groupedByDate[dateKey].length}
                                noun={(() => {
                                  const items =
                                    groupedFaxesByDate.groupedByDate[dateKey];
                                  const faxCount = items.filter(
                                    (f) => f.is_faxed_report,
                                  ).length;
                                  const reportCount = items.filter(
                                    (f) => !f.is_faxed_report,
                                  ).length;
                                  if (faxCount > 0 && reportCount > 0) {
                                    return items.length === 1 ? "file" : "files";
                                  } else if (faxCount > 0) {
                                    return faxCount === 1 ? "fax" : "faxes";
                                  }
                                  return reportCount === 1 ? "report" : "reports";
                                })()}
                                isCollapsed={inboxDates.isCollapsed(dateKey)}
                                onToggle={inboxDates.toggle}
                              />
                              <VStack
                                spacing={3}
                                align="stretch"
                                display={inboxDates.isCollapsed(dateKey) ? "none" : undefined}
                              >
                                {groupedFaxesByDate.groupedByDate[dateKey].map(
                                  (fax) => (
                                    <Box
                                      key={fax.file_path || fax.filename}
                                      data-fax-doc={faxDocumentKey(fax)}
                                      p={4}
                                      border="1px"
                                      borderColor={
                                        fax.is_unmatched
                                          ? "red.300"
                                          : "gray.200"
                                      }
                                      borderRadius="md"
                                      bg={fax.is_unmatched ? "red.50" : "white"}
                                    >
                                      <HStack
                                        justify="space-between"
                                        align="flex-start"
                                        spacing={3}
                                      >
                                        <Checkbox
                                          isChecked={selectedFaxes.has(
                                            fax.file_path,
                                          )}
                                          onChange={() =>
                                            toggleFaxSelection(fax.file_path)
                                          }
                                        />
                                        <VStack
                                          align="flex-start"
                                          spacing={2}
                                          flex={1}
                                        >
                                          <HStack>
                                            <Text fontWeight="bold">
                                              {fax.filename || "Unknown"}
                                            </Text>
                                            {fax.is_unmatched && (
                                              <Text
                                                fontSize="xs"
                                                color="red.600"
                                                fontWeight="bold"
                                                bg="red.100"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                              >
                                                UNMATCHED
                                              </Text>
                                            )}
                                          </HStack>
                                          <Text fontSize="sm" color="gray.600">
                                            {fax.file_path || "N/A"}
                                          </Text>
                                          <FaxClassificationBadge fax={fax} />
                                    <FaxSchemaBadges fax={fax} viewingSchema={userSchema} />
                                          {!fax.is_unmatched &&
                                            fax.patient_name && (
                                              <HStack spacing={2} flexWrap="wrap" rowGap={1}>
                                                <Text
                                                  fontSize="sm"
                                                  fontWeight="medium"
                                                  color="blue.600"
                                                >
                                                  Patient: {faxPatientDisplayName(fax)}
                                                </Text>
                                                {/* Replaces the raw `07-31-26`
                                                    folder name this view used
                                                    to print. */}
                                                <FaxMatchedDate fax={fax} />
                                    <FaxPendingIdentity
                                      fax={fax}
                                      onResolve={handleResolveIdentity}
                                    />
                                              </HStack>
                                            )}
                                          {fax.summary && (
                                            <Text
                                              fontSize="xs"
                                              color="gray.500"
                                              noOfLines={2}
                                            >
                                              {fax.summary}
                                            </Text>
                                          )}
                                          {fax.metadata &&
                                            typeof fax.metadata === "object" &&
                                            Object.keys(fax.metadata).filter(
                                              (k) => k !== "truncation_info",
                                            ).length > 0 && (
                                              <VStack
                                                align="flex-start"
                                                spacing={1}
                                                mt={2}
                                                p={2}
                                                bg="blue.50"
                                                borderRadius="md"
                                                border="1px"
                                                borderColor="blue.200"
                                              >
                                                <Text
                                                  fontSize="xs"
                                                  fontWeight="bold"
                                                  color="blue.700"
                                                >
                                                  Metadata:
                                                </Text>
                                                {Object.entries(fax.metadata)
                                                  .filter(
                                                    ([key, val]) =>
                                                      key !==
                                                        "truncation_info" &&
                                                      val !== null &&
                                                      val !== "" &&
                                                      val !== undefined,
                                                  )
                                                  .map(([key, val]) => (
                                                    <Text
                                                      key={key}
                                                      fontSize="xs"
                                                      color="gray.700"
                                                      noOfLines={1}
                                                    >
                                                      <strong>
                                                        {key
                                                          .replace(/_/g, " ")
                                                          .replace(
                                                            /\b\w/g,
                                                            (c) =>
                                                              c.toUpperCase(),
                                                          )}
                                                        :
                                                      </strong>{" "}
                                                      {Array.isArray(val)
                                                        ? val.join(", ")
                                                        : typeof val ===
                                                            "object"
                                                          ? JSON.stringify(val)
                                                          : String(val)}
                                                    </Text>
                                                  ))}
                                              </VStack>
                                            )}
                                          <Text fontSize="xs" color="gray.400">
                                            Received:{" "}
                                            {fax.created_at
                                              ? new Date(
                                                  fax.created_at,
                                                ).toLocaleTimeString("en-US", {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                  timeZoneName: "short",
                                                })
                                              : "Unknown time"}
                                          </Text>
                                          {!fax.is_unmatched && (
                                            <Box
                                              display="inline-block"
                                              px={2}
                                              py="1px"
                                              borderRadius="md"
                                              fontSize="xs"
                                              fontWeight="500"
                                              bg={
                                                fax.uploaded_by_name
                                                  ? "purple.50"
                                                  : "gray.100"
                                              }
                                              color={
                                                fax.uploaded_by_name
                                                  ? "purple.700"
                                                  : "gray.500"
                                              }
                                              border="1px solid"
                                              borderColor={
                                                fax.uploaded_by_name
                                                  ? "purple.200"
                                                  : "gray.300"
                                              }
                                            >
                                              {fax.is_faxed_report
                                                ? fax.uploaded_by_name
                                                  ? `Assigned by: ${fax.uploaded_by_name}`
                                                  : "Assigned by: System"
                                                : fax.uploaded_by_name
                                                  ? `Uploaded by: ${fax.uploaded_by_name}`
                                                  : "Uploaded by: System"}
                                            </Box>
                                          )}
                                          {orphanedFilePaths.has(
                                            fax.file_path,
                                          ) && (
                                            <HStack
                                              mt={2}
                                              p={2}
                                              bg="orange.50"
                                              border="1px"
                                              borderColor="orange.300"
                                              borderRadius="md"
                                              spacing={2}
                                            >
                                              <Text
                                                fontSize="xs"
                                                color="orange.700"
                                                fontWeight="medium"
                                              >
                                                File for this{" "}
                                                {fax.is_faxed_report
                                                  ? "fax"
                                                  : "report"}{" "}
                                                not found in database.
                                              </Text>
                                              <Button
                                                size="xs"
                                                colorScheme="red"
                                                onClick={() =>
                                                  deleteOrphanedFax(fax)
                                                }
                                              >
                                                Delete
                                              </Button>
                                            </HStack>
                                          )}
                                        </VStack>
                                        <VStack spacing={2} align="flex-end">
                                          {/* First row of buttons */}
                                          <HStack spacing={2} flexWrap="wrap">
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={async () => {
                                                try {
                                                  const filePath =
                                                    fax.file_path || "";
                                                  const hostname =
                                                    window.location.hostname;
                                                  const baseUrl =
                                                    hostname === "localhost"
                                                      ? BASE_URLS.local.backend
                                                      : BASE_URLS.render
                                                          .backend;

                                                  const response = await fetch(
                                                    `${baseUrl}/api/fax/get-view-url/?file_path=${encodeURIComponent(
                                                      filePath,
                                                    )}`,
                                                    { headers: await authHeader() },
                                                  );

                                                  if (response.ok) {
                                                    const data =
                                                      await response.json();
                                                    window.open(
                                                      data.url,
                                                      "_blank",
                                                    );
                                                  } else {
                                                    // Fallback to public URL if API fails (using supabaseUrl from env.js)
                                                    const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
                                                    window.open(
                                                      storageUrl,
                                                      "_blank",
                                                    );
                                                  }
                                                } catch (error) {
                                                  console.error(
                                                    "Error getting fax view URL:",
                                                    error,
                                                  );
                                                  // Fallback to public URL (using supabaseUrl from env.js)
                                                  const filePath =
                                                    fax.file_path || "";
                                                  const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
                                                  window.open(
                                                    storageUrl,
                                                    "_blank",
                                                  );
                                                }
                                              }}
                                            >
                                              View File
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="blue"
                                              onClick={() =>
                                                handleExtractTextMetadata(fax)
                                              }
                                              isDisabled={
                                                fax?.extracted_text_metadata ||
                                                processingTasks[
                                                  `extractText_${fax.file_path}`
                                                ]
                                              }
                                              isLoading={
                                                processingTasks[
                                                  `extractText_${fax.file_path}`
                                                ]
                                              }
                                              loadingText="Extracting..."
                                            >
                                              Extract Text
                                            </Button>
                                            {(fax?.has_full_text ||
                                              fax?.full_text ||
                                              fax?.extracted_text_metadata) && (
                                              <Button
                                                size="sm"
                                                colorScheme="teal"
                                                variant="outline"
                                                onClick={() =>
                                                  handleViewExtractedText(fax)
                                                }
                                              >
                                                View Extracted Text
                                              </Button>
                                            )}
                                            <Button
                                              size="sm"
                                              colorScheme="purple"
                                              onClick={() => {
                                                setSelectedFaxForRename(fax);
                                                // Extract current filename without extension for default value
                                                const currentFilename =
                                                  fax.filename || "";
                                                const nameWithoutExt =
                                                  currentFilename.includes(".")
                                                    ? currentFilename.substring(
                                                        0,
                                                        currentFilename.lastIndexOf(
                                                          ".",
                                                        ),
                                                      )
                                                    : currentFilename;
                                                setNewFaxName(nameWithoutExt);
                                                onRenameFaxOpen();
                                              }}
                                            >
                                              Rename
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="red"
                                              onClick={() => {
                                                setFaxToDelete(fax);
                                                onDeleteFaxOpen();
                                              }}
                                              isDisabled={deletingFax}
                                            >
                                              Delete
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="cyan"
                                              onClick={() => {
                                                // Move the fax to Tasks tab
                                                setTaskFaxes((prev) => {
                                                  // Check if already in tasks
                                                  if (
                                                    prev.some(
                                                      (f) =>
                                                        f.file_path ===
                                                        fax.file_path,
                                                    )
                                                  ) {
                                                    return prev;
                                                  }
                                                  return [...prev, fax];
                                                });
                                                setActiveFaxTab(1); // Switch to Tasks tab
                                              }}
                                            >
                                              Move to Tasks
                                            </Button>
                                          </HStack>
                                          {/* Second row of buttons */}
                                          <HStack spacing={2} flexWrap="wrap">
                                            {fax.is_unmatched && (
                                              <Button
                                                size="sm"
                                                colorScheme="purple"
                                                onClick={() =>
                                                  handleMatchSingleFax(fax)
                                                }
                                                isDisabled={
                                                  matchingFaxes !== null ||
                                                  assigningFax
                                                }
                                                isLoading={
                                                  matchingFaxes ===
                                                  fax.file_path
                                                }
                                                loadingText="Matching..."
                                              >
                                                Match
                                              </Button>
                                            )}
                                            {!fax.is_unmatched && (
                                              <Button
                                                size="sm"
                                                colorScheme="orange"
                                                onClick={() =>
                                                  handleUnmatchFax(fax)
                                                }
                                                isDisabled={
                                                  matchingFaxes !== null ||
                                                  assigningFax ||
                                                  rematchingFax !== null ||
                                                  bulkRematching
                                                }
                                                title={
                                                  (fax.matched_schemas || [])
                                                    .length > 1
                                                    ? `Take this fax off every chart it was filed onto (${fax.matched_schemas.join(
                                                        ", ",
                                                      )}) and return it to Unmatched`
                                                    : "Take this fax off the patient's chart and return it to Unmatched"
                                                }
                                              >
                                                Unmatch
                                              </Button>
                                            )}
                                            {!fax.is_unmatched && (
                                              <Button
                                                size="sm"
                                                colorScheme="purple"
                                                variant="outline"
                                                onClick={() =>
                                                  handleRematchFax(fax)
                                                }
                                                isDisabled={
                                                  matchingFaxes !== null ||
                                                  assigningFax ||
                                                  rematchingFax !== null ||
                                                  bulkRematching
                                                }
                                                isLoading={
                                                  rematchingFax ===
                                                  fax.file_path
                                                }
                                                loadingText="Rematching..."
                                                title="Unmatch and run auto-match again"
                                              >
                                                Rematch
                                              </Button>
                                            )}
                                            <Button
                                              size="sm"
                                              colorScheme="blue"
                                              onClick={() => {
                                                setSelectedFaxForAssignment(
                                                  fax,
                                                );
                                              }}
                                              isDisabled={
                                                matchingFaxes !== null ||
                                                assigningFax
                                              }
                                            >
                                              {fax.is_unmatched
                                                ? "Assign to Patient"
                                                : "Reassign to Patient"}
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="teal"
                                              onClick={() => {
                                                setSelectedFaxForClassification(
                                                  fax,
                                                );
                                                setNewFaxClassification(
                                                  fax.classification || "Other",
                                                );
                                                onChangeFaxClassificationOpen();
                                              }}
                                              isDisabled={
                                                matchingFaxes !== null ||
                                                assigningFax
                                              }
                                            >
                                              Change Category
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="orange"
                                              variant="outline"
                                              onClick={() => markFaxAsSpam(fax)}
                                              isDisabled={markingAsSpam}
                                              title="Mark this fax as spam"
                                            >
                                              Spam
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={() =>
                                                handleSendFaxClick(fax)
                                              }
                                              isDisabled={
                                                matchingFaxes !== null ||
                                                assigningFax ||
                                                sendingFax
                                              }
                                            >
                                              Send as a fax
                                            </Button>
                                          </HStack>
                                        </VStack>
                                      </HStack>
                                    </Box>
                                  ),
                                )}
                              </VStack>
                            </Box>
                          ))}
                          {filteredFaxesList.length === 0 && (
                            <Center py={8}>
                              <Text color="gray.500">No reports found</Text>
                            </Center>
                          )}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* Tasks Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    {/* Task Faxes List */}
                    {taskFaxes.length > 0 && (
                      <Box>
                        <Text fontSize="lg" fontWeight="bold" mb={3}>
                          Reports in Tasks ({taskFaxes.length})
                        </Text>
                        <Box
                          maxH="500px"
                          overflowY="auto"
                          border="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={4}
                          sx={{ overscrollBehavior: "contain" }}
                        >
                          <VStack spacing={3} align="stretch">
                            {taskFaxes.map((fax) => (
                              <Box
                                key={fax.file_path || fax.filename}
                                      data-fax-doc={faxDocumentKey(fax)}
                                p={4}
                                border="1px"
                                borderColor={
                                  fax.is_unmatched ? "red.300" : "gray.200"
                                }
                                borderRadius="md"
                                bg={fax.is_unmatched ? "red.50" : "white"}
                              >
                                <HStack
                                  justify="space-between"
                                  align="flex-start"
                                  spacing={3}
                                >
                                  <VStack
                                    align="flex-start"
                                    spacing={2}
                                    flex={1}
                                  >
                                    <HStack>
                                      <Text fontWeight="bold">
                                        {fax.filename || "Unknown"}
                                      </Text>
                                      {fax.is_unmatched && (
                                        <Text
                                          fontSize="xs"
                                          color="red.600"
                                          fontWeight="bold"
                                          bg="red.100"
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          UNMATCHED
                                        </Text>
                                      )}
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600">
                                      {fax.file_path || "N/A"}
                                    </Text>
                                    <FaxClassificationBadge fax={fax} />
                                    <FaxSchemaBadges fax={fax} viewingSchema={userSchema} />
                                    {!fax.is_unmatched && fax.patient_name && (
                                      <HStack spacing={2} flexWrap="wrap" rowGap={1}>
                                        <Text
                                          fontSize="sm"
                                          fontWeight="medium"
                                          color="blue.600"
                                        >
                                          Patient: {faxPatientDisplayName(fax)}
                                        </Text>
                                        <FaxMatchedDate fax={fax} />
                                    <FaxPendingIdentity
                                      fax={fax}
                                      onResolve={handleResolveIdentity}
                                    />
                                      </HStack>
                                    )}
                                    {orphanedFilePaths.has(fax.file_path) && (
                                      <HStack
                                        mt={2}
                                        p={2}
                                        bg="orange.50"
                                        border="1px"
                                        borderColor="orange.300"
                                        borderRadius="md"
                                        spacing={2}
                                      >
                                        <Text
                                          fontSize="xs"
                                          color="orange.700"
                                          fontWeight="medium"
                                        >
                                          File for this{" "}
                                          {fax.is_faxed_report
                                            ? "fax"
                                            : "report"}{" "}
                                          not found in database.
                                        </Text>
                                        <Button
                                          size="xs"
                                          colorScheme="red"
                                          onClick={() => deleteOrphanedFax(fax)}
                                        >
                                          Delete
                                        </Button>
                                      </HStack>
                                    )}
                                  </VStack>
                                  <VStack spacing={2} align="flex-end">
                                    <HStack spacing={2}>
                                      <Button
                                        size="sm"
                                        colorScheme="green"
                                        onClick={() => handleUploadToEHR(fax)}
                                        isDisabled={
                                          fax?.uploaded_to_ehr ||
                                          processingTasks[
                                            `uploadToEHR_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `uploadToEHR_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Upload to EHR
                                      </Button>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={() =>
                                          handleExtractTextMetadata(fax)
                                        }
                                        isDisabled={
                                          fax?.extracted_text_metadata ||
                                          processingTasks[
                                            `extractText_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `extractText_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Extract Text and Metadata
                                      </Button>
                                      {(fax?.has_full_text ||
                                        fax?.full_text ||
                                        fax?.extracted_text_metadata) && (
                                        <Button
                                          size="sm"
                                          colorScheme="teal"
                                          variant="outline"
                                          onClick={() =>
                                            handleViewExtractedText(fax)
                                          }
                                        >
                                          View Extracted Text
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        colorScheme="purple"
                                        onClick={() =>
                                          handleAssignTaskClick(fax)
                                        }
                                        isDisabled={
                                          processingTasks[
                                            `assignTask_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `assignTask_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Assign Task
                                      </Button>
                                    </HStack>
                                    <Button
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => {
                                        setTaskFaxes((prev) =>
                                          prev.filter(
                                            (f) =>
                                              f.file_path !== fax.file_path,
                                          ),
                                        );
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </VStack>
                                </HStack>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      </Box>
                    )}
                    {taskFaxes.length === 0 && myAssignedTasks.length === 0 && (
                      <Box
                        p={4}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        textAlign="center"
                      >
                        <Text color="gray.500">
                          No reports in Tasks. Use "Move to Tasks" button in the
                          Inbox tab to add reports here.
                        </Text>
                      </Box>
                    )}

                    {/* My Assigned Tasks */}
                    {myAssignedTasks.length > 0 && (
                      <Box>
                        <Text fontSize="lg" fontWeight="bold" mb={3}>
                          My Assigned Tasks ({myAssignedTasks.length})
                        </Text>
                        <Box
                          maxH="500px"
                          overflowY="auto"
                          border="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={4}
                          sx={{ overscrollBehavior: "contain" }}
                        >
                          <VStack spacing={3} align="stretch">
                            {myAssignedTasks.map((fax) => (
                              <Box
                                key={fax.file_path || fax.filename}
                                      data-fax-doc={faxDocumentKey(fax)}
                                p={4}
                                border="1px"
                                borderColor="blue.300"
                                borderRadius="md"
                                bg="blue.50"
                              >
                                <HStack
                                  justify="space-between"
                                  align="flex-start"
                                  spacing={3}
                                >
                                  <VStack
                                    align="flex-start"
                                    spacing={2}
                                    flex={1}
                                  >
                                    <HStack>
                                      <Text fontWeight="bold">
                                        {fax.filename || "Unknown"}
                                      </Text>
                                      {fax.is_unmatched && (
                                        <Text
                                          fontSize="xs"
                                          color="red.600"
                                          fontWeight="bold"
                                          bg="red.100"
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          UNMATCHED
                                        </Text>
                                      )}
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600">
                                      {fax.file_path || "N/A"}
                                    </Text>
                                    <FaxClassificationBadge fax={fax} />
                                    <FaxSchemaBadges fax={fax} viewingSchema={userSchema} />
                                    {!fax.is_unmatched && fax.patient_name && (
                                      <HStack spacing={2} flexWrap="wrap" rowGap={1}>
                                        <Text
                                          fontSize="sm"
                                          fontWeight="medium"
                                          color="blue.600"
                                        >
                                          Patient: {faxPatientDisplayName(fax)}
                                        </Text>
                                        <FaxMatchedDate fax={fax} />
                                    <FaxPendingIdentity
                                      fax={fax}
                                      onResolve={handleResolveIdentity}
                                    />
                                      </HStack>
                                    )}
                                    {orphanedFilePaths.has(fax.file_path) && (
                                      <HStack
                                        mt={2}
                                        p={2}
                                        bg="orange.50"
                                        border="1px"
                                        borderColor="orange.300"
                                        borderRadius="md"
                                        spacing={2}
                                      >
                                        <Text
                                          fontSize="xs"
                                          color="orange.700"
                                          fontWeight="medium"
                                        >
                                          File for this{" "}
                                          {fax.is_faxed_report
                                            ? "fax"
                                            : "report"}{" "}
                                          not found in database.
                                        </Text>
                                        <Button
                                          size="xs"
                                          colorScheme="red"
                                          onClick={() => deleteOrphanedFax(fax)}
                                        >
                                          Delete
                                        </Button>
                                      </HStack>
                                    )}
                                  </VStack>
                                  <VStack spacing={2} align="flex-end">
                                    <HStack spacing={2}>
                                      <Button
                                        size="sm"
                                        colorScheme="green"
                                        onClick={() => handleUploadToEHR(fax)}
                                        isDisabled={
                                          fax?.uploaded_to_ehr ||
                                          processingTasks[
                                            `uploadToEHR_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `uploadToEHR_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Upload to EHR
                                      </Button>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={() =>
                                          handleExtractTextMetadata(fax)
                                        }
                                        isDisabled={
                                          fax?.extracted_text_metadata ||
                                          processingTasks[
                                            `extractText_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `extractText_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Extract Text and Metadata
                                      </Button>
                                      {(fax?.has_full_text ||
                                        fax?.full_text ||
                                        fax?.extracted_text_metadata) && (
                                        <Button
                                          size="sm"
                                          colorScheme="teal"
                                          variant="outline"
                                          onClick={() =>
                                            handleViewExtractedText(fax)
                                          }
                                        >
                                          View Extracted Text
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        colorScheme="purple"
                                        onClick={() =>
                                          handleAssignTaskClick(fax)
                                        }
                                        isDisabled={
                                          processingTasks[
                                            `assignTask_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `assignTask_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Assign Task
                                      </Button>
                                      <Button
                                        size="sm"
                                        colorScheme="orange"
                                        variant="outline"
                                        onClick={() => handleUnassignTask(fax)}
                                        isDisabled={
                                          processingTasks[
                                            `unassignTask_${fax.file_path}`
                                          ]
                                        }
                                        isLoading={
                                          processingTasks[
                                            `unassignTask_${fax.file_path}`
                                          ]
                                        }
                                      >
                                        Unassign
                                      </Button>
                                    </HStack>
                                  </VStack>
                                </HStack>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* Search Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <style>{`
                        .report-type-select-wrapper .chakra-select__wrapper > div[data-icon],
                        .report-type-select-wrapper .chakra-select__wrapper svg,
                        .report-type-select-wrapper [data-icon],
                        .report-type-select-wrapper svg {
                          top: 25% !important;
                          transform: translateY(-50%) !important;
                        }
                      `}</style>
                    {/* Search Section */}
                    <Box
                      p={4}
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                    >
                      <Text fontSize="lg" fontWeight="bold" mb={3}>
                        Search Faxes & Reports
                      </Text>
                      <VStack spacing={3} align="stretch">
                        <InputGroup>
                          <Input
                            ref={searchQueryRef}
                            placeholder="Search by text, patient name, or metadata..."
                            defaultValue={searchQuery}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                // Update state from ref before searching
                                setSearchQuery(
                                  searchQueryRef.current?.value || "",
                                );
                                handleSearchFaxes();
                              }
                            }}
                          />
                          <InputRightElement width="4.5rem">
                            <Button
                              h="1.75rem"
                              size="sm"
                              onClick={() => {
                                // Update state from ref before searching
                                setSearchQuery(
                                  searchQueryRef.current?.value || "",
                                );
                                handleSearchFaxes();
                              }}
                              isLoading={searchingFaxes}
                            >
                              Search
                            </Button>
                          </InputRightElement>
                        </InputGroup>
                        <HStack spacing={2} flexWrap="wrap" mb={3}>
                          <Button
                            size="sm"
                            colorScheme={
                              searchReportTypeFilter === "all" ? "blue" : "gray"
                            }
                            variant={
                              searchReportTypeFilter === "all"
                                ? "solid"
                                : "outline"
                            }
                            onClick={() => setSearchReportTypeFilter("all")}
                          >
                            All
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              searchReportTypeFilter === "reports"
                                ? "purple"
                                : "gray"
                            }
                            variant={
                              searchReportTypeFilter === "reports"
                                ? "solid"
                                : "outline"
                            }
                            onClick={() => setSearchReportTypeFilter("reports")}
                          >
                            Reports
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={
                              searchReportTypeFilter === "fax"
                                ? "purple"
                                : "gray"
                            }
                            variant={
                              searchReportTypeFilter === "fax"
                                ? "solid"
                                : "outline"
                            }
                            onClick={() => setSearchReportTypeFilter("fax")}
                          >
                            Fax
                          </Button>
                        </HStack>
                        <HStack spacing={4}>
                          <FormControl>
                            <FormLabel fontSize="sm">Patient Name</FormLabel>
                            <Input
                              ref={searchPatientNameRef}
                              size="sm"
                              placeholder="Filter by patient name..."
                              defaultValue={searchPatientName}
                              onBlur={(e) =>
                                setSearchPatientName(e.target.value)
                              }
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Report Type</FormLabel>
                            <Box
                              className="report-type-select-wrapper"
                              position="relative"
                              display="inline-block"
                              width="100%"
                            >
                              <Select
                                size="sm"
                                placeholder="All types"
                                value={searchReportType}
                                onChange={(e) =>
                                  setSearchReportType(e.target.value)
                                }
                                sx={{
                                  "& > option": {
                                    padding: "8px",
                                  },
                                }}
                              >
                                <option value="Labs">Labs</option>
                                <option value="Images">Images</option>
                                <option value="Medical Records">
                                  Medical Records
                                </option>
                                <option value="Other">Other</option>
                              </Select>
                            </Box>
                          </FormControl>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchPatientName("");
                              // Uncontrolled inputs: clearing state alone left the old text showing.
                              if (searchQueryRef.current) searchQueryRef.current.value = "";
                              if (searchPatientNameRef.current) searchPatientNameRef.current.value = "";
                              setSearchReportType("");
                              setSearchReportTypeFilter("all");
                              setSearchResults([]);
                            }}
                          >
                            Clear
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <Box>
                        <Text fontSize="md" fontWeight="bold" mb={2}>
                          Search Results ({searchResults.length})
                        </Text>
                        <Box
                          maxH="400px"
                          overflowY="auto"
                          border="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={4}
                          sx={{ overscrollBehavior: "contain" }}
                        >
                          <VStack spacing={3} align="stretch">
                            {searchResults.map((fax) => (
                              <Box
                                key={fax.file_path || fax.filename}
                                      data-fax-doc={faxDocumentKey(fax)}
                                p={3}
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="white"
                              >
                                <HStack
                                  justify="space-between"
                                  align="flex-start"
                                >
                                  <VStack
                                    align="flex-start"
                                    spacing={1}
                                    flex={1}
                                  >
                                    <Text fontWeight="bold">
                                      {fax.filename || "Unknown"}
                                    </Text>
                                    {fax.patient_name && (
                                      <Text fontSize="sm" color="blue.600">
                                        Patient: {faxPatientDisplayName(fax)}
                                      </Text>
                                    )}
                                    <FaxClassificationBadge fax={fax} />
                                    <FaxSchemaBadges fax={fax} viewingSchema={userSchema} />
                                    <FaxMatchedDate fax={fax} />
                                    <FaxPendingIdentity
                                      fax={fax}
                                      onResolve={handleResolveIdentity}
                                    />
                                    {fax.summary && (
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        noOfLines={2}
                                      >
                                        {fax.summary}
                                      </Text>
                                    )}
                                    {orphanedFilePaths.has(fax.file_path) && (
                                      <HStack
                                        mt={2}
                                        p={2}
                                        bg="orange.50"
                                        border="1px"
                                        borderColor="orange.300"
                                        borderRadius="md"
                                        spacing={2}
                                      >
                                        <Text
                                          fontSize="xs"
                                          color="orange.700"
                                          fontWeight="medium"
                                        >
                                          File for this{" "}
                                          {fax.is_faxed_report
                                            ? "fax"
                                            : "report"}{" "}
                                          not found in database.
                                        </Text>
                                        <Button
                                          size="xs"
                                          colorScheme="red"
                                          onClick={() => deleteOrphanedFax(fax)}
                                        >
                                          Delete
                                        </Button>
                                      </HStack>
                                    )}
                                  </VStack>
                                </HStack>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* Send Tab */}
                <TabPanel p={0}>
                  <Box
                    maxH="55vh"
                    overflowY="auto"
                    overflowX="hidden"
                    p={4}
                    sx={{
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "thin",
                      overscrollBehavior: "contain",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <VStack spacing={4} align="stretch">
                      <Box
                        p={4}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <Text fontSize="lg" fontWeight="bold" mb={3}>
                          Send Fax
                        </Text>
                        <VStack spacing={4} align="stretch">
                          <Box>
                            <Box
                              py={2}
                              px={3}
                              borderRadius="md"
                              bg="gray.50"
                              borderWidth="1px"
                              borderColor="gray.200"
                              cursor="pointer"
                              onClick={() =>
                                setSendFaxAttachmentsExpanded((v) => !v)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSendFaxAttachmentsExpanded((v) => !v);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-expanded={sendFaxAttachmentsExpanded}
                            >
                              <HStack justify="space-between" align="center">
                                <Text fontSize="md" fontWeight="semibold">
                                  {sendFaxAttachmentsExpanded ? "▼" : "▶"} 3.
                                  Attachment(s)
                                </Text>
                              </HStack>
                            </Box>
                            <Collapse
                              in={sendFaxAttachmentsExpanded}
                              animateOpacity
                            >
                              <VStack spacing={4} align="stretch" mt={3}>
                                {/* File Upload Section */}
                                <Box
                                  p={4}
                                  border="2px"
                                  borderColor="blue.300"
                                  borderStyle="dashed"
                                  borderRadius="md"
                                  bg="blue.50"
                                >
                                  <VStack spacing={3} align="stretch">
                                    <Text
                                      fontSize="md"
                                      fontWeight="bold"
                                      color="blue.700"
                                    >
                                      Upload File to Send
                                    </Text>
                                    <FormControl>
                                      <Input
                                        type="file"
                                        accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) {
                                            handleFileUpload(file);
                                          }
                                        }}
                                        isDisabled={uploadingFile}
                                        display="none"
                                        id="file-upload-input"
                                      />
                                      <Button
                                        as="label"
                                        htmlFor="file-upload-input"
                                        colorScheme="blue"
                                        variant="outline"
                                        cursor="pointer"
                                        isDisabled={uploadingFile}
                                        width="100%"
                                      >
                                        {uploadingFile
                                          ? "Uploading..."
                                          : "Choose File to Upload"}
                                      </Button>
                                    </FormControl>
                                    {uploadingFile && (
                                      <Box>
                                        <Progress
                                          value={uploadProgress}
                                          colorScheme="blue"
                                          size="sm"
                                          mb={2}
                                        />
                                        <Text
                                          fontSize="xs"
                                          color="gray.600"
                                          textAlign="center"
                                        >
                                          {uploadProgress}% uploaded
                                        </Text>
                                      </Box>
                                    )}
                                    {uploadError && (
                                      <Alert status="error" size="sm">
                                        <AlertIcon />
                                        <AlertDescription fontSize="xs">
                                          {uploadError}
                                        </AlertDescription>
                                      </Alert>
                                    )}
                                    {uploadedFile && (
                                      <Box
                                        p={2}
                                        bg="green.50"
                                        borderRadius="md"
                                        border="1px"
                                        borderColor="green.200"
                                        position="relative"
                                      >
                                        <HStack
                                          justify="space-between"
                                          align="flex-start"
                                        >
                                          <Box flex={1}>
                                            <Text
                                              fontSize="sm"
                                              fontWeight="bold"
                                              color="green.700"
                                              mb={1}
                                            >
                                              ✓ File Uploaded:
                                            </Text>
                                            <Text fontSize="sm">
                                              {uploadedFile.filename}
                                            </Text>
                                            <Text
                                              fontSize="xs"
                                              color="gray.600"
                                            >
                                              {(
                                                uploadedFile.size /
                                                1024 /
                                                1024
                                              ).toFixed(2)}{" "}
                                              MB
                                            </Text>
                                          </Box>
                                          <IconButton
                                            icon={<DeleteIcon />}
                                            aria-label="Remove uploaded file"
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={() => {
                                              setUploadedFile(null);
                                              if (
                                                selectedFaxForSending &&
                                                selectedFaxForSending.file_path ===
                                                  uploadedFile.file_path
                                              ) {
                                                setSelectedFaxForSending(null);
                                              }
                                            }}
                                          />
                                        </HStack>
                                      </Box>
                                    )}
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      textAlign="center"
                                    >
                                      Supported formats: PDF, TIFF, PNG, JPG
                                      (Max 50MB)
                                    </Text>
                                  </VStack>
                                </Box>

                                <Divider />

                                {/* File Selection from Inbox */}
                                <FormControl>
                                  <FormLabel>
                                    Or Select File from Inbox
                                  </FormLabel>
                                  <Select
                                    placeholder="Select a file from inbox"
                                    value={
                                      selectedFaxForSending?.file_path || ""
                                    }
                                    onChange={(e) => {
                                      const filePath = e.target.value;
                                      if (filePath) {
                                        const fax = allFaxes.find(
                                          (f) => f.file_path === filePath,
                                        );
                                        if (fax) {
                                          setSelectedFaxForSending(fax);
                                          setUploadedFile(null);
                                        } else {
                                          setSelectedFaxForSending({
                                            file_path: filePath,
                                            filename:
                                              filePath.split("/").pop() ||
                                              filePath,
                                          });
                                          setUploadedFile(null);
                                        }
                                      } else {
                                        setSelectedFaxForSending(null);
                                        setUploadedFile(null);
                                      }
                                    }}
                                    isDisabled={uploadingFile}
                                  >
                                    <option value="">
                                      -- Select from inbox --
                                    </option>
                                    {allFaxes.map((fax) => (
                                      <option
                                        key={fax.file_path}
                                        value={fax.file_path}
                                      >
                                        {fax.filename || fax.file_path}
                                      </option>
                                    ))}
                                  </Select>
                                </FormControl>

                                {selectedFaxForSending && (
                                  <Box
                                    p={3}
                                    bg="blue.50"
                                    borderRadius="md"
                                    border="1px"
                                    borderColor="blue.200"
                                  >
                                    <Text
                                      fontSize="sm"
                                      fontWeight="bold"
                                      mb={1}
                                    >
                                      Selected File:
                                    </Text>
                                    <Text fontSize="sm">
                                      {selectedFaxForSending.filename ||
                                        "Unknown"}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                      {selectedFaxForSending.file_path}
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            </Collapse>
                          </Box>

                          <Divider />

                          {/* Quick Select Contact */}
                          <Box>
                            <HStack
                              justify="space-between"
                              align="center"
                              mb={3}
                            >
                              <Text fontSize="md" fontWeight="bold">
                                Quick Select Contact
                              </Text>
                              <Button
                                size="sm"
                                colorScheme="green"
                                leftIcon={<AddIcon />}
                                onClick={() => setIsAddContactOpen(true)}
                              >
                                Add Contact
                              </Button>
                            </HStack>

                            {loadingContacts ? (
                              <HStack justify="center" py={4}>
                                <Spinner size="sm" />
                                <Text fontSize="sm" color="gray.500">
                                  Loading contacts...
                                </Text>
                              </HStack>
                            ) : faxContacts.length > 0 ? (
                              <Box
                                maxH="220px"
                                overflowY="auto"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                sx={{ overscrollBehavior: "contain" }}
                              >
                                <VStack spacing={0} align="stretch">
                                  {faxContacts.map((contact, idx) => (
                                    <HStack
                                      key={contact.id}
                                      p={2}
                                      bg={idx % 2 === 0 ? "gray.50" : "white"}
                                      _hover={{
                                        bg: "blue.50",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleSelectContact(contact)
                                      }
                                      justify="space-between"
                                      borderBottom={
                                        idx < faxContacts.length - 1
                                          ? "1px"
                                          : "none"
                                      }
                                      borderColor="gray.100"
                                      align="flex-start"
                                    >
                                      <Box flex={1}>
                                        <HStack spacing={2} align="center">
                                          <Badge
                                            colorScheme={
                                              contact.business_type ===
                                              "pharmacy"
                                                ? "green"
                                                : contact.business_type ===
                                                    "hospital"
                                                  ? "red"
                                                  : contact.business_type ===
                                                      "clinic"
                                                    ? "blue"
                                                    : contact.business_type ===
                                                        "insurance"
                                                      ? "purple"
                                                      : "gray"
                                            }
                                            fontSize="xs"
                                            textTransform="capitalize"
                                          >
                                            {contact.business_type || "Other"}
                                          </Badge>
                                          <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                          >
                                            {contact.name}
                                          </Text>
                                          {/* Info icon - click to view/edit contact details */}
                                          <IconButton
                                            icon={<InfoOutlineIcon />}
                                            aria-label="View/Edit contact"
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="blue"
                                            minW="auto"
                                            h="auto"
                                            p={0.5}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingContact(contact);
                                              setIsEditContactOpen(true);
                                            }}
                                          />
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600">
                                          {contact.fax_number}
                                        </Text>
                                        {/* Show truncated notes preview */}
                                        {contact.notes && (
                                          <Text
                                            fontSize="xs"
                                            color="gray.500"
                                            fontStyle="italic"
                                            noOfLines={2}
                                            mt={0.5}
                                          >
                                            {contact.notes}
                                          </Text>
                                        )}
                                      </Box>
                                      <IconButton
                                        icon={<DeleteIcon />}
                                        aria-label="Delete contact"
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteContact(
                                            contact.id,
                                            contact.name,
                                          );
                                        }}
                                      />
                                    </HStack>
                                  ))}
                                </VStack>
                              </Box>
                            ) : (
                              <Box
                                p={4}
                                bg="gray.50"
                                borderRadius="md"
                                textAlign="center"
                              >
                                <Text fontSize="sm" color="gray.500">
                                  No saved contacts. Click "Add Contact" to save
                                  frequently used fax numbers.
                                </Text>
                              </Box>
                            )}
                          </Box>

                          <Divider />

                          {/* Form Fields - Using isolated component to prevent parent re-renders */}
                          <FaxFormFields
                            ref={faxFormFieldsRef}
                            srfaxAuthEmails={srfaxAuthEmails}
                            isDisabled={sendingFax}
                            isSubmitting={sendingFax}
                            canSubmit={!!selectedFaxForSending}
                            onSubmit={async (formData) => {
                              // Handle send with form data from isolated component
                              if (
                                !selectedFaxForSending ||
                                !selectedFaxForSending.file_path
                              ) {
                                toast({
                                  title: "Error",
                                  description:
                                    "Please select or upload a file to send",
                                  status: "error",
                                  duration: 3000,
                                  isClosable: true,
                                });
                                return;
                              }

                              setSendingFax(true);
                              try {
                                const hostname = window.location.hostname;
                                const baseUrl =
                                  hostname === "localhost"
                                    ? BASE_URLS.local.backend
                                    : BASE_URLS.render.backend;

                                let sentByUser = null;
                                let sentByUserId = null;
                                try {
                                  const supabaseClient = await getSupabase();
                                  const {
                                    data: { session },
                                  } = await supabaseClient.auth.getSession();
                                  if (session?.user) {
                                    sentByUserId = session.user.id;
                                    const { data: userData, error: userError } =
                                      await supabasePublic()
                                        .from("users")
                                        .select("name, email")
                                        .eq("id", session.user.id)
                                        .single();
                                    if (!userError) {
                                      sentByUser =
                                        userData?.name ||
                                        userData?.email ||
                                        session.user.email;
                                    } else {
                                      sentByUser = session.user.email;
                                    }
                                  }
                                } catch (e) {
                                  console.warn(
                                    "Could not get user session:",
                                    e,
                                  );
                                }

                                const response = await fetch(
                                  `${baseUrl}/api/fax/send/`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                      file_path:
                                        selectedFaxForSending.file_path,
                                      fax_number: formData.fax_number.trim(),
                                      recipient_name:
                                        formData.recipient_name.trim(),
                                      sender_name: formData.sender_name.trim(),
                                      sender_fax: formData.sender_fax.trim(),
                                      ...(formData.sender_email.trim() && {
                                        sender_email:
                                          formData.sender_email.trim(),
                                      }),
                                      subject: formData.subject.trim(),
                                      cover_page: formData.cover_page,
                                      cover_page_text:
                                        formData.cover_page_text.trim(),
                                      sent_by: sentByUser,
                                      sent_by_user_id: sentByUserId,
                                      schema: userSchema, // Include schema to isolate sent faxes by clinic
                                    }),
                                  },
                                );

                                const result = await response.json();
                                if (response.ok && result.success) {
                                  toast({
                                    title: "Fax Queued",
                                    description: `Fax queued successfully. Queue ID: ${result.queue_id}`,
                                    status: "success",
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                  fetchSentFaxes();
                                  setUploadedFile(null);
                                  setSelectedFaxForSending(null);
                                  if (faxFormFieldsRef.current) {
                                    faxFormFieldsRef.current.resetForm();
                                  }
                                } else {
                                  toast({
                                    title: "Error",
                                    description:
                                      result.error || "Failed to send fax",
                                    status: "error",
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                }
                              } catch (error) {
                                console.error("Error sending fax:", error);
                                toast({
                                  title: "Error",
                                  description:
                                    "Failed to send fax: " + error.message,
                                  status: "error",
                                  duration: 5000,
                                  isClosable: true,
                                });
                              } finally {
                                setSendingFax(false);
                              }
                            }}
                          />
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </TabPanel>

                {/* Sent Tab */}
                <TabPanel p={0}>
                  <Box
                    maxH="55vh"
                    overflowY="auto"
                    overflowX="hidden"
                    p={4}
                    sx={{
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "thin",
                      overscrollBehavior: "contain",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between" align="center">
                        <Text fontSize="lg" fontWeight="bold">
                          Sent Fax History
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={fetchSentFaxes}
                          isLoading={loadingSentFaxes}
                        >
                          Refresh
                        </Button>
                      </HStack>

                      {loadingSentFaxes ? (
                        <Box textAlign="center" py={8}>
                          <Text color="gray.500">Loading sent faxes...</Text>
                        </Box>
                      ) : sentFaxes.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Text color="gray.500">No sent faxes yet</Text>
                          <Text fontSize="sm" color="gray.400" mt={2}>
                            Faxes you send will appear here
                          </Text>
                        </Box>
                      ) : (
                        <VStack spacing={3} align="stretch">
                          {sentFaxes.map((fax) => (
                            <Box
                              key={fax.id}
                              p={4}
                              border="1px"
                              borderColor={
                                fax.status === "sent"
                                  ? "green.200"
                                  : fax.status === "failed"
                                    ? "red.200"
                                    : "gray.200"
                              }
                              borderRadius="md"
                              bg={
                                fax.status === "sent"
                                  ? "green.50"
                                  : fax.status === "failed"
                                    ? "red.50"
                                    : "gray.50"
                              }
                            >
                              <HStack
                                justify="space-between"
                                align="flex-start"
                              >
                                <VStack align="flex-start" spacing={1} flex={1}>
                                  <HStack>
                                    <Text fontWeight="bold" fontSize="sm">
                                      To: {fax.recipient_fax_number}
                                    </Text>
                                    {fax.recipient_name && (
                                      <Text fontSize="sm" color="gray.600">
                                        ({fax.recipient_name})
                                      </Text>
                                    )}
                                  </HStack>
                                  <Text fontSize="xs" color="gray.600">
                                    File:{" "}
                                    {fax.filename ||
                                      fax.file_path?.split("/").pop() ||
                                      "Unknown"}
                                  </Text>
                                  {fax.subject && (
                                    <Text fontSize="xs" color="gray.500">
                                      Subject: {fax.subject}
                                    </Text>
                                  )}
                                  <Text fontSize="xs" color="gray.500">
                                    Sent:{" "}
                                    {new Date(fax.sent_at).toLocaleString(
                                      "en-US",
                                      {
                                        month: "numeric",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        timeZoneName: "short",
                                      },
                                    )}
                                    {fax.sent_by && ` by ${fax.sent_by}`}
                                  </Text>
                                  {fax.srfax_job_id && (
                                    <Text fontSize="xs" color="gray.400">
                                      Job ID: {fax.srfax_job_id}
                                    </Text>
                                  )}
                                  {fax.error_message && (
                                    <Text fontSize="xs" color="red.600">
                                      Error: {fax.error_message}
                                    </Text>
                                  )}
                                </VStack>
                                <VStack align="flex-end" spacing={2}>
                                  <Box
                                    px={2}
                                    py={1}
                                    borderRadius="md"
                                    bg={
                                      fax.status === "sent"
                                        ? "green.100"
                                        : fax.status === "failed"
                                          ? "red.100"
                                          : "yellow.100"
                                    }
                                  >
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color={
                                        fax.status === "sent"
                                          ? "green.700"
                                          : fax.status === "failed"
                                            ? "red.700"
                                            : "yellow.700"
                                      }
                                      textTransform="uppercase"
                                    >
                                      {fax.status}
                                    </Text>
                                  </Box>
                                  {fax.file_path && (
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={async () => {
                                        // Files bucket is private — open via a
                                        // short-lived signed URL (the public
                                        // URL 400s on a private bucket).
                                        const fileUrl = await getSignedFilesUrl(
                                          fax.file_path,
                                        );
                                        if (fileUrl)
                                          window.open(fileUrl, "_blank");
                                      }}
                                    >
                                      View File
                                    </Button>
                                  )}
                                </VStack>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                </TabPanel>

                {/* Spam Tab */}
                <TabPanel p={0}>
                  <Box
                    maxH="55vh"
                    overflowY="auto"
                    overflowX="hidden"
                    p={4}
                    sx={{
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "thin",
                      overscrollBehavior: "contain",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between" align="center">
                        <Text fontSize="lg" fontWeight="bold" color="red.600">
                          Spam Faxes ({spamFaxes.length})
                        </Text>
                        <HStack spacing={2}>
                          {selectedSpamFaxes.size > 0 && (
                            <>
                              <Text fontSize="sm" color="gray.600">
                                {selectedSpamFaxes.size} selected
                              </Text>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={massMoveSpamToInbox}
                                isLoading={movingSpamToInbox}
                                isDisabled={deletingSpamFaxes}
                              >
                                Move to Inbox ({selectedSpamFaxes.size})
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="red"
                                onClick={massDeleteSpamFaxes}
                                isLoading={deletingSpamFaxes}
                                isDisabled={movingSpamToInbox}
                              >
                                Delete Selected ({selectedSpamFaxes.size})
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={clearSpamSelection}
                              >
                                Clear
                              </Button>
                            </>
                          )}
                          {selectedSpamFaxes.size === 0 &&
                            spamFaxes.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={selectAllSpamFaxes}
                              >
                                Select All
                              </Button>
                            )}
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={fetchSpamFaxes}
                            isLoading={loadingSpamFaxes}
                          >
                            Refresh
                          </Button>
                        </HStack>
                      </HStack>

                      {loadingSpamFaxes ? (
                        <Box textAlign="center" py={8}>
                          <Text color="gray.500">Loading spam faxes...</Text>
                        </Box>
                      ) : spamFaxes.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Text color="gray.500">No spam faxes</Text>
                          <Text fontSize="sm" color="gray.400" mt={2}>
                            Faxes detected as spam will appear here
                          </Text>
                        </Box>
                      ) : (
                        <VStack spacing={4} align="stretch">
                          {groupedSpamFaxesByDate.sortedDates.map((dateKey) => (
                            <Box key={dateKey} mb={4}>
                              <FaxDateHeader
                                dateKey={dateKey}
                                count={groupedSpamFaxesByDate.groupedByDate[dateKey].length}
                                noun={groupedSpamFaxesByDate.groupedByDate[dateKey].length === 1
                                  ? "fax" : "faxes"}
                                isCollapsed={spamDates.isCollapsed(dateKey)}
                                onToggle={spamDates.toggle}
                                color="red.700"
                                borderColor="red.200"
                              />
                              <VStack
                                spacing={3}
                                align="stretch"
                                display={spamDates.isCollapsed(dateKey) ? "none" : undefined}
                              >
                                {groupedSpamFaxesByDate.groupedByDate[
                                  dateKey
                                ].map((fax) => (
                                  <Box
                                    key={fax.file_path || fax.filename}
                                      data-fax-doc={faxDocumentKey(fax)}
                                    p={4}
                                    border="1px"
                                    borderColor="red.200"
                                    borderRadius="md"
                                    bg="red.50"
                                  >
                                    <HStack
                                      justify="space-between"
                                      align="flex-start"
                                      spacing={3}
                                    >
                                      <Checkbox
                                        isChecked={selectedSpamFaxes.has(
                                          fax.file_path,
                                        )}
                                        onChange={() =>
                                          toggleSpamFaxSelection(fax.file_path)
                                        }
                                        colorScheme="red"
                                      />
                                      <VStack
                                        align="flex-start"
                                        spacing={2}
                                        flex={1}
                                      >
                                        <HStack>
                                          <Text fontWeight="bold">
                                            {fax.filename || "Unknown"}
                                          </Text>
                                          <Text
                                            fontSize="xs"
                                            color="red.600"
                                            fontWeight="bold"
                                            bg="red.100"
                                            px={2}
                                            py={1}
                                            borderRadius="md"
                                          >
                                            SPAM
                                          </Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600">
                                          {fax.file_path || "N/A"}
                                        </Text>
                                        <FaxClassificationBadge fax={fax} />
                                    <FaxSchemaBadges fax={fax} viewingSchema={userSchema} />
                                    <FaxMatchedDate fax={fax} />
                                    <FaxPendingIdentity
                                      fax={fax}
                                      onResolve={handleResolveIdentity}
                                    />
                                        {fax.summary && (
                                          <Text
                                            fontSize="xs"
                                            color="gray.500"
                                            noOfLines={2}
                                          >
                                            {fax.summary}
                                          </Text>
                                        )}
                                        <Text fontSize="xs" color="gray.400">
                                          Received:{" "}
                                          {fax.created_at
                                            ? new Date(
                                                fax.created_at,
                                              ).toLocaleTimeString("en-US", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                timeZoneName: "short",
                                              })
                                            : "Unknown time"}
                                        </Text>
                                        {orphanedFilePaths.has(
                                          fax.file_path,
                                        ) && (
                                          <HStack
                                            mt={2}
                                            p={2}
                                            bg="orange.50"
                                            border="1px"
                                            borderColor="orange.300"
                                            borderRadius="md"
                                            spacing={2}
                                          >
                                            <Text
                                              fontSize="xs"
                                              color="orange.700"
                                              fontWeight="medium"
                                            >
                                              File for this{" "}
                                              {fax.is_faxed_report
                                                ? "fax"
                                                : "report"}{" "}
                                              not found in database.
                                            </Text>
                                            <Button
                                              size="xs"
                                              colorScheme="red"
                                              onClick={() =>
                                                deleteOrphanedFax(fax)
                                              }
                                            >
                                              Delete
                                            </Button>
                                          </HStack>
                                        )}
                                      </VStack>
                                      <VStack spacing={2} align="flex-end">
                                        <HStack spacing={2}>
                                          <Button
                                            size="sm"
                                            colorScheme="green"
                                            onClick={async () => {
                                              try {
                                                const filePath =
                                                  fax.file_path || "";
                                                const hostname =
                                                  window.location.hostname;
                                                const baseUrl =
                                                  hostname === "localhost"
                                                    ? BASE_URLS.local.backend
                                                    : BASE_URLS.render.backend;

                                                const response = await fetch(
                                                  `${baseUrl}/api/fax/get-view-url/?file_path=${encodeURIComponent(filePath)}`,
                                                  { headers: await authHeader() },
                                                );

                                                if (response.ok) {
                                                  const data =
                                                    await response.json();
                                                  window.open(
                                                    data.url,
                                                    "_blank",
                                                  );
                                                } else {
                                                  const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
                                                  window.open(
                                                    storageUrl,
                                                    "_blank",
                                                  );
                                                }
                                              } catch (error) {
                                                console.error(
                                                  "Error getting fax view URL:",
                                                  error,
                                                );
                                                const filePath =
                                                  fax.file_path || "";
                                                const storageUrl = `${supabaseUrl}/storage/v1/object/public/files/${filePath}`;
                                                window.open(
                                                  storageUrl,
                                                  "_blank",
                                                );
                                              }
                                            }}
                                          >
                                            View File
                                          </Button>
                                          <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={() =>
                                              handleExtractTextMetadata(fax)
                                            }
                                            isDisabled={
                                              fax?.extracted_text_metadata ||
                                              processingTasks[
                                                `extractText_${fax.file_path}`
                                              ]
                                            }
                                            isLoading={
                                              processingTasks[
                                                `extractText_${fax.file_path}`
                                              ]
                                            }
                                            loadingText="Extracting..."
                                          >
                                            Extract Text
                                          </Button>
                                          {(fax?.has_full_text ||
                                            fax?.full_text ||
                                            fax?.extracted_text_metadata) && (
                                            <Button
                                              size="sm"
                                              colorScheme="teal"
                                              variant="outline"
                                              onClick={() =>
                                                handleViewExtractedText(fax)
                                              }
                                            >
                                              View Text
                                            </Button>
                                          )}
                                        </HStack>
                                        <HStack spacing={2}>
                                          <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={() =>
                                              markFaxAsNotSpam(fax)
                                            }
                                          >
                                            Move to Inbox
                                          </Button>
                                          <Button
                                            size="sm"
                                            colorScheme="red"
                                            onClick={() => deleteSpamFax(fax)}
                                          >
                                            Delete
                                          </Button>
                                        </HStack>
                                      </VStack>
                                    </HStack>
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onFaxInboxClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Extracted Text Modal - Non-blocking, draggable, resizable */}
      <DraggableResizableModal
        isOpen={isViewTextModalOpen}
        onClose={onViewTextModalClose}
        title={`Extracted Text: ${viewingTextData?.filename || "Unknown"}`}
        defaultSize={{ width: 800, height: 600 }}
        minSize={{ width: 400, height: 300 }}
        zIndex={1450}
      >
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#4A5568",
              }}
            >
              File Path:
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#2D3748",
                fontFamily: "monospace",
                padding: "8px",
                background: "#F7FAFC",
                borderRadius: "6px",
                margin: 0,
              }}
            >
              {viewingTextData?.file_path || "N/A"}
            </p>
          </div>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleReExtractText}
            isLoading={isReExtractingText}
            loadingText="Re-extracting..."
            ml={4}
            mt={6}
          >
            Re-extract
          </Button>
        </div>

        <div>
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#4A5568",
            }}
          >
            Extracted Text ({viewingTextData?.full_text?.length || 0}{" "}
            characters):
          </h3>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#F7FAFC",
              borderRadius: "6px",
              border: "1px solid #E2E8F0",
            }}
          >
            <pre
              style={{
                fontSize: "14px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                margin: 0,
                color: "#2D3748",
              }}
            >
              {viewingTextData?.full_text || "No text available"}
            </pre>
          </div>
        </div>
      </DraggableResizableModal>

      {/* Truncation Confirmation Modal - higher z-index to appear above DraggableResizableModal */}
      <Modal
        isOpen={isTruncationModalOpen}
        onClose={() => {}}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="600px" containerProps={{ zIndex: 10002 }}>
          <ModalHeader fontSize="lg">Document Truncated</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm">
                The document <strong>{truncationInfo?.filename}</strong> has{" "}
                <strong>{truncationInfo?.total_pages} pages</strong>, but only
                the first{" "}
                <strong>{truncationInfo?.pages_processed} pages</strong> were
                processed for AI extraction.
              </Text>
              <Text fontSize="sm">
                There are{" "}
                <strong>
                  {truncationInfo?.remaining_pages} remaining pages
                </strong>{" "}
                that have not been processed.
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                Would you like to process the remaining pages and append them to
                the extracted content?
              </Text>
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                fontSize="xs"
                onClick={handleViewTextFromTruncationModal}
              >
                View Extracted Text (first {truncationInfo?.pages_processed}{" "}
                pages)
              </Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              fontSize="xs"
              mr={3}
              onClick={handleDeclineContinueExtraction}
            >
              No, this is fine
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              fontSize="xs"
              onClick={handleContinueExtraction}
              isLoading={isContinuingExtraction}
              loadingText={continueOcrProgressLabel(continueProgress)}
            >
              Yes, process remaining pages
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Task Modal */}
      <Modal isOpen={isAssignTaskModalOpen} onClose={onAssignTaskModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Task to User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Search and select a user to assign {selectedFaxes.size} fax
                task(s) to:
              </Text>
              <Box position="relative">
                <Input
                  placeholder="Search by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setUserSearchQuery(newValue);
                    if (!newValue) {
                      setSelectedUserForTask(null);
                      setShowUserDropdown(false);
                    } else {
                      // If user is typing something different than the selected user, clear selection
                      if (
                        selectedUserForTask &&
                        newValue !==
                          (selectedUserForTask.name ||
                            selectedUserForTask.email)
                      ) {
                        setSelectedUserForTask(null);
                      }
                      setShowUserDropdown(true);
                    }
                  }}
                  onFocus={() => {
                    // Show dropdown when input is focused, if there's a query
                    if (userSearchQuery) {
                      setShowUserDropdown(true);
                      if (
                        userSearchResults.length === 0 &&
                        !selectedUserForTask
                      ) {
                        searchUsers(userSearchQuery);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Delay to allow click events on dropdown items to fire first
                    setTimeout(() => {
                      // Close dropdown when clicking outside
                      setShowUserDropdown(false);
                    }, 200);
                  }}
                />
                {userSearchQuery && showUserDropdown && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    maxH="300px"
                    overflowY="auto"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="white"
                    zIndex={1000}
                    boxShadow="lg"
                  >
                    {searchingUsers ? (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                      >
                        Searching...
                      </Text>
                    ) : userSearchResults.length === 0 ? (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                      >
                        No users found
                      </Text>
                    ) : (
                      <VStack spacing={0} align="stretch">
                        {userSearchResults.map((user) => (
                          <Box
                            key={user.id}
                            p={3}
                            borderBottom="1px"
                            borderColor="gray.100"
                            cursor="pointer"
                            onMouseDown={(e) => {
                              // Prevent onBlur from firing before onClick
                              e.preventDefault();
                            }}
                            onClick={() => {
                              setSelectedUserForTask(user);
                              setUserSearchQuery(user.name || user.email);
                              setUserSearchResults([]);
                              setShowUserDropdown(false);
                            }}
                            _hover={{
                              bg: "gray.50",
                            }}
                            _last={{
                              borderBottom: "none",
                            }}
                          >
                            <VStack align="flex-start" spacing={1}>
                              <Text fontWeight="bold">
                                {user.name || user.email}
                              </Text>
                              {user.name && (
                                <Text fontSize="sm" color="gray.600">
                                  {user.email}
                                </Text>
                              )}
                              {user.role && (
                                <Text fontSize="xs" color="gray.500">
                                  Role: {user.role}
                                </Text>
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}
              </Box>
              {selectedUserForTask && (
                <Box
                  p={3}
                  border="1px"
                  borderColor="blue.500"
                  borderRadius="md"
                  bg="blue.50"
                >
                  <Text fontSize="sm" fontWeight="bold" mb={1}>
                    Selected User:
                  </Text>
                  <Text fontWeight="bold">
                    {selectedUserForTask.name || selectedUserForTask.email}
                  </Text>
                  {selectedUserForTask.name && (
                    <Text fontSize="sm" color="gray.600">
                      {selectedUserForTask.email}
                    </Text>
                  )}
                  {selectedUserForTask.role && (
                    <Text fontSize="xs" color="gray.500">
                      Role: {selectedUserForTask.role}
                    </Text>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                onAssignTaskModalClose();
                setUserSearchQuery("");
                setSelectedUserForTask(null);
                setUserSearchResults([]);
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleAssignTask}
              isDisabled={!selectedUserForTask}
            >
              Assign Task
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Fax to Patient Modal */}
      <Modal
        isOpen={!!selectedFaxForAssignment}
        onClose={() => {
          setSelectedFaxForAssignment(null);
          setSelectedPatientForAssignment(null);
        }}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        {/* A column: header and footer stay put, only the body scrolls.
            ModalContent used to scroll as a whole, which pushed Assign/Cancel
            below the fold on a long patient list — the buttons were there, just
            unreachable without scrolling a nested scroller to its end. */}
        <ModalContent maxW="90vw" h="88vh" display="flex" flexDirection="column">
          <ModalHeader>
            {selectedFaxForAssignment?.is_unmatched
              ? "Assign Fax to Patient"
              : "Reassign Fax to Patient"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" minH="0" display="flex" flexDirection="column" pb={2}>
            <VStack spacing={3} align="stretch" flex="1" minH="0">
              <Text>
                {selectedFaxForAssignment?.is_unmatched
                  ? "Select a patient to assign:"
                  : "Select a new patient to reassign:"}{" "}
                <strong>{selectedFaxForAssignment?.filename}</strong>
              </Text>
              {/* Read from the path like the inbox rows do, so reassigning
                  shows the same visit the entry showed — not the raw
                  `MM-DD-YY` folder name this printed before. */}
              {faxMatchedVisitDate(selectedFaxForAssignment) && (
                <Text fontSize="sm" color="gray.600">
                  Currently filed onto the{" "}
                  {formatVisitDate(
                    faxMatchedVisitDate(selectedFaxForAssignment),
                  )}{" "}
                  visit
                </Text>
              )}
              {/* The list takes the remaining height instead of a fixed
                  60vh, so more rows are visible; `minW=1200px` is gone, which
                  is what forced sideways scrolling on every screen. */}
              <Box
                borderWidth="1px"
                borderRadius="lg"
                p={3}
                flex="1"
                minH="0"
                overflowY="auto"
                overflowX="auto"
                w="100%"
              >
                <SearchComponent
                  onPatientSelect={(patient) => {
                    setSelectedPatientForAssignment(patient);
                  }}
                  showCheckboxes={true}
                  disableUploader={true}
                />
              </Box>
              {selectedPatientForAssignment &&
                selectedPatientForAssignment.id && (
                  <Box>
                    <Text fontSize="sm" color="green.600" fontWeight="medium">
                      Selected:{" "}
                      {selectedPatientForAssignment[prompts[0][0]] ||
                        selectedPatientForAssignment.name ||
                        "Unknown Patient"}
                      {selectedPatientForAssignment.date_ &&
                        ` (Scheduled: ${selectedPatientForAssignment.date_})`}
                    </Text>
                    {/* Says where the fax will actually go, because it is more
                        than the row that was clicked: the same patient in every
                        schema of the clinic group, on their visit and on their
                        chart row. */}
                    <Text fontSize="xs" color="gray.600">
                      Filed onto this patient's visit and chart row, in every
                      schema of the clinic group they appear in.
                    </Text>
                  </Box>
                )}
              {assigningFax && (
                <Text fontSize="sm" color="gray.600">
                  {selectedFaxForAssignment?.is_unmatched
                    ? "Assigning"
                    : "Reassigning"}{" "}
                  fax...
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedFaxForAssignment(null);
                setSelectedPatientForAssignment(null);
              }}
              isDisabled={assigningFax}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                if (
                  selectedPatientForAssignment &&
                  selectedPatientForAssignment.id &&
                  selectedFaxForAssignment
                ) {
                  assignFaxToPatient(
                    selectedFaxForAssignment,
                    selectedPatientForAssignment.id,
                  );
                }
              }}
              isDisabled={
                !selectedPatientForAssignment ||
                !selectedPatientForAssignment.id ||
                assigningFax
              }
              ml={3}
            >
              {selectedFaxForAssignment?.is_unmatched ? "Assign" : "Reassign"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Match Confirmation Modal */}
      <Modal
        isOpen={isConfirmMatchOpen}
        onClose={onConfirmMatchClose}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Match</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {confirmationModal && (
              <VStack spacing={4} align="stretch">
                <Text>
                  The AI extracted the name{" "}
                  <Text as="span" fontWeight="bold">
                    "{confirmationModal.extracted_name}"
                  </Text>{" "}
                  from the fax.
                </Text>
                <Text>
                  Should we match it to{" "}
                  <Text as="span" fontWeight="bold" color="blue.600">
                    {confirmationModal.matched_patient.name}
                  </Text>
                  ?
                </Text>
                <Box p={3} bg="blue.50" borderRadius="md">
                  <VStack align="flex-start" spacing={2}>
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="bold">
                        Similarity:
                      </Text>{" "}
                      {confirmationModal.matched_patient.similarity}%
                    </Text>
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="bold">
                        Scheduled Date:
                      </Text>{" "}
                      {confirmationModal.matched_patient
                        .scheduled_date_display ||
                        confirmationModal.matched_patient.scheduled_date ||
                        "Unknown"}
                    </Text>
                  </VStack>
                </Box>
                <Button
                  colorScheme="green"
                  onClick={openFaxViewFromModal}
                  size="sm"
                >
                  View File
                </Button>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={() => handleConfirmMatch("try_again")}
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => handleConfirmMatch("leave_unmatched")}
              >
                Leave Unmatched
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => handleConfirmMatch("confirm")}
              >
                Confirm Match
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rename Fax Modal */}
      <Modal isOpen={isRenameFaxOpen} onClose={onRenameFaxClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Fax</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Rename fax:{" "}
                <Text as="span" fontWeight="bold">
                  {selectedFaxForRename?.filename || "N/A"}
                </Text>
              </Text>
              <FormControl>
                <FormLabel>New Filename</FormLabel>
                <Input
                  value={newFaxName}
                  onChange={(e) => setNewFaxName(e.target.value)}
                  placeholder="Enter new filename (without extension)"
                  isDisabled={renamingFax}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !renamingFax) {
                      renameFax();
                    }
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  The file extension will be preserved automatically
                </Text>
              </FormControl>
              {renamingFax && (
                <Text fontSize="sm" color="gray.600">
                  Renaming fax...
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onRenameFaxClose}
              variant="ghost"
              isDisabled={renamingFax}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={renameFax}
              isLoading={renamingFax}
              isDisabled={!newFaxName.trim()}
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change Fax Classification Modal */}
      <Modal
        isOpen={isChangeFaxClassificationOpen}
        onClose={onChangeFaxClassificationClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Fax Classification</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Change classification for:{" "}
                <Text as="span" fontWeight="bold">
                  {selectedFaxForClassification?.filename || "N/A"}
                </Text>
              </Text>
              <FormControl>
                <FormLabel>Classification Category</FormLabel>
                <Select
                  value={newFaxClassification}
                  onChange={(e) => setNewFaxClassification(e.target.value)}
                  isDisabled={changingFaxClassification}
                >
                  <option value="Labs">Labs</option>
                  <option value="Images">Images</option>
                  <option value="Medical Records">Medical Records</option>
                  <option value="Other">Other</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={onChangeFaxClassificationClose}
              isDisabled={changingFaxClassification}
            >
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={changeFaxClassification}
              isLoading={changingFaxClassification}
              ml={3}
            >
              Update Classification
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Fax Confirmation Modal */}
      <Modal isOpen={isDeleteFaxOpen} onClose={onDeleteFaxClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Fax</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to delete:{" "}
                <Text as="span" fontWeight="bold">
                  {faxToDelete?.filename || "N/A"}
                </Text>
                ?
              </Text>
              <Text color="red.600" fontWeight="bold" fontSize="sm">
                ⚠️ Warning: This will delete the fax from being assigned to any
                patients.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button ref={cancelRef} onClick={onDeleteFaxClose} variant="ghost">
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={deleteFax}
              ml={3}
              isLoading={deletingFax}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mass Assign Modal */}
      <Modal isOpen={isMassAssignOpen} onClose={onMassAssignClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mass Assign Faxes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Assign {selectedFaxes.size} selected fax(es) to a patient:
              </Text>
              <FormControl>
                <FormLabel>Select Patient</FormLabel>
                <Select
                  placeholder="Search for patient..."
                  onChange={(e) => {
                    const patientId = e.target.value;
                    if (patientId) {
                      massAssignFaxes(patientId);
                    }
                  }}
                  isDisabled={massAssigning}
                >
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient[prompts[0][0]] ||
                        patient.name ||
                        patient[colid2] ||
                        "Unknown Patient"}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {massAssigning && (
                <Text fontSize="sm" color="gray.600">
                  Assigning faxes...
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onMassAssignClose}
              variant="ghost"
              isDisabled={massAssigning}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mass Delete Confirmation Modal */}
      <Modal isOpen={isMassDeleteOpen} onClose={onMassDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mass Delete Faxes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to delete {selectedFaxes.size} selected
                fax(es)?
              </Text>
              <Text color="red.600" fontWeight="bold" fontSize="sm">
                ⚠️ Warning: This will delete the faxes from being assigned to
                any patients.
              </Text>
              {massDeleting && (
                <Text fontSize="sm" color="gray.600">
                  Deleting faxes...
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onMassDeleteClose}
              variant="ghost"
              isDisabled={massDeleting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={massDeleteFaxes}
              ml={3}
              isLoading={massDeleting}
            >
              Delete {selectedFaxes.size} Fax(es)
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reminder Modal */}
      <Modal
        isOpen={isReminderModalOpen}
        onClose={onReminderModalClose}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="space-between" w="full" pr={8}>
              <Text>
                {reminderModalTab === 1
                  ? "Send Confirmation Reminders"
                  : "Send Form Reminders"}
              </Text>
              {reminderModalTab === 1 ? (
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => openTriggerPreview("confirmations")}
                  isLoading={isTriggeringAutoConfirmations}
                  loadingText="Triggering..."
                  data-testid="trigger-auto-confirmations"
                >
                  Trigger Auto-Confirmations
                </Button>
              ) : (
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => openTriggerPreview("reminders")}
                  isLoading={isTriggeringAutoReminders}
                  loadingText="Triggering..."
                  data-testid="trigger-auto-reminders"
                >
                  Trigger Auto-Reminders
                </Button>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          {/* Two tabs over one modal: the original form reminders content
              (untouched, tab 0) and its confirmation reminders copy (tab 1).
              Lazy so the confirmations tab only loads once it is opened;
              kept mounted so switching back and forth keeps its state. */}
          <Tabs
            index={reminderModalTab}
            onChange={setReminderModalTab}
            isLazy
            lazyBehavior="keepMounted"
            variant="soft-rounded"
            colorScheme="teal"
          >
            <TabList px={6} pt={1}>
              <Tab>Form reminders</Tab>
              <Tab>Confirmation reminders</Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Summary Stats */}
              <Box
                p={4}
                bg="blue.50"
                borderRadius="md"
                border="1px"
                borderColor="blue.200"
              >
                <HStack spacing={6} justify="center">
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                      {reminderStatus.total_patients}
                    </Text>
                    <Text fontSize="sm" color="blue.600">
                      Total Patients
                    </Text>
                  </VStack>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color="green.700">
                      {reminderStatus.patients_with_forms}
                    </Text>
                    <Text fontSize="sm" color="green.600">
                      Completed Forms
                    </Text>
                  </VStack>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color="red.700">
                      {reminderStatus.patients_without_forms}
                    </Text>
                    <Text fontSize="sm" color="red.600">
                      Pending Forms
                    </Text>
                  </VStack>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color="purple.700">
                      {reminderStatus.completion_rate}%
                    </Text>
                    <Text fontSize="sm" color="purple.600">
                      Completion Rate
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* Contact Method Selection */}
              <FormControl>
                <FormLabel>Contact Method</FormLabel>
                <VStack align="flex-start">
                  <Checkbox
                    isChecked={manualReminderContactMethods.email}
                    onChange={(e) =>
                      setManualReminderContactMethods((prev) => ({
                        ...prev,
                        email: e.target.checked,
                      }))
                    }
                  >
                    Email
                  </Checkbox>
                  <Checkbox
                    isChecked={manualReminderContactMethods.phone}
                    onChange={(e) =>
                      setManualReminderContactMethods((prev) => ({
                        ...prev,
                        phone: e.target.checked,
                      }))
                    }
                  >
                    Phone/SMS
                  </Checkbox>
                </VStack>
                {manualReminderContactMethods.phone && (
                  <Checkbox
                    mt={3}
                    isChecked={reminderSplitSms}
                    onChange={(e) => setReminderSplitSms(e.target.checked)}
                    colorScheme="blue"
                  >
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">Split SMS into multiple parts</Text>
                      <Text fontSize="xs" color="gray.500">
                        Uses your configured character limit (
                        {customSmsCharacterLimit} — Change Limit in SMS tools).
                        Recommended for long reminders.
                      </Text>
                    </VStack>
                  </Checkbox>
                )}
              </FormControl>

              {/* Scheduling */}
              <FormControl>
                <FormLabel>Scheduling</FormLabel>
                <VStack align="flex-start" spacing={2}>
                  <Checkbox
                    isChecked={manualScheduleReminder}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setManualScheduleReminder(checked);
                      // First time toggling ON: seed with one default row
                      if (checked && manualScheduledReminders.length === 0) {
                        const defaultDate =
                          getDayBeforeDateString(
                            selectedDate,
                            reminderDaysBeforeAppointment,
                          ) || selectedDate || "";
                        const defaultTime = reminderScheduledTime || "09:00";
                        setManualScheduledReminders([
                          {
                            id: `r_${Date.now()}_${Math.random()
                              .toString(36)
                              .slice(2, 7)}`,
                            date: defaultDate,
                            time: defaultTime,
                          },
                        ]);
                      }
                      // Toggling OFF: clear list so the next ON starts fresh
                      if (!checked) setManualScheduledReminders([]);
                    }}
                  >
                    Schedule reminders instead of sending immediately
                  </Checkbox>
                  <Text fontSize="sm" color="gray.600">
                    When enabled, reminders are queued now and sent at the
                    scheduled day and time. Add one or more send dates
                    manually — each date must be different.
                  </Text>
                  {manualScheduleReminder && (
                    <Box w="100%" mt={1}>
                      {manualScheduledReminders.length === 0 && (
                        <Text fontSize="xs" color="orange.600" mb={2}>
                          Add at least one send date below before sending.
                        </Text>
                      )}
                      <VStack align="stretch" spacing={2}>
                        {manualScheduledReminders.map((r, idx) => {
                          const dupDate = manualScheduledReminders.find(
                            (other, otherIdx) =>
                              otherIdx !== idx &&
                              other.date &&
                              other.date === r.date,
                          );
                          const isInvalid = !r.date;
                          return (
                            <HStack
                              key={r.id}
                              spacing={2}
                              p={2}
                              borderWidth="1px"
                              borderColor={
                                dupDate || isInvalid ? "red.300" : "gray.200"
                              }
                              borderRadius="md"
                              bg="white"
                              align="flex-end"
                            >
                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                  Reminder #{idx + 1} — Send on date
                                </Text>
                                <Input
                                  type="date"
                                  size="sm"
                                  value={r.date}
                                  onChange={(e) => {
                                    const next = [...manualScheduledReminders];
                                    next[idx] = {
                                      ...next[idx],
                                      date: e.target.value,
                                    };
                                    setManualScheduledReminders(next);
                                  }}
                                  w="170px"
                                />
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                  At time (PST)
                                </Text>
                                <Input
                                  type="time"
                                  size="sm"
                                  value={r.time}
                                  onChange={(e) => {
                                    const next = [...manualScheduledReminders];
                                    next[idx] = {
                                      ...next[idx],
                                      time: e.target.value,
                                    };
                                    setManualScheduledReminders(next);
                                  }}
                                  w="120px"
                                />
                              </Box>
                              {dupDate && (
                                <Text
                                  fontSize="xs"
                                  color="red.500"
                                  flex="1"
                                  pb={2}
                                >
                                  Duplicate date
                                </Text>
                              )}
                              {isInvalid && !dupDate && (
                                <Text
                                  fontSize="xs"
                                  color="red.500"
                                  flex="1"
                                  pb={2}
                                >
                                  Date required
                                </Text>
                              )}
                              <IconButton
                                aria-label="Remove reminder"
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<span style={{ fontWeight: 700 }}>×</span>}
                                isDisabled={manualScheduledReminders.length <= 1}
                                onClick={() => {
                                  setManualScheduledReminders(
                                    manualScheduledReminders.filter(
                                      (x) => x.id !== r.id,
                                    ),
                                  );
                                }}
                              />
                            </HStack>
                          );
                        })}
                      </VStack>
                      <Button
                        mt={2}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => {
                          // Find the last filled date and suggest the next day
                          // so the user can quickly add a follow-up reminder.
                          const filled = manualScheduledReminders.filter(
                            (x) => x.date && /^\d{4}-\d{2}-\d{2}$/.test(x.date),
                          );
                          const seedDate = filled.length
                            ? getDayBeforeDateString(
                                filled[filled.length - 1].date,
                                -1,
                              ) || ""
                            : "";
                          setManualScheduledReminders([
                            ...manualScheduledReminders,
                            {
                              id: `r_${Date.now()}_${Math.random()
                                .toString(36)
                                .slice(2, 7)}`,
                              date: seedDate,
                              time: reminderScheduledTime || "09:00",
                            },
                          ]);
                        }}
                      >
                        + Add another reminder date
                      </Button>
                    </Box>
                  )}
                </VStack>
              </FormControl>

              {/* Reminders split into two independent windows:
                  (1) Scheduled (active: scheduled / processing / pending)
                  (2) Sent / Cancelled / Failed (history)
                  Each window has its own header, refresh, scroll area, and
                  empty state so the user can scan them independently. */}
              {(() => {
                const typeFilter =
                  manualReminderPatientTypeFilter.length > 0
                    ? manualReminderPatientTypeFilter.map((t) =>
                        t.toUpperCase().trim(),
                      )
                    : null;
                const filteredByType = typeFilter
                  ? (row) => {
                      const reminderType = (row.type || "")
                        .toString()
                        .toUpperCase()
                        .trim();
                      return typeFilter.includes(reminderType);
                    }
                  : () => true;

                const activeReminders = pendingScheduledReminders
                  .filter((r) =>
                    ["scheduled", "processing", "pending"].includes(
                      String(r.status || "").toLowerCase(),
                    ),
                  )
                  .filter(filteredByType);

                const completedReminders = pendingScheduledReminders
                  .filter(
                    (r) =>
                      !["scheduled", "processing", "pending"].includes(
                        String(r.status || "").toLowerCase(),
                      ),
                  )
                  .filter(filteredByType);

                const renderReminderRow = (r, { isActive }) => {
                  const typeColor = r.type
                    ? r.type
                        .toUpperCase()
                        .includes("FF")
                      ? "purple"
                      : r.type
                          .toUpperCase()
                          .includes("NP")
                        ? "green"
                        : r.type
                            .toUpperCase()
                            .includes("NS")
                          ? "teal"
                          : "gray"
                    : "gray";
                  const statusColor = isActive
                    ? r.status === "processing"
                      ? "purple"
                      : "blue"
                    : r.status === "sent"
                      ? "green"
                      : r.status === "cancelled"
                        ? "orange"
                        : r.status === "failed"
                          ? "red"
                          : "gray";
                  return (
                    <Box
                      key={r.id}
                      p={2}
                      bg={isActive ? "white" : "gray.50"}
                      border="1px"
                      borderColor={isActive ? "blue.200" : "gray.200"}
                      borderRadius="md"
                      opacity={isActive ? 1 : 0.85}
                    >
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="flex-start" spacing={0} flex={1}>
                          <HStack spacing={1} align="center">
                            <Text fontWeight="semibold" fontSize="sm">
                              {r.name}
                            </Text>
                            {r.type && (
                              <Badge
                                colorScheme={typeColor}
                                fontSize="xs"
                                px={2}
                                borderRadius="full"
                              >
                                {r.type.toUpperCase()}
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.600">
                            Appointment:{" "}
                            {r.appointment_date
                              ? `${formatDateString(r.appointment_date)}`
                              : "N/A"}
                            {r.appointment_time
                              ? ` at ${r.appointment_time}`
                              : ""}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {isActive ? "Will send:" : "Was scheduled:"}{" "}
                            {r.scheduled_for
                              ? formatLocalDateTimeString(r.scheduled_for)
                              : "N/A"}
                          </Text>
                          {(r.phone || r.email) && (
                            <Text fontSize="xs" color="gray.600">
                              {r.phone ? `📞 ${r.phone}` : ""}
                              {r.phone && <PhoneStatusBadge phone={r.phone} compact ml={1} />}
                              {r.phone && r.email ? "  |  " : ""}
                              {r.email ? `📧 ${r.email}` : ""}
                            </Text>
                          )}
                        </VStack>
                        <VStack align="flex-end" spacing={1}>
                          <Badge colorScheme={statusColor}>
                            {String(
                              r.status || (isActive ? "scheduled" : ""),
                            ).toUpperCase()}
                          </Badge>
                          <Text fontSize="xs" color="gray.600">
                            Attempts: {r.attempts ?? 0}
                          </Text>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="blue"
                              onClick={() =>
                                handlePreviewScheduledReminder(r)
                              }
                            >
                              Preview
                            </Button>
                            {isActive && (
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="red"
                                onClick={() => cancelScheduledReminder(r.id)}
                                isLoading={
                                  cancellingScheduledReminderId === r.id
                                }
                                isDisabled={[
                                  "sent",
                                  "failed",
                                  "cancelled",
                                ].includes(
                                  String(r.status || "").toLowerCase(),
                                )}
                              >
                                Cancel
                              </Button>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                    </Box>
                  );
                };

                const renderReminderList = (list, { isActive, emptyText }) => {
                  if (loadingPendingScheduledReminders) {
                    return (
                      <HStack>
                        <Spinner size="sm" />
                        <Text fontSize="sm">Loading reminders...</Text>
                      </HStack>
                    );
                  }
                  if (list.length === 0) {
                    return (
                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                        {emptyText}
                      </Text>
                    );
                  }
                  return (
                    <VStack
                      align="stretch"
                      spacing={2}
                      maxHeight={isActive ? "220px" : "180px"}
                      overflowY="auto"
                    >
                      {list.map((r) =>
                        renderReminderRow(r, { isActive }),
                      )}
                    </VStack>
                  );
                };

                return (
                  <>
                    {/* Window 1: Scheduled (active) */}
                    <Box
                      p={4}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px"
                      borderColor="blue.200"
                    >
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="flex-start" spacing={0}>
                          <HStack spacing={2} align="center">
                            <Text fontWeight="bold" color="blue.700">
                              Scheduled
                            </Text>
                            <Badge colorScheme="blue">
                              {activeReminders.length}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="blue.700">
                            Pending, scheduled, or processing reminders for
                            appointments on {selectedDate}. Times in PST.
                          </Text>
                        </VStack>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            onClick={fetchPendingScheduledReminders}
                            isLoading={loadingPendingScheduledReminders}
                          >
                            Refresh
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            onClick={cancelAllScheduledRemindersForDay}
                            isLoading={cancellingAllScheduledReminders}
                            isDisabled={activeReminders.length === 0}
                          >
                            Cancel all for this day
                          </Button>
                        </HStack>
                      </HStack>
                      <Box mt={3}>
                        {renderReminderList(activeReminders, {
                          isActive: true,
                          emptyText: "No scheduled reminders for this day.",
                        })}
                      </Box>
                    </Box>

                    {/* Window 2: Sent / Cancelled / Failed (history) */}
                    <Box
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.300"
                    >
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="flex-start" spacing={0}>
                          <HStack spacing={2} align="center">
                            <Text fontWeight="bold" color="gray.700">
                              Sent / Cancelled / Failed
                            </Text>
                            <Badge colorScheme="gray">
                              {completedReminders.length}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            History of reminders already delivered, cancelled,
                            or failed for appointments on {selectedDate}.
                            Times in PST.
                          </Text>
                        </VStack>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={fetchPendingScheduledReminders}
                          isLoading={loadingPendingScheduledReminders}
                        >
                          Refresh
                        </Button>
                      </HStack>
                      <Box mt={3}>
                        {renderReminderList(completedReminders, {
                          isActive: false,
                          emptyText:
                            "No sent, cancelled, or failed reminders for this day.",
                        })}
                      </Box>
                    </Box>
                  </>
                );
              })()}

              {/* Patient Type Filter */}
              <FormControl>
                <FormLabel>
                  Filter by Patient Type ({reminderPatientTypeFilter.length}{" "}
                  selected)
                </FormLabel>
                <VStack
                  align="flex-start"
                  spacing={2}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  bg="gray.50"
                  maxHeight="200px"
                  overflowY="auto"
                >
                  {availablePatientTypes.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      No patient types found
                    </Text>
                  ) : (
                    <>
                      <Checkbox
                        isChecked={manualReminderPatientTypeFilter.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setManualReminderPatientTypeFilter([]);
                            // Auto-select all patients when "All Types" is selected
                            // (handled by useEffect)
                          }
                        }}
                      >
                        All Types ({allowedPatientsWithoutForms.length})
                      </Checkbox>
                      {availablePatientTypes.map((type) => {
                        const typeCount = allowedPatientsWithoutForms.filter(
                          (p) => (p.type || "").toUpperCase().trim() === type,
                        ).length;
                        return (
                          <Checkbox
                            key={type}
                            isChecked={manualReminderPatientTypeFilter.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setManualReminderPatientTypeFilter((prev) => [
                                  ...prev,
                                  type,
                                ]);
                              } else {
                                setManualReminderPatientTypeFilter((prev) =>
                                  prev.filter((t) => t !== type),
                                );
                              }
                              // Auto-select all filtered patients when filter changes
                              // (handled by useEffect)
                            }}
                          >
                            {type} ({typeCount})
                          </Checkbox>
                        );
                      })}
                    </>
                  )}
                </VStack>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Showing {filteredPatientsWithoutForms.length} of{" "}
                  {allowedPatientsWithoutForms.length} patients
                </Text>
              </FormControl>

              {/* Patient Selection */}
              <FormControl>
                <FormLabel>
                  Select Patients to Send Reminders (
                  {selectedVisibleReminderCount} selected of{" "}
                  {filteredPatientsWithoutForms.length} shown)
                </FormLabel>
                <VStack
                  align="stretch"
                  spacing={3}
                  maxHeight="400px"
                  overflowY="auto"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  bg="gray.50"
                >
                  {loadingReminders ? (
                    <Center py={8}>
                      <Spinner size="md" mr={2} />
                      <Text>Loading patients...</Text>
                    </Center>
                  ) : filteredPatientsWithoutForms.length === 0 ? (
                    <Center py={8}>
                      <VStack spacing={2}>
                        <Text color="gray.500" fontWeight="semibold">
                          {patientsWithoutForms.length === 0
                            ? "No patients found without forms"
                            : manualReminderPatientTypeFilter.length > 0
                              ? `No patients found with selected type(s): ${manualReminderPatientTypeFilter.join(", ")}`
                              : "No patients found"}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          {patientsWithoutForms.length === 0
                            ? reminderStatus.total_patients > 0
                              ? `All ${reminderStatus.total_patients} patients have completed their forms.`
                              : "No patients scheduled for this date."
                            : `Try selecting different patient type filter(s).`}
                        </Text>
                      </VStack>
                    </Center>
                  ) : (
                    reminderPatientRows.map(
                      ({
                        patient,
                        patientName,
                        patientType,
                        submissionCount,
                        patientPhone,
                        patientEmail,
                        reminderDisplayTime,
                      }) => (
                          <Box
                            key={patient.id}
                            p={3}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            bg="white"
                          >
                            <HStack justify="space-between">
                              <VStack align="flex-start" spacing={1} flex={1}>
                                <HStack>
                                  <Checkbox
                                    isChecked={
                                      selectedReminderPatients[patient.id] ||
                                      false
                                    }
                                    onChange={(e) => {
                                      setSelectedReminderPatients((prev) => ({
                                        ...prev,
                                        [patient.id]: e.target.checked,
                                      }));
                                    }}
                                  />
                                  <Text fontWeight="bold">{patientName}</Text>
                                  <Text fontSize="sm" color="gray.600">
                                    ({patientType})
                                  </Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                  Time: {reminderDisplayTime} | Submissions:{" "}
                                  {submissionCount}
                                </Text>
                                <HStack spacing={4} flexWrap="wrap">
                                  {patientPhone && (
                                    <Text fontSize="sm" color="blue.600">
                                      📞 {patientPhone}
                                      <PhoneStatusBadge phone={patientPhone} compact ml={2} />
                                    </Text>
                                  )}
                                  {patientEmail && (
                                    <Text fontSize="sm" color="green.600">
                                      📧 {patientEmail}
                                    </Text>
                                  )}
                                  {!patientPhone && !patientEmail && (
                                    <Text
                                      fontSize="xs"
                                      color="orange.600"
                                      fontStyle="italic"
                                    >
                                      No contact information
                                    </Text>
                                  )}
                                </HStack>
                              </VStack>
                              {!patient.has_submissions && (
                                <Text
                                  fontSize="xs"
                                  color="red.600"
                                  fontWeight="bold"
                                  bg="red.100"
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                  whiteSpace="nowrap"
                                >
                                  NO SUBMISSION
                                </Text>
                              )}
                            </HStack>
                          </Box>
                      ),
                    )
                  )}
                </VStack>
              </FormControl>

              {/* Message Template Selection */}
              <FormControl>
                <FormLabel>Message Template</FormLabel>
                <Select
                  value={reminderMessageTemplate}
                  onChange={(e) => setReminderMessageTemplate(e.target.value)}
                >
                  <option value="default">Default Reminder</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={() => {
                  // Select all filtered patients
                  const allSelection = {};
                  filteredPatientsWithoutForms.forEach((patient) => {
                    allSelection[patient.id] = true;
                  });
                  setSelectedReminderPatients(allSelection);
                }}
              >
                Select All ({filteredPatientsWithoutForms.length})
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Clear all selections
                  setSelectedReminderPatients({});
                }}
              >
                Clear All
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSendReminders}
                isLoading={isSendingReminders}
                loadingText="Sending..."
                isDisabled={
                  isSendingReminders ||
                  selectedVisibleReminderCount === 0 ||
                  (!manualReminderContactMethods.phone &&
                    !manualReminderContactMethods.email)
                }
              >
                {manualScheduleReminder
                  ? "Schedule Reminders"
                  : "Send Reminders"}
              </Button>
              <Button
                variant="outline"
                onClick={handlePreviewReminderMessage}
                isDisabled={filteredPatientsWithoutForms.length === 0}
              >
                Preview Message
              </Button>
              <Button variant="ghost" onClick={onReminderModalClose}>
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
              </TabPanel>
              <TabPanel p={0}>
                <ConfirmationRemindersPanel
                  schema={supabaseSchema || "public"}
                  date={selectedDate}
                  patients={patients}
                  viewerTimezone={reminderViewerTimezone}
                  onClose={onReminderModalClose}
                  refreshKey={confirmationPanelRefresh}
                  onSent={async () => {
                    await fetchPatients();
                    // SchedulesTable owns the Status badge's "no auto" grading; make it reload.
                    window.dispatchEvent(new CustomEvent("patientDataRefresh"));
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalContent>
      </Modal>
      <TriggerPreviewDialog
        isOpen={Boolean(triggerPreview.kind)}
        onClose={closeTriggerPreview}
        title={
          triggerPreview.kind === "confirmations"
            ? "Trigger auto-confirmations?"
            : "Trigger auto-reminders?"
        }
        loading={triggerPreview.loading}
        error={triggerPreview.error}
        preview={triggerPreview.preview}
        doLabel={triggerPreview.kind === "confirmations" ? "Texts to queue" : "Reminders to schedule"}
        skipLabel={triggerPreview.kind === "confirmations" ? "Queued texts to cancel" : "Patients to skip"}
        proceedLabel={triggerPreview.kind === "confirmations" ? "Queue confirmations" : "Schedule reminders"}
        isProceeding={triggerPreview.kind === "confirmations" ? isTriggeringAutoConfirmations : isTriggeringAutoReminders}
        onProceed={proceedWithTrigger}
      />

      {/* Preview Reminder Message Modal */}
      <Modal
        isOpen={isPreviewReminderModalOpen}
        onClose={onPreviewReminderModalClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Message Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              whiteSpace="pre-wrap"
              fontFamily="sans-serif"
              fontSize="sm"
              maxHeight="400px"
              overflowY="auto"
              border="1px"
              borderColor="gray.200"
            >
              {previewReminderMessage || "No message to display."}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onPreviewReminderModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Welcome Message Preview Confirmation Modal */}
      <Modal
        isOpen={isWelcomePreviewOpen}
        onClose={onWelcomePreviewClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>Welcome Message Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <div
              style={{
                overflowY: "scroll",
                maxHeight: "55vh",
                padding: "0 24px 16px 24px",
                WebkitOverflowScrolling: "touch",
              }}
              ref={(node) => {
                if (node) {
                  const styles = window.getComputedStyle(node);
                  console.log("[WelcomePreview] scroll container mounted:", {
                    scrollHeight: node.scrollHeight,
                    clientHeight: node.clientHeight,
                    offsetHeight: node.offsetHeight,
                    overflowY: styles.overflowY,
                    maxHeight: styles.maxHeight,
                  });
                  // Manually handle scroll — a parent is blocking native scroll via preventDefault
                  node.addEventListener(
                    "wheel",
                    (e) => {
                      const maxScrollTop =
                        node.scrollHeight - node.clientHeight;
                      if (maxScrollTop > 0) {
                        e.preventDefault();
                        e.stopPropagation();
                        node.scrollTop = Math.min(
                          Math.max(0, node.scrollTop + e.deltaY),
                          maxScrollTop,
                        );
                        // console.log("[WelcomePreview] manual scroll:", {
                        //   deltaY: e.deltaY,
                        //   newScrollTop: node.scrollTop,
                        //   maxScrollTop,
                        // });
                      }
                    },
                    { passive: false },
                  );
                }
              }}
            >
              <Text mb={3} fontSize="sm" color="gray.600">
                The following welcome message will be sent to the selected
                patients:
              </Text>
              <Box
                p={4}
                bg="gray.50"
                borderRadius="md"
                whiteSpace="pre-wrap"
                fontFamily="sans-serif"
                fontSize="sm"
                border="1px"
                borderColor="gray.200"
              >
                {welcomePreviewMessage || "No message to display."}
              </Box>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              mr={3}
              onClick={confirmSendWelcome}
              isLoading={isSendingWelcome}
              loadingText="Sending..."
            >
              Confirm & Send
            </Button>
            <Button variant="ghost" onClick={onWelcomePreviewClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isComplianceSettingsOpen}
        onClose={onComplianceSettingsClose}
        size={{ base: "full", sm: "lg" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          maxH={{ base: "100dvh", sm: "90vh" }}
          maxW={{ base: "100vw", sm: "lg" }}
          m={{ base: 0, sm: 4 }}
          display="flex"
          flexDirection="column"
        >
          <ModalHeader flexShrink={0} fontSize={{ base: "md", md: "lg" }} pr={12}>
            Compliance monitoring
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={4}
            flex="1"
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <ComplianceAdminSettings
              isOpen={isComplianceSettingsOpen}
              onClose={onComplianceSettingsClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isComplianceDashboardOpen}
        onClose={onComplianceDashboardClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="90vh">
          <ModalHeader>
            Compliance & quality — HEDIS / Stars gaps
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            <ComplianceQualityDashboard />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onComplianceSettingsOpen}>
              Settings
            </Button>
            <Button
              variant="outline"
              mr={2}
              onClick={() => {
                onComplianceDashboardClose();
                navigate("/compliance");
              }}
            >
              Open full page
            </Button>
            <Button colorScheme="blue" onClick={onComplianceDashboardClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Auto Reminder Settings Modal */}
      <AutoReminderSettingsModal
        isOpen={isAutoReminderSettingsOpen}
        onClose={onAutoReminderSettingsClose}
        reminderDaysBeforeAppointment={reminderDaysBeforeAppointment}
        setReminderDaysBeforeAppointment={setReminderDaysBeforeAppointment}
        reminderScheduledTime={reminderScheduledTime}
        setReminderScheduledTime={setReminderScheduledTime}
        reminderPatientTypeFilter={reminderPatientTypeFilter}
        setReminderPatientTypeFilter={setReminderPatientTypeFilter}
        patients={patients}
        onApplySettings={handleApplyAutoReminderSettings}
        scheduleReminderOneDayBefore={scheduleReminderOneDayBefore}
        setScheduleReminderOneDayBefore={setScheduleReminderOneDayBefore}
      />
      {/* Scheduled SMS Modal */}
      <ScheduledSMSModal
        isOpen={isScheduledSMSModalOpen}
        onClose={onScheduledSMSModalClose}
      />
      <Modal
        isOpen={isPatientTableConfirmOpen}
        onClose={closePatientTableConfirm}
        isCentered
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{PATIENT_TABLE_CONFIRMATION.title}</ModalHeader>
          <ModalCloseButton isDisabled={isBuildingPatientTable} />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>{PATIENT_TABLE_CONFIRMATION.body}</Text>
              <Text fontSize="sm" color="gray.600">
                Current schema: {supabaseSchema || "public"}
              </Text>
              {isBuildingPatientTable && (
                <HStack
                  spacing={3}
                  bg="blue.50"
                  borderWidth="1px"
                  borderColor="blue.100"
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <Spinner size="sm" color="blue.500" />
                  <Text fontSize="sm" color="blue.800">
                    {PATIENT_TABLE_PROGRESS_MESSAGES[patientTableProgressIndex]}
                  </Text>
                </HStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter flexWrap="wrap" gap={3}>
            <Button
              variant="outline"
              colorScheme="green"
              mr="auto"
              flexShrink={0}
              whiteSpace="nowrap"
              onClick={() => { closePatientTableConfirm(); setIsPatientImportOpen(true); }}
              isDisabled={isBuildingPatientTable}
            >
              Import from CSV / Excel
            </Button>
            <Button
              variant="ghost"
              flexShrink={0}
              whiteSpace="nowrap"
              onClick={closePatientTableConfirm}
              isDisabled={isBuildingPatientTable}
            >
              {PATIENT_TABLE_CONFIRMATION.cancelLabel}
            </Button>
            <Button
              colorScheme="blue"
              flexShrink={0}
              whiteSpace="nowrap"
              onClick={handleBuildPatientTable}
              isLoading={isBuildingPatientTable}
              loadingText="Building"
            >
              {PATIENT_TABLE_CONFIRMATION.confirmLabel}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <PatientImportModal
        isOpen={isPatientImportOpen}
        onClose={() => setIsPatientImportOpen(false)}
        onImported={() => { if (typeof fetchPatients === "function") fetchPatients(); }}
      />
      {/* Followup Message Modal */}
      <FollowupMessageModal
        isOpen={isFollowupModalOpen}
        onClose={() => {
          setIsFollowupModalOpen(false);
          setFollowupPatientData(null);
        }}
        patientData={followupPatientData}
        schema={userSchema}
      />

      {/* Patient Messaging Modal for viewing unread messages */}
      <PatientMessagingModal
        isOpen={isMessagingModalOpen}
        onClose={() => {
          setIsMessagingModalOpen(false);
          setSelectedPatientForMessaging(null);
          // Refresh unread messages count when modal closes (messages have been marked as reviewed)
          setTimeout(() => {
            fetchTotalUnreadMessages();
          }, 500);
        }}
        patientData={selectedPatientForMessaging}
        smsCache={smsCache}
        unreadCount={selectedPatientForMessaging ? (unreadMessagesList.find(u => u.patientId === selectedPatientForMessaging.id)?.unreadCount || 0) : 0}
        appointmentDate={selectedPatientForMessaging?.date_ || selectedDate}
        onMessagesViewed={() => {
          // Refresh the notification icon when messages are viewed
          fetchTotalUnreadMessages();
        }}
      />
    </Center>
  );
};

export default AdminPage;
